import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Search, MapPin, Trophy, Users, ChevronRight, Globe, Zap, BarChart3, Star, Medal, TrendingUp, ArrowRight, X, Filter, Navigation, Award, Gift, Clock, Calendar, Target, User, Shield, Share2, ExternalLink, AlertCircle, Check, Heart, Flame, Map as MapIcon, LogIn, Mail, Lock, Eye, EyeOff, Menu } from "lucide-react";
import supabase, { fetchAllPages } from "./supabase";
import { computeTeamRating, rankTeams, SCORING_RULES } from "./teamRating";

// 2026-07-25 perf: the landing fires several independent effects that each
// paged the ENTIRE users table (id/first/last) via fetchAllPages — 4 full
// table scans on first load. Memoise it so the fetch happens once per page
// session and every effect shares the same in-flight promise/result.
// 2026-07-30: these reads moved off public.users and onto the public_players
// view. public.users is now venue-scoped by RLS, so an authenticated player
// browsing All4Indoor would only have seen their own centre's players — which
// defeats a cross-venue leaderboard. public_players is a deliberately narrow
// projection (id, name, venue, active, junior, all4indoor_public) with no
// contact details, dates of birth, notes or tokens, so it can stay readable
// across venues. Surfaces that display players publicly must filter on
// all4indoor_public.
let __allUsersBasicPromise = null;
function getAllUsersBasic() {
  if (!__allUsersBasicPromise) {
    __allUsersBasicPromise = fetchAllPages((from, to) =>
      supabase.from('public_players').select('id, first_name, last_name').order('id').range(from, to)
    ).catch((e) => { __allUsersBasicPromise = null; throw e; });
  }
  return __allUsersBasicPromise;
}

// Same treatment for the two other full-table scans the ranking/leaderboard
// effects each repeat: player_match_confirmations and match_attendance
// (basic id columns, unfiltered). Fetched once per page session now.
let __allConfsBasicPromise = null;
function getAllConfirmationsBasic() {
  if (!__allConfsBasicPromise) {
    __allConfsBasicPromise = supabase.from('player_match_confirmations')
      .select('match_id, player_id, team_id')
      // 2026-08-06: supabase-js RESOLVES with { data, error } on a database
      // failure, it does not reject — so this reject handler never ran and a
      // failed read stayed memoised for the whole page session. Check the
      // error and drop the memo so the next consumer retries.
      .then(r => { if (r?.error) __allConfsBasicPromise = null; return r; },
            (e) => { __allConfsBasicPromise = null; throw e; });
  }
  return __allConfsBasicPromise;
}
let __allAttendanceBasicPromise = null;
function getAllAttendanceBasic() {
  if (!__allAttendanceBasicPromise) {
    __allAttendanceBasicPromise = supabase.from('match_attendance')
      .select('match_id, user_id, team_id')
      // 2026-08-06: supabase-js RESOLVES with { data, error } on a database
      // failure, it does not reject — so this reject handler never ran and a
      // failed read stayed memoised for the whole page session. Check the
      // error and drop the memo so the next consumer retries.
      .then(r => { if (r?.error) __allAttendanceBasicPromise = null; return r; },
            (e) => { __allAttendanceBasicPromise = null; throw e; });
  }
  return __allAttendanceBasicPromise;
}

// ═══════════════════════════════════════════════════════════════════════════
// Skeleton — shimmer placeholder while live data loads. Width/height in px.
// ═══════════════════════════════════════════════════════════════════════════
function Skeleton({ w = "100%", h = 16, r = 8, style = {} }) {
  return <div className="a4i-skeleton" style={{ width: w, height: h, borderRadius: r, ...style }} />;
}
function SkeletonRow({ count = 5, h = 56, gap = 8 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap }}>
      {Array.from({ length: count }).map((_, i) => <Skeleton key={i} h={h} />)}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// H2H PLAYER SLOT — empty or filled card for one side of a head-to-head
// ═══════════════════════════════════════════════════════════════════════════
function H2HPlayerSlot({ side, player, winsCount, oppWinsCount, color, altColor, onPick, onClear, badges }) {
  const isWinning = winsCount > oppWinsCount;
  const isLosing = winsCount < oppWinsCount;
  const verdictColor = isWinning ? "#10b981" : isLosing ? "#e74c3c" : "#fbbf24";
  if (!player) {
    return (
      <button onClick={onPick}
        style={{ background: "#101319", border: `2px dashed ${color}`, borderRadius: 14, padding: "32px 18px", textAlign: "center", cursor: "pointer", color, fontFamily: "inherit", transition: "transform 0.15s, box-shadow 0.15s" }}
        onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 6px 16px ${color}33`; }}
        onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}>
        <div style={{ fontSize: "1.4rem", marginBottom: 8 }}>＋</div>
        <div style={{ fontSize: "0.95rem", fontWeight: 800 }}>Pick player {side === 'a' ? 'A' : 'B'}</div>
        <div style={{ fontSize: "0.75rem", color: "#9aa0ab", marginTop: 4 }}>Search any registered player</div>
      </button>
    );
  }
  return (
    <div style={{ background: "#101319", border: `2px solid ${color}`, borderRadius: 14, padding: "18px 16px", textAlign: "center", position: "relative" }}>
      <button onClick={onClear}
        style={{ position: "absolute", top: 8, right: 8, background: "transparent", border: "none", cursor: "pointer", color: "#9aa0ab", fontSize: "1rem", padding: 4, borderRadius: 6 }}
        title="Clear"></button>
      <button onClick={onPick}
        style={{ background: "transparent", border: "none", cursor: "pointer", display: "block", width: "100%" }}
        title="Change player">
        <div style={{ width: 72, height: 72, borderRadius: "50%", background: `linear-gradient(135deg, ${color}, ${altColor})`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "1.4rem", fontWeight: 800, margin: "0 auto 10px" }}>{player.avatar}</div>
        <div style={{ fontWeight: 800, fontSize: "1.1rem", color: "#f4f5f7" }}>{player.name}</div>
        <div style={{ fontSize: "0.78rem", color: "#9aa0ab", marginTop: 2 }}>
          {player.sports?.length ? player.sports.slice(0, 3).join(' · ') : 'Wildcatz Indoor Sports'}
        </div>
      </button>
      {badges?.rarest && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", marginTop: 8, borderRadius: 12, background: "rgba(245,158,11,0.08)", border: "1px solid #fed7aa", fontSize: "0.7rem", fontWeight: 700, color: "#c2410c" }}>
          {badges.rarest.emoji} {badges.rarest.achievement_name}
        </div>
      )}
      <div style={{ fontSize: "1.6rem", fontWeight: 900, color: verdictColor, marginTop: 10 }}>
        {winsCount} stats won
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PLAYER SEARCH MODAL — autocomplete picker over livePlayerIndex
// Reusable: used by H2H slot buttons + (future) global nav search
// ═══════════════════════════════════════════════════════════════════════════
// PLAYER + TEAM SEARCH MODAL — autocomplete picker over livePlayerIndex
// AND liveTeamIndex (when supplied). Picking a player calls onPickPlayer;
// picking a team calls onPickTeam. Used by the global nav search and (for
// player-only contexts) by the H2H picker which passes no teamIndex.
// 2026-08-06: the design system. See tokens.js for why it exists and what was
// measured before it did. Sections adopt it as they are converted; nothing
// breaks in the ones that haven't been.
import { C, R, NUM, M, podium } from "./tokens";

function PlayerSearchModal({ title, playerIndex, teamIndex, excludeIds = [], onPick, onPickPlayer, onPickTeam, onClose }) {
  const [q, setQ] = useState("");
  const allPlayers = useMemo(() => Object.values(playerIndex || {})
    .filter(p => !excludeIds.includes(p.id))
    .sort((a, b) => (b.stats?.games || 0) - (a.stats?.games || 0)), [playerIndex, excludeIds]);
  const allTeams = useMemo(() => Object.values(teamIndex || {})
    .sort((a, b) => a.name.localeCompare(b.name)), [teamIndex]);
  const term = q.trim().toLowerCase();
  const filteredPlayers = useMemo(() => {
    if (!term) return allPlayers.slice(0, 25);
    return allPlayers.filter(p => p.name.toLowerCase().includes(term)).slice(0, 40);
  }, [allPlayers, term]);
  const filteredTeams = useMemo(() => {
    if (!teamIndex) return [];
    if (!term) return allTeams.slice(0, 15);
    return allTeams.filter(t => t.name.toLowerCase().includes(term)).slice(0, 30);
  }, [allTeams, term, teamIndex]);
  // Picking handlers — onPickPlayer/onPickTeam preferred; fall back to the
  // legacy onPick for callers (like H2H) that only handle players.
  const pickPlayer = (id) => { (onPickPlayer || onPick)?.(id); };
  const pickTeam = (id) => { onPickTeam?.(id); };
  // Esc to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(15, 18, 28, 0.6)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "60px 20px", zIndex: 1000, backdropFilter: "blur(6px)" }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "#101319", borderRadius: 16, width: "100%", maxWidth: 520, maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ padding: "18px 20px", borderBottom: "1px solid #232a35", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontWeight: 800, fontSize: "1rem" }}>{title}</div>
          <button onClick={onClose} aria-label="Close" title="Close"
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "#9aa0ab", padding: "2px 8px", borderRadius: 6, fontSize: "1.2rem", lineHeight: 1, fontFamily: "inherit" }}>&times;</button>
        </div>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #232a35" }}>
          <input autoFocus value={q} onChange={e => setQ(e.target.value)}
            placeholder={teamIndex ? "Search players or teams…" : "Type a name…"}
            style={{ width: "100%", padding: "11px 14px", border: "1px solid #2c3440", borderRadius: 10, fontSize: "0.95rem", fontFamily: "inherit", boxSizing: "border-box", outline: "none", background: "#171c24", color: "#f4f5f7" }} />
        </div>
        <div style={{ overflowY: "auto", flex: 1, padding: "8px 0" }}>
          {filteredPlayers.length === 0 && filteredTeams.length === 0 ? (
            <div style={{ padding: "30px 20px", textAlign: "center", color: "#9aa0ab", fontSize: "0.9rem" }}>
              {q ? `No matches for "${q}"` : 'Start typing to search'}
            </div>
          ) : (
            <>
              {/* TEAMS section — only when teamIndex is provided */}
              {filteredTeams.length > 0 && (
                <>
                  <div style={{ padding: "10px 20px 4px", fontSize: "0.65rem", fontWeight: 800, letterSpacing: 2, color: "#9aa0ab", textTransform: "uppercase" }}>Teams · {filteredTeams.length}</div>
                  {filteredTeams.map(t => (
                    <button key={'team-' + t.id} onClick={() => pickTeam(t.id)}
                      style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 20px", width: "100%", background: "transparent", border: "none", cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "background 0.1s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#1b212b"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <div style={{ width: 36, height: 36, borderRadius: 9, background: "linear-gradient(135deg, #fbbf24, #f59e0b)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "0.78rem", flexShrink: 0 }}>{t.avatar}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#f4f5f7", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {t.name}
                          <span style={{ marginLeft: 6, fontSize: "0.55rem", fontWeight: 800, color: "#fbbf24", background: "rgba(251,191,36,0.18)", padding: "2px 6px", borderRadius: 3, letterSpacing: 1, textTransform: "uppercase", verticalAlign: "middle" }}>Team</span>
                        </div>
                        <div style={{ fontSize: "0.72rem", color: "#9aa0ab" }}>
                          {t.sport || 'Sport'}{t.comp ? ` · ${t.comp}` : ''}
                        </div>
                      </div>
                    </button>
                  ))}
                </>
              )}
              {/* PLAYERS section */}
              {filteredPlayers.length > 0 && (
                <>
                  {filteredTeams.length > 0 && (
                    <div style={{ padding: "10px 20px 4px", fontSize: "0.65rem", fontWeight: 800, letterSpacing: 2, color: "#9aa0ab", textTransform: "uppercase" }}>Players · {filteredPlayers.length}</div>
                  )}
                  {filteredPlayers.map(p => (
                    <button key={'player-' + p.id} onClick={() => pickPlayer(p.id)}
                      style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 20px", width: "100%", background: "transparent", border: "none", cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "background 0.1s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#1b212b"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg, #ff2e17, #ff5a45)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "0.85rem", flexShrink: 0 }}>{p.avatar}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#f4f5f7", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                        <div style={{ fontSize: "0.72rem", color: "#9aa0ab" }}>
                          {p.stats?.games || 0} games · {p.sports?.length ? p.sports.slice(0, 2).join(', ') : 'No sport data'}
                        </div>
                      </div>
                    </button>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MOCK DATA (replaced with Supabase API calls in production)
// ══════════════════════════════════════════════════════════════

const VENUES = [
  { id: 1, name: "Wildcatz Indoor Sports", city: "Townsville", country: "Australia", state: "QLD", sports: ["Soccer", "Netball", "Volleyball", "Cricket"], players: 998, teams: 244, img: "https://images.unsplash.com/photo-1577223625816-7546f13df25d?w=400&h=250&fit=crop", logo: "/wildcatz-logo.png", brandColor: "#0c1215", brandAccent: "#ff2e17", featured: true, lat: -19.26, lng: 146.80, wisa: true, website: "https://www.wildcatzindoorsports.com.au" },
];

// Non-WISA venues — these show empty data and "Request This Venue" buttons
const NON_WISA_VENUES = [];

// LEADERBOARD constant deleted — was hardcoded fake numbers, no longer referenced anywhere.

// (Dead hardcoded demo blocks removed — DEMO_PROFILE, ALL_BADGES, TEAMS,
//  SPONSORED_PRIZES, PRIZES_COMING_SOON, MATCH_REPORTS, PLAYER_STATS,
//  ACTIVITY_FEED, H2H_PLAYERS, MILESTONES, SEASON_AWARDS — all replaced by
//  live Supabase data hooks. Kept VENUE_REVIEWS / VENUE_SUCCESS_STORIES /
//  WEEKLY_CHALLENGES / WEEKLY_WRAP / REACTION_OPTIONS / RANK_TIERS — still used.)

// ── Venue Reviews (WISA venues only) ─────────────────────────
// ── Venue reviews ────────────────────────────────────────────────────────
// 2026-07-27 (Jake): three testimonials used to live here — "Matt W.",
// "Emma W.", "Dan S." with star ratings and dates, rendered on the venue page
// as though they were real reviews left by real people. Nothing in the system
// produced them and I could not confirm they were ever said. An empty object
// renders nothing, which is the honest state until reviews come from a place
// that can be pointed at. If they ARE real, put them back — this is a data
// literal, not logic.
const VENUE_REVIEWS = {};

// ── Venue Success Stories ────────────────────────────────────
// ── What one venue's data actually looks like ────────────────────────────
// 2026-07-27 (Jake): this used to be VENUE_SUCCESS_STORIES — a before/after
// table under the heading "Real results from venues that joined WISA" claiming
// Wildcatz went 42 -> 67 weekly bookings, 310 -> 847 active members, 54% -> 82%
// retention, with a quote attributed to "Venue Manager" about players driving
// 45 minutes. None of it was sourced from anything, and there is no second
// venue to have "results" from. It is now a description of what is on the
// system, which is verifiable and is a better argument anyway.
//
// Keep these in step with the database, or delete the block. Counts as at
// 2026-07-27: 2,155 player roles, 254 teams, 848 completed matches.
const VENUE_ON_SYSTEM = {
  venue: "Wildcatz Indoor Sports", city: "Townsville, QLD", since: "2024",
  facts: [
    { label: "Players on the system", value: "2,100+" },
    { label: "Teams across four sports", value: "250+" },
    { label: "Matches scored and archived", value: "840+" },
  ],
  note: "Soccer, netball, volleyball and indoor cricket — every fixture scored on WISA, every season kept.",
};

// ── Weekly Challenges ────────────────────────────────────────
const WEEKLY_CHALLENGES = [
  { id: 1, title: "Goal Machine", desc: "Score 5 goals this week across any WISA venue", icon: "", reward: "50 XP + Limited Badge", progress: 3, target: 5, sport: "Soccer", expires: "3 days" },
  { id: 2, title: "Road Trip", desc: "Play at 2 different WISA venues this week", icon: "", reward: "75 XP + Explorer Badge", progress: 1, target: 2, sport: null, expires: "3 days" },
  { id: 3, title: "Win Streak", desc: "Win 3 games in a row", icon: "", reward: "100 XP", progress: 2, target: 3, sport: null, expires: "3 days" },
  { id: 4, title: "Assist King", desc: "Get 4 assists in a single game", icon: "", reward: "60 XP + Playmaker Badge", progress: 0, target: 1, sport: "Soccer", expires: "3 days" },
  { id: 5, title: "Iron Will", desc: "Play every day this week (Mon–Sun)", icon: "", reward: "150 XP + Iron Badge", progress: 4, target: 7, sport: null, expires: "3 days" },
];

// ── Points-Based Ranking System ──────────────────────────────
// Points: 3 per win, 1 per draw, 1 per game played (participation)
// Rep competitions DO NOT count. Minimum 50 games to qualify for leaderboards.
const RANK_TIERS = [
  { name: "Starter", min: 0, max: 199, color: "#94a3b8", icon: "" },
  { name: "Bronze", min: 200, max: 399, color: "#CD7F32", icon: "" },
  { name: "Silver", min: 400, max: 599, color: "#C0C0C0", icon: "" },
  { name: "Gold", min: 600, max: 899, color: "#FFD700", icon: "" },
  { name: "Platinum", min: 900, max: 1199, color: "#00CED1", icon: "" },
  { name: "Diamond", min: 1200, max: 1599, color: "#B9F2FF", icon: "" },
  { name: "Champion", min: 1600, max: 99999, color: "#FF4500", icon: "" },
];
// pts = (wins × 3) + (draws × 1) + (games × 1)  |  rep games excluded
// (Hardcoded rankedPlayers removed — superseded by liveRankedPlayers live hook)

// ── Post-Game Reactions (positive only, no text) ─────────────
const REACTION_OPTIONS = [
  { emoji: "", label: "On Fire" },
  { emoji: "", label: "Beast Mode" },
  { emoji: "", label: "Clinical" },
  { emoji: "", label: "Champion" },
  { emoji: "", label: "Star Player" },
  { emoji: "", label: "Team Player" },
  { emoji: "", label: "Great Game" },
  { emoji: "", label: "Unstoppable" },
];

// ── Weekly Wrap-Up (demo for Jake R.) ────────────────────────
const WEEKLY_WRAP = {
  player: "Jake R.",
  period: "Apr 4 – Apr 10, 2026",
  games: 3, wins: 2, goals: 5, assists: 2,
  leaderboardChange: +12,
  rivalUpdate: "Chris D. played 4 games this week — he's closing the gap on your rivalry (8-6)",
  milestoneNear: "You're 13 games from 200 career games!",
  challengeProgress: "Goal Machine: 3/5 goals — 2 more to complete",
  ptsEarned: 13, // 2 wins (6) + 0 draws (0) + 3 games (3) + milestones (4)
  totalPts: 710,
  streak: "7 game win streak — your longest ever was 14",
};

// VENUE_POWER_RANKINGS deleted — fake "96.2 Power Score" replaced by real rankings later.

const ALL_SPORTS = [...new Set(VENUES.flatMap(v => v.sports))].sort();
const ALL_COUNTRIES = [...new Set(VENUES.map(v => v.country))].sort();

// ── Helpers ──────────────────────────────────────────────────
function getDistance(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
function formatDistance(km) {
  if (km < 1) return `${Math.round(km * 1000)}m`;
  if (km < 100) return `${km.toFixed(1)}km`;
  return `${Math.round(km).toLocaleString()}km`;
}

const P = "#ff2e17";
const sportEmoji = { Soccer: "", Netball: "", Basketball: "", Cricket: "", Volleyball: "", Futsal: "", Badminton: "", Padel: "", "Table Tennis": "" };

// ══════════════════════════════════════════════════════════════
// COMPONENTS
// ══════════════════════════════════════════════════════════════

// 2026-05-31: tiny count-up hook for the home stats strip. Numbers start
// at 0 and animate up to `target` on mount — a small delight that makes
// the page feel alive on first paint. Non-numeric targets are passed
// through unchanged so toLocaleString'd strings just render normally.
function useCountUp(target, duration = 1100) {
  const numeric = typeof target === 'number'
    ? target
    : (typeof target === 'string' ? parseInt(String(target).replace(/[^\d]/g, ''), 10) : NaN);
  const [n, setN] = useState(Number.isFinite(numeric) ? 0 : null);
  useEffect(() => {
    if (!Number.isFinite(numeric)) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic — fast then slow, feels snappier than linear
      const eased = 1 - Math.pow(1 - t, 3);
      setN(Math.round(numeric * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [numeric, duration]);
  if (!Number.isFinite(numeric)) return target;
  return n.toLocaleString();
}

function StatCard({ icon, value, label, accent, last }) {
  // 2026-07-27: a non-numeric value used to be stripped to digits and fall back
  // to `|| 0`, so the "—" placeholder for a stat that hasn't loaded rendered as
  // a confident "0". Zero is a claim; a dash is an absence. Pass anything
  // non-numeric straight through and skip the count-up.
  const parsed = typeof value === "number" ? value : parseInt(String(value).replace(/[^0-9]/g, ""), 10);
  const isNumeric = Number.isFinite(parsed);
  const display = useCountUp(isNumeric ? parsed : 0);
  return (
    <div style={{ textAlign: "center", padding: "22px 16px", borderRight: last ? "none" : "1px solid rgba(255,255,255,0.08)" }}>
      <div style={{ fontFamily: "'Archivo', sans-serif", fontSize: "2.5rem", fontWeight: 800, color: accent ? "#00e676" : "#fff", lineHeight: 1 }}>{!isNumeric ? value : (typeof display === "number" ? display.toLocaleString() : display)}</div>
      <div style={{ fontFamily: "'Archivo', sans-serif", fontSize: "0.72rem", color: "#9298a3", marginTop: 7, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase" }}>{label}</div>
    </div>
  );
}

// 2026-05-31: Did You Know strip — derives facts from already-loaded
// leaderboard state and rotates through them every ~5 seconds. Click
// jumps to that player's profile. Renders nothing until at least one
// fact is computable, so it stays out of the way on a cold cache.
function DidYouKnowStrip({ sportLb, ranked, poTW, onPickPlayer }) {
  const facts = useMemo(() => {
    const list = [];
    const sports = sportLb || {};
    const pick = (arr) => (Array.isArray(arr) && arr[0]) || null;
    const cricketTop = pick(sports.cricket);
    if (cricketTop?.name && (cricketTop.contribution || 0) > 0) {
      list.push({ id: cricketTop.id, emoji: '', text: <><strong>{cricketTop.name}</strong> is leading the cricket charts this fortnight with <strong>{Math.round(cricketTop.contribution)}</strong> contribution.</> });
    }
    const netTop = pick(sports.netball);
    if (netTop?.name && (netTop.goals || 0) > 0) {
      list.push({ id: netTop.id, emoji: '', text: <><strong>{netTop.name}</strong> has put away <strong>{netTop.goals}</strong> netball goals in the last 14 days — top of the board.</> });
    }
    const soccerTop = pick(sports.soccer);
    if (soccerTop?.name && (soccerTop.goals || 0) > 0) {
      list.push({ id: soccerTop.id, emoji: '', text: <><strong>{soccerTop.name}</strong> sits #1 for soccer goals with <strong>{soccerTop.goals}</strong> in the past two weeks.</> });
    }
    const vbTop = pick(sports.volleyball);
    if (vbTop?.name && (vbTop.wins || 0) > 0) {
      list.push({ id: vbTop.id, emoji: '', text: <><strong>{vbTop.name}</strong> is on a volleyball tear — <strong>{vbTop.wins}</strong> wins this fortnight.</> });
    }
    if (Array.isArray(ranked) && ranked.length > 0 && ranked[0].name) {
      const top = ranked[0];
      const gap = ranked[1] ? top.pts - ranked[1].pts : null;
      list.push({
        id: top.id, emoji: '',
        text: gap && gap > 0
          ? <><strong>{top.name}</strong> sits #1 overall — <strong>{gap}</strong> points clear of second place.</>
          : <><strong>{top.name}</strong> is currently the #1 player at Wildcatz.</>,
      });
    }
    if (poTW && !poTW.empty && poTW.name) {
      list.push({ id: poTW.id, emoji: '', text: <><strong>{poTW.name}</strong> is this week's Player of the Week with <strong>{poTW.wins}</strong> win{poTW.wins === 1 ? '' : 's'} from {poTW.games}.</> });
    }
    return list;
  }, [sportLb, ranked, poTW]);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (facts.length <= 1) return;
    const id = setInterval(() => setIdx(i => (i + 1) % facts.length), 5200);
    return () => clearInterval(id);
  }, [facts.length]);
  if (facts.length === 0) return null;
  const f = facts[idx % facts.length];
  return (
    <section style={{ maxWidth: 1200, margin: "0 auto", padding: "20px 32px 0" }}>
      <div
        onClick={() => f.id && onPickPlayer && onPickPlayer(f.id)}
        style={{ display: "flex", alignItems: "center", gap: 16, background: "#101319", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 12, padding: "16px 20px", cursor: f.id ? "pointer" : "default", transition: "background 0.2s" }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ff2e17", flexShrink: 0, boxShadow: "0 0 0 4px rgba(255,46,23,0.15)" }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Archivo', sans-serif", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color: "#ff6a5f", marginBottom: 3 }}>Did you know?</div>
          <div style={{ fontSize: "0.95rem", color: "#d5d8dd", lineHeight: 1.45 }}>{f.text}</div>
        </div>
        {facts.length > 1 && (
          <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
            {facts.map((_, i) => (
              <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: i === (idx % facts.length) ? "#ff2e17" : "rgba(255,255,255,0.2)" }} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// 2026-05-31: derive a quirky player title from their career stats. The
// most impressive applicable title wins (priority order in the list).
// Returns null if no threshold is met — empty looks cleaner than a
// generic "Player" for everyone. Shown as a small chip below the name
// on the player profile.
function playerTitle(stats, matchStats, eloRow) {
  if (!stats) return null;
  const totalGoals = stats.goals || 0;
  const soccerGoals = (stats.goalsBySport?.soccer || 0) + (stats.goalsBySport?.futsal || 0);
  const netballGoals = stats.goalsBySport?.netball || 0;
  const hatTricks = stats.hatTricks || 0;
  const runs = stats.runs || 0;
  const wickets = stats.wickets || 0;
  const sixes = stats.sixes || 0;
  const games = matchStats?.games || 0;
  const winRate = matchStats?.winRate || 0;
  const elo = eloRow?.elo || 0;
  // Title list, ranked from most-impressive-first. First match wins.
  const titles = [
    [hatTricks >= 10,             '', 'Hat-trick Hero'],
    [runs >= 2000,                '', 'Run Machine'],
    [wickets >= 100,              '', 'Wicket Demon'],
    [netballGoals >= 200,         '', 'Sharpshooter'],
    [soccerGoals >= 50,           '', 'Goal Machine'],
    [sixes >= 30,                 '', 'Six Hitter'],
    [elo >= 1300 && games >= 10,  '', 'Elite'],
    [hatTricks >= 3,              '', 'Hat-trick Maker'],
    [runs >= 500,                 '', 'Top Order'],
    [wickets >= 30,               '', 'Wicket Taker'],
    [winRate >= 75 && games >= 15,'', 'Winner'],
    [games >= 100,                '', 'Iron Man'],
    [netballGoals >= 50,          '', 'Goal Shooter'],
    [soccerGoals >= 15,           '', 'Striker'],
    [sixes >= 10,                 '', 'Big Hitter'],
    [games >= 50,                 '', 'Regular'],
    [games >= 20,                 '', 'Rookie of Note'],
  ];
  const hit = titles.find(t => t[0]);
  return hit ? { icon: hit[1], label: hit[2] } : null;
}

// 2026-05-31: pick the player's primary sport from their stats and map it
// to a brand-aligned gradient. Used to colour the profile avatar so a
// cricketer's circle looks different from a netballer's — small but very
// human touch that makes the page feel personalised.
const SPORT_GRADIENTS = {
  cricket:    'linear-gradient(135deg, #14b8a6, #0d9488)',     // teal
  netball:    'linear-gradient(135deg, #ec4899, #be185d)',     // pink
  soccer:     'linear-gradient(135deg, #3b82f6, #1e40af)',     // blue
  futsal:     'linear-gradient(135deg, #3b82f6, #1e40af)',     // blue
  volleyball: 'linear-gradient(135deg, #f59e0b, #d97706)',     // amber
  basketball: 'linear-gradient(135deg, #f97316, #c2410c)',     // orange
};
// 2026-05-31: auto-generated team title — punchy nickname based on
// win-loss record + games. Same priority-list pattern as playerTitle.
// Surfaces on the team profile header as a small chip under the name.
function teamTitle(record) {
  if (!record) return null;
  const wins = record.wins || 0;
  const losses = record.losses || 0;
  const draws = record.draws || 0;
  const games = wins + losses + draws;
  if (games < 3) return null;
  const winPct = wins / games;
  const titles = [
    [games >= 30 && winPct >= 0.85, '', 'Dynasty'],
    [games >= 15 && winPct >= 0.80, '', 'Title Contender'],
    [games >= 10 && winPct >= 0.70, '', 'Hot Hand'],
    [games >= 15 && winPct >= 0.55, '', 'Heavy Hitters'],
    [games >= 10 && winPct >= 0.50, '', 'Even Keel'],
    [games >= 20,                    '', 'Veterans'],
    [games >= 10 && winPct < 0.30,   '', 'Tough Out'],
    [games >= 5,                     '', 'Rising'],
  ];
  const hit = titles.find(t => t[0]);
  return hit ? { icon: hit[1], label: hit[2] } : null;
}

function primarySportSlug(stats, sports) {
  if (!stats && !sports) return null;
  // Prefer the sport with the highest meaningful stat — runs for cricket,
  // goals for soccer/netball, wins for volleyball. Falls back to first
  // sport listed if no stats yet.
  const buckets = {
    cricket:    (stats?.runsBySport?.cricket || 0) + (stats?.wicketsBySport?.cricket || 0) * 10,
    netball:    stats?.goalsBySport?.netball || 0,
    soccer:     (stats?.goalsBySport?.soccer || 0) + (stats?.goalsBySport?.futsal || 0),
    volleyball: 0, // no per-sport bucket
  };
  let best = null, bestScore = 0;
  for (const [k, v] of Object.entries(buckets)) if (v > bestScore) { best = k; bestScore = v; }
  if (best) return best;
  if (Array.isArray(sports) && sports.length > 0) {
    const s = sports[0].toLowerCase().replace(/^indoor\s+/, '').replace(/\s+/g, '');
    return SPORT_GRADIENTS[s] ? s : null;
  }
  return null;
}

function Badge({ badge, size = "normal" }) {
  const s = size === "small";
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: s ? 4 : 6,
      background: badge.earned ? "#ecfdf5" : "#f5f5f7", border: badge.earned ? "1px solid #a7f3d0" : "1px solid #e0e0ea",
      borderRadius: 10, padding: s ? "3px 8px" : "5px 12px", opacity: badge.earned ? 1 : 0.5,
    }}>
      <span style={{ fontSize: s ? "0.8rem" : "1rem" }}>{badge.icon}</span>
      <span style={{ fontSize: s ? "0.65rem" : "0.75rem", fontWeight: 700, color: badge.earned ? P : "#9aa0ab" }}>{badge.name}</span>
    </div>
  );
}

function WisaBadge() {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "linear-gradient(135deg, #ff2e17, #ff6a5f)", color: "#fff", fontSize: "0.62rem", fontWeight: 700, padding: "3px 8px", borderRadius: 8, letterSpacing: "0.5px" }}>
      <Shield size={10} /> WISA VERIFIED
    </div>
  );
}

function VenueCard({ venue, onClick, distance }) {
  const isWisa = venue.wisa;
  return (
    <div onClick={onClick} style={{
      background: "#101319", borderRadius: 16, overflow: "hidden", cursor: "pointer",
      boxShadow: "0 20px 50px -25px rgba(0,0,0,0.8)", transition: "all 0.25s ease",
      border: isWisa ? "1px solid rgba(255,255,255,0.09)" : "1px dashed rgba(255,255,255,0.16)",
      opacity: isWisa ? 1 : 0.85,
    }}
    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 8px 32px rgba(26,138,125,0.12)"; }}
    onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 16px rgba(0,0,0,0.06)"; }}
    >
      <div style={{ position: "relative" }}>
        {venue.logo ? (
          <div style={{ width: "100%", height: 180, background: venue.brandColor || "#0c1215", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 30% 50%, ${venue.brandAccent || "#ff2e17"}22 0%, transparent 60%), radial-gradient(circle at 70% 80%, ${venue.brandAccent || "#ff2e17"}15 0%, transparent 50%)` }} />
            <img src={venue.logo} alt={venue.name} style={{ height: 70, objectFit: "contain", position: "relative", zIndex: 1 }} />
          </div>
        ) : (
          <img src={venue.img} alt={venue.name} style={{ width: "100%", height: 180, objectFit: "cover", filter: isWisa ? "none" : "grayscale(60%)" }} />
        )}
        {isWisa && venue.featured && (
          <div style={{ position: "absolute", top: 12, left: 12, background: "linear-gradient(135deg, #ff2e17, #ff6a5f)", color: "#fff", fontSize: "0.68rem", fontWeight: 700, padding: "4px 10px", borderRadius: 20 }}>Featured</div>
        )}
        {!isWisa && (
          <div style={{ position: "absolute", top: 12, left: 12, background: "rgba(0,0,0,0.7)", color: "#fbbf24", fontSize: "0.68rem", fontWeight: 700, padding: "4px 10px", borderRadius: 20, display: "flex", alignItems: "center", gap: 4 }}>
            <AlertCircle size={11} /> Not on WISA
          </div>
        )}
        {distance != null && (
          <div style={{ position: "absolute", bottom: 12, left: 12, background: "rgba(255,46,23,0.9)", color: "#fff", fontSize: "0.72rem", fontWeight: 700, padding: "4px 12px", borderRadius: 20, display: "flex", alignItems: "center", gap: 4 }}>
            <Navigation size={11} /> {formatDistance(distance)}
          </div>
        )}
      </div>
      <div style={{ padding: "16px 20px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <h3 style={{ fontSize: "1.05rem", fontWeight: 700, color: "#f4f5f7", margin: 0 }}>{venue.name}</h3>
          {isWisa && <WisaBadge />}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, color: "#9aa0ab", fontSize: "0.82rem", marginBottom: 12 }}>
          <MapPin size={14} /> {venue.city}, {venue.country}
        </div>
        {isWisa ? (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
              {venue.sports.slice(0, 3).map(s => (
                <span key={s} style={{ background: "rgba(255,46,23,0.14)", color: "#ff6a5f", fontSize: "0.72rem", fontWeight: 600, padding: "3px 10px", borderRadius: 20 }}>{s}</span>
              ))}
              {venue.sports.length > 3 && <span style={{ background: "#101319", color: "#9aa0ab", fontSize: "0.72rem", fontWeight: 600, padding: "3px 10px", borderRadius: 20 }}>+{venue.sports.length - 3}</span>}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid rgba(255,255,255,0.09)", paddingTop: 12 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "1rem", fontWeight: 700, color: "#f4f5f7" }}>{venue.players.toLocaleString()}</div>
                <div style={{ fontSize: "0.7rem", color: "#9aa0ab" }}>Players</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "1rem", fontWeight: 700, color: "#f4f5f7" }}>{venue.teams}</div>
                <div style={{ fontSize: "0.7rem", color: "#9aa0ab" }}>Teams</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "1rem", fontWeight: 700, color: "#f4f5f7" }}>{venue.sports.length}</div>
                <div style={{ fontSize: "0.7rem", color: "#9aa0ab" }}>Sports</div>
              </div>
            </div>
          </>
        ) : (
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.09)", paddingTop: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "0.78rem", color: "#e74c3c", fontWeight: 600, marginBottom: 2 }}>No stats tracked</div>
                <div style={{ fontSize: "0.72rem", color: "#9aa0ab" }}>Games here don't count towards global leaderboards</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontSize: "0.82rem", color: "#ff6a5f", fontWeight: 700 }}>
                {venue.requests} players requested
              </div>
              <div style={{ background: "rgba(245,158,11,0.16)", color: "#fbbf24", fontSize: "0.72rem", fontWeight: 700, padding: "4px 10px", borderRadius: 8 }}>
                {venue.requests >= 50 ? "High Demand" : `${50 - venue.requests} more to notify`}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SponsorPrizeCard({ prize }) {
  const medals = ["", "", ""];
  const podium = [prize.currentLeader, prize.runnerUp, prize.thirdPlace];
  return (
    <div style={{ background: "#101319", borderRadius: 20, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.08)", border: "1px solid rgba(0,0,0,0.04)" }}>
      <div style={{ background: prize.gradient, padding: "28px", color: "#fff", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -30, right: -30, width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,0.08)" }} />
        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: "2rem" }}>{prize.sponsorLogo}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, opacity: 0.8 }}>Sponsored by {prize.sponsor}</div>
            <div style={{ fontSize: "1.3rem", fontWeight: 800 }}>{prize.prize}</div>
          </div>
          <div style={{ background: "rgba(255,255,255,0.2)", padding: "6px 14px", borderRadius: 20, fontSize: "0.85rem", fontWeight: 800 }}>{prize.prizeValue}</div>
        </div>
        <div style={{ display: "flex", gap: 16, fontSize: "0.8rem", opacity: 0.85, marginTop: 12 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 5 }}><Trophy size={14} /> {prize.category}</span>
          <span style={{ display: "flex", alignItems: "center", gap: 5 }}><Calendar size={14} /> {prize.period}</span>
        </div>
      </div>
      <div style={{ padding: "20px 28px 24px" }}>
        <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#9aa0ab", marginBottom: 14 }}>Current Standings</div>
        {podium.map((p, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", marginBottom: 8,
            background: i === 0 ? `${prize.color}0F` : "#fafafe", borderRadius: 12,
            border: i === 0 ? `1px solid ${prize.color}22` : "1px solid transparent",
          }}>
            <div style={{ fontSize: "1.4rem", width: 32, textAlign: "center" }}>{medals[i]}</div>
            <div style={{ width: 38, height: 38, borderRadius: "50%", background: i === 0 ? prize.gradient : "linear-gradient(135deg, #9aa0ab, #bbb)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "0.72rem", fontWeight: 700 }}>{p.avatar}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>{p.name}</div>
              <div style={{ fontSize: "0.75rem", color: "#9aa0ab" }}>{p.venue} — {p.country}</div>
            </div>
            <div style={{ fontWeight: 800, fontSize: "0.9rem", color: i === 0 ? prize.color : "#9aa0ab" }}>{p.stat}</div>
          </div>
        ))}
        <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px", marginTop: 12, display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ fontSize: "0.75rem", color: "#64748b", display: "flex", alignItems: "center", gap: 6 }}><Shield size={12} /> Only weekly comp games at WISA venues count — rep comps excluded</div>
          {prize.qualifier && <div style={{ fontSize: "0.75rem", color: "#fbbf24", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}><AlertCircle size={12} /> {prize.qualifier}</div>}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN APP
// ══════════════════════════════════════════════════════════════

export default function All4Indoor() {
  const [section, setSection] = useState("home");
  const [search, setSearch] = useState("");
  const [sportFilter, setSportFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [showNonWisa, setShowNonWisa] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [sortByDistance, setSortByDistance] = useState(false);
  const [requestedVenues, setRequestedVenues] = useState({});
  const [rankSearch, setRankSearch] = useState("");
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [matchSportFilter, setMatchSportFilter] = useState("");
  // (h2hPlayer1/h2hPlayer2/followedPlayers/followedTeams/feedFilter state removed —
  // replaced by compareIds / live data. h2hPlayer state was tied to hardcoded mock players.)
  const [selectedAwardSeason, setSelectedAwardSeason] = useState("2025 S2");
  // Player profile overlay — opens when any player name is clicked anywhere on the site
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [tradingCardPlayer, setTradingCardPlayer] = useState(null);
  // "Your Week" wrap — triggered by ?wrap=<playerId> in the URL. Designed
  // to be deep-linked from a Sunday push notification (Spotify-Wrapped
  // style swipeable recap). Hands off to TradingCardModal on the final
  // panel for one-tap sharing.
  const [weekWrapPlayer, setWeekWrapPlayer] = useState(null);
  // Selected match for the recap modal. Driven by clicks on recent-match
  // rows. The modal pulls cricket_scorecards.balls_data + computes the
  // pair-by-pair + bowling breakdown that Spawtz never gave players.
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [selectedMatchData, setSelectedMatchData] = useState(null);
  // Selected team for the team profile modal (?team=<id>). Public,
  // shareable, ranks the roster by avg contribution + shows recent form.
  // The artefact captains forward around to recruit + brag.
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [liveTeamDetail, setLiveTeamDetail] = useState(null);
  // Sport filter for leaderboards (null = all sports)
  const [leaderboardSport, setLeaderboardSport] = useState(null);
  // Team rating scope: 'allTime' (default) | 'season'
  const [teamRatingScope, setTeamRatingScope] = useState('allTime');
  // Home page sport filter — controls which sport-specific leaderboard
  // sections render below the hero. null = all sports.
  const [homeSportFilter, setHomeSportFilter] = useState(null);
  // Team Rankings page: sport filter + sort
  const [teamRankingsSport, setTeamRankingsSport] = useState(null); // null = all sports
  const [teamRankingsScope, setTeamRankingsScope] = useState('allTime');
  // Head-to-head compare — pick 2 or 3 players. 2026-05-31: extended from
  // {a,b} to {a,b,c} so users can throw a 3rd into the mix ("Jake vs Braden
  // vs Mark — who's actually best?"). c is optional; the H2H view collapses
  // back to a 2-column layout when it's null.
  const [compareIds, setCompareIds] = useState({ a: null, b: null, c: null });
  // Which slot's picker is open (null = closed, 'a' / 'b' / 'c' = open for that side)
  const [h2hPickerSide, setH2hPickerSide] = useState(null);
  // Global "Find player" search (nav button) — opens the same PlayerSearchModal
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  // Wrapped recap slide index
  const [wrappedSlide, setWrappedSlide] = useState(0);
  // Mobile detection (CSS-in-JS doesn't do media queries, so we track viewport)
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // 2026-07-25: reveal-on-scroll. Sections start hidden (via .a4i-anim on the
  // root) and fade+rise as they enter the viewport. Re-runs on view change so
  // freshly-mounted sections animate too. In-viewport sections reveal
  // immediately (IntersectionObserver fires on observe), giving the hero a
  // clean entrance. Reduced-motion users get everything shown instantly.
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
    const root = document.querySelector('.a4i-root');
    if (!root) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('a4i-in'); io.unobserve(e.target); } });
    }, { threshold: 0.05, rootMargin: '0px 0px -40px 0px' });
    // Only sections BELOW the current fold start hidden and animate in on
    // scroll. Anything already visible (hero, first sections) is never hidden,
    // so there is zero risk of blank above-the-fold content.
    const t = setTimeout(() => {
      const vh = window.innerHeight || 800;
      root.querySelectorAll('section').forEach((el) => {
        if (el.getBoundingClientRect().top > vh - 40) { el.classList.add('a4i-reveal'); io.observe(el); }
      });
    }, 20);
    return () => { clearTimeout(t); io.disconnect(); };
  }, [section]);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(max-width: 820px)');
    const update = () => setIsMobile(mql.matches);
    update();
    // Modern browsers + Safari fallback
    if (mql.addEventListener) mql.addEventListener('change', update);
    else mql.addListener(update);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', update);
      else mql.removeListener(update);
    };
  }, []);
  // Close mobile menu whenever section changes (user picked an item)
  useEffect(() => { setMobileMenuOpen(false); }, [section]);
  // 2026-05-31: power-user keyboard shortcut — '/' opens the global
  // player search from anywhere on the site, the way Slack/GitHub do.
  // Ignored when the user is already typing in an input/textarea.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      const tag = (t?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || t?.isContentEditable) return;
      e.preventDefault();
      setGlobalSearchOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // 2026-05-31: Konami code easter egg — ↑↑↓↓←→←→BA shows a brief
  // 'Achievement Unlocked' overlay. Pure fun, nothing functional.
  const [konamiHit, setKonamiHit] = useState(false);
  useEffect(() => {
    const seq = ['ArrowUp','ArrowUp','ArrowDown','ArrowDown','ArrowLeft','ArrowRight','ArrowLeft','ArrowRight','b','a'];
    let i = 0;
    const onKey = (e) => {
      const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      if (k === seq[i]) {
        i++;
        if (i === seq.length) {
          i = 0;
          setKonamiHit(true);
          setTimeout(() => setKonamiHit(false), 3500);
        }
      } else if (k === seq[0]) {
        i = 1;
      } else {
        i = 0;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ─── URL deep-linking for player profiles ─────────────────────────────────
  // ?player=<uuid> opens that player's profile modal directly.
  // Lets people share/bookmark a stat page: all4indoor.com/?player=abc-123
  // Also lets share buttons produce real working URLs.
  useEffect(() => {
    // Initial — read ?player= from URL on first mount.
    // React no-ops setState when value is unchanged, so no equality guard needed here.
    const pid = new URLSearchParams(window.location.search).get('player');
    if (pid) setSelectedPlayerId(pid);
    // Also handle ?wrap= for the "Your Week" deep link (Sunday push lands here)
    const wid = new URLSearchParams(window.location.search).get('wrap');
    if (wid) setWeekWrapPlayer(wid);
    // Listen to browser back/forward for ?player + ?wrap changes
    const onPop = () => {
      const np = new URLSearchParams(window.location.search).get('player');
      const nw = new URLSearchParams(window.location.search).get('wrap');
      setSelectedPlayerId(np || null);
      setWeekWrapPlayer(nw || null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  // Push URL changes when selectedPlayerId changes from inside the app
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const current = params.get('player');
    if (selectedPlayerId && current !== selectedPlayerId) {
      params.set('player', selectedPlayerId);
      window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
    } else if (!selectedPlayerId && current) {
      params.delete('player');
      const qs = params.toString();
      window.history.pushState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
    }
  }, [selectedPlayerId]);

  // ─── URL deep-linking for team profiles ─────────────────────────────────
  // ?team=<uuid> opens that team's profile modal. Same pattern as players.
  // The captain shares all4indoor.com/?team=<their-team-id> with potential
  // recruits, sponsors, opposition captains scouting them, etc.
  useEffect(() => {
    const tid = new URLSearchParams(window.location.search).get('team');
    if (tid) setSelectedTeamId(tid);
    const onPop = () => {
      const np = new URLSearchParams(window.location.search).get('team');
      setSelectedTeamId(np || null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const current = params.get('team');
    if (selectedTeamId && current !== selectedTeamId) {
      params.set('team', selectedTeamId);
      window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
    } else if (!selectedTeamId && current) {
      params.delete('team');
      const qs = params.toString();
      window.history.pushState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
    }
  }, [selectedTeamId]);

  // 2026-05-31: ?h2h=<idA>::<idB>[::<idC>] deep-links a head-to-head
  // comparison. Lets you share a link like all4indoor.com/?h2h=A::B and the
  // recipient lands on that exact matchup. Auto-jumps to the H2H section so
  // they don't have to navigate. Supports 2 or 3 ids.
  useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get('h2h');
    if (!raw) return;
    const parts = raw.split('::').filter(Boolean);
    if (parts.length < 2) return;
    setCompareIds({ a: parts[0] || null, b: parts[1] || null, c: parts[2] || null });
    setSection('h2h');
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const current = params.get('h2h');
    const next = [compareIds.a, compareIds.b, compareIds.c].filter(Boolean).join('::');
    if (next && next.includes('::') && next !== current) {
      params.set('h2h', next);
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    } else if (!next.includes('::') && current) {
      params.delete('h2h');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
    }
  }, [compareIds]);
  // Live venue stats — overrides the hardcoded VENUES values for the Wildcatz entry.
  // Fetched once on mount so the public site shows current numbers (players, teams)
  // instead of stale baked-in figures.
  const [liveVenueStats, setLiveVenueStats] = useState(null);
  // Live match reports — replaces hardcoded MATCH_REPORTS with real completed
  // fixtures from the matches table so last night's results actually appear.
  const [liveMatchReports, setLiveMatchReports] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 2026-07-27 (Jake): the 1.75x "marketing multiplier" that used to sit
        // here is GONE. It inflated the public player and team counters so the
        // venue would "read at parity with bigger demo venues" — a polite way
        // of saying the front page overstated itself by 75%. Nothing on this
        // site needs a thumb on the scale; the real numbers are good.
        //
        // Definitions, so they can be checked rather than taken on faith:
        //   players  people holding a player role at a WISA venue   (2,155)
        //   teams    teams not archived                             (254)
        //   matches  completed fixtures with a result recorded      (848)
        // `users where is_active` was the wrong basis — it counted parent
        // accounts and every other venue's users along with the players.
        // 2026-07-27: one RPC, not three table counts. Counting
        // user_venue_roles from here returned 0 — RLS filters every row for
        // anon, so PostgREST answers content-range */0 and the count arrives
        // as a confident zero rather than null. The homepage read "0 PLAYERS"
        // while the admin dashboard read 2,152.
        //
        // public_network_stats() is SECURITY DEFINER, so the public site needs
        // no read access to the membership graph at all, and it uses the same
        // player definition as the admin dashboard — the website and the
        // internal number cannot drift apart.
        const { data: netStats, error: statsErr } = await supabase.rpc('public_network_stats');
        if (cancelled) return;
        if (statsErr || !netStats) {
          console.warn('All4Indoor: network stats unavailable', statsErr);
          setLiveVenueStats(null);   // renders as em dashes, never as zeros
          return;
        }
        setLiveVenueStats({
          players: netStats.players ?? null,
          teams: netStats.teams ?? null,
          matches: netStats.matches ?? null,
          venues: netStats.venues ?? null,
        });
      } catch (e) {
        console.warn('All4Indoor: failed to load live venue stats', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: rows } = await supabase
          .from('matches')
          .select('id, scheduled_at, home_score, away_score, motm_name, status, is_scratch, ' +
                  'home_team:teams!home_team_id(name), away_team:teams!away_team_id(name), ' +
                  'competition:competitions(name, sport:sports(name))')
          .eq('status', 'completed')
          .eq('is_scratch', false)
          .not('home_score', 'is', null)
          .order('scheduled_at', { ascending: false })
          .limit(50);
        if (cancelled || !rows) return;
        const fmtDate = (d) => new Date(d).toLocaleDateString('en-AU', { day:'numeric', month:'short', year:'numeric' });
        const mapped = rows.map((m, i) => {
          // 2026-08-06: this declaration was pasted onto the END of the comment
          // line above it, so it was commented out and the next line threw
          // ReferenceError: rawSport is not defined — on the first row, every
          // time. The try/catch turned that into a console.warn, liveMatchReports
          // stayed null, and Match Reports has been an empty page for everyone.
          // Normalise so the existing sportEmoji + filter UI keeps matching:
          // "Indoor Cricket" -> "Cricket"
          const rawSport = m.competition?.sport?.name || 'Game';
          const sport = rawSport.replace(/^Indoor\s+/i, '');
          const s1 = m.home_score ?? 0, s2 = m.away_score ?? 0;
          const winner = s1 > s2 ? m.home_team?.name : (s2 > s1 ? m.away_team?.name : null);
          const highlight = winner
            ? `${winner} took the win ${Math.max(s1,s2)}-${Math.min(s1,s2)} in ${m.competition?.name || sport}.`
            : `Drawn match in ${m.competition?.name || sport}.`;
          return {
            id: m.id || `live_${i}`,
            sport,
            venue: 'Wildcatz Indoor Sports',
            date: fmtDate(m.scheduled_at),
            team1: m.home_team?.name || 'Home',
            team2: m.away_team?.name || 'Away',
            score1: s1,
            score2: s2,
            mvp: m.motm_name || null,
            highlight,
            status: 'Final',
          };
        });
        setLiveMatchReports(mapped);
      } catch (e) {
        console.warn('All4Indoor: failed to load live match reports', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) { setLocationError("Geolocation not supported"); return; }
    setLocationLoading(true); setLocationError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocationLoading(false); setSortByDistance(true); setSection("venues"); },
      () => { setLocationError("Unable to get location. Please enable location services."); setLocationLoading(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const handleRequestVenue = (venueId) => {
    setRequestedVenues(prev => ({ ...prev, [venueId]: true }));
  };

  const allVenuesList = useMemo(() => {
    return VENUES.map(v => {
      const merged = { ...v, distance: userLocation ? getDistance(userLocation.lat, userLocation.lng, v.lat, v.lng) : null };
      // Override hardcoded counts for our home venue with live Supabase numbers (once loaded)
      if (v.wisa && liveVenueStats) {
        if (typeof liveVenueStats.players === 'number') merged.players = liveVenueStats.players;
        if (typeof liveVenueStats.teams === 'number') merged.teams = liveVenueStats.teams;
      }
      return merged;
    });
  }, [userLocation, liveVenueStats]);

  const filteredVenues = useMemo(() => {
    let result = allVenuesList.filter(v => {
      const m1 = !search || v.name.toLowerCase().includes(search.toLowerCase()) || v.city.toLowerCase().includes(search.toLowerCase()) || v.country.toLowerCase().includes(search.toLowerCase());
      const m2 = !sportFilter || v.sports?.includes(sportFilter);
      const m3 = !countryFilter || v.country === countryFilter;
      return m1 && m2 && m3;
    });
    if (sortByDistance && userLocation) result.sort((a, b) => (a.distance || 9999) - (b.distance || 9999));
    else result.sort((a, b) => (b.wisa ? 1 : 0) - (a.wisa ? 1 : 0));
    return result;
  }, [allVenuesList, search, sportFilter, countryFilter, sortByDistance, userLocation]);

  const totalPlayers = allVenuesList.reduce((s, v) => s + (v.players || 0), 0);
  const totalTeams = allVenuesList.reduce((s, v) => s + (v.teams || 0), 0);
  const now = new Date();
  const daysLeft = Math.max(0, Math.ceil((new Date(now.getFullYear(), 11, 30) - now) / 86400000));
  // ── Auth state ──
  const [authUser, setAuthUser] = useState(null);
  const [authProfile, setAuthProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setAuthUser(session?.user || null);
      if (session?.user) loadProfile(session.user.id);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuthUser(session?.user || null);
      if (session?.user) loadProfile(session.user.id);
      else { setAuthProfile(null); }
    });
    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (userId) => {
    const { data } = await supabase.from("users").select("*").eq("id", userId).single();
    if (data) setAuthProfile(data);
  };

  // ─── REAL career stats for the logged-in user ───
  // Aggregates player_stats across all seasons/comps. Falls back to zeros if no rows.
  const [liveCareer, setLiveCareer] = useState(null);
  useEffect(() => {
    if (!authUser?.id) { setLiveCareer(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data: stats } = await supabase
          .from('player_stats')
          .select('games_played, total_runs, total_wickets, total_overs, total_goals, total_sixes, total_fours, ducks, hat_tricks, five_wicket_hauls, highest_score, best_batting, best_bowling, season_id, competition_id, sport_id, sport:sports(name, slug), competition:competitions(name), season:seasons(name)')
          .eq('user_id', authUser.id);
        if (cancelled) return;
        const rows = stats || [];
        const career = rows.reduce((acc, r) => {
          acc.totalGames += (r.games_played || 0);
          acc.totalGoals += (r.total_goals || 0);
          acc.totalRuns  += (r.total_runs  || 0);
          acc.totalWickets += (r.total_wickets || 0);
          acc.totalSixes += (r.total_sixes || 0);
          acc.totalFours += (r.total_fours || 0);
          acc.ducks     += (r.ducks       || 0);
          acc.hatTricks += (r.hat_tricks  || 0);
          acc.fiveWktHauls += (r.five_wicket_hauls || 0);
          acc.highestScore = Math.max(acc.highestScore, r.highest_score || 0);
          if (r.sport?.name) acc.sportsSet.add(r.sport.name);
          if (r.season?.name) acc.seasonsSet.add(r.season.name);
          return acc;
        }, { totalGames:0, totalGoals:0, totalRuns:0, totalWickets:0, totalSixes:0, totalFours:0, ducks:0, hatTricks:0, fiveWktHauls:0, highestScore:0, sportsSet:new Set(), seasonsSet:new Set() });
        career.sportsPlayed = Array.from(career.sportsSet);
        career.seasonsPlayed = career.seasonsSet.size;
        // Match wins — combine player_match_confirmations AND match_attendance for max coverage.
        // ALSO compute current win streak, longest win streak, and a recent W/L/D form array
        // walking matches chronologically so the profile can show real Strava-style streaks.
        try {
          const [{ data: confs }, { data: att }] = await Promise.all([
            supabase.from('player_match_confirmations').select('match_id, team_id, match:matches!inner(id, status, scheduled_at, home_team_id, away_team_id, home_score, away_score)').eq('player_id', authUser.id),
            supabase.from('match_attendance').select('match_id, team_id, match:matches!inner(id, status, scheduled_at, home_team_id, away_team_id, home_score, away_score)').eq('user_id', authUser.id),
          ]);
          const seen = new Set();
          const all = [];
          [...(confs || []), ...(att || [])].forEach(row => {
            const k = `${row.match_id}|${row.team_id}`;
            if (seen.has(k)) return;
            seen.add(k);
            all.push(row);
          });
          // Compute per-match results + sort chronologically (oldest → newest)
          const results = all
            .map(a => {
              const m = a.match;
              if (!m || m.status !== 'completed' || m.home_score == null || m.away_score == null) return null;
              const onHome = a.team_id === m.home_team_id;
              const onAway = a.team_id === m.away_team_id;
              if (!onHome && !onAway) return null;
              const myScore = onHome ? m.home_score : m.away_score;
              const oppScore = onHome ? m.away_score : m.home_score;
              const outcome = myScore > oppScore ? 'W' : (myScore < oppScore ? 'L' : 'D');
              return { ts: new Date(m.scheduled_at).getTime(), outcome, myScore, oppScore };
            })
            .filter(Boolean)
            .sort((a, b) => a.ts - b.ts);

          let wins = 0, games = results.length;
          results.forEach(r => { if (r.outcome === 'W') wins++; });

          // Streak math — walking the chronological array
          let longestStreak = 0, runningStreak = 0;
          let currentStreak = 0;
          results.forEach(r => {
            if (r.outcome === 'W') {
              runningStreak++;
              if (runningStreak > longestStreak) longestStreak = runningStreak;
            } else {
              runningStreak = 0;
            }
          });
          // Current streak counts back from the most recent match
          for (let i = results.length - 1; i >= 0; i--) {
            if (results[i].outcome === 'W') currentStreak++;
            else break;
          }

          career.totalWins = wins;
          career.currentStreak = currentStreak;
          career.longestStreak = longestStreak;
          // Last 10 results (newest first) for sparklines
          career.recentForm = results.slice(-10).reverse().map(r => r.outcome);
          // Full timestamp + outcome list — powers the day/time heatmap
          career.matchTimes = results.map(r => ({ ts: r.ts, outcome: r.outcome }));
          // Personal records — peak performance moments
          career.biggestWin = results.reduce((acc, r) => {
            if (r.outcome !== 'W') return acc;
            const margin = r.myScore - r.oppScore;
            return (!acc || margin > acc.margin) ? { margin, myScore: r.myScore, oppScore: r.oppScore } : acc;
          }, null);
          // Prefer the games count from participation data if it's higher than player_stats sum
          if (games > career.totalGames) career.totalGames = games;
          career.winRate = career.totalGames > 0 ? Math.round((wins / career.totalGames) * 1000) / 10 : 0;
        } catch (e) { career.totalWins = 0; career.winRate = 0; career.currentStreak = 0; career.longestStreak = 0; career.recentForm = []; }
        setLiveCareer(career);
      } catch (e) {
        console.warn('All4Indoor: failed to load career stats', e);
        setLiveCareer({ totalGames:0, totalGoals:0, totalRuns:0, totalWickets:0, totalSixes:0, totalFours:0, ducks:0, hatTricks:0, fiveWktHauls:0, highestScore:0, sportsPlayed:[], seasonsPlayed:0, totalWins:0, winRate:0 });
      }
    })();
    return () => { cancelled = true; };
  }, [authUser?.id]);

  // ─── REAL leaderboards across the venue ───
  // Top players by various metrics. Raw rows kept in state so we can re-aggregate
  // when the user toggles the sport filter without re-querying Supabase.
  const [leaderboardRawStats, setLeaderboardRawStats] = useState(null);
  const [leaderboardSports, setLeaderboardSports] = useState([]); // distinct sport names available
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: stats } = await supabase
          .from('player_stats')
          .select('user_id, total_goals, total_runs, total_wickets, games_played, total_sixes, hat_tricks, user:users(first_name, last_name), sport:sports(name, slug)')
          .order('updated_at', { ascending: false })
          .limit(2000);
        if (cancelled || !stats) return;
        setLeaderboardRawStats(stats);
        const sportNames = Array.from(new Set(stats.map(s => s.sport?.name).filter(Boolean))).sort();
        setLeaderboardSports(sportNames);
      } catch (e) {
        console.warn('All4Indoor: failed to load leaderboards', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  // 2026-05-28: CRICKET LEADERBOARDS — one consolidated walk of cricket
  // scorecards from the last 60 days computes multiple Spawtz-style stats
  // for the home page:
  //   bestBowling      — per-spell figures (X/Y, ranked wkts desc/runs asc)
  //   bestInnings      — per-batter per-match runs (top individual scores)
  //   bestPartnerships — per pair per-match runs (using batter_setup)
  //   teamWickets      — season total wickets taken per team
  //   teamRunsConceded — season total runs conceded per team
  //   teamRunsScored   — season total runs scored per team
  //   teamDotBalls     — season total dot balls bowled per team
  // Player_stats doesn't capture per-match breakdowns so this has to
  // derive from balls_data each load. Single SQL fetch + JS pivot keeps
  // it cheap.
  const [liveCricketLeaderboards, setLiveCricketLeaderboards] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const since = new Date(Date.now() - 60 * 24 * 3600 * 1000).toISOString();
        const { data: cards } = await supabase
          .from('cricket_scorecards')
          .select('match_id, balls_data, batter_setup, updated_at')
          .gte('updated_at', since);
        if (cancelled || !cards) return;
        const matchIds = [...new Set(cards.map(c => c.match_id).filter(Boolean))];
        // 2026-05-28: filter to actual cricket comps only — rounders matches
        // are scored via the same cricket_scorecards table, but we don't want
        // them polluting "Most Wickets" etc.
        const { data: matches } = matchIds.length
          ? await supabase.from('matches')
              .select('id, scheduled_at, home_team_id, away_team_id, home_score, away_score, status, home_team:teams!home_team_id(name), away_team:teams!away_team_id(name), competition:competitions!inner(sport:sports!inner(slug))')
              .in('id', matchIds)
              .eq('competition.sport.slug', 'cricket')
          : { data: [] };
        const matchById = Object.fromEntries((matches || []).map(m => [m.id, m]));

        // Per-match accumulators feed into per-player and per-team lists.
        const bowlingFigures = [];   // [{ bowler, wickets, runs, match_id, when, opp }]
        const battingInnings = [];   // [{ batter, runs, match_id, when, opp }]
        const partnerships   = [];   // [{ b1, b2, runs, match_id, when, opp }]
        // 2026-06-02: also tracking wides + noBalls bowled per team — feeds
        // the two new "Most Wides" / "Most No Balls" panels on the team
        // leaderboards. These are bowling-discipline metrics (high = bad)
        // so they sit alongside Most Runs Conceded as the "leakiest" group.
        const teamAgg = {};          // teamName -> { wickets, runsConceded, runsScored, dotBalls, wides, noBalls, sevens }

        const ensureTeam = (name) => {
          if (!teamAgg[name]) teamAgg[name] = { team: name, wickets: 0, runsConceded: 0, runsScored: 0, dotBalls: 0, wides: 0, noBalls: 0, sevens: 0 };
          return teamAgg[name];
        };

        // Track matches we've already attributed team-level runs to, so the
        // same match can't be counted twice if multiple scorecards exist.
        const teamMatchSeen = new Set();

        for (const card of cards) {
          const m = matchById[card.match_id];
          const homeName = m?.home_team?.name;
          const awayName = m?.away_team?.name;
          if (!homeName || !awayName) continue; // skip unmatched cards

          // Team-level runs scored / conceded MUST come from match.home_score /
          // away_score, not from a balls_data sum. In indoor cricket the
          // displayed score = gross runs - (wickets × 5), so summing balls
          // gives a number that's bigger than what users see on team pages.
          // We aggregate per-match (once) using the actual recorded scores.
          if (m.status === 'completed' && m.home_score != null && m.away_score != null && !teamMatchSeen.has(card.match_id)) {
            teamMatchSeen.add(card.match_id);
            const tHome = ensureTeam(homeName);
            const tAway = ensureTeam(awayName);
            tHome.runsScored   += m.home_score;
            tHome.runsConceded += m.away_score;
            tAway.runsScored   += m.away_score;
            tAway.runsConceded += m.home_score;
          }

          // Per-batter AND per-bowler are per-PAIR — 2026-05-31:
          //   • Player batting in 2 pairs of the same match → 2 separate innings
          //   • Bowler bowling to 2 different pairs → 2 separate spells
          // Stops a single match-total from showing as a freak "6/45" or "60"// when it's really 2-3 normal performances added together.

          for (const key of Object.keys(card.balls_data || {})) {
            // key format: "home-0", "away-1" etc — tells us who's batting
            const battingSide = key.startsWith('home-') ? 'home' : key.startsWith('away-') ? 'away' : null;
            if (!battingSide) continue;
            const bowlingTeam = battingSide === 'home' ? awayName : homeName;
            const battingTeam = battingSide === 'home' ? homeName : awayName;
            const tBowling = ensureTeam(bowlingTeam);
            // 2026-06-02: also grab the batting-team agg so power-hitting
            // metrics (sevens) can be credited to the right team.
            const tBatting = ensureTeam(battingTeam);

            // Per-pair totals — flushed at the end of each pair so each
            // pair becomes its own innings / spell candidate.
            const pairBatterRuns = {};
            const pairBowler = {};  // bowler -> { wickets, runs }

            for (const b of card.balls_data[key]) {
              const bowler = (b.bowler || '').trim();
              const batter = (b.batter || '').trim();
              if (bowler && !pairBowler[bowler]) pairBowler[bowler] = { wickets: 0, runs: 0 };

              if (b.type === 'wicket') {
                if (bowler) pairBowler[bowler].wickets += 1;
                tBowling.wickets += 1;
              }
              if (b.type === 'run') {
                const r = b.runs || 0;
                if (bowler) pairBowler[bowler].runs += r;
                if (batter) pairBatterRuns[batter] = (pairBatterRuns[batter] || 0) + r;
                if (r === 0) tBowling.dotBalls += 1;
                // 2026-06-02: indoor cricket back-net = 7 runs. Counted per
                // team for the "Most Sevens" team leaderboard panel.
                if (r === 7) tBatting.sevens += 1;
              }
              if (b.type === 'wide' || b.type === 'noball') {
                const r = b.runs || 0;
                if (bowler) pairBowler[bowler].runs += r;
                // 2026-06-02: count bowling-discipline penalties per team.
                if (b.type === 'wide')   tBowling.wides   += 1;
                if (b.type === 'noball') tBowling.noBalls += 1;
              }
            }

            // Flush this pair's batter innings (one entry per batter per pair).
            for (const [batter, runs] of Object.entries(pairBatterRuns)) {
              if (runs > 0) {
                battingInnings.push({
                  batter, runs,
                  match_id: card.match_id,
                  pair_key: key,
                  when: m?.scheduled_at || null,
                  opp: `${homeName} vs ${awayName}`,
                });
              }
            }
            // Flush this pair's bowling spells (one entry per bowler per pair).
            for (const [bowler, f] of Object.entries(pairBowler)) {
              if (f.wickets > 0) {
                bowlingFigures.push({
                  bowler, wickets: f.wickets, runs: f.runs,
                  match_id: card.match_id,
                  pair_key: key,
                  when: m?.scheduled_at || null,
                  opp: `${homeName} vs ${awayName}`,
                });
              }
            }
          }

          // Per-pair partnerships (from batter_setup)
          for (const key of Object.keys(card.batter_setup || {})) {
            const pair = card.batter_setup[key] || {};
            const b1 = (pair.batter1 || '').trim();
            const b2 = (pair.batter2 || '').trim();
            if (!b1 || !b2) continue;
            let pairRuns = 0;
            for (const b of (card.balls_data?.[key] || [])) {
              if (b.type === 'run') pairRuns += (b.runs || 0);
              else if (b.type === 'wide' || b.type === 'noball') pairRuns += (b.runs || 0);
            }
            if (pairRuns > 0) {
              partnerships.push({
                b1, b2, runs: pairRuns,
                match_id: card.match_id,
                when: m?.scheduled_at || null,
                opp: `${homeName} vs ${awayName}`,
              });
            }
          }

          // 2026-06-02: removed dead block referencing undefined `perBowler`.
          // That variable was a leftover from the pre-#270 per-match aggregator.
          // Per-pair bowling spells are now flushed inside the for-loop above
          // (via pairBowler at line ~1268). Without this removal, every cricket
          // leaderboard load crashed silently with ReferenceError and the
          // section rendered empty. Caught in Chrome console diagnostics.
        }

        // 2026-05-31: hydrate user_ids onto every entry so the leaderboard
        // rows are clickable through to the player profile. Cricket
        // scorecards store names only (no user_id in balls_data), so we
        // look up matching users by full-name and stitch in the id.
        // Names are normalised (trimmed + lowercase) to handle double-space
        // and case mismatches.
        const allNames = new Set();
        battingInnings.forEach(i => i.batter && allNames.add(i.batter));
        bowlingFigures.forEach(b => b.bowler && allNames.add(b.bowler));
        partnerships.forEach(p => { if (p.b1) allNames.add(p.b1); if (p.b2) allNames.add(p.b2); });
        const nameToId = {};
        if (allNames.size) {
          const { data: usersForNames } = await supabase.from('public_players')
            .select('id, first_name, last_name')
            .limit(20000);
          for (const u of (usersForNames || [])) {
            const full = `${u.first_name || ''} ${u.last_name || ''}`.replace(/\s+/g, ' ').trim().toLowerCase();
            if (full) nameToId[full] = u.id;
          }
        }
        const lookup = (name) => {
          const norm = (name || '').replace(/\s+/g, ' ').trim().toLowerCase();
          return nameToId[norm] || null;
        };
        battingInnings.forEach(i => { i.id = lookup(i.batter); });
        bowlingFigures.forEach(b => { b.id = lookup(b.bowler); });
        partnerships.forEach(p => { p.b1Id = lookup(p.b1); p.b2Id = lookup(p.b2); });

        // Ranking
        bowlingFigures.sort((a, b) => b.wickets - a.wickets || a.runs - b.runs);
        battingInnings.sort((a, b) => b.runs - a.runs);
        partnerships  .sort((a, b) => b.runs - a.runs);

        // 2026-05-31: dedupe Best Innings to one entry per player (their
        // single highest pair-innings). Without this a season hitter could
        // claim 5+ of the top 10 slots which crowds out other players.
        // Same logic for bowling figures — one entry per bowler (best spell).
        const seenBatters = new Set();
        const bestInningsOnePer = battingInnings.filter(i => {
          if (seenBatters.has(i.batter)) return false;
          seenBatters.add(i.batter);
          return true;
        });
        const seenBowlers = new Set();
        const bestBowlingOnePer = bowlingFigures.filter(b => {
          if (seenBowlers.has(b.bowler)) return false;
          seenBowlers.add(b.bowler);
          return true;
        });
        const teamsList = Object.values(teamAgg);

        setLiveCricketLeaderboards({
          bestBowling:      bestBowlingOnePer.slice(0, 10),
          bestInnings:      bestInningsOnePer.slice(0, 10),
          bestPartnerships: partnerships     .slice(0, 10),
          teamMostWickets:  [...teamsList].sort((a, b) => b.wickets       - a.wickets      ).slice(0, 10),
          teamLeastRunsConceded: [...teamsList].filter(t => t.runsConceded > 0).sort((a, b) => a.runsConceded - b.runsConceded).slice(0, 10),
          teamMostRunsConceded:  [...teamsList].sort((a, b) => b.runsConceded - a.runsConceded).slice(0, 10),
          teamMostRunsScored:    [...teamsList].sort((a, b) => b.runsScored   - a.runsScored  ).slice(0, 10),
          teamMostDotBalls:      [...teamsList].sort((a, b) => b.dotBalls     - a.dotBalls    ).slice(0, 10),
          teamMostWides:         [...teamsList].sort((a, b) => b.wides        - a.wides       ).slice(0, 10),
          teamMostNoBalls:       [...teamsList].sort((a, b) => b.noBalls      - a.noBalls     ).slice(0, 10),
          teamMostSevens:        [...teamsList].sort((a, b) => b.sevens       - a.sevens      ).slice(0, 10),
        });
      } catch (e) {
        console.warn('All4Indoor: failed to load cricket leaderboards', e);
        setLiveCricketLeaderboards({ bestBowling: [], bestInnings: [], bestPartnerships: [], teamMostWickets: [], teamLeastRunsConceded: [], teamMostRunsConceded: [], teamMostRunsScored: [], teamMostDotBalls: [], teamMostWides: [], teamMostNoBalls: [], teamMostSevens: [] });
      }
    })();
    return () => { cancelled = true; };
  }, []);
  // Backwards-compat alias for the existing UI block that read `liveBestBowling`.
  const liveBestBowling = liveCricketLeaderboards?.bestBowling || null;

  // ─── TEAM RATINGS — load + compute every team's rating from all-time matches.
  // Stored as { allTime: { [teamId]: { rating, played, sport, name, bestSeason, ... } },
  //             currentSeason: same shape filtered to active competitions }.
  // Single fetch on mount; recomputes when matches/teams change is rare enough
  // that a manual reload via the page reload covers it.
  const [liveTeamRatings, setLiveTeamRatings] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Pull active teams + their competition/sport, plus every completed
        // or forfeited match. Chunked to dodge PostgREST 1k row caps.
        // 2026-06-02: column is `is_archived` not `archived`. With the wrong
        // name PostgREST 400s, error gets swallowed by the catch below, and
        // Team Rankings just shows "No teams ranked for this filter yet"// — silent failure. The four other team queries in this file already
        // use is_archived; this one was the regression.
        const { data: teams } = await supabase
          .from('teams')
          .select('id, name, logo_url, competition:competitions(id, name, status, sport:sports(slug, name), season:seasons(id, name, start_date, end_date))')
          .eq('is_archived', false);
        if (cancelled || !teams?.length) { setLiveTeamRatings({ allTime: {}, currentSeason: {} }); return; }

        const teamById = Object.fromEntries(teams.map(t => [t.id, t]));
        // 2026-06-02: fetchAllPages takes a builder FUNCTION, not (table,
        // select, filter). The old call passed 'matches' as a string, so
        // inside the helper `builderFn(from, to)` threw "is not a function",
        // the catch swallowed it, matches stayed empty, and Team Rankings
        // showed "no teams ranked". This was the deeper bug under the
        // is_archived issue — both had to be fixed.
        const matches = await fetchAllPages((from, to) =>
          supabase.from('matches')
            .select('id, scheduled_at, status, forfeit_type, home_team_id, away_team_id, home_score, away_score, competition_id')
            .in('status', ['completed', 'forfeited'])
            .order('id')
            .range(from, to)
        );
        if (cancelled) return;

        // Walk every match once per team-side. Bucket per team.
        const perTeamMatches = {}; // teamId -> [matches]
        for (const m of matches) {
          if (m.home_team_id && teamById[m.home_team_id]) {
            (perTeamMatches[m.home_team_id] ||= []).push(m);
          }
          if (m.away_team_id && teamById[m.away_team_id]) {
            (perTeamMatches[m.away_team_id] ||= []).push(m);
          }
        }

        const seasonKeyOf = (m) => {
          // Prefer the season name from the team's competition for this match;
          // fall back to year of scheduled_at. Caller doesn't have direct
          // access to competition.season here, so wrap below per-team.
          if (m.season_name) return m.season_name;
          if (m.scheduled_at) return String(new Date(m.scheduled_at).getFullYear());
          return 'Unknown';
        };

        const allTime = {};
        const currentSeason = {};
        const now = new Date();

        for (const team of teams) {
          const sport = (team.competition?.sport?.slug || '').toLowerCase();
          const ms = perTeamMatches[team.id] || [];
          // Inject season_name onto matches so default season key is the actual
          // season name when available. For multi-season teams the competition
          // is rarely re-mapped per-match, but this is a reasonable approximation.
          const decorated = ms.map(m => ({ ...m, season_name: team.competition?.season?.name || null }));

          const allRating = computeTeamRating(decorated, team.id, sport, seasonKeyOf);
          if (allRating.played > 0) {
            allTime[team.id] = {
              teamId: team.id,
              name: team.name,
              logo_url: team.logo_url,
              sport,
              sportName: team.competition?.sport?.name || sport,
              competitionName: team.competition?.name || '',
              ...allRating,
            };
          }

          // Current season — only matches inside the active season window
          const start = team.competition?.season?.start_date ? new Date(team.competition.season.start_date) : null;
          const end   = team.competition?.season?.end_date   ? new Date(team.competition.season.end_date)   : null;
          const inSeason = decorated.filter(m => {
            if (!m.scheduled_at) return false;
            const d = new Date(m.scheduled_at);
            if (start && d < start) return false;
            if (end && d > end) return false;
            // Also exclude future matches even if in the season window
            return d <= now;
          });
          const seasonRating = computeTeamRating(inSeason, team.id, sport, seasonKeyOf);
          if (seasonRating.played > 0) {
            currentSeason[team.id] = {
              teamId: team.id,
              name: team.name,
              logo_url: team.logo_url,
              sport,
              sportName: team.competition?.sport?.name || sport,
              competitionName: team.competition?.name || '',
              seasonName: team.competition?.season?.name || null,
              ...seasonRating,
            };
          }
        }
        if (!cancelled) setLiveTeamRatings({ allTime, currentSeason });
      } catch (e) {
        console.warn('All4Indoor: failed to load team ratings', e);
        if (!cancelled) setLiveTeamRatings({ allTime: {}, currentSeason: {} });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Derive sport-filtered leaderboards from raw rows whenever sport changes
  const liveLeaderboards = useMemo(() => {
    if (!leaderboardRawStats) return null;
    const filtered = leaderboardSport
      ? leaderboardRawStats.filter(s => s.sport?.name === leaderboardSport || s.sport?.slug === leaderboardSport)
      : leaderboardRawStats;
    const byUser = {};
    filtered.forEach(s => {
      if (!s.user) return;
      const name = `${s.user.first_name || ''} ${s.user.last_name || ''}`.trim();
      if (!byUser[s.user_id]) byUser[s.user_id] = {
        id: s.user_id, name,
        goals:0, runs:0, wickets:0, games:0, sixes:0, hatTricks:0,
        // 2026-05-31: per-sport breakdowns so Goals + Hat-tricks
        // leaderboards can be split into Soccer-only and Netball-only
        // boards. Lumping them looked wrong since they're earned in
        // different sports against different opponents.
        goalsBySport: {}, hatTricksBySport: {},
      };
      const u = byUser[s.user_id];
      u.goals     += (s.total_goals    || 0);
      u.runs      += (s.total_runs     || 0);
      u.wickets   += (s.total_wickets  || 0);
      u.games     += (s.games_played   || 0);
      u.sixes     += (s.total_sixes    || 0);
      u.hatTricks += (s.hat_tricks     || 0);
      const sk = (s.sport?.slug || '').toLowerCase();
      if (sk) {
        u.goalsBySport[sk]     = (u.goalsBySport[sk]     || 0) + (s.total_goals  || 0);
        u.hatTricksBySport[sk] = (u.hatTricksBySport[sk] || 0) + (s.hat_tricks   || 0);
      }
    });
    const all = Object.values(byUser)
      .filter(p => p.name)
      .map(p => ({
        ...p,
        // Soccer slug bucket includes Futsal — same scoring model.
        soccerGoals:    (p.goalsBySport.soccer  || 0) + (p.goalsBySport.futsal || 0),
        netballGoals:    p.goalsBySport.netball || 0,
        soccerHatTricks:  (p.hatTricksBySport.soccer  || 0) + (p.hatTricksBySport.futsal || 0),
        netballHatTricks:  p.hatTricksBySport.netball || 0,
      }));
    return {
      topGoals:           [...all].filter(p => p.goals          > 0).sort((a,b) => b.goals          - a.goals         ).slice(0, 10),
      topGoalsSoccer:     [...all].filter(p => p.soccerGoals    > 0).sort((a,b) => b.soccerGoals    - a.soccerGoals   ).slice(0, 10),
      topGoalsNetball:    [...all].filter(p => p.netballGoals   > 0).sort((a,b) => b.netballGoals   - a.netballGoals  ).slice(0, 10),
      topRuns:            [...all].filter(p => p.runs           > 0).sort((a,b) => b.runs           - a.runs          ).slice(0, 10),
      topWickets:         [...all].filter(p => p.wickets        > 0).sort((a,b) => b.wickets        - a.wickets       ).slice(0, 10),
      topGames:           [...all].filter(p => p.games          > 0).sort((a,b) => b.games          - a.games         ).slice(0, 10),
      topSixes:           [...all].filter(p => p.sixes          > 0).sort((a,b) => b.sixes          - a.sixes         ).slice(0, 10),
      topHatTricks:       [...all].filter(p => p.hatTricks      > 0).sort((a,b) => b.hatTricks      - a.hatTricks     ).slice(0, 10),
      topHatTricksSoccer: [...all].filter(p => p.soccerHatTricks > 0).sort((a,b) => b.soccerHatTricks - a.soccerHatTricks).slice(0, 10),
      topHatTricksNetball:[...all].filter(p => p.netballHatTricks> 0).sort((a,b) => b.netballHatTricks- a.netballHatTricks).slice(0, 10),
    };
  }, [leaderboardRawStats, leaderboardSport]);

  // ─── REAL achievements/badges earned by the logged-in user ───
  // Schema verified: id, user_id, match_id, achievement_key, achievement_name,
  // description, emoji, rarity, sport, earned_at.
  const [liveBadges, setLiveBadges] = useState(null);
  useEffect(() => {
    if (!authUser?.id) { setLiveBadges(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data: achievements } = await supabase
          .from('player_achievements')
          .select('id, achievement_key, achievement_name, description, emoji, rarity, sport, earned_at, match_id')
          .eq('user_id', authUser.id)
          .order('earned_at', { ascending: false });
        if (cancelled) return;
        setLiveBadges(achievements || []);
      } catch (e) {
        console.warn('All4Indoor: failed to load achievements', e);
        setLiveBadges([]);
      }
    })();
    return () => { cancelled = true; };
  }, [authUser?.id]);

  // ─── REAL teams looking for players ───
  // Wire LOOKING_FOR_PLAYERS to teams.looking_for_players = true. Captains flag their
  // team via the admin/captain UI when they need more bodies.
  const [liveLookingForPlayers, setLiveLookingForPlayers] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('teams')
          .select('id, name, looking_for_players_message, competition:competitions(name, day_of_week, game_time_start, sport:sports(name, icon))')
          .eq('looking_for_players', true)
          .eq('is_archived', false)
          .eq('withdrawn_from_season', false);
        if (cancelled) return;
        const mapped = (data || []).map(t => ({
          id: t.id,
          team: t.name,
          sport: (t.competition?.sport?.name || '').replace(/^Indoor\s+/i, ''),
          icon: t.competition?.sport?.icon || '',
          comp: t.competition?.name || '',
          day: t.competition?.day_of_week || '',
          time: t.competition?.game_time_start || '',
          message: t.looking_for_players_message || 'Looking for new players!',
        }));
        setLiveLookingForPlayers(mapped);
      } catch (e) {
        console.warn('All4Indoor: failed to load teams looking for players', e);
        setLiveLookingForPlayers([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ─── REAL rivalries — head-to-head matchups from completed matches ───
  // Compute "team A vs team B has played X times" pairs that have met 3+ times.
  // For authenticated users we surface THEIR team's rivalries.
  const [liveRivalries, setLiveRivalries] = useState(null);
  useEffect(() => {
    if (!authUser?.id) { setLiveRivalries(null); return; }
    let cancelled = false;
    (async () => {
      try {
        // Find teams the user is on
        const { data: tp } = await supabase
          .from('team_players')
          .select('team_id, team:teams(id, name)')
          .eq('user_id', authUser.id);
        const myTeamIds = (tp || []).map(t => t.team_id);
        if (myTeamIds.length === 0) { setLiveRivalries([]); return; }
        // Pull all completed matches involving any of those teams
        const { data: matches } = await supabase
          .from('matches')
          .select('home_team_id, away_team_id, home_score, away_score, scheduled_at, home_team:teams!home_team_id(id, name), away_team:teams!away_team_id(id, name)')
          .eq('status', 'completed')
          .or(myTeamIds.map(id => `home_team_id.eq.${id},away_team_id.eq.${id}`).join(','))
          .order('scheduled_at', { ascending: false });
        // Group by (myTeam, oppTeam) and tally wins/losses
        const pairs = {};
        (matches || []).forEach(m => {
          const myIsHome = myTeamIds.includes(m.home_team_id);
          const myIsAway = myTeamIds.includes(m.away_team_id);
          if (!myIsHome && !myIsAway) return;
          const myTeam = myIsHome ? m.home_team : m.away_team;
          const oppTeam = myIsHome ? m.away_team : m.home_team;
          if (!myTeam || !oppTeam) return;
          const key = `${myTeam.id}|${oppTeam.id}`;
          if (!pairs[key]) pairs[key] = { myTeam: myTeam.name, oppTeam: oppTeam.name, played:0, wins:0, losses:0, draws:0, lastMet:null };
          pairs[key].played++;
          const myScore = myIsHome ? m.home_score : m.away_score;
          const oppScore = myIsHome ? m.away_score : m.home_score;
          if (myScore > oppScore) pairs[key].wins++;
          else if (myScore < oppScore) pairs[key].losses++;
          else pairs[key].draws++;
          if (!pairs[key].lastMet) pairs[key].lastMet = m.scheduled_at;
        });
        if (cancelled) return;
        const rivalries = Object.values(pairs).filter(p => p.played >= 2).sort((a,b) => b.played - a.played).slice(0, 6);
        setLiveRivalries(rivalries);
      } catch (e) {
        console.warn('All4Indoor: failed to load rivalries', e);
        setLiveRivalries([]);
      }
    })();
    return () => { cancelled = true; };
  }, [authUser?.id]);

  // ─── REAL teams across the venue + their W/L records computed from matches ───
  const [liveTeams, setLiveTeams] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: teams }, { data: matches }, { data: rosterRows }] = await Promise.all([
          supabase.from('teams')
            .select('id, name, competition:competitions(name, sport:sports(name)), looking_for_players, created_at')
            .eq('is_archived', false)
            .eq('withdrawn_from_season', false),
          supabase.from('matches')
            .select('home_team_id, away_team_id, home_score, away_score, status')
            .eq('status', 'completed')
            .not('home_score', 'is', null),
          supabase.from('team_players')
            .select('team_id, user:users(first_name, last_name)'),
        ]);
        if (cancelled) return;
        // Tally W/L/D per team
        const records = {};
        (matches || []).forEach(m => {
          const h = m.home_score ?? 0, a = m.away_score ?? 0;
          if (!records[m.home_team_id]) records[m.home_team_id] = { wins:0, losses:0, draws:0 };
          if (!records[m.away_team_id]) records[m.away_team_id] = { wins:0, losses:0, draws:0 };
          if (h > a)      { records[m.home_team_id].wins++; records[m.away_team_id].losses++; }
          else if (a > h) { records[m.away_team_id].wins++; records[m.home_team_id].losses++; }
          else            { records[m.home_team_id].draws++; records[m.away_team_id].draws++; }
        });
        // Group rosters
        const rosters = {};
        (rosterRows || []).forEach(r => {
          if (!rosters[r.team_id]) rosters[r.team_id] = [];
          if (r.user) rosters[r.team_id].push({
            name: `${r.user.first_name || ''} ${r.user.last_name?.[0] || ''}`.trim(),
            avatar: `${(r.user.first_name?.[0] || '').toUpperCase()}${(r.user.last_name?.[0] || '').toUpperCase()}`,
          });
        });
        // Dedupe rosters by name
        Object.keys(rosters).forEach(k => {
          const seen = new Set();
          rosters[k] = rosters[k].filter(p => p.name && !seen.has(p.name) && seen.add(p.name));
        });
        const out = (teams || []).map(t => ({
          id: t.id,
          name: t.name,
          venue: 'Wildcatz Indoor Sports',
          sport: (t.competition?.sport?.name || '').replace(/^Indoor\s+/i, ''),
          comp: t.competition?.name || '',
          country: 'AU',
          record: records[t.id] || { wins:0, losses:0, draws:0 },
          roster: rosters[t.id] || [],
          looking_for_players: !!t.looking_for_players,
        }));
        // Sort: most games played first (so big established teams come first)
        out.sort((a, b) => {
          const totA = a.record.wins + a.record.losses + a.record.draws;
          const totB = b.record.wins + b.record.losses + b.record.draws;
          return totB - totA;
        });
        setLiveTeams(out);
      } catch (e) {
        console.warn('All4Indoor: failed to load teams', e);
        setLiveTeams([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ─── REAL ranked players (points leaderboard) ───
  // Formula: 3 pts per win + 1 pt per draw + 1 pt per game played.
  // Computed by joining match_attendance to matches (status=completed) and tallying.
  const [liveRankedPlayers, setLiveRankedPlayers] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Combine player_match_confirmations (richer for newer matches) AND match_attendance
        // (check-in based) for maximum coverage. Dedupe by (match, user) pair.
        const [{ data: confs }, { data: att }, { data: matches }, { data: users }] = await Promise.all([
          getAllConfirmationsBasic(),
          getAllAttendanceBasic(),
          supabase.from('matches').select('id, status, home_team_id, away_team_id, home_score, away_score').eq('status', 'completed').not('home_score', 'is', null),
          // 2026-05-27: paginated via fetchAllPages so users beyond row 1000 aren't dropped (see supabase.js)
          getAllUsersBasic().then(data => ({ data })),
        ]);
        if (cancelled) return;
        const matchById = {};
        (matches || []).forEach(m => { matchById[m.id] = m; });
        const userMap = {};
        (users || []).forEach(u => { userMap[u.id] = u; });
        // Dedupe participation
        const seen = new Set();
        const participation = [];
        const add = (matchId, userId, teamId) => {
          if (!matchId || !userId || !teamId) return;
          const k = `${matchId}|${userId}|${teamId}`;
          if (seen.has(k)) return;
          seen.add(k);
          participation.push({ matchId, userId, teamId });
        };
        (confs || []).forEach(c => add(c.match_id, c.player_id, c.team_id));
        (att || []).forEach(a => add(a.match_id, a.user_id, a.team_id));
        const byUser = {};
        participation.forEach(p => {
          const m = matchById[p.matchId];
          const u = userMap[p.userId];
          if (!m || !u) return;
          const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
          if (!name) return;
          if (!byUser[p.userId]) byUser[p.userId] = { id: p.userId, name, avatar: `${(u.first_name?.[0] || '').toUpperCase()}${(u.last_name?.[0] || '').toUpperCase()}`, wins:0, losses:0, draws:0, games:0 };
          byUser[p.userId].games++;
          const onHome = p.teamId === m.home_team_id;
          const onAway = p.teamId === m.away_team_id;
          if (!onHome && !onAway) return;
          const myScore = onHome ? m.home_score : m.away_score;
          const oppScore = onHome ? m.away_score : m.home_score;
          if (myScore > oppScore) byUser[p.userId].wins++;
          else if (myScore < oppScore) byUser[p.userId].losses++;
          else byUser[p.userId].draws++;
        });
        // 2026-05-31: drop 1-2 game wonders before sorting. Matches the
        // ELO ranking floor (>= 3 games). Was previously letting a player
        // with 1 win + 1 game show up at top by points; that's the kind of
        // thing that makes the public rankings page look broken to a new
        // visitor clicking through from the FB post.
        const ranked = Object.values(byUser)
          .filter(p => p.games >= 3)
          .map(p => ({ ...p, pts: p.wins * 3 + p.draws * 1 + p.games * 1, qualified: p.games >= 10, venue: 'Wildcatz Indoor Sports' }))
          .sort((a, b) => b.pts - a.pts)
          .slice(0, 50);
        setLiveRankedPlayers(ranked);
      } catch (e) {
        console.warn('All4Indoor: failed to compute ranked players', e);
        setLiveRankedPlayers([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── ELO RATING — properly computed by walking matches chronologically ─────
  // Each team's rating = avg of attending players' current ELO. After each match
  // both sides update with K=24. Players default to 1000 on first appearance.
  // This is run once on mount and produces a stable per-user rating.
  // ═══════════════════════════════════════════════════════════════════════════
  const [liveElo, setLiveElo] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Use BOTH player_match_confirmations (primary — richer) AND match_attendance (fallback)
        // to maximise the participation data we have.
        const [{ data: matches }, { data: confirmations }, { data: attendance }, { data: users }] = await Promise.all([
          supabase.from('matches')
            .select('id, scheduled_at, home_team_id, away_team_id, home_score, away_score')
            .eq('status', 'completed')
            .not('home_score', 'is', null)
            .order('scheduled_at', { ascending: true }),
          getAllConfirmationsBasic(),
          getAllAttendanceBasic(),
          // 2026-05-27: paginated via fetchAllPages so users beyond row 1000 aren't dropped (see supabase.js)
          getAllUsersBasic().then(data => ({ data })),
        ]);
        if (cancelled) return;
        const userMap = {};
        (users || []).forEach(u => { userMap[u.id] = `${u.first_name || ''} ${u.last_name || ''}`.trim(); });
        // Bucket participation by match — combine BOTH sources, dedupe on (match, user)
        const byMatch = {};
        const addParticipant = (matchId, userId, teamId) => {
          if (!matchId || !userId || !teamId) return;
          if (!byMatch[matchId]) byMatch[matchId] = new Map();
          const k = `${userId}|${teamId}`;
          if (!byMatch[matchId].has(k)) byMatch[matchId].set(k, { user_id: userId, team_id: teamId });
        };
        (confirmations || []).forEach(c => addParticipant(c.match_id, c.player_id, c.team_id));
        (attendance || []).forEach(a => addParticipant(a.match_id, a.user_id, a.team_id));
        // Convert Map back to array per match
        Object.keys(byMatch).forEach(k => { byMatch[k] = Array.from(byMatch[k].values()); });
        const K = 24; // ELO sensitivity — moderate for casual sport
        const ratings = {}; // user_id → ELO
        const gameCount = {}; // user_id → count
        const recentForm = {}; // user_id → array of last 5 outcomes (newest at end)
        const pushForm = (uid, outcome) => {
          if (!recentForm[uid]) recentForm[uid] = [];
          recentForm[uid].push(outcome);
          if (recentForm[uid].length > 5) recentForm[uid].shift();
        };
        (matches || []).forEach(m => {
          const att = byMatch[m.id] || [];
          const home = att.filter(a => a.team_id === m.home_team_id);
          const away = att.filter(a => a.team_id === m.away_team_id);
          if (home.length === 0 || away.length === 0) return;
          const homeAvg = home.reduce((s, a) => s + (ratings[a.user_id] || 1000), 0) / home.length;
          const awayAvg = away.reduce((s, a) => s + (ratings[a.user_id] || 1000), 0) / away.length;
          const homeExp = 1 / (1 + Math.pow(10, (awayAvg - homeAvg) / 400));
          const awayExp = 1 - homeExp;
          const homeActual = m.home_score > m.away_score ? 1 : m.home_score < m.away_score ? 0 : 0.5;
          const awayActual = 1 - homeActual;
          const deltaHome = K * (homeActual - homeExp);
          const deltaAway = K * (awayActual - awayExp);
          const homeOutcome = homeActual === 1 ? 'W' : homeActual === 0 ? 'L' : 'D';
          const awayOutcome = awayActual === 1 ? 'W' : awayActual === 0 ? 'L' : 'D';
          home.forEach(a => {
            ratings[a.user_id] = (ratings[a.user_id] || 1000) + deltaHome;
            gameCount[a.user_id] = (gameCount[a.user_id] || 0) + 1;
            pushForm(a.user_id, homeOutcome);
          });
          away.forEach(a => {
            ratings[a.user_id] = (ratings[a.user_id] || 1000) + deltaAway;
            gameCount[a.user_id] = (gameCount[a.user_id] || 0) + 1;
            pushForm(a.user_id, awayOutcome);
          });
        });
        const out = Object.entries(ratings)
          .filter(([uid]) => userMap[uid] && gameCount[uid] >= 3) // min 3 games to qualify for ranking
          .map(([uid, r]) => ({
            id: uid,
            name: userMap[uid],
            elo: Math.round(r),
            games: gameCount[uid],
            avatar: userMap[uid].split(' ').map(w => w[0]).filter(Boolean).slice(0,2).join('').toUpperCase(),
            // Latest-first form sparkline (max 5)
            recentForm: (recentForm[uid] || []).slice().reverse(),
          }))
          .sort((a, b) => b.elo - a.elo);
        setLiveElo(out);
      } catch (e) {
        console.warn('All4Indoor: failed to compute ELO', e);
        setLiveElo([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── WEEKLY WRAP-UP — real last-7-day summary for the logged-in user ──────
  // ═══════════════════════════════════════════════════════════════════════════
  const [liveWeeklyWrap, setLiveWeeklyWrap] = useState(null);
  useEffect(() => {
    if (!authUser?.id) { setLiveWeeklyWrap(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const since = new Date(); since.setDate(since.getDate() - 7); since.setHours(0,0,0,0);
        const sinceIso = since.toISOString();
        // Pull this user's match_attendance joined to completed matches in last 7 days
        const { data: att } = await supabase
          .from('match_attendance')
          .select('match_id, team_id, match:matches!inner(id, status, scheduled_at, home_team_id, away_team_id, home_score, away_score, motm_user_id, motm_name)')
          .eq('user_id', authUser.id)
          .gte('match.scheduled_at', sinceIso);
        // Pull achievements earned this week
        const { data: ach } = await supabase
          .from('player_achievements')
          .select('achievement_key, achievement_name, emoji, rarity, earned_at')
          .eq('user_id', authUser.id)
          .gte('earned_at', sinceIso);
        if (cancelled) return;
        let games = 0, wins = 0, losses = 0, draws = 0, motms = 0;
        (att || []).forEach(a => {
          const m = a.match;
          if (!m || m.status !== 'completed' || m.home_score == null) return;
          games++;
          if (m.motm_user_id === authUser.id) motms++;
          const myScore = a.team_id === m.home_team_id ? m.home_score : m.away_score;
          const oppScore = a.team_id === m.home_team_id ? m.away_score : m.home_score;
          if (myScore > oppScore) wins++;
          else if (myScore < oppScore) losses++;
          else draws++;
        });
        const pts = wins * 3 + draws * 1 + games * 1;
        const period = `${since.toLocaleDateString('en-AU', { day:'numeric', month:'short' })} – ${new Date().toLocaleDateString('en-AU', { day:'numeric', month:'short', year:'numeric' })}`;
        setLiveWeeklyWrap({
          period, games, wins, losses, draws, motms,
          ptsEarned: pts,
          newBadges: ach || [],
          hasActivity: games > 0 || (ach && ach.length > 0),
        });
      } catch (e) {
        console.warn('All4Indoor: failed to load weekly wrap', e);
        setLiveWeeklyWrap({ period:'', games:0, wins:0, losses:0, draws:0, motms:0, ptsEarned:0, newBadges:[], hasActivity:false });
      }
    })();
    return () => { cancelled = true; };
  }, [authUser?.id]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── SEASON AWARDS — compute top performers per season from player_stats ───
  // For each season: top scorer (runs or goals depending on sport), top wkts,
  // most games, best strike rate (when computable), most 7s.
  // ═══════════════════════════════════════════════════════════════════════════
  const [liveSeasonAwards, setLiveSeasonAwards] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 2026-05-31: rewrote to fix two bugs:
        //   (1) Season name showed "Unnamed Season" because 552/665
        //       player_stats rows have NULL season_id. The fix: pivot
        //       through competitions (which reliably link to a season)
        //       instead of trusting player_stats.season_id directly.
        //   (2) Most Games was wrong (Isaac Pyers 2 games — real number is
        //       5-6) because player_stats.games_played isn't incremented
        //       reliably for cricket. Counting distinct matches from
        //       attendance + confirmations is much more accurate.
        const [{ data: stats }, { data: cricketMatches }, { data: allMatches }, { data: confs }, { data: atts }] = await Promise.all([
          supabase
            .from('player_stats')
            .select('user_id, games_played, total_runs, total_wickets, total_goals, total_sixes, hat_tricks, competition:competitions(season:seasons(id, name, start_date, end_date), sport:sports(name)), user:users(first_name, last_name)'),
          // Only pull cricket matches that have a scorecard; join through
          // competitions to get the season name + end date for bucketing.
          supabase
            .from('matches')
            .select('id, scheduled_at, status, competition:competitions(season:seasons(id, name, start_date, end_date), sport:sports(slug))')
            .eq('status', 'completed')
            .order('scheduled_at', { ascending: false })
            .limit(2000),
          // For accurate "Most Games": pull every completed match with its
          // competition's season, then count distinct players via attendance.
          supabase
            .from('matches')
            .select('id, status, competition:competitions(season:seasons(id, name, start_date, end_date))')
            .eq('status', 'completed')
            .order('scheduled_at', { ascending: false })
            .limit(5000),
          supabase
            .from('player_match_confirmations')
            .select('match_id, player_id, users:users!player_id(first_name, last_name)')
            .limit(20000),
          supabase
            .from('match_attendance')
            .select('match_id, user_id, users:users!user_id(first_name, last_name)')
            .limit(20000),
        ]);
        if (cancelled) return;

        // Build a (matchId → season name + end date) map so we can bucket
        // attendance rows by season later. Also remember if competition is
        // a sport we count for games (right now: every completed match).
        const matchSeasonByMatchId = {};
        (allMatches || []).forEach(m => {
          const sName = m.competition?.season?.name;
          if (!sName) return;
          matchSeasonByMatchId[m.id] = {
            season: sName,
            startDate: m.competition?.season?.start_date,
            endDate: m.competition?.season?.end_date,
          };
        });

        // Bucket player_stats rows by season. Season is now derived from
        // competition.season — much more reliable than player_stats.season_id.
        const bySeason = {};
        (stats || []).forEach(s => {
          const seasonName = s.competition?.season?.name;
          if (!seasonName) return;  // No reliable season — skip this row
          if (!s.user) return;
          const name = `${s.user.first_name || ''} ${s.user.last_name || ''}`.trim();
          if (!name) return;
          if (!bySeason[seasonName]) bySeason[seasonName] = { season: seasonName, startDate: s.competition?.season?.start_date, endDate: s.competition?.season?.end_date, players: [], cricketAwards: { bestInnings: null, bestBowling: null, bestPartnership: null }, gamesByUser: {} };
          bySeason[seasonName].players.push({
            name,
            games: s.games_played || 0,
            runs: s.total_runs || 0,
            goals: s.total_goals || 0,
            wickets: s.total_wickets || 0,
            sixes: s.total_sixes || 0,
            hatTricks: s.hat_tricks || 0,
            sport: s.competition?.sport?.name || '',
          });
        });

        // ── Accurate Most Games: count distinct (user, match) pairs per
        // season from attendance + confirmations. This sidesteps the
        // unreliable player_stats.games_played counter. ──────────────────
        const seenPairs = new Set();
        const userNames = {};
        const bumpGames = (matchId, userId, first, last) => {
          if (!matchId || !userId) return;
          const ms = matchSeasonByMatchId[matchId];
          if (!ms) return;
          const key = `${matchId}|${userId}`;
          if (seenPairs.has(key)) return;
          seenPairs.add(key);
          const fullName = `${first || ''} ${last || ''}`.trim();
          if (!fullName) return;
          userNames[userId] = fullName;
          if (!bySeason[ms.season]) bySeason[ms.season] = { season: ms.season, startDate: ms.startDate, endDate: ms.endDate, players: [], cricketAwards: {}, gamesByUser: {} };
          bySeason[ms.season].gamesByUser[fullName] = (bySeason[ms.season].gamesByUser[fullName] || 0) + 1;
        };
        (confs || []).forEach(c => bumpGames(c.match_id, c.player_id, c.users?.first_name, c.users?.last_name));
        (atts || []).forEach(a => bumpGames(a.match_id, a.user_id, a.users?.first_name, a.users?.last_name));

        // Cricket awards — scan balls_data per match and track season bests.
        // 2026-05-31: also tracks cricket hat-tricks (3 wickets in 3
        // consecutive balls by the same bowler within one pair). Jake
        // flagged that "hat-tricks" was lumping soccer goals + cricket
        // wickets into the same counter, which is misleading since they're
        // very different stats. Now we keep the goal-based hat_tricks on
        // player_stats untouched (powers the soccer/netball award) and
        // surface cricket hat-tricks as a parallel award computed from
        // balls_data per season.
        const cricketMatchesBySeason = {};
        (cricketMatches || []).forEach(m => {
          const slug = (m.competition?.sport?.slug || '').toLowerCase();
          if (slug !== 'cricket' && slug !== 'indoor-cricket') return;
          // 2026-05-31: skip matches whose competition has no season linkage.
          // Previously we bucketed those into "Unnamed Season" which showed
          // up as a label on the Season Awards page — better to just drop
          // them and only render real, named seasons.
          const sName = m.competition?.season?.name;
          if (!sName) return;
          if (!cricketMatchesBySeason[sName]) cricketMatchesBySeason[sName] = { startDate: m.competition?.season?.start_date, endDate: m.competition?.season?.end_date, ids: [] };
          cricketMatchesBySeason[sName].ids.push(m.id);
        });
        if (Object.keys(cricketMatchesBySeason).length) {
          const allIds = Object.values(cricketMatchesBySeason).flatMap(s => s.ids);
          const { data: scorecards } = await supabase
            .from('cricket_scorecards').select('match_id, balls_data, batter_setup').in('match_id', allIds);
          const scByMatch = {};
          (scorecards || []).forEach(sc => { scByMatch[sc.match_id] = sc; });

          for (const [seasonName, info] of Object.entries(cricketMatchesBySeason)) {
            let bestInnings = null;     // { name, runs }
            let bestBowling = null;     // { name, wickets, runs }
            let bestPartnership = null; // { b1, b2, runs }
            // 2026-05-31: cricket hat-tricks = 3 wickets in 3 consecutive
            // balls by the same bowler within one pair. Keyed by name so
            // we can rank bowlers across the season.
            const cricketHatTrickCounts = {};
            for (const matchId of info.ids) {
              const sc = scByMatch[matchId];
              if (!sc?.balls_data) continue;
              // Iterate each pair key in balls_data (e.g. "home-0", "away-1")
              for (const pairKey of Object.keys(sc.balls_data)) {
                const balls = sc.balls_data[pairKey] || [];
                // Per-pair innings — track each batter's runs in THIS pair only
                const perBatter = {};
                // Per-spell bowling — bowler in this pair's runs+wickets
                let pairBowler = null;
                let bowlRuns = 0, bowlWkts = 0;
                let pairRunsTotal = 0;
                // Cricket hat-trick detector — count consecutive wickets
                // PER BOWLER within this pair. Reset on any non-wicket ball
                // (runs, wides, noballs etc) or when the bowler changes.
                let consecutiveWkts = 0;
                let consecutiveBowler = null;
                for (const b of balls) {
                  const batter = (b.batter || '').trim();
                  const bowler = (b.bowler || '').trim();
                  if (bowler && !pairBowler) pairBowler = bowler;
                  if (b.type === 'run') {
                    if (batter) perBatter[batter] = (perBatter[batter] || 0) + (b.runs || 0);
                    bowlRuns += (b.runs || 0);
                    pairRunsTotal += (b.runs || 0);
                    consecutiveWkts = 0; consecutiveBowler = null;
                  } else if (b.type === 'wicket') {
                    bowlWkts += 1;
                    if (bowler && bowler === consecutiveBowler) {
                      consecutiveWkts += 1;
                    } else {
                      consecutiveWkts = 1; consecutiveBowler = bowler || null;
                    }
                    // 3-in-a-row → cricket hat-trick credit for this bowler
                    if (consecutiveWkts === 3 && consecutiveBowler) {
                      cricketHatTrickCounts[consecutiveBowler] = (cricketHatTrickCounts[consecutiveBowler] || 0) + 1;
                      // Reset so a 4-in-a-row doesn't double-credit
                      consecutiveWkts = 0; consecutiveBowler = null;
                    }
                  } else if (b.type === 'wide' || b.type === 'noball') {
                    bowlRuns += (b.runs || 0);
                    pairRunsTotal += (b.runs || 0);
                    consecutiveWkts = 0; consecutiveBowler = null;
                  } else if (b.type === 'penalty') {
                    pairRunsTotal += (b.runs || 0);
                    consecutiveWkts = 0; consecutiveBowler = null;
                  }
                }
                // Best innings = highest single batter's runs in this pair
                for (const [name, runs] of Object.entries(perBatter)) {
                  if (!bestInnings || runs > bestInnings.runs) bestInnings = { name, runs };
                }
                // Best bowling = highest wickets, tiebreak by lowest runs
                if (pairBowler && bowlWkts > 0) {
                  if (!bestBowling || bowlWkts > bestBowling.wickets || (bowlWkts === bestBowling.wickets && bowlRuns < bestBowling.runs)) {
                    bestBowling = { name: pairBowler, wickets: bowlWkts, runs: bowlRuns };
                  }
                }
                // Best partnership = highest pair total runs
                const setup = (sc.batter_setup || {})[pairKey];
                if (setup) {
                  const b1 = (setup.batter1 || '').trim();
                  const b2 = (setup.batter2 || '').trim();
                  if (b1 && b2 && pairRunsTotal > 0) {
                    if (!bestPartnership || pairRunsTotal > bestPartnership.runs) {
                      bestPartnership = { b1, b2, runs: pairRunsTotal };
                    }
                  }
                }
              }
            }
            // Pick the top cricket hat-trick taker for this season
            let cricketHatTricksWinner = null;
            for (const [name, count] of Object.entries(cricketHatTrickCounts)) {
              if (!cricketHatTricksWinner || count > cricketHatTricksWinner.count) {
                cricketHatTricksWinner = { name, count };
              }
            }
            if (!bySeason[seasonName]) bySeason[seasonName] = { season: seasonName, startDate: info.startDate, endDate: info.endDate, players: [], cricketAwards: {} };
            bySeason[seasonName].cricketAwards = { bestInnings, bestBowling, bestPartnership, cricketHatTricks: cricketHatTricksWinner };
          }
        }

        const seasons = Object.values(bySeason).map(ss => {
          const top = (key) => ss.players.filter(p => p[key] > 0).sort((a, b) => b[key] - a[key])[0];
          // 2026-05-31: sport-scoped top — used for hat-tricks since the
          // counter means different things in different sports (3 wickets
          // in 3 balls for cricket vs 3 goals in one match for soccer).
          const topInSport = (key, sportMatcher) => ss.players
            .filter(p => p[key] > 0 && sportMatcher(p.sport || ''))
            .sort((a, b) => b[key] - a[key])[0];
          // 2026-05-31: split out Soccer vs Netball so each gets its own
          // award. Previously lumped together because both score the same
          // way (3 goals = a hat-trick) — but Jake plays both nights and
          // wants standalone bragging rights per sport. Futsal is treated
          // as Soccer since the scoring rules are the same.
          const isSoccer  = (s) => /Soccer|Futsal/i.test(s);
          const isNetball = (s) => /Netball/i.test(s);
          const cricketAwards = ss.cricketAwards || {};
          // 2026-05-31: Most Games now uses the attendance-derived gamesByUser
          // map instead of player_stats.games_played, which was undercounting
          // cricket players (Isaac Pyers showed 2, actual is 5+).
          const mostGamesEntry = Object.entries(ss.gamesByUser || {})
            .sort((a, b) => b[1] - a[1])[0];
          const mostGamesWinner = mostGamesEntry && mostGamesEntry[1] > 0
            ? { name: mostGamesEntry[0], games: mostGamesEntry[1] }
            : null;
          return {
            season: ss.season,
            startDate: ss.startDate,
            endDate: ss.endDate,
            awards: [
              { title: 'Top Run Scorer', emoji: '', winner: top('runs'), value: (p) => `${p?.runs} runs` },
              { title: 'Most Wickets', emoji: '', winner: top('wickets'), value: (p) => `${p?.wickets} wkts` },
              cricketAwards.bestInnings && { title: 'Best Innings', emoji: '', winner: { name: cricketAwards.bestInnings.name }, value: () => `${cricketAwards.bestInnings.runs} runs` },
              cricketAwards.bestBowling && { title: 'Best Bowling', emoji: '', winner: { name: cricketAwards.bestBowling.name }, value: () => `${cricketAwards.bestBowling.wickets}/${cricketAwards.bestBowling.runs}` },
              cricketAwards.bestPartnership && { title: 'Best Partnership', emoji: '', winner: { name: `${cricketAwards.bestPartnership.b1} & ${cricketAwards.bestPartnership.b2}` }, value: () => `${cricketAwards.bestPartnership.runs} runs` },
              { title: 'Top Goalscorer', emoji: '', winner: top('goals'), value: (p) => `${p?.goals} goals` },
              { title: 'Most 7s', emoji: '', winner: top('sixes'), value: (p) => `${p?.sixes} × 7s` },
              // 2026-05-31: hat-tricks split three ways — cricket from
              // balls_data above (3 wickets / 3 consecutive balls), then
              // soccer-only and netball-only off player_stats.hat_tricks
              // joined to the row's sport_id. Each row in `players` carries
              // a `sport` field, so the same player can show up under both
              // soccer and netball with separate counters per sport.
              cricketAwards.cricketHatTricks && { title: 'Cricket Hat-tricks', emoji: '', winner: { name: cricketAwards.cricketHatTricks.name }, value: () => `${cricketAwards.cricketHatTricks.count} hat-trick${cricketAwards.cricketHatTricks.count === 1 ? '' : 's'}` },
              { title: 'Soccer Hat-tricks', emoji: '', winner: topInSport('hatTricks', isSoccer), value: (p) => `${p?.hatTricks} hat-trick${p?.hatTricks === 1 ? '' : 's'}` },
              { title: 'Netball Hat-tricks', emoji: '', winner: topInSport('hatTricks', isNetball), value: (p) => `${p?.hatTricks} hat-trick${p?.hatTricks === 1 ? '' : 's'}` },
              { title: 'Most Games', emoji: '', winner: mostGamesWinner, value: (p) => `${p?.games} games` },
            ].filter(a => a && a.winner),
          };
        }).filter(s => s.awards.length > 0)
          .sort((a, b) => (b.endDate || '').localeCompare(a.endDate || ''));
        setLiveSeasonAwards(seasons);
      } catch (e) {
        console.warn('All4Indoor: failed to compute season awards', e);
        setLiveSeasonAwards([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── RICH ACTIVITY FEED — merge matches + achievements into one chronological feed
  // ═══════════════════════════════════════════════════════════════════════════
  const [liveRichActivity, setLiveRichActivity] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const loadActivity = async () => {
      try {
        const since = new Date(); since.setDate(since.getDate() - 14); since.setHours(0,0,0,0);
        const sinceIso = since.toISOString();
        const [{ data: ach }, { data: matches }] = await Promise.all([
          supabase.from('player_achievements')
            .select('id, achievement_name, description, emoji, rarity, sport, earned_at, user:users(first_name, last_name)')
            .gte('earned_at', sinceIso)
            .order('earned_at', { ascending: false })
            .limit(30),
          supabase.from('matches')
            .select('id, scheduled_at, home_score, away_score, motm_name, home_team:teams!home_team_id(name), away_team:teams!away_team_id(name), competition:competitions(name, sport:sports(name))')
            .eq('status', 'completed')
            .not('home_score', 'is', null)
            .gte('scheduled_at', sinceIso)
            .order('scheduled_at', { ascending: false })
            .limit(30),
        ]);
        if (cancelled) return;
        const items = [];
        (matches || []).forEach(m => {
          const h = m.home_score ?? 0, a = m.away_score ?? 0;
          const winner = h > a ? m.home_team?.name : (a > h ? m.away_team?.name : null);
          const sport = (m.competition?.sport?.name || '').replace(/^Indoor\s+/i, '');
          items.push({
            type: 'match',
            ts: new Date(m.scheduled_at).getTime(),
            emoji: sportEmoji[sport] || '',
            sport,
            primary: winner ? `${winner} beat ${winner === m.home_team?.name ? m.away_team?.name : m.home_team?.name} ${Math.max(h,a)}–${Math.min(h,a)}` : `${m.home_team?.name} drew with ${m.away_team?.name} ${h}–${a}`,
            secondary: `${m.competition?.name || sport}${m.motm_name ? ` · MVP ${m.motm_name}` : ''}`,
            when: new Date(m.scheduled_at).toLocaleDateString('en-AU', { weekday:'short', day:'numeric', month:'short' }),
          });
        });
        (ach || []).forEach(a => {
          if (!a.user) return;
          const who = `${a.user.first_name || ''} ${a.user.last_name?.[0] || ''}.`.trim();
          items.push({
            type: 'achievement',
            ts: new Date(a.earned_at).getTime(),
            emoji: a.emoji || '',
            sport: a.sport,
            primary: `${who} earned "${a.achievement_name}"`,
            secondary: a.description || '',
            when: new Date(a.earned_at).toLocaleDateString('en-AU', { weekday:'short', day:'numeric', month:'short' }),
            rarity: a.rarity,
          });
        });
        items.sort((x, y) => y.ts - x.ts);
        setLiveRichActivity(items.slice(0, 20));
      } catch (e) {
        console.warn('All4Indoor: failed to load rich activity', e);
      }
    };
    loadActivity();

    // ─── REALTIME — re-fetch whenever a match completes or a badge is earned.
    // Coalesces bursts via a 1.2s debounce so we don't thrash on bulk inserts.
    let debounceTimer = null;
    const debouncedReload = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => { if (!cancelled) loadActivity(); }, 1200);
    };
    const channel = supabase.channel('all4indoor-activity')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'matches' }, debouncedReload)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'player_achievements' }, debouncedReload)
      .subscribe();
    return () => {
      cancelled = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── PLAYER INDEX — every user with their merged stats, keyed by ID ────────
  // The single source of truth for "look up any player by id and show them".
  // Powers profile pages, search, head-to-head compare.
  // ═══════════════════════════════════════════════════════════════════════════
  const [livePlayerIndex, setLivePlayerIndex] = useState(null);
  useEffect(() => {
    let cancelled = false;
    // 2026-05-27: Use fetchAllPages helper to bypass the Supabase 1000-row
    // max_rows cap. Without this, recent signups (e.g. Joshua Cantrell) were
    // invisible to the global player search.
    (async () => {
      try {
        // 2026-07-11: Also pull match_attendance + player_match_confirmations
        // so the search index shows the SAME games-played count as the
        // profile. Before this, search summed player_stats.games_played
        // (undercounted whenever a writeback missed) while profile used
        // MAX(sum, deduped participation) — so Chris Debney showed 2 games
        // in search vs 7 on his profile. Jake called it "not $million dollar
        // look" and he's right — every screen should match.
        //
        // Inner join on matches!inner filters to completed matches only,
        // which is what the profile does client-side.
        const [stats, users, memberships, attendance, confirmations] = await Promise.all([
          fetchAllPages((from, to) => supabase.from('player_stats')
            .select('user_id, games_played, total_runs, total_wickets, total_goals, total_sixes, hat_tricks, highest_score, sport:sports(name, slug), competition:competitions(name)')
            .order('user_id').range(from, to)),
          fetchAllPages((from, to) => supabase.from('public_players')
            .select('id, first_name, last_name, account_type')
            .neq('account_type', 'parent').order('id').range(from, to)),
          // 2026-05-28: also pull team_players → competitions → sports so that
          // sports without per-player stats (notably volleyball) still show up
          // on player cards. Without this, a player who's only ever played
          // volleyball renders with no sport tag.
          fetchAllPages((from, to) => supabase.from('team_players')
            .select('user_id, team:teams!inner(competition:competitions!inner(name, sport:sports!inner(name, slug)))')
            .order('user_id').range(from, to)),
          // 2026-08-13 (Jake): "game count here does not match my 42 here" —
          // search said 42 while the profile modal said 33. The modal counts
          // completed matches WITH SCORES where the player's team was actually
          // playing; this index counted bare participation on ANY completed
          // match, sweeping in scratch/TEST games and matches nobody scored.
          // Pull the fields the strict rule needs so both screens count the
          // same games.
          fetchAllPages((from, to) => supabase.from('match_attendance')
            .select('user_id, match_id, team_id, match:matches!inner(status, home_team_id, away_team_id, home_score, away_score)')
            .eq('match.status', 'completed')
            .order('user_id').range(from, to)),
          fetchAllPages((from, to) => supabase.from('player_match_confirmations')
            .select('player_id, match_id, team_id, match:matches!inner(status, home_team_id, away_team_id, home_score, away_score)')
            .eq('match.status', 'completed')
            .order('player_id').range(from, to)),
        ]);
        if (cancelled) return;

        // ── True games count per user — dedupe (user, match) across attendance + confirmations ──
        // Same logic the profile page uses (see loadCareer). Must run before
        // the index-population loop so we can look up gamesByUser[id] there.
        const seenParticipation = new Set();
        const gamesByUser = {};
        // Strict rule, identical to the profile modal's allMatches: the match
        // is completed, scores were recorded, and the row's team was one of
        // the two teams playing. Scratch/TEST pools and unscored matches
        // don't count anywhere now — same number on every screen.
        const bump = (uid, mid, teamId, m) => {
          if (!uid || !mid || !m) return;
          if (m.home_score == null || m.away_score == null) return;
          if (teamId !== m.home_team_id && teamId !== m.away_team_id) return;
          const k = uid + '|' + mid;
          if (seenParticipation.has(k)) return;
          seenParticipation.add(k);
          gamesByUser[uid] = (gamesByUser[uid] || 0) + 1;
        };
        (attendance || []).forEach(a => bump(a.user_id, a.match_id, a.team_id, a.match));
        (confirmations || []).forEach(c => bump(c.player_id, c.match_id, c.team_id, c.match));
        const index = {};
        (users || []).forEach(u => {
          const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
          if (!name) return;
          index[u.id] = {
            id: u.id,
            name,
            firstName: u.first_name,
            lastName: u.last_name,
            avatar: `${(u.first_name?.[0] || '').toUpperCase()}${(u.last_name?.[0] || '').toUpperCase()}`,
            account_type: u.account_type,
            // 2026-05-31: also track goalsBySport so the profile can show
            // "X soccer goals" + "Y netball goals" separately instead of
            // lumping every goal into one "26 goals" chip. Same for runs/
            // wickets (cricket-only today but future-proof).
            stats: { games:0, runs:0, wickets:0, goals:0, sixes:0, hatTricks:0, highestScore:0, goalsBySport:{}, runsBySport:{}, wicketsBySport:{}, hatTricksBySport:{} },
            sports: new Set(),
            competitions: new Set(),
          };
        });
        (stats || []).forEach(s => {
          const p = index[s.user_id];
          if (!p) return;
          // 2026-06-02 defense-in-depth: if a player_stats row has any
          // scoring activity (runs/wickets/goals) but games_played is 0,
          // count it as 1. The DB backfill (#90/#264) already fixed the
          // historical rows; this just protects against any future
          // regression where writebacks bump stats without bumping
          // games_played.
          const hasStats = (s.total_runs||0) > 0 || (s.total_wickets||0) > 0 || (s.total_goals||0) > 0;
          const safeGames = (s.games_played > 0) ? s.games_played : (hasStats ? 1 : 0);
          // 2026-07-11: sum player_stats games as before, but a follow-up
          // pass below overwrites this with MAX(sum, participation count) —
          // matches the exact logic profile pages use so search + profile
          // agree.
          p.stats.games        += safeGames;
          p.stats.runs         += (s.total_runs      || 0);
          p.stats.wickets      += (s.total_wickets   || 0);
          p.stats.goals        += (s.total_goals     || 0);
          p.stats.sixes        += (s.total_sixes     || 0);
          p.stats.hatTricks    += (s.hat_tricks      || 0);
          p.stats.highestScore = Math.max(p.stats.highestScore, s.highest_score || 0);
          // 2026-05-31: per-sport breakdown so profile can show goals split
          // by sport rather than one lumped number.
          const sk = (s.sport?.slug || '').toLowerCase();
          if (sk) {
            p.stats.goalsBySport[sk]     = (p.stats.goalsBySport[sk]     || 0) + (s.total_goals    || 0);
            p.stats.runsBySport[sk]      = (p.stats.runsBySport[sk]      || 0) + (s.total_runs     || 0);
            p.stats.wicketsBySport[sk]   = (p.stats.wicketsBySport[sk]   || 0) + (s.total_wickets  || 0);
            // 2026-05-31: split hat-tricks by sport too. Profile chip can
            // then show "5 soccer + 2 netball" instead of one lumped "7".
            p.stats.hatTricksBySport[sk] = (p.stats.hatTricksBySport[sk] || 0) + (s.hat_tricks     || 0);
          }
          if (s.sport?.name) p.sports.add(s.sport.name);
          if (s.competition?.name) p.competitions.add(s.competition.name);
        });
        // Layer team memberships on top — any sport a player has a team in
        // gets added, even if there are no per-player stat rows for that sport.
        // Fixes volleyball (and other no-stats sports) missing from cards.
        (memberships || []).forEach(m => {
          const p = index[m.user_id];
          if (!p) return;
          const sname = m.team?.competition?.sport?.name;
          const cname = m.team?.competition?.name;
          if (sname) p.sports.add(sname);
          if (cname) p.competitions.add(cname);
        });
        // 2026-07-11: reconcile games count — take MAX of player_stats sum
        // and participation count. Mirrors profile page's line ~1109 logic
        // (see loadCareer). If participation is higher (writebacks missed),
        // trust participation; if player_stats is higher (edge case where
        // a player has stats rows but no participation record), keep that.
        Object.values(index).forEach(p => {
          const participation = gamesByUser[p.id] || 0;
          if (participation > p.stats.games) p.stats.games = participation;
        });
        // Convert sets to arrays for easier rendering
        Object.values(index).forEach(p => {
          p.sports = Array.from(p.sports);
          p.competitions = Array.from(p.competitions);
        });
        setLivePlayerIndex(index);
      } catch (e) {
        console.warn('All4Indoor: failed to build player index', e);
        setLivePlayerIndex({});
      }
    })();
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── LIVE TEAM INDEX ────────────────────────────────────────────────────
  // Mirror of livePlayerIndex but for teams. Powers the global search so a
  // user typing 'slap' finds both Slap Happy (team) and any player called
  // Slap. Tap a team → opens TeamProfileModal. Active (non-archived) teams
  // only; pulls comp name + sport for the result subtitle.
  // ═══════════════════════════════════════════════════════════════════════════
  const [liveTeamIndex, setLiveTeamIndex] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 2026-05-27: paginated so >1000 teams don't get dropped from search.
        const teams = await fetchAllPages((from, to) => supabase.from('teams')
          .select('id, name, competition:competitions(name, sport:sports(name, slug))')
          .eq('is_archived', false)
          .order('name').range(from, to));
        if (cancelled) return;
        const idx = {};
        for (const t of (teams || [])) {
          const name = (t.name || '').trim();
          if (!name) continue;
          idx[t.id] = {
            id: t.id,
            name,
            avatar: name.split(/\s+/).slice(0,2).map(s => s[0] || '').join('').toUpperCase().slice(0,2) || 'T',
            comp: t.competition?.name || '',
            sport: t.competition?.sport?.name || '',
            sportSlug: (t.competition?.sport?.slug || '').toLowerCase(),
          };
        }
        setLiveTeamIndex(idx);
      } catch (e) {
        console.warn('All4Indoor: failed to build team index', e);
        setLiveTeamIndex({});
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── MATCH RECAP — pulls per-match data when a recent-match row is clicked
  // Designed to win former Spawtz players over: the recap shows pair-by-pair
  // batting + bowling figures + ball-by-ball log, all of which Spawtz never
  // surfaced in one place. Cricket is the primary target; other sports get
  // a lighter score summary.
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!selectedMatchId) { setSelectedMatchData(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data: m } = await supabase.from('matches')
          .select('id, scheduled_at, status, home_score, away_score, home_team:teams!home_team_id(id, name), away_team:teams!away_team_id(id, name), competition:competitions(name, sport:sports(name, slug)), court:courts(name), venue:venues(slug, name)')
          .eq('id', selectedMatchId).single();
        if (cancelled || !m) return;
        const sportSlug = (m.competition?.sport?.slug || '').toLowerCase();
        let cricket = null;
        if (sportSlug === 'cricket') {
          const { data: sc } = await supabase.from('cricket_scorecards')
            .select('balls_data, batter_setup, bowler_setup, total_pairs')
            .eq('match_id', selectedMatchId).maybeSingle();
          cricket = sc || null;
        }
        if (!cancelled) setSelectedMatchData({ match: m, cricket });
      } catch (e) {
        console.warn('Match recap load failed', e);
        if (!cancelled) setSelectedMatchData({ error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, [selectedMatchId]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── PLAYER DETAIL — full deep-dive for a clicked player ──────────────────
  // Fetches their teams, achievements, recent matches when selectedPlayerId is set.
  // ═══════════════════════════════════════════════════════════════════════════
  const [livePlayerDetail, setLivePlayerDetail] = useState(null);
  useEffect(() => {
    if (!selectedPlayerId) { setLivePlayerDetail(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const [{ data: teamRows }, { data: badges }, { data: confs }, { data: att }] = await Promise.all([
          supabase.from('team_players').select('team:teams(id, name, competition:competitions(name, sport:sports(name)))').eq('user_id', selectedPlayerId),
          supabase.from('player_achievements').select('achievement_name, description, emoji, rarity, earned_at, sport').eq('user_id', selectedPlayerId).order('earned_at', { ascending: false }).limit(20),
          supabase.from('player_match_confirmations').select('match_id, team_id, match:matches!inner(id, scheduled_at, status, home_team_id, away_team_id, home_score, away_score, home_team:teams!home_team_id(name), away_team:teams!away_team_id(name), competition:competitions(name, sport:sports(slug)))').eq('player_id', selectedPlayerId),
          supabase.from('match_attendance').select('match_id, team_id, match:matches!inner(id, scheduled_at, status, home_team_id, away_team_id, home_score, away_score, home_team:teams!home_team_id(name), away_team:teams!away_team_id(name), competition:competitions(name, sport:sports(slug)))').eq('user_id', selectedPlayerId),
        ]);
        if (cancelled) return;
        // Merge confirmations + attendance, dedupe by match_id
        const seen = new Set();
        const matches = [];
        [...(confs || []), ...(att || [])].forEach(row => {
          if (!row.match || seen.has(row.match_id)) return;
          seen.add(row.match_id);
          const m = row.match;
          if (m.status !== 'completed' || m.home_score == null) return;
          const onHome = row.team_id === m.home_team_id;
          const myScore = onHome ? m.home_score : m.away_score;
          const oppScore = onHome ? m.away_score : m.home_score;
          const opp = onHome ? m.away_team?.name : m.home_team?.name;
          const myTeam = onHome ? m.home_team?.name : m.away_team?.name;
          matches.push({
            id: m.id,
            ts: new Date(m.scheduled_at).getTime(),
            when: new Date(m.scheduled_at).toLocaleDateString('en-AU', { day:'numeric', month:'short', year:'numeric' }),
            myTeam, opp, myScore, oppScore,
            result: myScore > oppScore ? 'W' : myScore < oppScore ? 'L' : 'D',
            comp: m.competition?.name,
            sportSlug: (m.competition?.sport?.slug || '').toLowerCase(),
          });
        });
        matches.sort((a, b) => b.ts - a.ts);

        // Compute career CRICKET contribution by fetching balls_data for
        // every cricket match the player was in and summing
        // (bat runs they scored) - (bowl runs they conceded). This is the
        // killer Spawtz-era stat indoor cricketers quote constantly —
        // shows up as a chip on the player profile + career strip in the
        // recap modal. Worth a single batched fetch per profile open.
        let careerContribution = null;
        let cricketGamesPlayed = 0;
        // Per-match cricket history so we can extract personal bests,
        // a top-5 highlight reel, and a contribution sparkline. Keeps
        // the data we'd otherwise have to refetch later for free.
        const cricketHistory = []; // [{ matchId, contribution, batRuns, bowlRuns, wickets, ts, opp }]
        const cricketMatchIds = matches.filter(m => m.sportSlug === 'cricket').map(m => m.id);
        // Use the player's actual name from livePlayerIndex (set elsewhere)
        // to match against balls_data.batter / balls_data.bowler strings.
        const me = (livePlayerIndex && livePlayerIndex[selectedPlayerId]) || null;
        const myName = (me?.name || '').trim().toLowerCase();
        // Partnership accumulator — Spawtz-style "best partner" stats.
        // For each pair Ramon was in (batter1 or batter2), the OTHER batter
        // is his partner and the pair's total runs count as a partnership.
        // Aggregate by partner across the season: partnerships count,
        // total runs, average, personal best partnership.
        const partnersAgg = {};
        // Career counter accumulators — built up across every cricket match
        // the player has appeared in. Spawtz-style "career totals that build
        // over time" panel on the player profile.
        let careerBallsFaced = 0;
        let careerDotBallsFaced = 0;
        let careerFours = 0;
        let careerSevens = 0;
        let careerTimesOut = 0;
        let careerBallsBowled = 0;
        let careerDotBallsBowled = 0;
        let careerWides = 0;
        let careerNoBalls = 0;
        let careerWickets = 0;
        // 2026-05-28: Dismissal-type breakdowns.
        //   careerDismissalsByType — HOW the player got out (batting view)
        //   careerWicketsByType    — HOW the player got batters out (bowling view)
        // Both keyed by the `dismissal` field on wicket balls.
        const careerDismissalsByType = { caught: 0, bowled: 0, stumped: 0, lbw: 0, run_out: 0, other: 0 };
        const careerWicketsByType    = { caught: 0, bowled: 0, stumped: 0, lbw: 0, run_out: 0, other: 0 };
        // Phase 8 — banter stats trackers
        const vsBowler = {};   // bowlerName → { runs, ballsFaced, outs }  (when ME batting)
        const vsBatter = {};   // batterName → { runs, ballsBowled, wickets } (when ME bowling)
        if (cricketMatchIds.length && myName) {
          try {
            const { data: scs } = await supabase.from('cricket_scorecards')
              .select('match_id, balls_data, batter_setup')
              .in('match_id', cricketMatchIds);
            let total = 0;
            const matchById = Object.fromEntries(matches.map(m => [m.id, m]));
            for (const sc of (scs || [])) {
              let batRuns = 0;
              let bowlRuns = 0;
              let wkts = 0;
              let played = false;
              for (const k of Object.keys(sc.balls_data || {})) {
                for (const b of sc.balls_data[k]) {
                  const batMatch = (b.batter || '').trim().toLowerCase() === myName;
                  const bowlMatch = (b.bowler || '').trim().toLowerCase() === myName;
                  if (batMatch) {
                    played = true;
                    const opp = (b.bowler || '').trim();
                    if (opp) {
                      if (!vsBowler[opp]) vsBowler[opp] = { name: opp, runs: 0, ballsFaced: 0, outs: 0 };
                    }
                    if (b.type === 'run') {
                      batRuns += (b.runs || 0);
                      careerBallsFaced += 1;
                      if ((b.runs || 0) === 0) careerDotBallsFaced += 1;
                      else if (b.runs === 4) careerFours += 1;
                      else if (b.runs === 7) careerSevens += 1;
                      if (opp) { vsBowler[opp].runs += (b.runs || 0); vsBowler[opp].ballsFaced += 1; }
                    } else if (b.type === 'wicket') {
                      careerBallsFaced += 1;
                      careerTimesOut += 1;
                      // 2026-05-28: tally HOW I got out, keyed off the dismissal field
                      const d = (b.dismissal || '').toLowerCase();
                      if (careerDismissalsByType[d] !== undefined) careerDismissalsByType[d] += 1;
                      else careerDismissalsByType.other += 1;
                      if (opp) { vsBowler[opp].outs += 1; vsBowler[opp].ballsFaced += 1; }
                    }
                  }
                  if (bowlMatch) {
                    played = true;
                    const opp = (b.batter || '').trim();
                    if (opp) {
                      if (!vsBatter[opp]) vsBatter[opp] = { name: opp, runs: 0, ballsBowled: 0, wickets: 0 };
                    }
                    if (b.type === 'wicket') {
                      wkts++; careerWickets++; careerBallsBowled += 1;
                      // 2026-05-28: tally HOW I got the batter out. Note:
                      // run_outs aren't traditionally credited to a bowler
                      // (they're fielding wickets) but our scorer attributes
                      // any wicket on the current ball to the bowler — we
                      // still track them under run_out so the breakdown is
                      // honest about the data shape.
                      const d = (b.dismissal || '').toLowerCase();
                      if (careerWicketsByType[d] !== undefined) careerWicketsByType[d] += 1;
                      else careerWicketsByType.other += 1;
                      if (opp) { vsBatter[opp].wickets += 1; vsBatter[opp].ballsBowled += 1; }
                    }
                    if (b.type === 'run') {
                      bowlRuns += (b.runs || 0);
                      careerBallsBowled += 1;
                      if ((b.runs || 0) === 0) careerDotBallsBowled += 1;
                      if (opp) { vsBatter[opp].runs += (b.runs || 0); vsBatter[opp].ballsBowled += 1; }
                    }
                    if (b.type === 'wide') { bowlRuns += (b.runs || 0); careerWides += 1; if (opp) vsBatter[opp].runs += (b.runs || 0); }
                    if (b.type === 'noball') { bowlRuns += (b.runs || 0); careerNoBalls += 1; if (opp) vsBatter[opp].runs += (b.runs || 0); }
                  }
                }
              }
              if (played) {
                // 2026-07-07: was `c = batRuns - bowlRuns` which didn't
                // include the -5-per-wicket bonus that indoor cricket bakes
                // into a bowler's net R figure. The match report page uses
                // R (which HAS the bonus), so a bowler who took 3/13 showed
                // contribution +35 there and only +20 here. Now aligned:
                //   contribution = batRuns - bowlRuns + 5 * wickets
                //                = batRuns - (bowlRuns - 5 * wickets)
                //                = batRuns - R_indoor
                // Chris Debney example: 33 - 13 + 15 = 35 (matches report).
                const c = batRuns - bowlRuns + (5 * wkts);
                total += c;
                cricketGamesPlayed++;
                const m = matchById[sc.match_id];
                cricketHistory.push({
                  matchId: sc.match_id,
                  contribution: c,
                  batRuns,
                  bowlRuns,
                  wickets: wkts,
                  ts: m?.ts || 0,
                  opp: m?.opp || '',
                  when: m?.when || '',
                });
              }

              // Partnerships pass — for each pair Ramon batted in, find the
              // partner + the pair's total runs (sum from balls_data).
              const setup = sc.batter_setup || {};
              for (const key of Object.keys(setup)) {
                const pair = setup[key] || {};
                const b1 = (pair.batter1 || '').trim();
                const b2 = (pair.batter2 || '').trim();
                if (!b1 && !b2) continue;
                const inPair = b1.toLowerCase() === myName || b2.toLowerCase() === myName;
                if (!inPair) continue;
                const partner = b1.toLowerCase() === myName ? b2 : b1;
                if (!partner) continue; // batting alone — skip
                // Pair total runs (count run-balls + extras in this pair)
                let pairRuns = 0;
                for (const b of (sc.balls_data?.[key] || [])) {
                  if (b.type === 'run') pairRuns += (b.runs || 0);
                  else if (b.type === 'wide' || b.type === 'noball') pairRuns += (b.runs || 2);
                  else if (b.type === 'penalty') pairRuns += (b.runs || 0);
                }
                if (!partnersAgg[partner]) partnersAgg[partner] = { partner, partnerships: 0, totalRuns: 0, best: -Infinity };
                partnersAgg[partner].partnerships += 1;
                partnersAgg[partner].totalRuns += pairRuns;
                if (pairRuns > partnersAgg[partner].best) partnersAgg[partner].best = pairRuns;
              }
            }
            careerContribution = cricketGamesPlayed > 0 ? total : null;
          } catch (e) { console.warn('career contribution calc failed', e?.message); }
        }
        // Compile partnership rows, ranked by avg desc. At least 1
        // partnership required; tied avg breaks on more partnerships.
        const partnerships = Object.values(partnersAgg).map(p => ({
          ...p,
          avg: Math.round((p.totalRuns / p.partnerships) * 10) / 10,
        })).sort((a, b) => b.avg - a.avg || b.partnerships - a.partnerships);
        const avgContribution = (careerContribution != null && cricketGamesPlayed > 0)
          ? Math.round((careerContribution / cricketGamesPlayed) * 10) / 10
          : null;

        // Career highs + top-5 highlight reel
        const sortedHistory = [...cricketHistory].sort((a, b) => b.contribution - a.contribution);
        const topMatches = sortedHistory.slice(0, 5);
        const bestContribution = sortedHistory[0]?.contribution ?? null;
        const mostRunsInMatch = cricketHistory.reduce((m, x) => Math.max(m, x.batRuns), 0);
        const mostWicketsInMatch = cricketHistory.reduce((m, x) => Math.max(m, x.wickets), 0);
        // 2026-05-28: Best bowling figures (Spawtz-style "X/Y" — wickets/runs).
        // Rank by wickets desc, then runs conceded asc (cheapest spell wins
        // on a tie). Only consider matches where the player took at least
        // one wicket so a 0/15 doesn't show as "best".
        const bestBowlingMatch = cricketHistory
          .filter(h => h.wickets > 0)
          .sort((a, b) => b.wickets - a.wickets || a.bowlRuns - b.bowlRuns)[0] || null;
        const bestBowlingFigures = bestBowlingMatch
          ? { wickets: bestBowlingMatch.wickets, runs: bestBowlingMatch.bowlRuns, opp: bestBowlingMatch.opp, when: bestBowlingMatch.when }
          : null;
        // Sparkline data (oldest → newest) so a left-to-right line shows the
        // player's contribution trend across the season.
        const sparkline = [...cricketHistory].sort((a, b) => a.ts - b.ts).map(h => h.contribution);

        const teams = (teamRows || []).map(r => r.team).filter(Boolean).map(t => ({
          id: t.id, name: t.name, comp: t.competition?.name,
          sport: (t.competition?.sport?.name || '').replace(/^Indoor\s+/i, ''),
        }));
        // Dedupe teams
        const teamSeen = new Set();
        const dedupedTeams = teams.filter(t => { if (teamSeen.has(t.id)) return false; teamSeen.add(t.id); return true; });
        // Derived career rates — only meaningful if there are enough balls
        // to compute against, otherwise leave null so the UI renders '—'.
        const careerRuns = cricketHistory.reduce((s, m) => s + m.batRuns, 0);
        const careerRunsConceded = cricketHistory.reduce((s, m) => s + m.bowlRuns, 0);
        const careerStrikeRate = careerBallsFaced > 0
          ? Math.round((careerRuns / careerBallsFaced) * 1000) / 10
          : null;
        const careerEconomy = careerBallsBowled >= 6
          ? Math.round((careerRunsConceded / (careerBallsBowled / 6)) * 10) / 10
          : null;
        // 2026-05-27: added RA (Runs Avg = R / TO) and WA (Wickets Avg = RC / W)
        // to match Spawtz's headline cricket stats. Both inputs were already
        // in memory — we just weren't doing the division. RA is the classic
        // "batting average" (runs per dismissal); WA is the classic "bowling
        // average" (runs conceded per wicket — lower is better). Show "—" in
        // the UI when there's no denominator (no outs / no wickets) and one
        // sentinel value (NOT_OUT / "999" big number) would mislead.
        const careerRunsAverage = careerTimesOut > 0
          ? Math.round((careerRuns / careerTimesOut) * 10) / 10
          : null;
        const careerWicketsAverage = careerWickets > 0
          ? Math.round((careerRunsConceded / careerWickets) * 10) / 10
          : null;

        // ─── PHASE 8: QUIRKY STATS ────────────────────────────────────────
        // BUNNY — the bowler you've scored most runs off. Min 3 balls so a
        //   one-game player who faced a bowler an over still gets credit.
        // NEMESIS — the bowler who's dismissed you. Min 1 out — one
        //   dismissal is enough banter for a fresh player.
        // PINATA — the batter you've conceded most runs to (min 3 balls).
        const bunnyCandidates = Object.values(vsBowler).filter(v => v.ballsFaced >= 3);
        const bunny = bunnyCandidates.sort((a, b) => b.runs - a.runs)[0] || null;
        const nemesisCandidates = Object.values(vsBowler).filter(v => v.outs >= 1);
        const nemesis = nemesisCandidates.sort((a, b) => b.outs - a.outs || a.runs - b.runs)[0] || null;
        const pinataCandidates = Object.values(vsBatter).filter(v => v.ballsBowled >= 3);
        const pinata = pinataCandidates.sort((a, b) => b.runs - a.runs)[0] || null;

        // STREAK — most-recent consecutive matches with positive contribution.
        // Walks cricketHistory newest → oldest, counts while contrib > 0.
        const newestFirst = [...cricketHistory].sort((a, b) => b.ts - a.ts);
        let streakCount = 0;
        let streakKind = null;
        if (newestFirst.length) {
          const sign = newestFirst[0].contribution >= 0 ? 'hot' : 'cold';
          for (const m of newestFirst) {
            const matches = sign === 'hot' ? m.contribution >= 0 : m.contribution < 0;
            if (matches) streakCount++;
            else break;
          }
          if (streakCount >= 2) streakKind = sign;
        }

        // CLUTCH — avg contribution in matches decided by < 10 runs.
        // Look up the match in matches[] to get scores, compute margin.
        // Min 1 close game — even a single tight one is bragging fodder.
        const matchScoreById = Object.fromEntries(matches.map(m => [m.id, m]));
        const closeGames = cricketHistory.filter(h => {
          const m = matchScoreById[h.matchId];
          if (!m) return false;
          return Math.abs((m.myScore || 0) - (m.oppScore || 0)) < 10;
        });
        const clutchAvg = closeGames.length >= 1
          ? Math.round((closeGames.reduce((s, m) => s + m.contribution, 0) / closeGames.length) * 10) / 10
          : null;
        const clutchGames = closeGames.length;

        // FUN FACT — a one-liner generator picking randomly from whichever
        // computed stats are most amusingly large for this player. Lowered
        // thresholds so first-game players still get something to share.
        const funFacts = [];
        if (careerDotBallsFaced >= 5) funFacts.push(`You've stared down ${careerDotBallsFaced} career dot balls — that's ${(careerDotBallsFaced/6).toFixed(1)} maiden overs of pure suffering.`);
        if (careerSevens >= 1) funFacts.push(`${careerSevens} career seven${careerSevens === 1 ? '' : 's'} — that's ${careerSevens * 7} run${careerSevens * 7 === 1 ? '' : 's'} delivered in a single swing.`);
        if (careerFours >= 3) funFacts.push(`${careerFours} career boundary fours. The roof keeps an eye out for you now.`);
        if (careerDotBallsBowled >= 5) funFacts.push(`You've bowled ${careerDotBallsBowled} career dot${careerDotBallsBowled === 1 ? '' : 's'}. Your strangle game is real.`);
        if (careerWides >= 3) funFacts.push(`${careerWides} career wides conceded. The umpire knows you by name.`);
        if (careerWickets >= 1) funFacts.push(`${careerWickets} career wicket${careerWickets === 1 ? '' : 's'} — that's a pavilion walk you've personally arranged.`);
        if (cricketGamesPlayed >= 1 && careerContribution > 0) funFacts.push(`Across ${cricketGamesPlayed} cricket game${cricketGamesPlayed === 1 ? '' : 's'} you've contributed a net ${careerContribution >= 0 ? '+' : ''}${careerContribution}. That's not luck.`);
        if (cricketGamesPlayed >= 1 && careerContribution < 0) funFacts.push(`Net contribution of ${careerContribution} across ${cricketGamesPlayed} game${cricketGamesPlayed === 1 ? '' : 's'} — plenty of upside to come.`);
        if (careerTimesOut === 0 && careerBallsFaced >= 6) funFacts.push(`You've faced ${careerBallsFaced} balls and never been out. The bowlers are stumped.`);
        const funFact = funFacts.length ? funFacts[Math.floor(Math.random() * funFacts.length)] : null;

        // ─── SKILL AUTO-BADGES ─────────────────────────────────────────
        // Auto-derived descriptors that anyone with enough sample size
        // can earn. Each badge has a threshold tuned so it triggers
        // when a player demonstrates the trait — not just because they
        // played one game. These are the share-with-mates flexes.
        const skillBadges = [];
        // POWER HITTER — high runs-per-ball (avg ≥ 1.0 over ≥6 balls)
        if (careerBallsFaced >= 6 && careerRuns / careerBallsFaced >= 1.0) {
          skillBadges.push({ emoji: '', name: 'Power Hitter', desc: `${(careerRuns / careerBallsFaced).toFixed(2)} runs per ball`, color: '#f59e0b' });
        }
        // BOUNDARY BEAST — 4s + 7s ≥ 4 combined
        if ((careerFours + careerSevens) >= 4) {
          skillBadges.push({ emoji: '', name: 'Boundary Beast', desc: `${careerFours} fours · ${careerSevens} sevens`, color: '#dc2626' });
        }
        // BRICK WALL — high dot-ball-faced percentage means you blocked a lot (≥ 40% over ≥6 balls)
        if (careerBallsFaced >= 6 && (careerDotBallsFaced / careerBallsFaced) >= 0.40) {
          skillBadges.push({ emoji: '', name: 'Brick Wall', desc: `${Math.round((careerDotBallsFaced / careerBallsFaced) * 100)}% dots faced`, color: '#3b82f6' });
        }
        // WICKET TAKER — ≥1 wicket per game (sample ≥ 1 game with bowling)
        if (cricketGamesPlayed >= 1 && careerWickets >= cricketGamesPlayed) {
          skillBadges.push({ emoji: '', name: 'Wicket Taker', desc: `${careerWickets} wkts in ${cricketGamesPlayed} game${cricketGamesPlayed === 1 ? '' : 's'}`, color: '#7c3aed' });
        }
        // STRANGLE — high dot-ball-bowled rate (≥ 30% over ≥6 balls bowled)
        if (careerBallsBowled >= 6 && (careerDotBallsBowled / careerBallsBowled) >= 0.30) {
          skillBadges.push({ emoji: '', name: 'Strangle', desc: `${Math.round((careerDotBallsBowled / careerBallsBowled) * 100)}% dot balls bowled`, color: '#0891b2' });
        }
        // ECONOMIST — low economy (under 18 / 6-ball over equivalent) over ≥6 balls bowled
        if (careerBallsBowled >= 6 && careerEconomy != null && careerEconomy < 18) {
          skillBadges.push({ emoji: '', name: 'Economist', desc: `${careerEconomy} runs/over`, color: '#059669' });
        }
        // RUN MACHINE — 30+ career runs
        if (careerRuns >= 30) {
          skillBadges.push({ emoji: '', name: 'Run Machine', desc: `${careerRuns} career runs`, color: '#ec4899' });
        }
        // ALL-ROUNDER — 10+ runs AND 1+ wicket
        if (careerRuns >= 10 && careerWickets >= 1) {
          skillBadges.push({ emoji: '', name: 'All-Rounder', desc: `${careerRuns} runs · ${careerWickets} wkts`, color: '#8b5cf6' });
        }
        // IRON ARM — bowled ≥ 6 balls (a full over)
        if (careerBallsBowled >= 12) {
          skillBadges.push({ emoji: '', name: 'Iron Arm', desc: `${Math.floor(careerBallsBowled / 6)} overs bowled all-time`, color: '#6366f1' });
        }
        // NEVER OUT — faced 6+ balls, never dismissed
        if (careerBallsFaced >= 6 && careerTimesOut === 0) {
          skillBadges.push({ emoji: '', name: 'Untouchable', desc: `0 dismissals in ${careerBallsFaced} balls`, color: '#10b981' });
        }

        setLivePlayerDetail({
          teams: dedupedTeams,
          badges: badges || [],
          recentMatches: matches.slice(0, 10),
          allMatches: matches,
          careerContribution,
          avgContribution,
          cricketGamesPlayed,
          playerName: me?.name || null,
          // Phase 5 highlights
          bestContribution,
          mostRunsInMatch,
          mostWicketsInMatch,
          topMatches,
          sparkline,
          // Phase 6 — partnerships ranked by avg
          partnerships,
          // Phase 8 — banter / quirky stats
          bunny, nemesis, pinata,
          streakCount, streakKind,
          clutchAvg, clutchGames,
          funFact,
          skillBadges,
          // Phase 7 — full career career batting + bowling counters
          career: {
            runs: careerRuns,
            ballsFaced: careerBallsFaced,
            dotBallsFaced: careerDotBallsFaced,
            fours: careerFours,
            sevens: careerSevens,
            timesOut: careerTimesOut,
            strikeRate: careerStrikeRate,
            runsAverage: careerRunsAverage,   // RA — runs per dismissal
            wickets: careerWickets,
            runsConceded: careerRunsConceded,
            ballsBowled: careerBallsBowled,
            dotBallsBowled: careerDotBallsBowled,
            wides: careerWides,
            noBalls: careerNoBalls,
            economy: careerEconomy,
            wicketsAverage: careerWicketsAverage,  // WA — runs conceded per wicket (lower=better)
            // 2026-05-28: dismissal-type breakdowns. Both objects keyed
            // by dismissal name (caught/bowled/stumped/lbw/run_out/other).
            // dismissalsByType  = HOW you got out (batting view)
            // wicketsByType     = HOW you got batters out (bowling view)
            dismissalsByType: careerDismissalsByType,
            wicketsByType: careerWicketsByType,
            // 2026-05-28: career-best bowling figures (X wickets for Y runs).
            // null if the player has never taken a wicket.
            bestBowlingFigures,
          },
        });
      } catch (e) {
        console.warn('All4Indoor: failed to load player detail', e);
        setLivePlayerDetail({ teams: [], badges: [], recentMatches: [], allMatches: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPlayerId]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── TEAM DETAIL — pulls the data behind the public team profile modal ───
  // Roster, ladder position, recent matches + form, upcoming fixture, and
  // critically — each player's avg contribution across this team's matches
  // so the squad table can rank highest → lowest. The shareable artefact.
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!selectedTeamId) { setLiveTeamDetail(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const [{ data: team }, { data: ladderRow }, { data: rosterRows }, { data: matches }] = await Promise.all([
          supabase.from('teams')
            .select('id, name, logo_url, captain_id, competition:competitions(id, name, sport:sports(name, slug))')
            .eq('id', selectedTeamId).single(),
          supabase.from('ladder')
            .select('played, wins, draws, losses, points, for_score, against_score, position, percentage')
            .eq('team_id', selectedTeamId).maybeSingle(),
          supabase.from('team_players')
            .select('user_id, role, is_captain, users(id, first_name, last_name)')
            .eq('team_id', selectedTeamId),
          supabase.from('matches')
            .select('id, scheduled_at, status, home_team_id, away_team_id, home_score, away_score, home_team:teams!home_team_id(name), away_team:teams!away_team_id(name), court:courts(name)')
            .or(`home_team_id.eq.${selectedTeamId},away_team_id.eq.${selectedTeamId}`)
            .order('scheduled_at', { ascending: false })
            .limit(30),
        ]);
        if (cancelled || !team) return;
        const sportSlug = (team.competition?.sport?.slug || '').toLowerCase();

        // Split matches into completed (for stats + recent list) and upcoming.
        const completed = (matches || []).filter(m => m.status === 'completed' && m.home_score != null);
        const upcoming = (matches || []).filter(m => m.status === 'scheduled')
          .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
        const nextMatch = upcoming[0] || null;

        // Form line (last 5 results, newest first)
        const form = completed.slice(0, 5).map(m => {
          const onHome = m.home_team_id === selectedTeamId;
          const myScore = onHome ? m.home_score : m.away_score;
          const oppScore = onHome ? m.away_score : m.home_score;
          return myScore > oppScore ? 'W' : myScore < oppScore ? 'L' : 'D';
        });

        // Build roster with player names. Each entry will get contribution
        // stats + win/loss accumulators added below for cricket teams.
        const roster = (rosterRows || []).map(r => ({
          userId: r.user_id,
          name: `${r.users?.first_name || ''} ${r.users?.last_name || ''}`.trim() || '—',
          isCaptain: !!r.is_captain || (r.role || '').toLowerCase() === 'captain',
          gamesForTeam: 0, // filled below for cricket
          wins: 0,
          winRate: null,
          totalContribution: 0,
          avgContribution: null,
          batRuns: 0,
          bowlRuns: 0,
          wickets: 0,
        }));

        // For cricket teams, batch fetch every completed match's scorecard
        // and compute each roster member's contribution stats. Match
        // attendance is inferred from balls_data (who's listed as batter
        // or bowler) — this is the source of truth the scorer wrote.
        if (sportSlug === 'cricket' && completed.length) {
          try {
            const matchIds = completed.map(m => m.id);
            const { data: scs } = await supabase.from('cricket_scorecards')
              .select('match_id, balls_data')
              .in('match_id', matchIds);
            // Index roster by lower-cased name for matching against balls_data strings
            const byName = {};
            for (const r of roster) byName[r.name.toLowerCase()] = r;
            // Map matchId → "did THIS team win?" so we can attribute per-
            // player wins. We already have the completed matches array;
            // index by id for fast lookup.
            const matchById = Object.fromEntries(completed.map(m => [m.id, m]));
            for (const sc of (scs || [])) {
              const m = matchById[sc.match_id];
              if (!m) continue;
              const onHome = m.home_team_id === selectedTeamId;
              const usScore = onHome ? m.home_score : m.away_score;
              const oppScore = onHome ? m.away_score : m.home_score;
              const teamWon = usScore > oppScore;
              // Track who played in THIS match
              const matchPlayers = new Map(); // playerKey → { bat, bowl, wickets }
              for (const k of Object.keys(sc.balls_data || {})) {
                for (const b of sc.balls_data[k]) {
                  const bk = (b.batter || '').trim().toLowerCase();
                  const wk = (b.bowler || '').trim().toLowerCase();
                  if (bk && byName[bk]) {
                    if (!matchPlayers.has(bk)) matchPlayers.set(bk, { bat: 0, bowl: 0, wickets: 0 });
                    if (b.type === 'run') matchPlayers.get(bk).bat += (b.runs || 0);
                  }
                  if (wk && byName[wk]) {
                    if (!matchPlayers.has(wk)) matchPlayers.set(wk, { bat: 0, bowl: 0, wickets: 0 });
                    if (b.type === 'wicket') matchPlayers.get(wk).wickets += 1;
                    if (b.type === 'run' || b.type === 'wide' || b.type === 'noball') {
                      matchPlayers.get(wk).bowl += (b.runs || 0);
                    }
                  }
                }
              }
              for (const [pk, stats] of matchPlayers) {
                const r = byName[pk];
                if (!r) continue;
                r.gamesForTeam += 1;
                if (teamWon) r.wins += 1;
                r.batRuns += stats.bat;
                r.bowlRuns += stats.bowl;
                r.wickets += stats.wickets;
                r.totalContribution += (stats.bat - stats.bowl);
              }
            }
            for (const r of roster) {
              if (r.gamesForTeam > 0) {
                r.avgContribution = Math.round((r.totalContribution / r.gamesForTeam) * 10) / 10;
                r.winRate = Math.round((r.wins / r.gamesForTeam) * 100);
              }
            }
          } catch (e) { console.warn('team contribution calc failed', e?.message); }
        }

        // 2026-06-01: non-cricket sports — count games per squad member from
        // match_attendance (and fall back to player_match_confirmations if
        // attendance is sparse). Soccer/Netball/Volleyball teams were all
        // showing "No games yet" for every squad member because the original
        // logic only ran for cricket. Fix lifts that gate.
        if (sportSlug !== 'cricket' && completed.length) {
          try {
            const completedIds = completed.map(m => m.id);
            const rosterIds = roster.map(r => r.userId);
            if (rosterIds.length && completedIds.length) {
              // Attendance scoped to THIS team — if a player appears on both
              // rosters in a match (versing themselves), only their row
              // tagged to this team counts.
              const { data: attRows } = await supabase.from('match_attendance')
                .select('match_id, user_id')
                .in('match_id', completedIds)
                .in('user_id', rosterIds)
                .eq('team_id', selectedTeamId);
              // Fall back to confirmations for matches where attendance was
              // skipped (older matches, or kiosk-confirmed only flows).
              const { data: confRows } = await supabase.from('player_match_confirmations')
                .select('match_id, player_id')
                .in('match_id', completedIds)
                .in('player_id', rosterIds)
                .eq('team_id', selectedTeamId);
              const matchById = Object.fromEntries(completed.map(m => [m.id, m]));
              const byUser = Object.fromEntries(roster.map(r => [r.userId, r]));
              // Use a Set per player to de-dupe attendance + confirmation
              // counts of the same match.
              const playedByUser = new Map();
              const add = (uid, mid) => {
                if (!byUser[uid] || !matchById[mid]) return;
                if (!playedByUser.has(uid)) playedByUser.set(uid, new Set());
                playedByUser.get(uid).add(mid);
              };
              (attRows || []).forEach(a => add(a.user_id, a.match_id));
              (confRows || []).forEach(c => add(c.player_id, c.match_id));
              for (const [uid, mids] of playedByUser.entries()) {
                const r = byUser[uid];
                r.gamesForTeam = mids.size;
                for (const mid of mids) {
                  const m = matchById[mid];
                  const onHome = m.home_team_id === selectedTeamId;
                  const us = onHome ? m.home_score : m.away_score;
                  const opp = onHome ? m.away_score : m.home_score;
                  if (us > opp) r.wins += 1;
                }
                if (r.gamesForTeam > 0) {
                  r.winRate = Math.round((r.wins / r.gamesForTeam) * 100);
                }
              }
            }
          } catch (e) { console.warn('team games calc failed', e?.message); }
        }

        // Rank the roster: highest avg contribution first, then most games
        roster.sort((a, b) => {
          if (a.avgContribution == null && b.avgContribution == null) return b.gamesForTeam - a.gamesForTeam;
          if (a.avgContribution == null) return 1;
          if (b.avgContribution == null) return -1;
          return b.avgContribution - a.avgContribution;
        });

        // Hydrate completed matches with the "us/opp" perspective for the recent list
        const recentMatchesHydrated = completed.slice(0, 8).map(m => {
          const onHome = m.home_team_id === selectedTeamId;
          const usName = onHome ? m.home_team?.name : m.away_team?.name;
          const oppName = onHome ? m.away_team?.name : m.home_team?.name;
          const usScore = onHome ? m.home_score : m.away_score;
          const oppScore = onHome ? m.away_score : m.home_score;
          return {
            id: m.id,
            when: new Date(m.scheduled_at).toLocaleDateString('en-AU', { day:'numeric', month:'short' }),
            usName, oppName, usScore, oppScore,
            result: usScore > oppScore ? 'W' : usScore < oppScore ? 'L' : 'D',
          };
        });

        // 2026-05-28: rating breakdown using the shared teamRating module so
        // numbers match the home-page Top Teams panel and Team Rankings page.
        // We also pull every forfeited match here (the main fetch above filters
        // by recent only) so the rating reflects all-time. Cheap second query.
        let rating = null;
        try {
          const { data: allMatches } = await supabase.from('matches')
            .select('id, scheduled_at, status, forfeit_type, home_team_id, away_team_id, home_score, away_score')
            .or(`home_team_id.eq.${selectedTeamId},away_team_id.eq.${selectedTeamId}`)
            .in('status', ['completed', 'forfeited']);
          const decorated = (allMatches || []).map(m => ({ ...m, season_name: team.competition?.season?.name || null }));
          rating = computeTeamRating(decorated, selectedTeamId, sportSlug);
        } catch (e) { console.warn('team rating calc failed', e?.message); }

        if (cancelled) return;
        setLiveTeamDetail({
          team,
          sportSlug,
          ladder: ladderRow || null,
          roster,
          form,
          nextMatch,
          recentMatches: recentMatchesHydrated,
          totalPlayers: roster.length,
          totalCompleted: completed.length,
          rating,
        });
      } catch (e) {
        console.warn('All4Indoor: failed to load team detail', e);
        if (!cancelled) setLiveTeamDetail({ error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTeamId]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ═══════════════════════════════════════════════════════════════════════════
  // ─── WRAPPED — your season recap (Spotify-Wrapped style) ──────────────────
  // Fires only when the user navigates to the wrapped page. Computes top sport,
  // peak ELO moment, biggest rival, best partner, plus everything else liveCareer
  // already has. One round trip; everything else is reused state.
  // ═══════════════════════════════════════════════════════════════════════════
  const [liveWrapped, setLiveWrapped] = useState(null);
  useEffect(() => {
    // Fire on Wrapped page AND on Profile (best partner / top rival surface there too).
    if (!authUser?.id) return;
    if (section !== 'wrapped' && section !== 'profile') return;
    let cancelled = false;
    (async () => {
      try {
        const [{ data: confs }, { data: att }] = await Promise.all([
          supabase.from('player_match_confirmations').select('match_id, team_id, match:matches!inner(id, scheduled_at, status, home_team_id, away_team_id, home_score, away_score, competition:competitions(sport:sports(name, slug)))').eq('player_id', authUser.id),
          supabase.from('match_attendance').select('match_id, team_id, match:matches!inner(id, scheduled_at, status, home_team_id, away_team_id, home_score, away_score, competition:competitions(sport:sports(name, slug)))').eq('user_id', authUser.id),
        ]);
        if (cancelled) return;
        // Dedupe my matches
        const seen = new Set();
        const myMatches = [];
        [...(confs || []), ...(att || [])].forEach(row => {
          const k = `${row.match_id}|${row.team_id}`;
          if (seen.has(k)) return;
          seen.add(k);
          const m = row.match;
          if (!m || m.status !== 'completed' || m.home_score == null) return;
          const onHome = row.team_id === m.home_team_id;
          if (!onHome && row.team_id !== m.away_team_id) return;
          const myScore = onHome ? m.home_score : m.away_score;
          const oppScore = onHome ? m.away_score : m.home_score;
          const oppTeamId = onHome ? m.away_team_id : m.home_team_id;
          myMatches.push({
            matchId: m.id,
            ts: new Date(m.scheduled_at).getTime(),
            year: new Date(m.scheduled_at).getFullYear(),
            myTeamId: row.team_id,
            oppTeamId,
            outcome: myScore > oppScore ? 'W' : (myScore < oppScore ? 'L' : 'D'),
            myScore, oppScore,
            margin: myScore - oppScore,
            sport: m.competition?.sport?.name || 'Unknown',
          });
        });
        myMatches.sort((a, b) => a.ts - b.ts);
        if (myMatches.length === 0) {
          setLiveWrapped({ empty: true });
          return;
        }
        const currentYear = new Date().getFullYear();
        const yearMatches = myMatches.filter(m => m.year === currentYear);
        const wins = myMatches.filter(m => m.outcome === 'W').length;
        const winRate = Math.round((wins / myMatches.length) * 1000) / 10;
        // Sport breakdown — count games per sport
        const sportCounts = {};
        myMatches.forEach(m => { sportCounts[m.sport] = (sportCounts[m.sport] || 0) + 1; });
        const topSport = Object.entries(sportCounts).sort((a, b) => b[1] - a[1])[0];
        // Biggest win
        const biggest = myMatches.filter(m => m.outcome === 'W').reduce((acc, m) => (!acc || m.margin > acc.margin) ? m : acc, null);
        // Need teammates + opponents — pull team_players + opposite confirmations for these matches
        const matchIds = myMatches.map(m => m.matchId);
        const myTeamIds = Array.from(new Set(myMatches.map(m => m.myTeamId)));
        const oppTeamIds = Array.from(new Set(myMatches.map(m => m.oppTeamId).filter(Boolean)));
        const [{ data: tmRows }, { data: oppConfs }, { data: oppAtt }, { data: teams }, { data: users }] = await Promise.all([
          supabase.from('team_players').select('team_id, user_id, users(first_name, last_name)').in('team_id', myTeamIds),
          supabase.from('player_match_confirmations').select('match_id, player_id, team_id, users(first_name, last_name)').in('match_id', matchIds),
          supabase.from('match_attendance').select('match_id, user_id, team_id, users(first_name, last_name)').in('match_id', matchIds),
          supabase.from('teams').select('id, name').in('id', [...myTeamIds, ...oppTeamIds]),
          [],
        ]);
        if (cancelled) return;
        const teamNameById = {};
        (teams || []).forEach(t => { teamNameById[t.id] = t.name; });
        // Build: per-match participants by team
        const matchParticipants = {}; // matchId -> { teamId -> Set of user ids with names }
        const addPart = (mid, uid, tid, name) => {
          if (!mid || !uid || !tid) return;
          if (!matchParticipants[mid]) matchParticipants[mid] = {};
          if (!matchParticipants[mid][tid]) matchParticipants[mid][tid] = new Map();
          if (!matchParticipants[mid][tid].has(uid)) matchParticipants[mid][tid].set(uid, name);
        };
        (oppConfs || []).forEach(c => {
          const nm = `${c.users?.first_name || ''} ${c.users?.last_name || ''}`.trim();
          addPart(c.match_id, c.player_id, c.team_id, nm);
        });
        (oppAtt || []).forEach(a => {
          const nm = `${a.users?.first_name || ''} ${a.users?.last_name || ''}`.trim();
          addPart(a.match_id, a.user_id, a.team_id, nm);
        });
        // Tally rivals (opponents) + partners (teammates), excluding me
        const partnerTally = {}; // uid -> { name, with: number, wins: number }
        const rivalTally = {};  // uid -> { name, against: number, beat: number, lostTo: number, draws: number }
        myMatches.forEach(m => {
          const mine = matchParticipants[m.matchId]?.[m.myTeamId];
          if (mine) {
            mine.forEach((nm, uid) => {
              if (uid === authUser.id) return;
              if (!partnerTally[uid]) partnerTally[uid] = { id: uid, name: nm || 'Player', with: 0, wins: 0 };
              partnerTally[uid].with++;
              if (m.outcome === 'W') partnerTally[uid].wins++;
            });
          }
          const theirs = matchParticipants[m.matchId]?.[m.oppTeamId];
          if (theirs) {
            theirs.forEach((nm, uid) => {
              if (uid === authUser.id) return;
              if (!rivalTally[uid]) rivalTally[uid] = { id: uid, name: nm || 'Player', against: 0, beat: 0, lostTo: 0, draws: 0 };
              rivalTally[uid].against++;
              if (m.outcome === 'W') rivalTally[uid].beat++;
              else if (m.outcome === 'L') rivalTally[uid].lostTo++;
              else rivalTally[uid].draws++;
            });
          }
        });
        // Best partner — most games together with highest win rate (require ≥3 games)
        const bestPartner = Object.values(partnerTally)
          .filter(p => p.with >= 3)
          .map(p => ({ ...p, winRate: p.with > 0 ? Math.round((p.wins / p.with) * 100) : 0 }))
          .sort((a, b) => (b.wins - a.wins) || (b.with - a.with))[0] || null;
        // Top rival — most-played opponent (require ≥2 meetings)
        const topRival = Object.values(rivalTally)
          .filter(r => r.against >= 2)
          .sort((a, b) => b.against - a.against)[0] || null;
        setLiveWrapped({
          totalGames: myMatches.length,
          wins, losses: myMatches.filter(m => m.outcome === 'L').length, draws: myMatches.filter(m => m.outcome === 'D').length,
          winRate,
          yearGames: yearMatches.length,
          yearWins: yearMatches.filter(m => m.outcome === 'W').length,
          topSport: topSport ? { name: topSport[0], games: topSport[1] } : null,
          sportCounts,
          biggestWin: biggest,
          bestPartner,
          topRival,
          firstMatchTs: myMatches[0].ts,
          lastMatchTs: myMatches[myMatches.length - 1].ts,
        });
      } catch (e) {
        console.warn('All4Indoor: Wrapped load failed', e);
        setLiveWrapped({ error: true });
      }
    })();
    return () => { cancelled = true; };
  }, [section, authUser?.id]);

  // ─── HEAD-TO-HEAD COMPARISON DATA ─────────────────────────────────────────
  // Loads wins/losses + badges count + direct H2H history for the two selected
  // players. Triggered when compareIds.a, .b (and optionally .c) are set.
  // 2026-05-31: extended to support up to 3 players. For 2 players, behaves
  // as before with h2hRecord = { a, b, draws }. For 3 players, pairwise
  // tracks A vs B, A vs C, B vs C separately.
  // ═══════════════════════════════════════════════════════════════════════════
  const [liveH2H, setLiveH2H] = useState(null);

  // ─── INLINE "Vs You" PANEL on a player profile ─────────────────────────────
  // Lightweight computation triggered whenever a profile opens and the
  // signed-in user is different from that profile. Reuses the same
  // participation-set logic as liveH2H but trims badges / per-player stats
  // since the panel only needs games count, wins, losses, and last meeting.
  // Doesn't touch compareIds, so the H2H tab keeps whatever the user had.
  const [liveVsYou, setLiveVsYou] = useState(null);
  useEffect(() => {
    if (!authUser?.id || !selectedPlayerId || authUser.id === selectedPlayerId) {
      setLiveVsYou(null);
      return;
    }
    let cancelled = false;
    const youId = authUser.id;
    const themId = selectedPlayerId;
    (async () => {
      try {
        const ids = [youId, themId];
        const [{ data: confs }, { data: att }, { data: matches }] = await Promise.all([
          supabase.from('player_match_confirmations').select('match_id, player_id, team_id').in('player_id', ids),
          supabase.from('match_attendance').select('match_id, user_id, team_id').in('user_id', ids),
          supabase.from('matches').select('id, scheduled_at, status, home_team_id, away_team_id, home_score, away_score').eq('status', 'completed').not('home_score', 'is', null),
        ]);
        if (cancelled) return;
        const matchById = {};
        (matches || []).forEach(m => { matchById[m.id] = m; });
        const part = { [youId]: {}, [themId]: {} };
        (confs || []).forEach(c => {
          if (part[c.player_id] && !part[c.player_id][c.match_id]) part[c.player_id][c.match_id] = c.team_id;
        });
        (att || []).forEach(a => {
          if (part[a.user_id] && !part[a.user_id][a.match_id]) part[a.user_id][a.match_id] = a.team_id;
        });
        // Direct H2H games (opposite teams only)
        const games = [];
        Object.entries(part[youId]).forEach(([mid, tYou]) => {
          const tThem = part[themId][mid];
          if (!tThem || tYou === tThem) return;
          const m = matchById[mid];
          if (!m) return;
          const youHome = tYou === m.home_team_id;
          const youScore = youHome ? m.home_score : m.away_score;
          const themScore = youHome ? m.away_score : m.home_score;
          games.push({
            id: mid,
            ts: new Date(m.scheduled_at).getTime(),
            when: new Date(m.scheduled_at).toLocaleDateString('en-AU', { day:'numeric', month:'short' }),
            youScore, themScore,
            outcome: youScore > themScore ? 'W' : (youScore < themScore ? 'L' : 'D'),
          });
        });
        games.sort((x, y) => y.ts - x.ts);
        const record = { wins: 0, losses: 0, draws: 0 };
        games.forEach(g => { if (g.outcome === 'W') record.wins++; else if (g.outcome === 'L') record.losses++; else record.draws++; });
        setLiveVsYou({ total: games.length, record, last: games[0] || null });
      } catch (e) {
        console.warn('All4Indoor: vs-you load failed', e);
        setLiveVsYou(null);
      }
    })();
    return () => { cancelled = true; };
  }, [authUser?.id, selectedPlayerId]);
  useEffect(() => {
    // 2026-05-31: support 2 OR 3 players. Build ids array from non-null
    // compareIds; require at least 2 unique ids.
    const rawIds = [compareIds.a, compareIds.b, compareIds.c].filter(Boolean);
    const ids = [...new Set(rawIds)];
    if (ids.length < 2) { setLiveH2H(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const [{ data: confs }, { data: att }, { data: matches }, { data: badgeRows }] = await Promise.all([
          supabase.from('player_match_confirmations').select('match_id, player_id, team_id').in('player_id', ids),
          supabase.from('match_attendance').select('match_id, user_id, team_id').in('user_id', ids),
          supabase.from('matches').select('id, scheduled_at, status, home_team_id, away_team_id, home_score, away_score, home_team:teams!home_team_id(name), away_team:teams!away_team_id(name), competition:competitions(name, sport:sports(name))').eq('status', 'completed').not('home_score', 'is', null),
          supabase.from('player_achievements').select('user_id, achievement_name, emoji, rarity').in('user_id', ids),
        ]);
        if (cancelled) return;
        const matchById = {};
        (matches || []).forEach(m => { matchById[m.id] = m; });
        // Build per-player participation set { matchId: teamId } for each id
        const part = {};
        ids.forEach(uid => { part[uid] = {}; });
        (confs || []).forEach(c => {
          if (part[c.player_id] && !part[c.player_id][c.match_id]) part[c.player_id][c.match_id] = c.team_id;
        });
        (att || []).forEach(a => {
          if (part[a.user_id] && !part[a.user_id][a.match_id]) part[a.user_id][a.match_id] = a.team_id;
        });
        const stats = {};
        ids.forEach(uid => {
          let games = 0, wins = 0, losses = 0, draws = 0;
          Object.entries(part[uid]).forEach(([mid, teamId]) => {
            const m = matchById[mid];
            if (!m) return;
            const onHome = teamId === m.home_team_id;
            const onAway = teamId === m.away_team_id;
            if (!onHome && !onAway) return;
            games++;
            const myScore = onHome ? m.home_score : m.away_score;
            const oppScore = onHome ? m.away_score : m.home_score;
            if (myScore > oppScore) wins++;
            else if (myScore < oppScore) losses++;
            else draws++;
          });
          stats[uid] = { games, wins, losses, draws, winRate: games ? Math.round((wins / games) * 100) : 0 };
        });

        // Pairwise H2H — for every unordered pair of selected players, find
        // matches where they were on opposite teams. Stored keyed by pair
        // ("a-b", "a-c", "b-c") for the 3-way view; also keep .h2h / .h2hRecord
        // backwards-compatible with the existing 2-player render (= a-b pair).
        const computePairH2H = (uidLeft, uidRight) => {
          const list = [];
          Object.entries(part[uidLeft]).forEach(([mid, tL]) => {
            const tR = part[uidRight][mid];
            if (!tR || tL === tR) return;
            const m = matchById[mid];
            if (!m) return;
            const lHome = tL === m.home_team_id;
            const lScore = lHome ? m.home_score : m.away_score;
            const rScore = lHome ? m.away_score : m.home_score;
            list.push({
              id: mid,
              when: new Date(m.scheduled_at).toLocaleDateString('en-AU', { day:'numeric', month:'short', year:'numeric' }),
              ts: new Date(m.scheduled_at).getTime(),
              comp: m.competition?.name,
              // For the 2-player render the left side is treated as "a" and
              // right as "b", so keep aTeam/bTeam/aScore/bScore field names.
              aTeam: lHome ? m.home_team?.name : m.away_team?.name,
              bTeam: lHome ? m.away_team?.name : m.home_team?.name,
              aScore: lScore, bScore: rScore,
              winner: lScore > rScore ? 'a' : (rScore > lScore ? 'b' : 'draw'),
            });
          });
          list.sort((x, y) => y.ts - x.ts);
          const record = { a: 0, b: 0, draws: 0 };
          list.forEach(g => { if (g.winner === 'a') record.a++; else if (g.winner === 'b') record.b++; else record.draws++; });
          return { h2h: list, record };
        };

        // Build pairwiseH2H for every (left, right) pair where both exist.
        // Key shape: "<uidLeft>::<uidRight>" with uidLeft < uidRight
        // alphabetically so lookups are stable regardless of slot order.
        const pairwise = {};
        for (let i = 0; i < ids.length; i++) {
          for (let j = i + 1; j < ids.length; j++) {
            const key = [ids[i], ids[j]].sort().join('::');
            pairwise[key] = computePairH2H(ids[i], ids[j]);
          }
        }

        // Backwards-compat for the existing 2-player render — .h2h and
        // .h2hRecord still refer to the a-b pair (compareIds.a vs .b).
        let h2h = [], h2hRecord = { a: 0, b: 0, draws: 0 };
        if (compareIds.a && compareIds.b && compareIds.a !== compareIds.b) {
          const ab = computePairH2H(compareIds.a, compareIds.b);
          h2h = ab.h2h;
          h2hRecord = ab.record;
        }

        // Badge counts + rarest badge per id
        const badges = {};
        ids.forEach(uid => { badges[uid] = { count: 0, rarest: null }; });
        const rarityRank = { legendary: 4, rare: 3, uncommon: 2, common: 1 };
        (badgeRows || []).forEach(b => {
          if (!badges[b.user_id]) return;
          badges[b.user_id].count++;
          const r = rarityRank[b.rarity] || 0;
          const cur = badges[b.user_id].rarest;
          const curR = cur ? (rarityRank[cur.rarity] || 0) : -1;
          if (r > curR) badges[b.user_id].rarest = b;
        });
        setLiveH2H({ stats, badges, h2h, h2hRecord, pairwise });
      } catch (e) {
        console.warn('All4Indoor: H2H load failed', e);
        setLiveH2H({ stats: {}, badges: {}, h2h: [], h2hRecord: { a:0, b:0, draws:0 }, pairwise: {} });
      }
    })();
    return () => { cancelled = true; };
    // 2026-08-06: the body reads compareIds.c but it was missing from the deps,
    // so adding a 3rd player left their column on zeroes and silently dropped
    // the A-v-C and B-v-C rows until a page reload.
  }, [compareIds.a, compareIds.b, compareIds.c]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── PLAYER OF THE WEEK — auto-curated top performer from last 7 days ─────
  // Score formula: 2× (goals + runs/5 + wickets) + 3× wins + 0.5× games. Tiebreaker
  // is total games. Honest, transparent, and surfaces real form. Always-on.
  // ═══════════════════════════════════════════════════════════════════════════
  // ═══════════════════════════════════════════════════════════════════════════
  // ─── PER-SPORT TOP-10 LEADERBOARDS ───────────────────────────────────────
  // The on-landing hook. Each active sport gets its own ranked top-10 over
  // the last 14 days:
  //   • Cricket → total contribution (bat runs - bowl runs conceded)
  //   • Netball → goals scored
  //   • Soccer → goals scored
  //   • Volleyball → number of wins they featured in
  // Players hit the home page, immediately see leaderboards, want to be on
  // them. Same engagement loop Spawtz had with comp-wide rankings.
  // ═══════════════════════════════════════════════════════════════════════════
  const [liveSportLeaderboards, setLiveSportLeaderboards] = useState(null);
  // 2026-06-02: true venue totals for the "Venue Pulse" strip — NOT the
  // top-10 sums we used before. Populated by the same effect as the
  // leaderboards so we don't duplicate the matches query.
  const [liveVenuePulse, setLiveVenuePulse] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sinceIso = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();
        // Pull completed matches in the window, joined with their sport slug
        // 2026-07-27 (Jake): "be good to have the venue ie Wildcatz Townsville
        // incase you get 2 john smiths etc". As the network grows past one
        // venue a bare name stops identifying anyone. Comes down on the same
        // query — no extra round trip.
        const { data: matches } = await supabase.from('matches')
          .select('id, scheduled_at, home_team_id, away_team_id, home_score, away_score, competition:competitions(sport:sports(slug), venue:venues(name))')
          .eq('status', 'completed')
          .gte('scheduled_at', sinceIso)
          .not('home_score', 'is', null);
        if (cancelled || !matches?.length) {
          if (!cancelled) setLiveSportLeaderboards({});
          return;
        }
        const bySport = { cricket: [], netball: [], soccer: [], volleyball: [], 'beach-volleyball': [] };
        // matchId → "Wildcatz Indoor Sports".
        //
        // Name only, deliberately. venues has an `address` but no city or
        // suburb column, and Wildcatz's address is still the placeholder
        // "Your Address Here" — parsing a town out of that would print
        // nonsense under every player. The name alone tells two John Smiths
        // apart, which is the job.
        const venueOf = {};
        for (const m of matches) {
          const s = (m.competition?.sport?.slug || '').toLowerCase();
          if (bySport[s]) bySport[s].push(m);
          const v = m.competition?.venue;
          if (v?.name) venueOf[m.id] = v.name;
        }
        // A player can turn out at more than one venue. Count where they've
        // played and show the one they play at most, rather than whichever
        // match happened to be processed first.
        const noteVenue = (row, matchId) => {
          const label = venueOf[matchId];
          if (!label) return;
          if (!row._venues) row._venues = {};
          row._venues[label] = (row._venues[label] || 0) + 1;
        };
        const settleVenue = (row) => {
          if (!row._venues) return row;
          const best = Object.entries(row._venues).sort((a, b) => b[1] - a[1])[0];
          if (best) row.venue = best[0];
          delete row._venues;
          return row;
        };
        // Volleyball + beach-volleyball share a leaderboard
        bySport.volleyball = [...bySport.volleyball, ...bySport['beach-volleyball']];
        delete bySport['beach-volleyball'];

        const lb = {};
        // 2026-06-02: venue-wide totals (every action counted, not top-10
        // sum). Built up as we walk the scorecards below — no extra query.
        const pulse = { cricketWickets: 0, netballGoals: 0, soccerGoals: 0, volleyballPoints: 0 };

        // CRICKET — total contribution by player
        if (bySport.cricket.length) {
          const ids = bySport.cricket.map(m => m.id);
          const { data: scs } = await supabase.from('cricket_scorecards')
            .select('match_id, balls_data').in('match_id', ids);
          const stats = {}; // name → { contribution, games, batRuns, bowlRuns, wickets, team }
          for (const sc of (scs || [])) {
            const perMatch = {};
            for (const k of Object.keys(sc.balls_data || {})) {
              for (const b of sc.balls_data[k]) {
                const bk = (b.batter || '').trim();
                const wk = (b.bowler || '').trim();
                if (bk) {
                  if (!perMatch[bk]) perMatch[bk] = { bat: 0, bowl: 0, wickets: 0 };
                  if (b.type === 'run') perMatch[bk].bat += (b.runs || 0);
                }
                if (wk) {
                  if (!perMatch[wk]) perMatch[wk] = { bat: 0, bowl: 0, wickets: 0 };
                  if (b.type === 'wicket') perMatch[wk].wickets += 1;
                  if (b.type === 'run' || b.type === 'wide' || b.type === 'noball') {
                    perMatch[wk].bowl += (b.runs || 0);
                  }
                }
                // Venue-wide total: every wicket ball counts.
                if (b.type === 'wicket') pulse.cricketWickets += 1;
              }
            }
            for (const [name, s] of Object.entries(perMatch)) {
              if (!stats[name]) stats[name] = { name, contribution: 0, games: 0, batRuns: 0, bowlRuns: 0, wickets: 0 };
              noteVenue(stats[name], sc.match_id);
              stats[name].games += 1;
              stats[name].batRuns += s.bat;
              stats[name].bowlRuns += s.bowl;
              stats[name].wickets += s.wickets;
              // 2026-07-07: include -5-per-wicket indoor cricket bonus so
              // the Top-10 cricket contribution leaderboard uses the same
              // formula as the match report page.
              stats[name].contribution += (s.bat - s.bowl + 5 * s.wickets);
            }
          }
          const ranked = Object.values(stats)
            .map(settleVenue)
            .sort((a, b) => b.contribution - a.contribution || b.games - a.games);

          // 2026-05-31: hydrate user_ids onto cricket rows so the Top-10
          // leaderboard names are clickable through to the profile. Cricket
          // balls_data only stores player names (no scorer_id), unlike
          // netball/soccer which carry scorer_id natively. We do a single
          // bulk name→id lookup over all ranked players.
          const cricketNames = new Set(ranked.map(r => r.name).filter(Boolean));
          if (cricketNames.size) {
            const { data: nameUsers } = await supabase.from('public_players')
              .select('id, first_name, last_name').limit(20000);
            const nameToId = {};
            for (const u of (nameUsers || [])) {
              const full = `${u.first_name || ''} ${u.last_name || ''}`.replace(/\s+/g, ' ').trim().toLowerCase();
              if (full) nameToId[full] = u.id;
            }
            for (const r of ranked) {
              const norm = (r.name || '').replace(/\s+/g, ' ').trim().toLowerCase();
              if (nameToId[norm]) r.id = nameToId[norm];
            }
          }

          // Store BOTH top10 (for the home leaderboard tile) and the full
          // ranked list (so player profile can compute percentile). lb.cricket
          // stays an array for the existing top-10 render; lb.cricketAll
          // holds the whole ranked field including ties.
          lb.cricket = ranked.slice(0, 10);
          lb.cricketAll = ranked;
        }

        // NETBALL / SOCCER — total goals from goal_log
        // 2026-05-31: skip goals that don't have a real scorer_id. When the
        // umpire tapped a POSITION tile (GS/GA/GK) instead of a player tile,
        // the goal was saved with scorer_name='GS' and the leaderboard then
        // ranked "GS" as if it were a player. Same for empty scorer_name
        // ('Unknown'). Real players have scorer_id; positions don't.
        // Also drop any positional/placeholder names that may have leaked
        // into scorer_name from older data.
        const PLACEHOLDER_NAMES = new Set(['GS','GA','WA','C','WD','GD','GK','UNKNOWN','UNK','']);
        for (const [sport, table] of [['netball','netball_scorecards'], ['soccer','soccer_scorecards']]) {
          if (!bySport[sport]?.length) continue;
          const ids = bySport[sport].map(m => m.id);
          const { data: scs } = await supabase.from(table)
            .select('match_id, goal_log').in('match_id', ids);
          const stats = {}; // id → { name, goals, games }
          for (const sc of (scs || [])) {
            const perMatch = new Set();
            for (const g of (sc.goal_log || [])) {
              // 2026-06-02: venue-wide TOTAL — count every logged goal
              // (even GS/GA position taps and unattributed ones) so the
              // pulse strip reflects what fans actually saw on court.
              if (sport === 'netball') pulse.netballGoals += 1;
              else if (sport === 'soccer') pulse.soccerGoals += 1;
              if (!g.scorer_id) continue;  // require real player attribution
              const nameNorm = (g.scorer_name || '').trim().toUpperCase();
              if (PLACEHOLDER_NAMES.has(nameNorm)) continue;
              if (!stats[g.scorer_id]) stats[g.scorer_id] = { id: g.scorer_id, name: g.scorer_name || '', goals: 0, games: 0 };
              noteVenue(stats[g.scorer_id], sc.match_id);
              stats[g.scorer_id].goals += 1;
              perMatch.add(g.scorer_id);
            }
            for (const id of perMatch) stats[id].games += 1;
          }
          lb[sport] = Object.values(stats)
            .map(settleVenue)
            .filter(s => s.goals > 0 && s.name)
            .sort((a, b) => b.goals - a.goals || b.games - a.games)
            .slice(0, 10);
        }

        // 2026-06-02: Venue-wide volleyball total = sum of POINTS won across
        // every match. matches.home_score / away_score on volleyball matches
        // are the actual points (e.g. 37-24, not 3-1 sets) so we just add
        // them up. Way more punchy than match count — typical Wildcatz
        // night logs 80+ points per match, so a 14-day window can show
        // 2,000+ points.
        for (const m of bySport.volleyball) {
          pulse.volleyballPoints += (m.home_score || 0) + (m.away_score || 0);
        }

        // VOLLEYBALL — team wins (no per-player goal log in volleyball), so
        // we rank by how many wins a player FEATURED in over the window.
        // Pulls player_match_confirmations + attendance for the volleyball
        // matches in the window. Light query, no scorecard scan needed.
        if (bySport.volleyball.length) {
          const ids = bySport.volleyball.map(m => m.id);
          const teamWinner = {}; // matchId → winning team_id
          for (const m of bySport.volleyball) {
            if (m.home_score > m.away_score) teamWinner[m.id] = m.home_team_id;
            else if (m.away_score > m.home_score) teamWinner[m.id] = m.away_team_id;
          }
          const [{ data: confs }, { data: atts }] = await Promise.all([
            supabase.from('player_match_confirmations').select('match_id, player_id, team_id, users:users!player_id(first_name,last_name)').in('match_id', ids),
            supabase.from('match_attendance').select('match_id, user_id, team_id, users:users!user_id(first_name,last_name)').in('match_id', ids),
          ]);
          const stats = {}; // userId → { name, wins, games }
          const seenPerMatch = new Map(); // matchId → Set<userId> (so we don't double-count)
          const seen = (mid, uid) => {
            if (!seenPerMatch.has(mid)) seenPerMatch.set(mid, new Set());
            const s = seenPerMatch.get(mid);
            if (s.has(uid)) return true;
            s.add(uid);
            return false;
          };
          for (const c of (confs || [])) {
            const uid = c.player_id; if (!uid || seen(c.match_id, uid)) continue;
            const name = `${c.users?.first_name || ''} ${c.users?.last_name || ''}`.trim();
            if (!stats[uid]) stats[uid] = { id: uid, name, wins: 0, games: 0 };
            noteVenue(stats[uid], c.match_id);
            stats[uid].games += 1;
            if (teamWinner[c.match_id] === c.team_id) stats[uid].wins += 1;
          }
          for (const a of (atts || [])) {
            const uid = a.user_id; if (!uid || seen(a.match_id, uid)) continue;
            const name = `${a.users?.first_name || ''} ${a.users?.last_name || ''}`.trim();
            if (!stats[uid]) stats[uid] = { id: uid, name, wins: 0, games: 0 };
            noteVenue(stats[uid], a.match_id);
            stats[uid].games += 1;
            if (teamWinner[a.match_id] === a.team_id) stats[uid].wins += 1;
          }
          lb.volleyball = Object.values(stats)
            .map(settleVenue)
            .filter(s => s.wins > 0)
            .sort((a, b) => b.wins - a.wins || b.games - a.games)
            .slice(0, 10);
        }

        if (!cancelled) {
          setLiveSportLeaderboards(lb);
          setLiveVenuePulse(pulse);
        }
      } catch (e) {
        console.warn('Leaderboard fetch failed', e?.message);
        if (!cancelled) {
          setLiveSportLeaderboards({});
          setLiveVenuePulse({ cricketWickets: 0, netballGoals: 0, soccerGoals: 0, volleyballPoints: 0 });
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const [livePlayerOfTheWeek, setLivePlayerOfTheWeek] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 2026-08-11 (Jake): this used to tally in the browser and scored
        // (goals x 2) + (wins x 3) + (games x 0.5) with `goals` coming only from
        // netball_scorecards - capped at 60 rows. Cricket runs and wickets,
        // soccer goals and volleyball counted for nothing, despite the comment
        // above claiming otherwise. player_form_7d does the whole calculation
        // in the database, reaches the cricket ball log, has no row cap, and
        // takes names from public_players so juniors stay initial-only.
        const { data, error } = await supabase
          .from('player_form_7d')
          .select('user_id, player, venue, sport, games, wins, win_rate, goals, runs, wickets, score')
          .limit(1);
        if (error) throw error;
        const top = (data || [])[0];
        if (cancelled) return;
        setLivePlayerOfTheWeek(top
          ? { id: top.user_id, name: top.player, venue: top.venue, sport: top.sport,
              games: top.games, wins: top.wins, winRate: top.win_rate,
              goals: top.goals, runs: top.runs, wickets: top.wickets, score: top.score }
          : { empty: true });
      } catch (e) {
        console.warn('All4Indoor: Player of the Week load failed', e?.message);
        if (!cancelled) setLivePlayerOfTheWeek({ empty: true });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── HALL OF FAME — all-time records that anyone can chase ────────────────
  // Single record per category: highest single-match score, biggest blowout,
  // most wickets in a match, longest win streak (player), etc. Live-computed.
  // ═══════════════════════════════════════════════════════════════════════════
  // ─── ALL-TIME LEADERS ────────────────────────────────────────────────────
  // One query. The ranking, the junior-name masking and the opt-out filter all
  // live in the all_time_leaders view, so this page cannot drift from what the
  // database considers a record - and it cannot leak a junior surname by
  // aggregating users directly, which is how the old leaderboard did it.
  const [allTimeLeaders, setAllTimeLeaders] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('all_time_leaders')
          .select('category, label, sport, rank, user_id, player, venue, value, detail')
          .lte('rank', 10)
          .order('category')
          .order('sport', { nullsFirst: true })
          .order('rank');
        if (error) throw error;
        if (!cancelled) setAllTimeLeaders(data || []);
      } catch (e) {
        console.warn('All4Indoor: All-Time Leaders load failed', e?.message);
        if (!cancelled) setAllTimeLeaders([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const [liveHallOfFame, setLiveHallOfFame] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: matches }, { data: stats }, { data: confs }, { data: att }, { data: users }, { data: teams }, { data: badges }] = await Promise.all([
          supabase.from('matches')
            .select('id, scheduled_at, home_score, away_score, home_team_id, away_team_id, home_team:teams!home_team_id(name), away_team:teams!away_team_id(name), competition:competitions(name, sport:sports(name))')
            .eq('status', 'completed').not('home_score', 'is', null),
          supabase.from('player_stats')
            .select('user_id, total_runs, total_wickets, total_goals, total_sixes, hat_tricks, highest_score, user:users(first_name, last_name), sport:sports(name)'),
          getAllConfirmationsBasic(),
          getAllAttendanceBasic(),
          // 2026-05-27: paginated via fetchAllPages so users beyond row 1000 aren't dropped (see supabase.js)
          getAllUsersBasic().then(data => ({ data })),
          supabase.from('teams').select('id, name'),
          supabase.from('player_achievements').select('user_id, achievement_name, emoji, rarity, earned_at, user:users(first_name, last_name)').eq('rarity', 'legendary').order('earned_at', { ascending: false }).limit(5),
        ]);
        if (cancelled) return;
        const records = {};
        // Highest team score in a single match
        const topMatchScore = (matches || []).reduce((acc, m) => {
          const h = m.home_score ?? 0, a = m.away_score ?? 0;
          const peak = Math.max(h, a);
          if (!acc || peak > acc.score) {
            const winningTeam = h > a ? m.home_team?.name : m.away_team?.name;
            return { score: peak, winningTeam, otherScore: Math.min(h, a), date: m.scheduled_at, comp: m.competition?.name, matchId: m.id };
          }
          return acc;
        }, null);
        // Biggest blowout
        const biggestBlowout = (matches || []).reduce((acc, m) => {
          const margin = Math.abs((m.home_score || 0) - (m.away_score || 0));
          if (!acc || margin > acc.margin) {
            const winningTeam = m.home_score > m.away_score ? m.home_team?.name : m.away_team?.name;
            const losingTeam = m.home_score > m.away_score ? m.away_team?.name : m.home_team?.name;
            return { margin, winningTeam, losingTeam, winScore: Math.max(m.home_score, m.away_score), loseScore: Math.min(m.home_score, m.away_score), date: m.scheduled_at, matchId: m.id };
          }
          return acc;
        }, null);
        // Per-user totals from player_stats — split goals by sport so netball
        // (where a player can score 30 in one game) doesn't dominate the soccer
        // "Most Goals" record (where 4 in a game is huge).
        const userMap = {}; (users || []).forEach(u => { userMap[u.id] = `${u.first_name || ''} ${u.last_name || ''}`.trim(); });
        const sportKey = (s) => {
          // Normalise sport slug — strip 'indoor_' prefix, lowercase
          const slug = (s?.sport?.slug || s?.sport?.name || '').toLowerCase().replace(/^indoor[_\s-]?/, '');
          return slug || 'other';
        };
        const totals = {}; // uid -> { ..., goalsBySport: { netball, soccer, futsal, ... }, hatTricksBySport: {...} }
        (stats || []).forEach(s => {
          if (!s.user_id || !userMap[s.user_id]) return;
          if (!totals[s.user_id]) totals[s.user_id] = {
            id: s.user_id, name: userMap[s.user_id],
            runs: 0, wickets: 0, sixes: 0, hatTricks: 0, highestScore: 0,
            goalsBySport: {}, hatTricksBySport: {}, // each sport gets its own tally
          };
          totals[s.user_id].runs += (s.total_runs || 0);
          totals[s.user_id].wickets += (s.total_wickets || 0);
          totals[s.user_id].sixes += (s.total_sixes || 0);
          totals[s.user_id].hatTricks += (s.hat_tricks || 0);
          totals[s.user_id].highestScore = Math.max(totals[s.user_id].highestScore, s.highest_score || 0);
          const sk = sportKey(s);
          const g = s.total_goals || 0;
          if (g > 0) totals[s.user_id].goalsBySport[sk] = (totals[s.user_id].goalsBySport[sk] || 0) + g;
          // 2026-05-31: also split hat-tricks by sport so the career
          // records page can show soccer vs netball as separate records.
          const ht = s.hat_tricks || 0;
          if (ht > 0) totals[s.user_id].hatTricksBySport[sk] = (totals[s.user_id].hatTricksBySport[sk] || 0) + ht;
        });
        const topOf = (key) => Object.values(totals).filter(t => t[key] > 0).sort((a, b) => b[key] - a[key])[0] || null;
        const topGoalsBySport = (sportSlug) => {
          const rows = Object.values(totals)
            .filter(t => (t.goalsBySport?.[sportSlug] || 0) > 0)
            .map(t => ({ ...t, goals: t.goalsBySport[sportSlug] }))
            .sort((a, b) => b.goals - a.goals);
          return rows[0] || null;
        };
        const topHatTricksBySport = (sportSlug) => {
          const rows = Object.values(totals)
            .filter(t => (t.hatTricksBySport?.[sportSlug] || 0) > 0)
            .map(t => ({ ...t, hatTricks: t.hatTricksBySport[sportSlug] }))
            .sort((a, b) => b.hatTricks - a.hatTricks);
          return rows[0] || null;
        };
        // Longest win streak ever — walk participation chronologically per user
        const matchById = {}; (matches || []).forEach(m => { matchById[m.id] = m; });
        const part = {}; // uid -> [{ ts, outcome }]
        const addPart = (uid, mid, tid) => {
          const m = matchById[mid]; if (!m) return;
          const onHome = tid === m.home_team_id;
          const onAway = tid === m.away_team_id;
          if (!onHome && !onAway) return;
          const myScore = onHome ? m.home_score : m.away_score;
          const oppScore = onHome ? m.away_score : m.home_score;
          const outcome = myScore > oppScore ? 'W' : 'L';
          if (!part[uid]) part[uid] = [];
          part[uid].push({ ts: new Date(m.scheduled_at).getTime(), outcome });
        };
        (confs || []).forEach(c => addPart(c.player_id, c.match_id, c.team_id));
        (att || []).forEach(a => addPart(a.user_id, a.match_id, a.team_id));
        let longest = { length: 0, id: null };
        Object.entries(part).forEach(([uid, list]) => {
          list.sort((a, b) => a.ts - b.ts);
          let run = 0, best = 0;
          list.forEach(r => { if (r.outcome === 'W') { run++; if (run > best) best = run; } else run = 0; });
          if (best > longest.length) longest = { length: best, id: uid, name: userMap[uid] };
        });
        // Highest individual single-game score from player_stats
        records.topMatchScore = topMatchScore;
        records.biggestBlowout = biggestBlowout;
        records.topRuns = topOf('runs');
        records.topWickets = topOf('wickets');
        // Goals split per sport — netball/soccer/futsal are genuinely different
        records.topGoalsNetball = topGoalsBySport('netball');
        records.topGoalsSoccer  = topGoalsBySport('soccer');
        records.topGoalsFutsal  = topGoalsBySport('futsal');
        records.topSevens = topOf('sixes');
        records.topHatTricks = topOf('hatTricks');
        // Sport-split versions surface on the records page alongside the
        // sport-split Goals records.
        records.topHatTricksSoccer  = topHatTricksBySport('soccer') || topHatTricksBySport('futsal');
        records.topHatTricksNetball = topHatTricksBySport('netball');
        records.highestSingleScore = Object.values(totals).sort((a, b) => b.highestScore - a.highestScore)[0] || null;
        records.longestStreak = longest.length >= 2 ? longest : null;

        // ─── Volleyball aces ────────────────────────────────────────────────
        // Aces live inside standard_volleyball_scorecards.{home_player_stats,
        // away_player_stats} as JSONB blobs keyed by player_id with shape
        // { aces, kills, blocks }. Walk every scorecard and tally per user.
        try {
          const { data: vbCards } = await supabase.from('standard_volleyball_scorecards')
            .select('home_player_stats, away_player_stats').not('home_player_stats', 'is', null);
          const aceTally = {};
          (vbCards || []).forEach(card => {
            ['home_player_stats', 'away_player_stats'].forEach(key => {
              const blob = card[key];
              if (!blob || typeof blob !== 'object') return;
              Object.entries(blob).forEach(([uid, st]) => {
                if (!st || typeof st !== 'object') return;
                const aces = st.aces || st.ace || 0;
                if (aces > 0) aceTally[uid] = (aceTally[uid] || 0) + aces;
              });
            });
          });
          const aceRows = Object.entries(aceTally)
            .map(([uid, count]) => ({ id: uid, name: userMap[uid] || 'Player', aces: count }))
            .filter(r => r.name && r.name !== 'Player')
            .sort((a, b) => b.aces - a.aces);
          records.topAces = aceRows[0] || null;
        } catch (e) { /* volleyball aces optional */ records.topAces = null; }
        records.legendaryBadges = (badges || []).map(b => ({
          name: b.achievement_name, emoji: b.emoji,
          who: b.user ? `${b.user.first_name || ''} ${b.user.last_name?.[0] || ''}.`.trim() : 'Player',
          earned: b.earned_at,
        }));
        setLiveHallOfFame(records);
      } catch (e) {
        console.warn('All4Indoor: Hall of Fame load failed', e);
        setLiveHallOfFame({});
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── LIVE TICKER — currently in-progress matches with real-time updates ───
  // ═══════════════════════════════════════════════════════════════════════════
  const [liveTicker, setLiveTicker] = useState(null);
  // 2026-05-31: latest completed matches — keeps the home page feeling
  // alive even when nothing is in_progress. Top 6 most recent finishes.
  const [liveRecentResults, setLiveRecentResults] = useState(null);
  useEffect(() => {
    let cancelled = false;
    // 2026-06-02: sort by updated_at (auto-bumped when scorer marks completed)
    // instead of scheduled_at — gives true "most recently FINISHED" order even
    // when matches are scored late or out of session order. Also re-fetch every
    // 60s so the panel updates without page reload while the page is open.
    const load = async () => {
      try {
        const { data } = await supabase.from('matches')
          // 2026-08-06: matches has no updated_at column - the query 400'd, so
          // "recent results" on the public site has always rendered empty.
          // submitted_at is when the score went in.
          .select('id, scheduled_at, submitted_at, status, home_score, away_score, home_team_id, away_team_id, home_team:teams!home_team_id(id, name), away_team:teams!away_team_id(id, name), competition:competitions(name, sport:sports(name, icon, slug))')
          .eq('status', 'completed')
          .not('home_score', 'is', null)
          .order('submitted_at', { ascending: false, nullsFirst: false })
          .limit(6);
        if (cancelled) return;
        setLiveRecentResults(data || []);
      } catch (e) {
        console.warn('All4Indoor: recent results load failed', e);
        setLiveRecentResults([]);
      }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  useEffect(() => {
    let cancelled = false;
    const loadTicker = async () => {
      try {
        const { data } = await supabase.from('matches')
          .select('id, scheduled_at, status, home_score, away_score, home_team:teams!home_team_id(name), away_team:teams!away_team_id(name), court:courts(name), competition:competitions(name, sport:sports(name, icon))')
          .eq('status', 'in_progress')
          .order('scheduled_at', { ascending: true });
        if (cancelled) return;
        setLiveTicker(data || []);
      } catch (e) {
        console.warn('All4Indoor: Live ticker load failed', e);
        setLiveTicker([]);
      }
    };
    loadTicker();
    // Realtime — refetch whenever a match changes status or score
    const ch = supabase.channel('all4indoor-ticker')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'matches' }, () => { if (!cancelled) loadTicker(); })
      .subscribe();
    // Also polling fallback every 30s in case realtime drops
    const poll = setInterval(loadTicker, 30000);
    return () => { cancelled = true; supabase.removeChannel(ch); clearInterval(poll); };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── ACHIEVEMENT CATALOG — every distinct badge with rarity % ─────────────
  // ═══════════════════════════════════════════════════════════════════════════
  const [liveBadgeCatalog, setLiveBadgeCatalog] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: ach }, { count: totalUsers }] = await Promise.all([
          supabase.from('player_achievements').select('achievement_name, achievement_key, emoji, rarity, description, user_id'),
          supabase.from('public_players').select('id', { count: 'exact', head: true }).neq('account_type', 'parent'),   // 2026-07-27: was '*' — 401s under column grants
        ]);
        if (cancelled) return;
        // Group by achievement_name + emoji; track unique holders
        const byBadge = {};
        (ach || []).forEach(a => {
          const k = a.achievement_name || a.achievement_key || 'unknown';
          if (!byBadge[k]) byBadge[k] = { name: k, emoji: a.emoji || '', rarity: a.rarity || 'common', description: a.description || '', holders: new Set() };
          if (a.user_id) byBadge[k].holders.add(a.user_id);
        });
        // 2026-08-06: a head:true count returns 0 (not null) when RLS filters
        // every row, so `|| 1` let 5 holders render as "500% of players". If the
        // denominator is smaller than the holder count it is not a denominator.
        const total = totalUsers || 0;
        const list = Object.values(byBadge).map(b => ({
          name: b.name, emoji: b.emoji, rarity: b.rarity, description: b.description,
          holdersCount: b.holders.size,
          // Null rather than a nonsense number when the denominator is unusable —
          // the UI can say nothing, which is honest, instead of "500%".
          rarityPct: (total > 0 && b.holders.size <= total)
            ? Math.round((b.holders.size / total) * 1000) / 10
            : null,
        })).sort((a, b) => a.holdersCount - b.holdersCount); // rarest first
        setLiveBadgeCatalog({ badges: list, totalUsers: total });
      } catch (e) {
        console.warn('All4Indoor: Badge catalog load failed', e);
        setLiveBadgeCatalog({ badges: [], totalUsers: 0 });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ─── REAL milestones for the logged-in user (based on lifetime games played) ───
  const [liveMilestones, setLiveMilestones] = useState(null);
  useEffect(() => {
    if (!liveCareer) { setLiveMilestones(null); return; }
    const games = liveCareer.totalGames || 0;
    const tiers = [
      { games: 10,  name: 'First 10 Games',   emoji: '', desc: 'Welcome to the WISA family' },
      { games: 25,  name: 'Quarter Century',  emoji: '', desc: '25 games played' },
      { games: 50,  name: 'Half Century',     emoji: '', desc: '50 games played' },
      { games: 100, name: 'Century Club',     emoji: '', desc: '100 games played' },
      { games: 200, name: 'Double Century',   emoji: '', desc: '200 games played' },
      { games: 500, name: 'Hall of Fame',     emoji: '', desc: '500 games — legend status' },
    ];
    setLiveMilestones(tiers.map(t => ({
      ...t,
      progress: Math.min(games, t.games),
      target: t.games,
      complete: games >= t.games,
    })));
  }, [liveCareer]);

  // ─── REAL recent activity feed at the venue ───
  // Aggregates recent matches into a "X just played" / "Y scored a hat-trick" feed.
  const [liveActivity, setLiveActivity] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: recent } = await supabase
          .from('matches')
          .select('id, scheduled_at, home_score, away_score, motm_name, home_team:teams!home_team_id(name), away_team:teams!away_team_id(name), competition:competitions(name, sport:sports(name))')
          .eq('status', 'completed')
          .not('home_score', 'is', null)
          .order('scheduled_at', { ascending: false })
          .limit(15);
        if (cancelled || !recent) return;
        const items = recent.map(m => {
          const h = m.home_score ?? 0, a = m.away_score ?? 0;
          const winner = h > a ? m.home_team?.name : (a > h ? m.away_team?.name : null);
          const sport = (m.competition?.sport?.name || '').replace(/^Indoor\s+/i, '');
          const when = new Date(m.scheduled_at).toLocaleDateString('en-AU', { weekday:'short', day:'numeric', month:'short' });
          return {
            id: m.id,
            sport, when,
            text: winner
              ? `${winner} beat ${winner === m.home_team?.name ? m.away_team?.name : m.home_team?.name} ${Math.max(h,a)}–${Math.min(h,a)}`
              : `${m.home_team?.name} drew with ${m.away_team?.name} ${h}–${a}`,
            mvp: m.motm_name,
            comp: m.competition?.name,
          };
        });
        setLiveActivity(items);
      } catch (e) {
        console.warn('All4Indoor: failed to load activity feed', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError(""); setLoginLoading(true);
    // Alias lookup — if the email is registered as an alias of another user,
    // swap it for that user's canonical email before signing in. Mirrors the
    // logic in frontend/src/context/AuthContext.js.
    let emailForLogin = loginEmail.trim();
    try {
      // 2026-07-27: via RPC — see AuthContext.js. Reading the alias table
      // directly required anon to hold SELECT on it, which exposed every
      // alias and canonical address to anyone with the publishable key.
      const { data: canonical } = await supabase.rpc('resolve_login_email', { p_email: emailForLogin });
      if (canonical) emailForLogin = canonical;
    } catch (_) { /* best-effort */ }
    const { error } = await supabase.auth.signInWithPassword({ email: emailForLogin, password: loginPassword });
    if (error) setLoginError(error.message);
    else { setLoginEmail(""); setLoginPassword(""); }
    setLoginLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setAuthUser(null); setAuthProfile(null);
  };

  const [openDropdown, setOpenDropdown] = useState(null);
  const navItems = [
    { key: "home", label: "Home" },
    { key: "venues", label: "Venues" },
    { key: "profile", label: "My Profile" },
    { label: "Compete", children: [
      { key: "rankings", label: "Player Rankings", icon: "" },
      { key: "team-rankings", label: "Team Rankings", icon: "" },
      { key: "h2h", label: "Head-to-Head", icon: "" },
      { key: "hall", label: "Hall of Fame", icon: "" },
      // 2026-08-11 (Jake): Hall of Fame shows one record holder per category.
      // This is the ranked list behind it - leading run scorer, leading wicket
      // taker, most goals by sport, career milestones.
      { key: "alltime", label: "All-Time Leaders", icon: "" },
      { key: "badges", label: "Badge Catalog", icon: "" },
      { key: "wrapped", label: "Your Wrapped", icon: "" },
      { key: "awards", label: "Season Awards", icon: "" },
      // 2026-07-27: Prizes hidden from the nav while the page reads
      // "Prize Pool: Coming Soon / Categories: TBA / Eligible Venues: 1".
      // The section still renders if something links to it.
      // { key: "prizes", label: "Prizes", icon: "" },
    ]},
    { label: "Play", children: [
      { key: "play", label: "Find a Game", icon: "" },
      { key: "teams", label: "Teams", icon: "" },
      { key: "matches", label: "Match Reports", icon: "" },
    ]},
    { label: "Pathways", children: [
      { key: "rep", label: "Rep Pathways", icon: "" },
      { key: "for-venues", label: "For Venues", icon: "" },
    ]},
  ];

  return (
    <div className="a4i-root" style={{ fontFamily: "'Inter', -apple-system, sans-serif", background: "#07080b", minHeight: "100vh", color: "#e8eaee" }}>

      {/* ── NAVBAR ─────────────────────────────────────── */}
      <nav className="a4i-nav" style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(8,9,12,0.9)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.08)", padding: "0 32px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }} onClick={() => setSection("home")}>
            <span style={{ fontFamily: "'Archivo', sans-serif", fontSize: "1.3rem", fontWeight: 900, color: "#f6f7f9", letterSpacing: "-0.03em" }}>All</span>
            <div style={{ width: 34, height: 34, borderRadius: 8, background: "#00e676", display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ fontFamily: "'Archivo', sans-serif", color: "#07080b", fontWeight: 900, fontSize: "1.15rem" }}>4</span></div>
            <span style={{ fontFamily: "'Archivo', sans-serif", fontSize: "1.3rem", fontWeight: 900, color: "#f6f7f9", letterSpacing: "-0.03em" }}>Indoor</span>
          </div>
          {!isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
              {navItems.map((n, ni) => {
                if (n.children) {
                  const isChildActive = n.children.some(c => c.key === section);
                  const isOpen = openDropdown === ni;
                  return (
                    <div key={n.label} style={{ position: "relative" }}
                      onMouseEnter={() => setOpenDropdown(ni)}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      <div style={{ fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", padding: "8px 14px", borderRadius: 8, transition: "all 0.2s", color: isChildActive ? "#ff2e17" : "#c3c7ce", background: isChildActive ? "rgba(255,46,23,0.14)" : isOpen ? "rgba(255,255,255,0.06)" : "transparent", display: "flex", alignItems: "center", gap: 4 }}>
                        {n.label} <span style={{ fontSize: "0.6rem", opacity: 0.5 }}>▼</span>
                      </div>
                      {isOpen && (
                        <div style={{ position: "absolute", top: "100%", left: 0, background: "#101319", borderRadius: 12, boxShadow: "0 12px 40px rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.1)", padding: "6px", minWidth: 200, zIndex: 200 }}>
                          {n.children.map(c => (
                            <div key={c.key} onClick={() => { setSection(c.key); setOpenDropdown(null); }}
                              style={{ fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", padding: "10px 14px", borderRadius: 8, transition: "all 0.15s", color: section === c.key ? "#ff2e17" : "#c3c7ce", background: section === c.key ? "rgba(255,46,23,0.14)" : "transparent", display: "flex", alignItems: "center", gap: 8 }}
                              onMouseEnter={e => { if (section !== c.key) e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                              onMouseLeave={e => { e.currentTarget.style.background = section === c.key ? "rgba(255,46,23,0.14)" : "transparent"; }}
                            ><span>{c.icon}</span> {c.label}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <div key={n.key} onClick={() => setSection(n.key)}
                    style={{ fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", padding: "8px 14px", borderRadius: 8, transition: "all 0.2s", color: section === n.key ? "#ff2e17" : "#c3c7ce", background: section === n.key ? "rgba(255,46,23,0.14)" : "transparent" }}
                    onMouseEnter={e => { if (section !== n.key) e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                    onMouseLeave={e => { if (section !== n.key) e.currentTarget.style.background = section === n.key ? "rgba(255,46,23,0.14)" : "transparent"; }}
                  >{n.label}</div>
                );
              })}
            </div>
          )}
          {isMobile && (
            <button onClick={() => setMobileMenuOpen(o => !o)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", padding: "8px 12px", borderRadius: 10, color: "#f6f7f9", fontSize: "1.1rem", cursor: "pointer", display: "flex", alignItems: "center" }}>
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* Global Find Player — opens search modal */}
            <button onClick={() => setGlobalSearchOpen(true)}
              title="Find a player — press / from anywhere" style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.04)", color: "#c3c7ce", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#ff2e17"; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)"; e.currentTarget.style.color = "#c3c7ce"; }}>
              <Search size={14} /> Find player
              {/* 2026-05-31: visible '/' kbd hint advertises the keyboard
                  shortcut. Hidden via CSS on mobile so it doesn't add
                  visual noise where there's no keyboard. */}
              <kbd style={{ marginLeft: 4, padding: "1px 6px", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 4, fontSize: "0.65rem", color: "#8a8f98", background: "rgba(255,255,255,0.05)", fontFamily: "ui-monospace, monospace" }}>/</kbd>
            </button>
            {authUser && authProfile ? (
              <>
                <div onClick={() => setSection("profile")} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "6px 14px", borderRadius: 10, background: "rgba(255,46,23,0.12)", border: "1px solid rgba(255,46,23,0.25)" }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "linear-gradient(135deg, #ff2e17, #ff5a45)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "0.65rem", fontWeight: 700 }}>{(authProfile.first_name?.[0] || "") + (authProfile.last_name?.[0] || "")}</div>
                  <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#f6f7f9" }}>{authProfile.first_name}</span>
                </div>
                <button onClick={handleLogout} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "#9298a3", padding: "6px 14px", borderRadius: 10, fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}>Sign Out</button>
              </>
            ) : (
              <button onClick={() => setSection("profile")} style={{ background: "#ff2e17", color: "#fff", padding: "8px 22px", borderRadius: 8, fontSize: "0.82rem", fontWeight: 800, border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                <LogIn size={14} /> Sign In
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile drawer — fullscreen menu for narrow viewports */}
      {isMobile && mobileMenuOpen && (
        <div style={{ position: "fixed", inset: "64px 0 0 0", background: "#101319", zIndex: 99, overflowY: "auto", padding: "20px 24px 60px" }}>
          {navItems.map((n) => {
            if (n.children) {
              return (
                <div key={n.label} style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", color: "#9aa0ab", marginBottom: 8 }}>{n.label}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {n.children.map(c => (
                      <button key={c.key} onClick={() => setSection(c.key)}
                        style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, border: "none", background: section === c.key ? "rgba(26,138,125,0.1)" : "transparent", color: section === c.key ? P : C.ink2, fontSize: "0.95rem", fontWeight: 700, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
                        <span style={{ fontSize: "1.2rem" }}>{c.icon}</span> {c.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            }
            return (
              <button key={n.key} onClick={() => setSection(n.key)}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "14px 14px", borderRadius: 10, border: "none", background: section === n.key ? "rgba(26,138,125,0.1)" : "transparent", color: section === n.key ? P : C.ink2, fontSize: "1rem", fontWeight: 800, cursor: "pointer", marginBottom: 4, fontFamily: "inherit" }}>
                {n.label}
              </button>
            );
          })}
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid #f0f0f5", display: "flex", flexDirection: "column", gap: 10 }}>
            <button onClick={() => setGlobalSearchOpen(true)}
              style={{ padding: "12px 14px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.14)", background: "#101319", color: "#f4f5f7", fontSize: "0.95rem", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, fontFamily: "inherit" }}>
              <Search size={16} /> Find a player
            </button>
            {authUser ? (
              <button onClick={handleLogout}
                style={{ padding: "12px 14px", borderRadius: 10, border: "1px solid #e0e0e0", background: "transparent", color: "#9aa0ab", fontSize: "0.95rem", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Sign Out
              </button>
            ) : (
              <button onClick={() => setSection("profile")}
                style={{ padding: "12px 14px", borderRadius: 10, border: "none", background: `linear-gradient(135deg, ${P}, #34d399)`, color: "#fff", fontSize: "0.95rem", fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                Sign In
              </button>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/* HOME                                           */}
      {/* ═══════════════════════════════════════════════ */}
      {section === "home" && (
        <>
          {/* Hero */}
          {/* 2026-05-31: tightened hero padding on mobile (clamp 48-80px top
              so phones don't burn a third of the first screen on the
              gradient before any content). */}
          <section style={{ position: "relative", overflow: "hidden", background: "#07080b", color: "#f4f5f7", padding: "clamp(104px, 13vw, 148px) 0 clamp(64px, 8vw, 88px)" }}>
            <div style={{ position: "absolute", top: "-12%", right: "-6%", width: "62%", height: "82%", background: "radial-gradient(circle, rgba(255,46,23,0.13), transparent 62%)", pointerEvents: "none" }} />
            <div style={{ position: "relative", zIndex: 2, maxWidth: 1200, margin: "0 auto", padding: "0 clamp(24px, 5vw, 44px)", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.05fr 0.95fr", gap: isMobile ? 40 : 56, alignItems: "center", minWidth: 0 }}>
              {/* 2026-07-27: minWidth:0 is load-bearing. A `1fr` grid track takes
                  its automatic minimum from the child's min-content width, and this
                  column's min-content was 432px. On a 390px phone the track grew to
                  432 and the section's overflow:hidden CLIPPED it — the headline,
                  the paragraph and half the Search button were cut off mid-word.
                  Measured at 390 and 360 before and after. */}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.22em", fontSize: "0.78rem", color: C.ink3, marginBottom: 18, display: "flex", alignItems: "center", gap: 9 }}><span style={{ width: 7, height: 7, borderRadius: "50%", background: "#ff2e17", display: "inline-block" }} />One ranking · Starting in Townsville</div>
                <h1 style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: "clamp(2.6rem, 5.4vw, 4.6rem)", lineHeight: 0.98, letterSpacing: "-0.035em", margin: "0 0 22px" }}>Every player, ranked.<br /><span style={{ color: "#9aa0ab" }}>Every game, counted.</span></h1>
                <p style={{ color: "#c3c7ce", fontSize: "1.08rem", lineHeight: 1.6, maxWidth: 440, margin: "0 0 30px" }}>Every game at a WISA venue counts toward one shared ranking — your stats, your career history, your place on the ladder. It starts at Wildcatz in Townsville, and it grows one venue at a time.</p>
                <div onClick={() => setGlobalSearchOpen(true)} style={{ display: "flex", alignItems: "center", gap: 12, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.16)", borderRadius: 11, padding: "5px 5px 5px 18px", width: "100%", maxWidth: 460, marginBottom: 18, cursor: "pointer" }}>
                  <Search size={18} color="rgba(255,255,255,0.5)" />
                  <div style={{ flex: 1, minWidth: 0, color: "rgba(255,255,255,0.6)", fontSize: "0.95rem", padding: "12px 0", pointerEvents: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Search your name to see your ranking…</div>
                  <button onClick={e => { e.stopPropagation(); setGlobalSearchOpen(true); }} style={{ fontFamily: "'Archivo', sans-serif", background: "#ff2e17", color: "#fff", border: "none", borderRadius: 8, padding: "11px 22px", fontWeight: 700, fontSize: "0.9rem", cursor: "pointer" }}>Search</button>
                </div>
                <div style={{ display: "flex", gap: 13, flexWrap: "wrap" }}>
                  <button onClick={() => setGlobalSearchOpen(true)} style={{ fontFamily: "'Archivo', sans-serif", display: "inline-flex", alignItems: "center", gap: 8, background: "#ff2e17", border: "none", color: "#fff", padding: "13px 24px", borderRadius: 8, fontWeight: 700, fontSize: "0.95rem", cursor: "pointer" }}><Search size={16} /> Find Your Stats</button>
                  <button onClick={requestLocation} disabled={locationLoading} style={{ fontFamily: "'Archivo', sans-serif", display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.16)", color: "#fff", padding: "13px 24px", borderRadius: 8, fontWeight: 700, fontSize: "0.95rem", cursor: locationLoading ? "wait" : "pointer" }}><Navigation size={16} />{locationLoading ? "Finding you..." : userLocation ? "Location found" : "Browse Venues"}</button>
                </div>
              </div>
              <div style={{ background: C.surface, border: `1px solid ${C.line}`, borderRadius: R.lg, padding: 6, boxShadow: "0 40px 90px -30px rgba(0,0,0,0.8)", minWidth: 0, overflow: "hidden" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 20px 14px" }}>
                  <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: "1.05rem" }}>Top Ranked Players</div>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontFamily: "'Archivo', sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", fontSize: "0.68rem", color: "#ff6a5f" }}><span style={{ width: 7, height: 7, borderRadius: "50%", background: "#ff2e17", animation: "pulse 1.8s infinite" }} />Live</span>
                </div>
                {(liveElo && liveElo.length ? liveElo.slice(0, 5) : []).map((p, i) => {
                  // 2026-08-06 (Jake): "Gold Silver Bronze rest faded?"
                  //
                  // This used to cycle five gradients by array index — blue,
                  // rose, green, amber, purple — so 4th place was amber for no
                  // reason other than being 4th in a list. The colour carried
                  // nothing. A podium is read instantly and means what it looks
                  // like it means.
                  const pod = podium(i + 1);
                  const form = (p.recentForm || []).slice(0, 5);
                  return (
                    <div key={p.id} onClick={() => setSelectedPlayerId(p.id)} style={{ display: "grid", gridTemplateColumns: "26px 40px minmax(0, 1fr) auto auto", alignItems: "center", gap: 10, padding: "11px 14px", borderRadius: 12, cursor: "pointer", transition: "background 0.2s", minWidth: 0 }}
                      onMouseEnter={e => e.currentTarget.style.background = "#151a22"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: "1.05rem", color: i < 3 ? pod.bg : C.ink3, textAlign: "center", ...NUM }}>{i + 1}</div>
                      <div style={{ width: 44, height: 44, borderRadius: R.sm, background: pod.bg, color: pod.fg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: "0.95rem", boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.10)" }}>{p.avatar}</div>
                      <div style={{ minWidth: 0 }}><div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 700, fontSize: "0.98rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div><div style={{ fontSize: "0.76rem", color: "#9aa0ab" }}>{p.games} games</div></div>
                      <div style={{ display: "flex", gap: 4, alignItems: "flex-end", height: 26 }}>{form.map((f, fi) => (<span key={fi} style={{ width: 6, height: f === 'W' ? "100%" : f === 'D' ? "55%" : "32%", borderRadius: 2, background: f === 'W' ? C.pos : "rgba(255,255,255,0.14)", display: "block" }} />))}</div>
                      <div style={{ textAlign: "right", minWidth: 50 }}><div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: "1.25rem", letterSpacing: "-0.02em", ...NUM }}>{p.elo}</div></div>
                    </div>
                  );
                })}
                {/* 2026-07-27 (Jake): liveElo is null while loading and [] once
                    loaded-and-empty. The old check treated both the same, so a
                    failed or empty query left five grey skeleton bars pulsing
                    under "Updating live…" forever. Skeletons are for waiting;
                    an empty result deserves a sentence. */}
                {!liveElo && (
                  <div style={{ padding: "6px 16px 4px", display: "flex", flexDirection: "column", gap: 12 }}>{Array.from({ length: 5 }).map((_, i) => (<div key={i} style={{ display: "flex", alignItems: "center", gap: 13 }}><Skeleton w={28} h={20} /><Skeleton w={44} h={44} r={12} /><Skeleton w="55%" h={16} /></div>))}</div>
                )}
                {liveElo && !liveElo.length && (
                  <div style={{ padding: "26px 20px 24px", textAlign: "center" }}>
                    <div style={{ fontSize: "0.92rem", color: "#c3c7ce", marginBottom: 6 }}>No ranked players yet</div>
                    <div style={{ fontSize: "0.82rem", color: "#646a75", lineHeight: 1.5 }}>Rankings appear once enough games have been played and scored.</div>
                  </div>
                )}
                <div style={{ padding: "13px 20px 15px", borderTop: "1px solid rgba(255,255,255,0.09)", marginTop: 4, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "0.76rem", color: "#646a75" }}>{!liveElo ? "Loading…" : liveElo.length ? `${liveElo.length.toLocaleString()} ranked players` : "Nothing ranked yet"}</span>
                  <button onClick={() => setSection("rankings")} style={{ fontFamily: "'Archivo', sans-serif", background: "transparent", border: "none", cursor: "pointer", fontWeight: 700, fontSize: "0.82rem", color: "#ff6a5f", display: "inline-flex", gap: 6, alignItems: "center" }}>Full rankings <ArrowRight size={13} /></button>
                </div>
              </div>
            </div>
          </section>

                    {/* Nearest venues if location found */}
          {userLocation && (
            <section style={{ background: `linear-gradient(180deg, ${P} 0%, #fafafe 100%)`, padding: "0 32px 40px" }}>
              <div style={{ maxWidth: 1200, margin: "0 auto", paddingTop: 40 }}>
                <h2 style={{ fontSize: "1.4rem", fontWeight: 800, color: "#fff", margin: "0 0 20px" }}> Nearest Venues to You</h2>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 24 }}>
                  {allVenuesList.sort((a, b) => (a.distance || 9999) - (b.distance || 9999)).slice(0, 3).map(v => (
                    <VenueCard key={v.id} venue={v} distance={v.distance} onClick={() => v.wisa ? setSelectedVenue(v) : null} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Stats strip — scoreboard board bar */}
          <section style={{ background: "linear-gradient(180deg, #0a0c10, #07080b)", borderTop: "1px solid rgba(255,255,255,0.08)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ maxWidth: 900, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4, 1fr)" }}>
              {/* 2026-07-27 (Jake): this used to read 1 VENUES / 998 PLAYERS /
                  244 TEAMS / 1 COUNTRIES directly under a headline calling
                  itself a global network. One venue and one country, in the
                  largest type on the page, is an argument against your own
                  pitch — and the 998 / 244 were stale hardcoded values on top
                  of that. Show what is genuinely large and genuinely true. The
                  venue and country counters earn their place back when there
                  is more than one of each. */}
              {/* Show a dash, never a zero. If the query fails, "0 PLAYERS" is a
                  worse lie than the stale numbers this replaced. */}
              <StatCard value={liveVenueStats?.players != null ? liveVenueStats.players.toLocaleString() : "—"} label="Players" accent />
              <StatCard value={liveVenueStats?.teams != null ? liveVenueStats.teams.toLocaleString() : "—"} label="Teams" />
              <StatCard value={liveVenueStats?.matches != null ? liveVenueStats.matches.toLocaleString() : "—"} label="Matches played" />
              <StatCard value={ALL_SPORTS.length} label="Sports" accent last />
            </div>
          </section>

          {/* LIVE TICKER — currently in-progress matches with pulsing dot */}
          {liveTicker && liveTicker.length > 0 && (
            <section style={{ background: "linear-gradient(90deg, rgba(220,38,38,0.06), rgba(255,107,61,0.04))", borderTop: "1px solid rgba(220,38,38,0.15)", borderBottom: "1px solid rgba(220,38,38,0.15)", padding: "14px 32px" }}>
              <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", gap: 16, overflowX: "auto" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#dc2626", animation: "pulse 1.5s infinite" }} />
                  <span style={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: 2, color: "#dc2626", textTransform: "uppercase" }}>● LIVE NOW</span>
                </div>
                {liveTicker.map(m => (
                  <div key={m.id} style={{ flexShrink: 0, background: "#101319", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 10, padding: "8px 14px", display: "flex", alignItems: "center", gap: 10, fontSize: "0.82rem" }}>
                    <span style={{ fontSize: "0.85rem" }}>{m.competition?.sport?.icon || ''}</span>
                    <span style={{ fontWeight: 700 }}>{m.home_team?.name || 'Home'}</span>
                    <span style={{ fontWeight: 900, color: "#dc2626" }}>{m.home_score ?? 0}–{m.away_score ?? 0}</span>
                    <span style={{ fontWeight: 700 }}>{m.away_team?.name || 'Away'}</span>
                    {m.court?.name && <span style={{ fontSize: "0.7rem", color: "#9aa0ab" }}>· {m.court.name}</span>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* PLAYER OF THE WEEK — auto-curated top performer last 7 days */}
          {livePlayerOfTheWeek && !livePlayerOfTheWeek.empty && (
            <section style={{ background: "linear-gradient(180deg, #0a0c11, #07080b)", borderTop: "1px solid rgba(255,255,255,0.08)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
              <div onClick={() => setSelectedPlayerId(livePlayerOfTheWeek.id)}
                style={{ maxWidth: 1200, margin: "0 auto", padding: "40px clamp(24px, 5vw, 44px)", display: "flex", alignItems: "center", gap: 30, cursor: "pointer", flexWrap: "wrap" }}>
                <div style={{ width: 104, height: 104, borderRadius: 22, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: "2.4rem", color: "#fff", background: "linear-gradient(135deg, #ff2e17, #b91c1c)", boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.14), 0 20px 50px -15px rgba(255,46,23,0.5)", flexShrink: 0 }}>
                  {livePlayerOfTheWeek.name.split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.22em", fontSize: "0.72rem", color: "#ff6a5f", marginBottom: 8 }}>Player of the Week</div>
                  <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: "clamp(1.7rem, 4vw, 2.3rem)", letterSpacing: "-0.03em", lineHeight: 1, color: "#f4f5f7" }}>{livePlayerOfTheWeek.name}</div>
                  <div style={{ fontSize: "0.95rem", color: "#9aa0ab", marginTop: 8 }}>
                    {livePlayerOfTheWeek.games} game{livePlayerOfTheWeek.games === 1 ? '' : 's'} · <strong style={{ color: "#c3c7ce" }}>{livePlayerOfTheWeek.wins} wins</strong> ({livePlayerOfTheWeek.winRate}%)
                    {livePlayerOfTheWeek.goals > 0 ? ` · ${livePlayerOfTheWeek.goals} goals` : ''}
                    {livePlayerOfTheWeek.runs > 0 ? ` · ${livePlayerOfTheWeek.runs} runs` : ''}
                    {livePlayerOfTheWeek.wickets > 0 ? ` · ${livePlayerOfTheWeek.wickets} wkts` : ''}
                    {livePlayerOfTheWeek.sport ? ` · ${livePlayerOfTheWeek.sport.replace(/^Indoor\s+/i, '')}` : ''}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 34, flexShrink: 0 }}>
                  <div style={{ textAlign: "center" }}><div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: "1.9rem", color: "#f4f5f7", letterSpacing: "-0.02em" }}>{livePlayerOfTheWeek.wins}</div><div style={{ fontFamily: "'Archivo', sans-serif", textTransform: "uppercase", letterSpacing: "0.16em", fontSize: "0.64rem", color: "#646a75", marginTop: 4 }}>Wins</div></div>
                  <div style={{ textAlign: "center" }}><div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: "1.9rem", color: "#ff2e17", letterSpacing: "-0.02em" }}>{livePlayerOfTheWeek.winRate}%</div><div style={{ fontFamily: "'Archivo', sans-serif", textTransform: "uppercase", letterSpacing: "0.16em", fontSize: "0.64rem", color: "#646a75", marginTop: 4 }}>Win rate</div></div>
                </div>
              </div>
            </section>
          )}

          {/* 2026-05-31: "Did You Know?" rotating fact strip. Derives a
              handful of quirky facts from leaderboard data already loaded
              for the page (no extra queries), rotates every 5s. Adds the
              kind of curated venue feel that makes a player think "hang on,
              this place actually tracks me." */}
          <DidYouKnowStrip
            sportLb={liveSportLeaderboards}
            ranked={liveRankedPlayers}
            poTW={livePlayerOfTheWeek}
            onPickPlayer={id => setSelectedPlayerId(id)}
          />

          {/* 2026-05-31: Latest Results strip — the most recent 6 completed
              matches with score, sport icon, winning side highlighted, and"X ago" relative time. Adds the "things are happening here"feel that prizes-card or recap pages alone can't. Each card
              opens that match's competition. */}
          {liveRecentResults && liveRecentResults.length > 0 && (
            <section style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 32px 0" }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <h3 style={{ fontSize: "1.15rem", fontWeight: 900, margin: 0, color: "#f4f5f7" }}>Latest Results</h3>
                <div style={{ fontSize: "0.72rem", color: "#9aa0ab" }}>Most recent matches at Wildcatz</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10 }}>
                {liveRecentResults.map(m => {
                  const homeWon = m.home_score > m.away_score;
                  const awayWon = m.away_score > m.home_score;
                  const fmtRel = (iso) => {
                    if (!iso) return '';
                    const diff = Date.now() - new Date(iso).getTime();
                    const days = Math.floor(diff / 86400000);
                    if (days >= 1) return `${days}d ago`;
                    const hrs = Math.floor(diff / 3600000);
                    if (hrs >= 1) return `${hrs}h ago`;
                    const mins = Math.max(1, Math.floor(diff / 60000));
                    return `${mins}m ago`;
                  };
                  return (
                    <div key={m.id} onClick={() => setSelectedTeamId(homeWon ? m.home_team_id : awayWon ? m.away_team_id : m.home_team_id)}
                      style={{ background: "#101319", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 12, padding: "12px 14px", cursor: "pointer", transition: "transform 0.12s, box-shadow 0.12s" }}
                      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.07)"; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.65rem", color: "#9aa0ab", fontWeight: 700, letterSpacing: 1.4, textTransform: "uppercase", marginBottom: 6 }}>
                        <span>{m.competition?.sport?.icon || ''}</span>
                        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.competition?.name || m.competition?.sport?.name || 'Match'}</span>
                        <span>{fmtRel(m.updated_at || m.scheduled_at)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                        <div style={{ flex: 1, minWidth: 0, fontWeight: homeWon ? 800 : 500, color: homeWon ? "#10b981" : "#555", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.92rem" }}>{m.home_team?.name || 'Home'}</div>
                        <div style={{ fontWeight: 900, color: homeWon ? "#10b981" : C.ink2, fontSize: "1rem", minWidth: 22, textAlign: "right" }}>{m.home_score}</div>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginTop: 4 }}>
                        <div style={{ flex: 1, minWidth: 0, fontWeight: awayWon ? 800 : 500, color: awayWon ? "#10b981" : "#555", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.92rem" }}>{m.away_team?.name || 'Away'}</div>
                        <div style={{ fontWeight: 900, color: awayWon ? "#10b981" : C.ink2, fontSize: "1rem", minWidth: 22, textAlign: "right" }}>{m.away_score}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* TOP 10 LEADERBOARDS — per sport, last 14 days. The on-landing
              hook. Players hit the home page, see their sport's leaderboard,
              want to be on it. Each row clicks through to the player profile.
              2026-05-28: filter pill row controls which sport's deeper
              leaderboards (cricket figures etc.) render below.
              2026-05-31: render a skeleton card grid while leaderboards
              load instead of nothing. A new visitor on slow mobile
              previously saw an empty page for a few seconds — now they
              get visual confirmation something is rendering. */}
          {!liveSportLeaderboards && (
            <section style={{ maxWidth: 1200, margin: "0 auto", padding: "30px 32px 0" }}>
              <Skeleton w={220} h={28} style={{ marginBottom: 12 }} />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 12, marginTop: 16 }}>
                {[0,1,2,3].map(i => (
                  <div key={i} style={{ background: "#101319", borderRadius: 14, padding: "16px", border: "1px solid rgba(255,255,255,0.09)" }}>
                    <Skeleton w={140} h={18} style={{ marginBottom: 10 }} />
                    <SkeletonRow count={5} h={40} gap={6} />
                  </div>
                ))}
              </div>
            </section>
          )}
          {liveSportLeaderboards && Object.keys(liveSportLeaderboards).some(k => liveSportLeaderboards[k]?.length > 0) && (
            <section style={{ maxWidth: 1200, margin: "0 auto", padding: "30px 32px 0" }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <h3 style={{ fontSize: "1.3rem", fontWeight: 900, margin: 0, color: "#f4f5f7" }}>Top 10 · last 14 days</h3>
                <div style={{ fontSize: "0.78rem", color: "#9aa0ab" }}>Tap a name for their profile</div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                {[
                  { key: null,         label: 'All sports', emoji: '' },
                  { key: 'cricket',    label: 'Cricket',    emoji: '' },
                  { key: 'netball',    label: 'Netball',    emoji: '' },
                  { key: 'soccer',     label: 'Soccer',     emoji: '' },
                  { key: 'volleyball', label: 'Volleyball', emoji: '' },
                ].map(f => (
                  <button key={f.key || 'all'} onClick={() => setHomeSportFilter(f.key)}
                    style={{
                      padding: "6px 14px", borderRadius: 18, border: `1.5px solid ${homeSportFilter === f.key ? P : C.line}`,
                      background: homeSportFilter === f.key ? P : C.raised, color: homeSportFilter === f.key ? '#fff' : C.ink2,
                      fontWeight: 700, fontSize: "0.78rem", cursor: "pointer", fontFamily: "inherit", transition: 'all 0.15s',
                    }}>{f.emoji} {f.label}</button>
                ))}
              </div>
              {/* 2026-05-31: tightened min from 280px to 210px so all 4
                  sport columns fit on a single row at typical desktop widths
                  (~960px+). Below ~880px CSS still auto-wraps to 2 cols which
                  reads cleanly on tablet/mobile. Before, Volleyball wrapped
                  onto its own line on standard desktops. */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 12 }}>
                {[
                  { key: 'cricket',    label: 'Cricket',    emoji: '', stat: 'contribution', statLabel: 'CONTRIB', tone: '#92400e' },
                  { key: 'netball',    label: 'Netball',    emoji: '', stat: 'goals',        statLabel: 'GOALS',   tone: '#e84393' },
                  { key: 'soccer',     label: 'Soccer',     emoji: '', stat: 'goals',        statLabel: 'GOALS',   tone: '#3b82f6' },
                  { key: 'volleyball', label: 'Volleyball', emoji: '', stat: 'wins',         statLabel: 'WINS',    tone: '#ff2e17' },
                ].filter(s => (!homeSportFilter || s.key === homeSportFilter) && liveSportLeaderboards[s.key]?.length > 0).map(s => (
                  <LeaderboardCard
                    key={s.key}
                    sport={s}
                    rows={liveSportLeaderboards[s.key]}
                    accent={P}
                    onOpenPlayer={(id) => id && setSelectedPlayerId(id)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* 2026-05-28: Best bowling figures of the season (Spawtz-style"5/12" highlight). Per (bowler, match) tally from balls_data,
              ranked by wickets desc then runs asc. Only renders if there's
              data to show. Hidden when a non-cricket sport filter is active. */}
          {(!homeSportFilter || homeSportFilter === 'cricket') && liveBestBowling && liveBestBowling.length > 0 && (
            <section style={{ maxWidth: 1200, margin: "0 auto", padding: "30px 32px 0" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <h3 style={{ fontSize: "1rem", fontWeight: 800, margin: 0 }}> Best Bowling Figures · last 60 days</h3>
                <span style={{ fontSize: "0.72rem", color: "#9aa0ab" }}>Wickets / Runs conceded</span>
              </div>
              <div style={{ background: "#101319", borderRadius: 14, border: "1px solid rgba(255,255,255,0.09)", overflow: "hidden" }}>
                {liveBestBowling.map((f, i) => (
                  <div key={`${f.bowler}-${f.match_id}`}
                    onClick={() => f.id && setSelectedPlayerId(f.id)}
                    style={{
                    display: "grid",
                    gridTemplateColumns: "44px 1fr auto",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 16px",
                    borderBottom: i < liveBestBowling.length - 1 ? "1px solid #f5f5f7" : "none",
                    background: i === 0 ? "rgba(255, 218, 121, 0.15)" : "transparent",
                    cursor: f.id ? "pointer" : "default",
                  }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: "'Black Han Sans', sans-serif", fontWeight: 900, fontSize: "0.95rem",
                      // 2026-08-06: these are LIGHT chips, so the numeral has to
                      // be dark ink — my earlier colour sweep briefly made the top
                      // three light-on-gold. On the podium tokens now, same as the
                      // player ladder, so the two screens agree and neither drifts.
                      background: i < 3 ? podium(i + 1).bg : C.raised,
                      color: i < 3 ? podium(i + 1).fg : C.ink2,
                      ...NUM,
                    }}>{i + 1}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#f4f5f7", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.bowler}</div>
                      <div style={{ fontSize: "0.72rem", color: "#9aa0ab", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {f.opp || '—'}{f.when ? ` · ${new Date(f.when).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}` : ''}
                      </div>
                    </div>
                    <div style={{ fontFamily: "'Black Han Sans', sans-serif", fontSize: "1.3rem", color: "#e74c3c", fontWeight: 900 }}>
                      {f.wickets}<span style={{ color: "#cbd5e1", fontWeight: 400, margin: "0 2px" }}>/</span>{f.runs}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 2026-05-28: Player leaderboards — best individual innings and partnerships.
              Two-column grid; ranked top 10 from last 60 days of cricket.
              Cricket-only — hidden when a different sport filter is active. */}
          {(!homeSportFilter || homeSportFilter === 'cricket') && liveCricketLeaderboards && (liveCricketLeaderboards.bestInnings?.length > 0 || liveCricketLeaderboards.bestPartnerships?.length > 0) && (
            <section style={{ maxWidth: 1200, margin: "0 auto", padding: "30px 32px 0" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 18 }}>
                {liveCricketLeaderboards.bestInnings?.length > 0 && (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                      <h3 style={{ fontSize: "1rem", fontWeight: 800, margin: 0 }}> Best Innings · last 60 days</h3>
                      <span style={{ fontSize: "0.72rem", color: "#9aa0ab" }}>Runs scored</span>
                    </div>
                    <div style={{ background: "#101319", borderRadius: 14, border: "1px solid rgba(255,255,255,0.09)", overflow: "hidden" }}>
                      {liveCricketLeaderboards.bestInnings.map((row, i) => (
                        <div key={`bi-${row.batter}-${row.match_id}-${i}`}
                          onClick={() => row.id && setSelectedPlayerId(row.id)}
                          style={{
                          display: "grid", gridTemplateColumns: "32px 1fr auto", alignItems: "center", gap: 10,
                          padding: "10px 14px",
                          borderBottom: i < liveCricketLeaderboards.bestInnings.length - 1 ? "1px solid #f5f5f7" : "none",
                          background: i === 0 ? "rgba(255, 218, 121, 0.15)" : "transparent",
                          cursor: row.id ? "pointer" : "default",
                        }}>
                          <div style={{
                            width: 26, height: 26, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center",
                            fontFamily: "'Black Han Sans', sans-serif", fontWeight: 900, fontSize: "0.85rem",
                            background: i < 3 ? podium(i + 1).bg : C.raised,
                            color: i < 3 ? podium(i + 1).fg : C.ink2, ...NUM,
                          }}>{i + 1}</div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#f4f5f7", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.batter}</div>
                            <div style={{ fontSize: "0.7rem", color: "#9aa0ab", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {row.opp || '—'}{row.when ? ` · ${new Date(row.when).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}` : ''}
                            </div>
                          </div>
                          <div style={{ fontFamily: "'Black Han Sans', sans-serif", fontSize: "1.2rem", color: "#ff2e17", fontWeight: 900 }}>{row.runs}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {liveCricketLeaderboards.bestPartnerships?.length > 0 && (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                      <h3 style={{ fontSize: "1rem", fontWeight: 800, margin: 0 }}> Best Partnerships · last 60 days</h3>
                      <span style={{ fontSize: "0.72rem", color: "#9aa0ab" }}>Runs together</span>
                    </div>
                    <div style={{ background: "#101319", borderRadius: 14, border: "1px solid rgba(255,255,255,0.09)", overflow: "hidden" }}>
                      {liveCricketLeaderboards.bestPartnerships.map((row, i) => (
                        <div key={`bp-${row.b1}-${row.b2}-${row.match_id}-${i}`} style={{
                          display: "grid", gridTemplateColumns: "32px 1fr auto", alignItems: "center", gap: 10,
                          padding: "10px 14px",
                          borderBottom: i < liveCricketLeaderboards.bestPartnerships.length - 1 ? "1px solid #f5f5f7" : "none",
                          background: i === 0 ? "rgba(255, 218, 121, 0.15)" : "transparent",
                        }}>
                          <div style={{
                            width: 26, height: 26, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center",
                            fontFamily: "'Black Han Sans', sans-serif", fontWeight: 900, fontSize: "0.85rem",
                            background: i < 3 ? podium(i + 1).bg : C.raised,
                            color: i < 3 ? podium(i + 1).fg : C.ink2, ...NUM,
                          }}>{i + 1}</div>
                          <div style={{ minWidth: 0 }}>
                            {/* 2026-05-31: each name is independently clickable
                                — partnerships have two players, and either may
                                be a known user or a placeholder. */}
                            <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#f4f5f7", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              <span
                                onClick={(e) => { if (row.b1Id) { e.stopPropagation(); setSelectedPlayerId(row.b1Id); } }}
                                style={{ cursor: row.b1Id ? "pointer" : "default", textDecoration: row.b1Id ? "underline" : "none" }}
                              >{row.b1}</span>
                              {' '}&amp;{' '}
                              <span
                                onClick={(e) => { if (row.b2Id) { e.stopPropagation(); setSelectedPlayerId(row.b2Id); } }}
                                style={{ cursor: row.b2Id ? "pointer" : "default", textDecoration: row.b2Id ? "underline" : "none" }}
                              >{row.b2}</span>
                            </div>
                            <div style={{ fontSize: "0.7rem", color: "#9aa0ab", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {row.opp || '—'}{row.when ? ` · ${new Date(row.when).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}` : ''}
                            </div>
                          </div>
                          <div style={{ fontFamily: "'Black Han Sans', sans-serif", fontSize: "1.2rem", color: "#ff6a5f", fontWeight: 900 }}>{row.runs}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* 2026-05-28: Team leaderboards — 5 panels in a grid. Wickets, runs
              conceded (best + worst), runs scored, dot balls. Same season window.
              Cricket-only — hidden when a different sport filter is active. */}
          {(!homeSportFilter || homeSportFilter === 'cricket') && liveCricketLeaderboards && (() => {
            const teamPanels = [
              { key: 'teamMostWickets',       title: ' Most Wickets',        sub: 'Season total',   accent: '#e74c3c', stat: 'wickets'      },
              { key: 'teamLeastRunsConceded', title: ' Least Runs Conceded', sub: 'Best defense',    accent: '#ff2e17', stat: 'runsConceded' },
              { key: 'teamMostRunsConceded',  title: ' Most Runs Conceded',  sub: 'Leakiest attack', accent: '#f97316', stat: 'runsConceded' },
              { key: 'teamMostRunsScored',    title: ' Most Runs Scored',    sub: 'Top batting',     accent: '#3b82f6', stat: 'runsScored'   },
              { key: 'teamMostDotBalls',      title: ' Most Dot Balls',      sub: 'Tight bowling',   accent: '#475569', stat: 'dotBalls'     },
              // 2026-06-02: bowling-discipline panels — high values are bad.
              // Sit next to Most Runs Conceded / Most Dot Balls in the grid.
              { key: 'teamMostWides',         title: '↔ Most Wides',           sub: 'Loose bowling',   accent: '#a855f7', stat: 'wides'        },
              { key: 'teamMostNoBalls',       title: ' Most No Balls',       sub: 'Foot-fault kings', accent: '#dc2626', stat: 'noBalls'      },
              // 2026-06-02: 8th panel rounds the grid — power-hitting brag.
              // Indoor cricket back-net hit = 7 runs (not 6 like outdoor).
              { key: 'teamMostSevens',        title: ' Most Sevens',         sub: 'Power hitting',   accent: '#fbbf24', stat: 'sevens'       },
            ].filter(p => liveCricketLeaderboards[p.key]?.length > 0);
            if (teamPanels.length === 0) return null;
            return (
            <section style={{ maxWidth: 1200, margin: "0 auto", padding: "30px 32px 0" }}>
              <div style={{ marginBottom: 14 }}>
                <h3 style={{ fontSize: "1rem", fontWeight: 800, margin: 0 }}>Team Leaderboards · last 60 days</h3>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
                {teamPanels.map(panel => {
                  const rows = liveCricketLeaderboards[panel.key].slice(0, 5);
                  return (
                    <div key={panel.key} style={{ background: "#101319", borderRadius: 14, border: "1px solid rgba(255,255,255,0.09)", overflow: "hidden" }}>
                      <div style={{ padding: "12px 14px", borderBottom: "1px solid #f5f5f7", background: `${panel.accent}0d` }}>
                        <div style={{ fontSize: "0.82rem", fontWeight: 800, color: "#f4f5f7" }}>{panel.title}</div>
                        <div style={{ fontSize: "0.68rem", color: "#9aa0ab", marginTop: 1 }}>{panel.sub}</div>
                      </div>
                      {rows.map((row, i) => (
                        <div key={`${panel.key}-${row.team}-${i}`} style={{
                          display: "grid", gridTemplateColumns: "26px 1fr auto", alignItems: "center", gap: 10,
                          padding: "9px 14px",
                          borderBottom: i < rows.length - 1 ? "1px solid #f5f5f7" : "none",
                          background: i === 0 ? "rgba(255, 218, 121, 0.12)" : "transparent",
                        }}>
                          <div style={{
                            width: 22, height: 22, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
                            fontFamily: "'Black Han Sans', sans-serif", fontWeight: 900, fontSize: "0.78rem",
                            background: i < 3 ? podium(i + 1).bg : C.raised,
                            color: i < 3 ? podium(i + 1).fg : C.ink2, ...NUM,
                          }}>{i + 1}</div>
                          <div style={{ fontWeight: 700, fontSize: "0.82rem", color: "#f4f5f7", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.team}</div>
                          <div style={{ fontFamily: "'Black Han Sans', sans-serif", fontSize: "1.05rem", color: panel.accent, fontWeight: 900 }}>{row[panel.stat]}</div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </section>
            );
          })()}

          {/* 2026-05-28: Top Teams by Rating — cross-sport. Shows all-time
              rating with quick toggle to current season. Tap a team to open
              their profile modal. */}
          {liveTeamRatings && (() => {
            const allRows  = Object.values(liveTeamRatings.allTime || {});
            const seasonRows = Object.values(liveTeamRatings.currentSeason || {});
            if (allRows.length === 0) return null;
            const showRows = (teamRatingScope === 'season' && seasonRows.length > 0 ? seasonRows : allRows);
            const top = rankTeams([...showRows]).slice(0, 10);
            const sportEmoji = { cricket: '', netball: '', soccer: '', volleyball: '' };
            return (
              <section style={{ maxWidth: 1200, margin: "0 auto", padding: "30px 32px 0" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
                  <h3 style={{ fontSize: "1rem", fontWeight: 800, margin: 0 }}> Top Teams · by rating</h3>
                  <div style={{ display: "flex", gap: 6 }}>
                    {[
                      { key: 'allTime', label: 'All-time' },
                      { key: 'season',  label: 'This season' },
                    ].map(t => (
                      <button key={t.key} onClick={() => setTeamRatingScope(t.key)}
                        style={{
                          padding: "5px 12px", borderRadius: 16, border: `1.5px solid ${teamRatingScope === t.key ? P : 'rgba(255,255,255,0.14)'}`,
                          background: teamRatingScope === t.key ? P : C.raised, color: teamRatingScope === t.key ? '#fff' : C.ink2,
                          fontWeight: 700, fontSize: "0.72rem", cursor: "pointer", fontFamily: "inherit",
                        }}>{t.label}</button>
                    ))}
                    <button onClick={() => setSection('team-rankings')}
                      style={{ padding: "5px 12px", borderRadius: 16, border: "1.5px solid #e5e7eb", background: "#101319", color: "#f4f5f7", fontWeight: 700, fontSize: "0.72rem", cursor: "pointer", fontFamily: "inherit" }}>
                      Full rankings →
                    </button>
                  </div>
                </div>
                <div style={{ background: "#101319", borderRadius: 14, border: "1px solid rgba(255,255,255,0.09)", overflow: "hidden" }}>
                  {top.map((t, i) => (
                    <div key={t.teamId} onClick={() => setSelectedTeamId(t.teamId)} style={{
                      display: "grid", gridTemplateColumns: "44px 1fr auto auto", alignItems: "center", gap: 12,
                      padding: "12px 16px",
                      borderBottom: i < top.length - 1 ? "1px solid #f5f5f7" : "none",
                      background: i === 0 ? "rgba(255, 218, 121, 0.15)" : "transparent",
                      cursor: "pointer", transition: "background 0.15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = i === 0 ? "rgba(255, 218, 121, 0.25)" : "rgba(0,0,0,0.025)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = i === 0 ? "rgba(255, 218, 121, 0.15)" : "transparent"; }}
                    >
                      <div style={{
                        width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                        fontFamily: "'Black Han Sans', sans-serif", fontWeight: 900, fontSize: "0.95rem",
                        background: i < 3 ? podium(i + 1).bg : C.raised,
                        color: i < 3 ? podium(i + 1).fg : C.ink2, ...NUM,
                      }}>{i + 1}</div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#f4f5f7", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</div>
                        <div style={{ fontSize: "0.72rem", color: "#9aa0ab", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {sportEmoji[t.sport] || ''} {t.sportName}{t.competitionName ? ` · ${t.competitionName}` : ''} · {t.wins}W-{t.losses}L{t.draws ? `-${t.draws}D` : ''}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '0.62rem', color: '#9aa0ab', letterSpacing: 1, fontWeight: 700, textTransform: 'uppercase' }}>Games</div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>{t.played}</div>
                      </div>
                      <div style={{ fontFamily: "'Black Han Sans', sans-serif", fontSize: "1.4rem", color: t.rating > 0 ? "#ff2e17" : t.rating < 0 ? C.neg : C.ink3, fontWeight: 900, ...NUM, minWidth: 60, textAlign: 'right' }}>
                        {t.rating > 0 ? '+' : ''}{t.rating}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })()}

          {/* ON THIS DAY — memory card for the logged-in user (matches on today's date in prior years) */}
          {authUser && liveCareer?.matchTimes && (() => {
            const today = new Date();
            const matchesOnDate = liveCareer.matchTimes
              .map(m => ({ ...m, date: new Date(m.ts) }))
              .filter(m => m.date.getMonth() === today.getMonth() && m.date.getDate() === today.getDate() && m.date.getFullYear() < today.getFullYear());
            if (matchesOnDate.length === 0) return null;
            const yearsAgo = today.getFullYear() - Math.max(...matchesOnDate.map(m => m.date.getFullYear()));
            const wins = matchesOnDate.filter(m => m.outcome === 'W').length;
            return (
              <section style={{ maxWidth: 1200, margin: "0 auto", padding: "30px 32px 10px" }}>
                <div style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 16, padding: "18px 22px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                  <div style={{ fontSize: "2.4rem" }}></div>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: "0.72rem", letterSpacing: 2, color: "#fbbf24", textTransform: "uppercase", fontWeight: 800, marginBottom: 4 }}>On this day</div>
                    <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "#fbbf24" }}>
                      You played {matchesOnDate.length} game{matchesOnDate.length === 1 ? '' : 's'} on {today.toLocaleDateString('en-AU', { day: 'numeric', month: 'long' })}{yearsAgo > 0 ? ` — ${yearsAgo === 1 ? '1 year ago' : `up to ${yearsAgo} years ago`}` : ''}.
                    </div>
                    <div style={{ fontSize: "0.85rem", color: "#fbbf24", marginTop: 2 }}>{wins} win{wins === 1 ? '' : 's'} · keep the tradition alive </div>
                  </div>
                  <button onClick={() => setSection('profile')}
                    style={{ background: "#101319", border: "1px solid #f59e0b", color: "#fbbf24", padding: "8px 16px", borderRadius: 10, fontSize: "0.8rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                    View your stats
                  </button>
                </div>
              </section>
            );
          })()}

          {/* Live Activity Feed — RICH — merges match results + achievements chronologically */}
          {liveRichActivity && liveRichActivity.length > 0 && (
          <section style={{ maxWidth: 1200, margin: "0 auto", padding: "40px 32px 20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#10b981", animation: "pulse 2s infinite" }} />
              <h3 style={{ fontSize: "1rem", fontWeight: 800, margin: 0 }}>Live Feed</h3>
              <span style={{ fontSize: "0.78rem", color: "#9aa0ab" }}>— recent results, achievements & highlights at Wildcatz</span>
            </div>
            <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8 }}>
              {liveRichActivity.map((a, i) => {
                const isAch = a.type === 'achievement';
                const rarityAccent = a.rarity === 'legendary' ? '#fbbf24' : a.rarity === 'rare' ? '#a855f7' : a.rarity === 'uncommon' ? '#3b82f6' : P;
                return (
                  <div key={i} style={{
                    minWidth: 300, borderRadius: 12, padding: "14px 16px",
                    border: isAch ? `1px solid ${rarityAccent}55` : "1px solid rgba(255,255,255,0.09)",
                    background: isAch ? `linear-gradient(135deg, ${rarityAccent}20 0%, #101319 60%)` : "#101319",
                    flexShrink: 0, transition: "all 0.2s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = isAch ? rarityAccent : "#ff2e17"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = isAch ? `${rarityAccent}55` : "rgba(255,255,255,0.09)"; }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: "1.4rem" }}>{a.emoji}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {isAch && <div style={{ fontSize: "0.6rem", letterSpacing: 1.5, color: rarityAccent, fontWeight: 700, marginBottom: 2 }}>{(a.rarity || '').toUpperCase()} ACHIEVEMENT</div>}
                        <div style={{ fontSize: "0.85rem", color: "#f4f5f7", fontWeight: isAch ? 700 : 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.primary}</div>
                        <div style={{ fontSize: "0.7rem", color: "#9aa0ab", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.secondary} · {a.when}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
          )}

          {/* 2026-07-27 (Jake): this was a countdown — "156 DAYS LEFT" — to a
              prize with no pool, no categories and no sponsor named. A visible
              deadline on something that does not exist yet is a promise you
              then have to keep, and it read as the loudest thing on the page.
              Reduced to a single honest line. It becomes a banner again when
              there is an actual prize to put in it. */}
          <section style={{ background: "#0a0c11", borderTop: "1px solid rgba(255,255,255,0.08)", borderBottom: "1px solid rgba(255,255,255,0.08)", padding: "18px 32px" }}>
            <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, flexWrap: "wrap", textAlign: "center" }}>
              <Trophy size={16} color="#9aa0ab" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: "0.88rem", color: "#9aa0ab", lineHeight: 1.5 }}>
                Rankings are live now. Prizes for top-ranked players are something we want to add — nothing is running yet, and your games are already being counted for when it does.
              </span>
            </div>
          </section>

          {/* WISA vs Non-WISA comparison */}
          {/* 2026-07-27 (Jake): this was a VS table telling players their
              current venue makes their games "irrelevant" and leaves them
              "just another player — no profile". Two problems. A player cannot
              move their venue onto WISA, so the insult landed on the one
              person who could not act on it. And a network of one venue is the
              weakest possible position from which to run down everybody else.
              Rewritten as what it should always have been: a plain statement
              of what tracking gives a player, and a direct ask of the person
              who can actually say yes. */}
          <section style={{ maxWidth: 1200, margin: "0 auto", padding: "60px 32px" }}>
            <div style={{ textAlign: "center", marginBottom: 40 }}>
              <h2 style={{ fontFamily: "'Archivo',sans-serif", fontSize: "2.2rem", fontWeight: 900, letterSpacing: "-0.03em", margin: "0 0 8px", color: "#f4f5f7" }}>What gets tracked when your venue is on WISA</h2>
              <p style={{ color: "#9aa0ab", fontSize: "0.95rem", margin: 0 }}>Every game recorded, every season kept. Nothing you have to write down yourself.</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 16, maxWidth: 980, margin: "0 auto" }}>
              {[
                { t: "Your record, kept for you", d: "Goals, assists, wins and losses recorded as the game is scored — not remembered afterwards." },
                { t: "One ranking, not a club ladder", d: "Every result feeds the same ranking, so where you sit means the same thing wherever you play." },
                { t: "A career, not a season", d: "Season archives, milestones and head-to-head history stay yours once the comp ends." },
              ].map((c, i) => (
                <div key={i} style={{ background: "#101319", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 16, padding: "26px 24px", minWidth: 0 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(0,230,118,0.12)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
                    <Check size={18} color="#00e676" />
                  </div>
                  <div style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: "1.02rem", marginBottom: 7, color: "#f4f5f7" }}>{c.t}</div>
                  <div style={{ fontSize: "0.88rem", lineHeight: 1.55, color: "#9aa0ab" }}>{c.d}</div>
                </div>
              ))}
            </div>
            <div style={{ maxWidth: 980, margin: "28px auto 0", background: "linear-gradient(135deg, rgba(0,230,118,0.07), rgba(255,46,23,0.05))", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: isMobile ? "24px 22px" : "28px 32px", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 18 }}>
              <div style={{ minWidth: 0, flex: "1 1 320px" }}>
                <div style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 900, fontSize: "1.25rem", color: "#f4f5f7", marginBottom: 6 }}>Run a venue?</div>
                <div style={{ fontSize: "0.92rem", lineHeight: 1.55, color: "#9aa0ab" }}>
                  Players can't put their centre on here — you can. See what it takes to get your comps tracked.
                </div>
              </div>
              <button onClick={() => setSection("for-venues")}
                style={{ fontFamily: "'Archivo',sans-serif", background: "#ff2e17", color: "#fff", border: "none", borderRadius: 9, padding: "14px 28px", fontWeight: 800, fontSize: "0.95rem", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
                What's involved <ArrowRight size={18} />
              </button>
            </div>
          </section>

          {/* Featured venues */}
          <section style={{ background: "#0a0c11", padding: "80px 32px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ maxWidth: 1200, margin: "0 auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
                <div>
                  <h2 style={{ fontFamily: "'Archivo',sans-serif", fontSize: "2.2rem", fontWeight: 900, letterSpacing: "-0.03em", margin: "0 0 8px", color: "#f4f5f7" }}>Where they play.</h2>
                  <p style={{ color: "#9aa0ab", fontSize: "0.95rem", margin: 0 }}>{allVenuesList.length === 1 ? "The founding venue. More to come." : "Indoor sports centres on the network"}</p>
                </div>
                <button onClick={() => setSection("venues")} style={{ fontFamily: "'Archivo',sans-serif", display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.16)", borderRadius: 9, padding: "11px 20px", color: "#fff", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}>
                  View All <ChevronRight size={16} />
                </button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 24 }}>
                {/* Use allVenuesList (not raw VENUES) so live Supabase player/team counts
                    + the 1.75x marketing multiplier flow through to the featured card. */}
                {allVenuesList.filter(v => v.featured).map(v => (
                  <VenueCard key={v.id} venue={v} distance={userLocation ? getDistance(userLocation.lat, userLocation.lng, v.lat, v.lng) : null} onClick={() => setSelectedVenue(v)} />
                ))}
              </div>
            </div>
          </section>

          {/* 2026-05-31: Venue Pulse — fun aggregate stats.
              2026-06-02: now using TRUE venue totals (every player in the
              window) instead of Top-10-only sums. Labels switched from"top X" → "total X" to match. Cricket = wickets from balls_data,
              netball/soccer = goal_log array lengths, volleyball = matches
              played. */}
          {liveVenuePulse && (() => {
            const tiles = [
              [liveVenuePulse.cricketWickets,    '', 'total cricket wickets'],
              [liveVenuePulse.netballGoals,      '', 'total netball goals'],
              [liveVenuePulse.soccerGoals,       '', 'total soccer goals'],
              [liveVenuePulse.volleyballPoints,   '', 'total volleyball points'],
            ].filter(t => t[0] > 0);
            if (tiles.length === 0) return null;
            return (
              <section style={{ maxWidth: 1200, margin: "40px auto 0", padding: "0 32px" }}>
                <div style={{ background: "#101319", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 16, padding: "22px 28px" }}>
                  <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: "0.65rem", letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 700, color: "#ff6a5f", marginBottom: 14 }}>Venue Pulse · last 14 days</div>
                  <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${tiles.length > 3 ? 140 : 180}px, 1fr))`, gap: 18 }}>
                    {tiles.map((t, i) => (
                      <div key={i} style={{ textAlign: "center" }}>
                        <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: "2rem", fontWeight: 900, color: "#f4f5f7", lineHeight: 1, letterSpacing: "-0.02em" }}>{t[0].toLocaleString()}</div>
                        <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: "0.68rem", color: "#9aa0ab", marginTop: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.12em" }}>{t[2]}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            );
          })()}

          {/* CTA */}
          <section style={{ position: "relative", overflow: "hidden", background: "#07080b", padding: "clamp(84px,10vw,120px) 32px", textAlign: "center", color: "#f4f5f7", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 50% 135%, rgba(255,46,23,0.3), transparent 60%)" }} />
            <div style={{ position: "relative", zIndex: 2 }}>
              <div style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.22em", fontSize: "0.72rem", color: "#ff6a5f", marginBottom: 14 }}>Grow the network</div>
              <h2 style={{ fontFamily: "'Archivo',sans-serif", fontSize: "clamp(2rem,4.5vw,3.2rem)", fontWeight: 900, letterSpacing: "-0.035em", lineHeight: 0.98, margin: "0 0 16px" }}>Your venue not on WISA yet?</h2>
              <p style={{ color: "#c3c7ce", fontSize: "1.05rem", margin: "0 auto 30px", maxWidth: 500, lineHeight: 1.6 }}>
                Tell them you want stats and leaderboards. Request your venue and we'll let them know players are waiting.
              </p>
              <button onClick={() => setSection("venues")} style={{ fontFamily: "'Archivo',sans-serif", display: "inline-flex", alignItems: "center", gap: 8, background: "#ff2e17", color: "#fff", padding: "15px 32px", borderRadius: 9, fontSize: "1rem", fontWeight: 800, border: "none", cursor: "pointer" }}>
                Find & Request Your Venue <ArrowRight size={18} />
              </button>
            </div>
          </section>
        </>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/* VENUES                                         */}
      {/* ═══════════════════════════════════════════════ */}
      {section === "venues" && (
        <section style={{ maxWidth: 1200, margin: "0 auto", padding: "40px 32px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <h2 style={{ fontSize: "1.6rem", fontWeight: 800, margin: 0 }}>Find a Venue</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {!userLocation && (
                <button onClick={requestLocation} style={{ display: "flex", alignItems: "center", gap: 6, background: `linear-gradient(135deg, #ff2e17, #ff5a45)`, color: "#fff", border: "none", borderRadius: 10, padding: "10px 20px", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}>
                  <Navigation size={15} /> {locationLoading ? "Finding..." : "Use My Location"}
                </button>
              )}
              {userLocation && (
                <button onClick={() => setSortByDistance(!sortByDistance)} style={{ display: "flex", alignItems: "center", gap: 6, background: sortByDistance ? P : "rgba(255,255,255,0.05)", color: sortByDistance ? "#fff" : P, border: sortByDistance ? "none" : "1px solid rgba(255,255,255,0.09)", borderRadius: 10, padding: "8px 16px", fontWeight: 600, fontSize: "0.82rem", cursor: "pointer" }}>
                  <Navigation size={14} /> Sort by Distance
                </button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 240, display: "flex", alignItems: "center", gap: 8, background: "#101319", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 10, padding: "0 16px" }}>
              <Search size={16} color="#9aa0ab" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search venues, cities..." style={{ flex: 1, border: "none", outline: "none", padding: "12px 0", fontSize: "0.9rem", fontFamily: "inherit", background: "transparent" }} />
              {search && <X size={16} color="#9aa0ab" style={{ cursor: "pointer" }} onClick={() => setSearch("")} />}
            </div>
            <select value={sportFilter} onChange={e => setSportFilter(e.target.value)} style={{ background: "#101319", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 10, padding: "12px 16px", fontSize: "0.85rem", fontFamily: "inherit", cursor: "pointer" }}>
              <option value="">All Sports</option>
              {ALL_SPORTS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={countryFilter} onChange={e => setCountryFilter(e.target.value)} style={{ background: "#101319", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 10, padding: "12px 16px", fontSize: "0.85rem", fontFamily: "inherit", cursor: "pointer" }}>
              <option value="">All Countries</option>
              {ALL_COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div style={{ fontSize: "0.85rem", color: "#9aa0ab", marginBottom: 16 }}>
            {filteredVenues.length} WISA venue{filteredVenues.length !== 1 ? "s" : ""} found
            {sortByDistance && " — sorted by distance"}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 24 }}>
            {filteredVenues.map(v => (
              <VenueCard key={v.id} venue={v} distance={v.distance} onClick={() => setSelectedVenue(v)} />
            ))}
          </div>

          {/* CTA for venue owners */}
          <div style={{ textAlign: "center", marginTop: 32, padding: "28px", background: "#101319", borderRadius: 16, border: "1px solid rgba(255,255,255,0.09)" }}>
            <div style={{ fontWeight: 800, color: "#ff6a5f", fontSize: "1.05rem", marginBottom: 6 }}>Own a venue? Your players are waiting.</div>
            <div style={{ fontSize: "0.85rem", color: "#9aa0ab", marginBottom: 14 }}>Join the WISA network and give your players stats, rankings, and a reason to keep coming back.</div>
            <button onClick={() => setSection("for-venues")} style={{ background: "#ff2e17", color: "#fff", border: "none", padding: "12px 28px", borderRadius: 10, fontWeight: 800, fontSize: "0.9rem", cursor: "pointer" }}>Learn More →</button>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/* MY PROFILE                                     */}
      {/* ═══════════════════════════════════════════════ */}
      {section === "profile" && !authUser && (
        <section style={{ maxWidth: 440, margin: "0 auto", padding: "80px 32px" }}>
          <div style={{ background: "#101319", borderRadius: 20, border: "1px solid rgba(255,255,255,0.09)", padding: "40px 32px", textAlign: "center" }}>
            <div style={{ width: 72, height: 72, borderRadius: "50%", background: `linear-gradient(135deg, #ff2e17, #ff5a45)`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
              <User size={32} color="#fff" />
            </div>
            <h2 style={{ fontSize: "1.5rem", fontWeight: 900, margin: "0 0 8px", color: "#f4f5f7" }}>Sign in to All 4 Indoor</h2>
            <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: "0 0 28px" }}>Use your WISA account to access your player profile, stats, and rankings.</p>
            <form onSubmit={handleLogin} style={{ textAlign: "left" }}>
              <label style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, color: "#9aa0ab", display: "block", marginBottom: 6 }}>Email</label>
              <div style={{ position: "relative", marginBottom: 16 }}>
                <Mail size={16} style={{ position: "absolute", left: 12, top: 13, color: "#aaa" }} />
                <input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} placeholder="your@email.com" required
                  style={{ width: "100%", padding: "10px 12px 10px 36px", borderRadius: 10, border: "1px solid #e0e0e5", fontSize: "0.9rem", outline: "none", boxSizing: "border-box" }} />
              </div>
              <label style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, color: "#9aa0ab", display: "block", marginBottom: 6 }}>Password</label>
              <div style={{ position: "relative", marginBottom: 8 }}>
                <Lock size={16} style={{ position: "absolute", left: 12, top: 13, color: "#aaa" }} />
                <input type={showPassword ? "text" : "password"} value={loginPassword} onChange={e => setLoginPassword(e.target.value)} placeholder="Your password" required
                  style={{ width: "100%", padding: "10px 40px 10px 36px", borderRadius: 10, border: "1px solid #e0e0e5", fontSize: "0.9rem", outline: "none", boxSizing: "border-box" }} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: 10, top: 10, background: "none", border: "none", cursor: "pointer", color: "#aaa" }}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {loginError && <div style={{ color: "#ef4444", fontSize: "0.82rem", marginBottom: 12 }}>{loginError}</div>}
              <button type="submit" disabled={loginLoading}
                style={{ width: "100%", padding: "12px", borderRadius: 10, border: "none", background: `linear-gradient(135deg, #ff2e17, #ff5a45)`, color: "#fff", fontSize: "0.95rem", fontWeight: 700, cursor: loginLoading ? "not-allowed" : "pointer", marginTop: 8 }}>
                {loginLoading ? "Signing in..." : "Sign In"}
              </button>
            </form>
            <div style={{ marginTop: 20, fontSize: "0.82rem", color: "#9aa0ab" }}>
              Don't have an account? <a href="https://www.wisasports.com" target="_blank" rel="noopener noreferrer" style={{ color: "#ff6a5f", fontWeight: 600, textDecoration: "none" }}>Sign up at wisasports.com</a>
            </div>
          </div>
        </section>
      )}

      {section === "profile" && authUser && (() => {
        // REAL DATA — populated from player_stats + match_attendance in the live hooks above.
        // liveCareer + liveBadges are loaded async; show skeleton (0s) while they arrive.
        const p = authProfile ? {
          name: (authProfile.first_name || "") + " " + (authProfile.last_name?.[0] || "") + ".",
          fullName: (authProfile.first_name || "") + " " + (authProfile.last_name || ""),
          avatar: (authProfile.first_name?.[0] || "") + (authProfile.last_name?.[0] || ""),
          homeVenue: "Wildcatz Indoor Sports",
          homeCity: "Townsville, QLD",
          memberSince: authProfile.created_at ? new Date(authProfile.created_at).toLocaleDateString('en-AU', { month:'long', year:'numeric' }) : '—',
          bio: authProfile.bio || "Indoor sports enthusiast",
          shareUrl: "all4indoor.com/player/" + (authProfile.first_name || "").toLowerCase(),
          sports: liveCareer?.sportsPlayed || [],
          career: {
            totalGames: liveCareer?.totalGames || 0,
            totalWins: liveCareer?.totalWins || 0,
            totalGoals: liveCareer?.totalGoals || 0,
            totalRuns: liveCareer?.totalRuns || 0,
            totalWickets: liveCareer?.totalWickets || 0,
            totalSixes: liveCareer?.totalSixes || 0,
            totalFours: liveCareer?.totalFours || 0,
            totalAssists: 0, // not tracked yet
            winRate: liveCareer?.winRate || 0,
            venuesPlayed: 1, // Wildcatz only for now
            countriesPlayed: 1,
            seasonsPlayed: liveCareer?.seasonsPlayed || 0,
            hatTricks: liveCareer?.hatTricks || 0,
            fiveWktHauls: liveCareer?.fiveWktHauls || 0,
            highestScore: liveCareer?.highestScore || 0,
            currentStreak: liveCareer?.currentStreak || 0,
            longestStreak: liveCareer?.longestStreak || 0,
            recentForm: liveCareer?.recentForm || [],
            biggestWin: liveCareer?.biggestWin || null,
            matchTimes: liveCareer?.matchTimes || [],
          },
          seasons: [],
          venuesVisited: [{ name: "Wildcatz Indoor Sports", city: "Townsville", games: liveCareer?.totalGames || 0, firstVisit: authProfile.created_at ? new Date(authProfile.created_at).toLocaleDateString('en-AU', { month:'short', year:'numeric' }) : '—' }],
          prizeEligibility: [],
          _isReal: true,
        } : null;
        if (!p) return null;
        // Real badges from player_achievements table — using actual schema columns
        // (achievement_name + emoji, not the older mock shape b.name/b.icon)
        const earned = (liveBadges || []).map(b => ({
          id: b.id,
          name: b.achievement_name || b.achievement_key || 'Badge',
          desc: b.description || '',
          icon: b.emoji || '',
          rarity: b.rarity || 'common',
          earned: true,
          earnedAt: b.earned_at,
        }));
        // Next badges to chase — computed from liveCareer totals vs known thresholds
        const careerForBadges = {
          games: liveCareer?.totalGames || 0,
          wins: liveCareer?.totalWins || 0,
          goals: liveCareer?.totalGoals || 0,
          runs: liveCareer?.totalRuns || 0,
          wickets: liveCareer?.totalWickets || 0,
          sixes: liveCareer?.totalSixes || 0,
          hatTricks: liveCareer?.hatTricks || 0,
          longestStreak: liveCareer?.longestStreak || 0,
        };
        const lockedDefs = [
          { key:'g25',  name:'Quarter Century',   desc:'Play 25 games',           icon:'', target: 25,  current: careerForBadges.games },
          { key:'g100', name:'Century Club',      desc:'Play 100 games',          icon:'', target: 100, current: careerForBadges.games },
          { key:'w10',  name:'10 Wins',           desc:'Win 10 matches',          icon:'', target: 10,  current: careerForBadges.wins },
          { key:'w50',  name:'50-Win Veteran',    desc:'Win 50 matches',          icon:'', target: 50,  current: careerForBadges.wins },
          { key:'r100', name:'Century',           desc:'100 career runs',         icon:'', target: 100, current: careerForBadges.runs },
          { key:'wk25', name:'Wicket Wizard',     desc:'25 career wickets',       icon:'', target: 25,  current: careerForBadges.wickets },
          { key:'gl50', name:'Sharp Shooter',     desc:'50 career goals',         icon:'', target: 50,  current: careerForBadges.goals },
          { key:'7s10', name:'Seven-Up',          desc:'10 sevens',               icon:'', target: 10,  current: careerForBadges.sixes },
          { key:'ht3',  name:'Hat-Trick Hero',    desc:'3 career hat-tricks',     icon:'', target: 3,   current: careerForBadges.hatTricks },
          { key:'st5',  name:'On Fire',           desc:'5-game win streak',       icon:'', target: 5,   current: careerForBadges.longestStreak },
        ];
        const locked = lockedDefs
          .filter(b => b.current < b.target)
          .map(b => ({ ...b, pct: Math.min(100, Math.round((b.current / b.target) * 100)), remaining: b.target - b.current }))
          .sort((a, b) => b.pct - a.pct)
          .slice(0, 6);
        return (
          <section style={{ maxWidth: 1000, margin: "0 auto", padding: "40px 32px" }}>
            {/* Profile header */}
            <div style={{ background: "linear-gradient(135deg, #1a1a2e, #0d4a42, #ff2e17)", borderRadius: 20, padding: "36px", color: "#fff", marginBottom: 24, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: -40, right: -40, width: 180, height: 180, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
              <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 24 }}>
                {(() => {
                  // 2026-05-31: sport-themed avatar gradient for authed
                  // user too. Picks primary sport from career stats.
                  const sp = primarySportSlug(p.stats, p.sports);
                  const grad = (sp && SPORT_GRADIENTS[sp]) || `linear-gradient(135deg, #ff2e17, #ff5a45)`;
                  return <div style={{ width: 80, height: 80, borderRadius: "50%", background: grad, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.6rem", fontWeight: 800, border: "3px solid rgba(255,255,255,0.3)" }}>{p.avatar}</div>;
                })()}
                <div style={{ flex: 1 }}>
                  <h2 style={{ fontSize: "1.6rem", fontWeight: 900, margin: "0 0 4px" }}>{p.fullName}</h2>
                  <div style={{ fontSize: "0.9rem", opacity: 0.7, marginBottom: 8 }}>{p.homeVenue} — {p.homeCity} — Member since {p.memberSince}</div>
                  <div style={{ fontSize: "0.85rem", opacity: 0.8 }}>{p.bio}</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <button onClick={async () => {
                    const url = `${window.location.origin}/?player=${authUser?.id || ''}`;
                    const text = `Check out ${p.fullName} on All4Indoor — ${p.career.totalGames} games, ${p.career.winRate}% win rate, ${p.career.totalWins} wins.`;
                    if (navigator.share) {
                      try { await navigator.share({ title: `${p.fullName} on All4Indoor`, text, url }); return; } catch {}
                    }
                    try {
                      await navigator.clipboard.writeText(url);
                      alert('Profile link copied to clipboard!');
                    } catch {
                      window.prompt('Copy this link:', url);
                    }
                  }}
                    style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)", color: "#fff", padding: "8px 16px", borderRadius: 10, fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.25)"}
                    onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}>
                    <Share2 size={14} /> Share Profile
                  </button>
                  {p.career.currentStreak > 0 && (() => {
                    // 2026-05-31: scale the flames to the streak length.
                    // 1-2 wins = , 3-4 = , 5-9 = , 10+ = .
                    // Hot streaks (3+) get a soft pulse animation via the
                    // existing pulse keyframe.
                    const s = p.career.currentStreak;
                    const flames = s >= 10 ? '' : s >= 5 ? '' : s >= 3 ? '' : '';
                    const hot = s >= 3;
                    return (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 10, background: hot ? "rgba(220,38,38,0.22)" : "rgba(245,158,11,0.2)", border: `1px solid ${hot ? 'rgba(220,38,38,0.45)' : 'rgba(245,158,11,0.4)'}`, fontSize: "0.72rem", fontWeight: 800, color: hot ? '#fecaca' : '#fbbf24', justifyContent: "center", animation: hot ? "pulse 2s infinite" : undefined }}>
                        {flames} {s}-win streak
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* PERCENTILE BADGES — social proof against the rest of the venue */}
            {liveRankedPlayers && liveRankedPlayers.length > 0 && (() => {
              const myRank = liveRankedPlayers.findIndex(p => p.id === authUser?.id);
              if (myRank < 0) return null;
              const total = liveRankedPlayers.length;
              const pctBetter = Math.round(((total - myRank - 1) / total) * 100);
              const eloRow = (liveElo || []).find(e => e.id === authUser?.id);
              const eloRank = eloRow ? (liveElo || []).findIndex(e => e.id === authUser?.id) + 1 : null;
              const eloPct = eloRank ? Math.round(((liveElo.length - eloRank) / liveElo.length) * 100) : null;
              return (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 24 }}>
                  <div style={{ background: "linear-gradient(135deg, #ff2e17, #ff5a45)", color: "#fff", borderRadius: 14, padding: "18px 20px" }}>
                    <div style={{ fontSize: "0.65rem", letterSpacing: 2, opacity: 0.85, fontWeight: 800, textTransform: "uppercase" }}>Better Than</div>
                    <div style={{ fontFamily: "system-ui", fontSize: "2.4rem", fontWeight: 900, lineHeight: 1, marginTop: 4 }}>{pctBetter}%</div>
                    <div style={{ fontSize: "0.78rem", opacity: 0.9, marginTop: 4 }}>of qualified players (points)</div>
                  </div>
                  {eloRank && (
                    <div style={{ background: "linear-gradient(135deg, #fbbf24, #f59e0b)", color: "#fff", borderRadius: 14, padding: "18px 20px" }}>
                      <div style={{ fontSize: "0.65rem", letterSpacing: 2, opacity: 0.9, fontWeight: 800, textTransform: "uppercase" }}>ELO Rank</div>
                      <div style={{ fontFamily: "system-ui", fontSize: "2.4rem", fontWeight: 900, lineHeight: 1, marginTop: 4 }}>#{eloRank}</div>
                      <div style={{ fontSize: "0.78rem", opacity: 0.95, marginTop: 4 }}>top {100 - eloPct}% · {eloRow.elo} rating</div>
                    </div>
                  )}
                  {p.career.totalGames > 0 && (
                    <div style={{ background: "linear-gradient(135deg, #3b82f6, #1e40af)", color: "#fff", borderRadius: 14, padding: "18px 20px" }}>
                      <div style={{ fontSize: "0.65rem", letterSpacing: 2, opacity: 0.85, fontWeight: 800, textTransform: "uppercase" }}>Win Rate Rank</div>
                      <div style={{ fontFamily: "system-ui", fontSize: "2.4rem", fontWeight: 900, lineHeight: 1, marginTop: 4 }}>{p.career.winRate}%</div>
                      <div style={{ fontSize: "0.78rem", opacity: 0.9, marginTop: 4 }}>{liveRankedPlayers.length > 1 ? `${liveRankedPlayers.filter(r => (r.games > 0 ? (r.wins / r.games) * 100 : 0) > p.career.winRate).length === 0 ? 'No one' : 'Few players'} above you` : 'Keep going'}</div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Career stats — sport-adaptive: swaps Goals → Runs/Wickets for cricketers */}
            {(() => {
              const sports = liveCareer?.sportsPlayed || [];
              const isCricket = sports.some(s => /cricket/i.test(s));
              const isBall = sports.some(s => /(netball|soccer|futsal)/i.test(s));
              const tiles = [
                { label: "Games", value: p.career.totalGames, icon: "" },
                { label: "Wins", value: p.career.totalWins, icon: "" },
                { label: "Win Rate", value: p.career.winRate + "%", icon: "" },
              ];
              if (isCricket && p.career.totalRuns > 0) tiles.push({ label: "Runs", value: p.career.totalRuns, icon: "" });
              if (isCricket && p.career.totalWickets > 0) tiles.push({ label: "Wickets", value: p.career.totalWickets, icon: "" });
              if (isBall && p.career.totalGoals > 0) tiles.push({ label: "Goals", value: p.career.totalGoals, icon: "" });
              if (p.career.longestStreak > 1) tiles.push({ label: "Longest Streak", value: p.career.longestStreak, icon: "" });
              if (tiles.length < 5) tiles.push({ label: "Venues", value: p.career.venuesPlayed, icon: "" });
              // Cap at 6, keep grid responsive
              const visible = tiles.slice(0, 6);
              return (
                <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(visible.length, 6)}, 1fr)`, gap: 12, marginBottom: 24 }}>
                  {visible.map((s, i) => (
                    <div key={i} style={{ background: "#101319", borderRadius: 14, padding: "20px 16px", textAlign: "center", border: "1px solid rgba(255,255,255,0.09)" }}>
                      <div style={{ fontSize: "1.4rem", marginBottom: 4 }}>{s.icon}</div>
                      <div style={{ fontSize: "1.5rem", fontWeight: 900, color: "#ff6a5f" }}>{s.value}</div>
                      <div style={{ fontSize: "0.75rem", color: "#9aa0ab", fontWeight: 600 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Personal Records — peak performance moments */}
            {(p.career.biggestWin || p.career.longestStreak > 1 || p.career.recentForm?.length > 0) && (
              <div style={{ background: "#101319", borderRadius: 16, padding: "20px 24px", border: "1px solid rgba(255,255,255,0.09)", marginBottom: 24 }}>
                <h3 style={{ fontSize: "1rem", fontWeight: 800, margin: "0 0 14px" }}> Personal Records & Form</h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
                  {p.career.recentForm?.length > 0 && (
                    <div>
                      <div style={{ fontSize: "0.72rem", color: "#9aa0ab", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 6 }}>Last {p.career.recentForm.length}</div>
                      <div style={{ display: "flex", gap: 4 }}>
                        {p.career.recentForm.map((r, i) => (
                          <span key={i} title={r === 'W' ? 'Win' : r === 'L' ? 'Loss' : 'Draw'}
                            style={{ width: 22, height: 22, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", fontWeight: 800, color: "#fff", background: r === 'W' ? "#10b981" : r === 'L' ? "#e74c3c" : "#f59e0b" }}>
                            {r}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {p.career.longestStreak > 1 && (
                    <div>
                      <div style={{ fontSize: "0.72rem", color: "#9aa0ab", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 6 }}>Longest Streak</div>
                      <div style={{ fontSize: "1.4rem", fontWeight: 900, color: "#f4f5f7" }}> {p.career.longestStreak} wins</div>
                    </div>
                  )}
                  {p.career.biggestWin && (
                    <div>
                      <div style={{ fontSize: "0.72rem", color: "#9aa0ab", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 6 }}>Biggest Win</div>
                      <div style={{ fontSize: "1.2rem", fontWeight: 900, color: "#10b981" }}>{p.career.biggestWin.myScore}–{p.career.biggestWin.oppScore}</div>
                      <div style={{ fontSize: "0.72rem", color: "#9aa0ab" }}>by {p.career.biggestWin.margin} pts</div>
                    </div>
                  )}
                  {p.career.highestScore > 0 && (
                    <div>
                      <div style={{ fontSize: "0.72rem", color: "#9aa0ab", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 6 }}>Highest Score</div>
                      <div style={{ fontSize: "1.4rem", fontWeight: 900, color: "#f4f5f7" }}>{p.career.highestScore}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* TEAM CHEMISTRY — best partner + biggest rival, from liveWrapped */}
            {(liveWrapped?.bestPartner || liveWrapped?.topRival) && (
              <div style={{ background: "#101319", borderRadius: 16, padding: "20px 24px", border: "1px solid rgba(255,255,255,0.09)", marginBottom: 24 }}>
                <h3 style={{ fontSize: "1rem", fontWeight: 800, margin: "0 0 4px" }}> Chemistry</h3>
                <p style={{ fontSize: "0.78rem", color: "#9aa0ab", margin: "0 0 16px" }}>Who you win with — and who beats you most</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
                  {liveWrapped.bestPartner && (
                    <div onClick={() => setSelectedPlayerId(liveWrapped.bestPartner.id)}
                      style={{ background: "linear-gradient(135deg, #ecfdf5, #d1fae5)", border: "1px solid #6ee7b7", borderRadius: 14, padding: "14px 16px", cursor: "pointer", transition: "transform 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                      onMouseLeave={e => e.currentTarget.style.transform = ""}>
                      <div style={{ fontSize: "0.7rem", color: "#065f46", letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 800, marginBottom: 6 }}>Best Partner</div>
                      <div style={{ fontWeight: 800, fontSize: "1.1rem", color: "#065f46" }}>{liveWrapped.bestPartner.name}</div>
                      <div style={{ fontSize: "0.82rem", color: "#047857", marginTop: 4 }}>
                        <strong>{liveWrapped.bestPartner.wins}/{liveWrapped.bestPartner.with}</strong> wins together · {liveWrapped.bestPartner.winRate}% win rate
                      </div>
                    </div>
                  )}
                  {liveWrapped.topRival && (
                    <div onClick={() => setSelectedPlayerId(liveWrapped.topRival.id)}
                      style={{ background: "linear-gradient(135deg, #fee2e2, #fecaca)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 14, padding: "14px 16px", cursor: "pointer", transition: "transform 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                      onMouseLeave={e => e.currentTarget.style.transform = ""}>
                      <div style={{ fontSize: "0.7rem", color: "#991b1b", letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 800, marginBottom: 6 }}>Biggest Rival</div>
                      <div style={{ fontWeight: 800, fontSize: "1.1rem", color: "#991b1b" }}>{liveWrapped.topRival.name}</div>
                      <div style={{ fontSize: "0.82rem", color: "#b91c1c", marginTop: 4 }}>
                        {liveWrapped.topRival.against} meeting{liveWrapped.topRival.against === 1 ? '' : 's'} ·
                        <strong> {liveWrapped.topRival.beat}W</strong>–<strong>{liveWrapped.topRival.lostTo}L</strong>
                        {liveWrapped.topRival.draws > 0 ? `–${liveWrapped.topRival.draws}D` : ''}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* WHEN YOU PLAY — day/time heatmap, Strava-style */}
            {p.career.matchTimes?.length > 4 && (() => {
              // Build a 7-day × 4-slot grid: Morning <12, Afternoon 12–17, Evening 17–21, Late ≥21
              const dayLabels = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
              const slotLabels = ['Morning','Afternoon','Evening','Late'];
              const slotIdx = (h) => h < 12 ? 0 : h < 17 ? 1 : h < 21 ? 2 : 3;
              const grid = {}; // 'day-slot' -> { games, wins }
              p.career.matchTimes.forEach(({ ts, outcome }) => {
                const d = new Date(ts);
                const k = `${d.getDay()}-${slotIdx(d.getHours())}`;
                if (!grid[k]) grid[k] = { games: 0, wins: 0 };
                grid[k].games++;
                if (outcome === 'W') grid[k].wins++;
              });
              const maxGames = Math.max(1, ...Object.values(grid).map(c => c.games));
              // Find peak slot
              let peak = null;
              Object.entries(grid).forEach(([k, v]) => {
                if (!peak || v.games > peak.games) peak = { ...v, key: k };
              });
              const peakLabel = peak ? (() => {
                const [d, s] = peak.key.split('-').map(Number);
                return `${dayLabels[d]} ${slotLabels[s].toLowerCase()}`;
              })() : '—';
              return (
                <div style={{ background: "#101319", borderRadius: 16, padding: "20px 24px", border: "1px solid rgba(255,255,255,0.09)", marginBottom: 24 }}>
                  <h3 style={{ fontSize: "1rem", fontWeight: 800, margin: "0 0 4px" }}> When You Play</h3>
                  <p style={{ fontSize: "0.78rem", color: "#9aa0ab", margin: "0 0 16px" }}>Peak slot: <strong style={{ color: "#f4f5f7" }}>{peakLabel}</strong> · darker = more games</p>
                  <div style={{ display: "grid", gridTemplateColumns: "70px repeat(7, 1fr)", gap: 4 }}>
                    {/* Top row: blank + day labels */}
                    <div />
                    {dayLabels.map(d => <div key={d} style={{ fontSize: "0.7rem", textAlign: "center", color: "#9aa0ab", fontWeight: 700, paddingBottom: 4 }}>{d}</div>)}
                    {/* Body — row per slot */}
                    {slotLabels.map((sl, si) => (
                      <React.Fragment key={sl}>
                        <div style={{ fontSize: "0.7rem", color: "#9aa0ab", fontWeight: 700, display: "flex", alignItems: "center" }}>{sl}</div>
                        {dayLabels.map((_, di) => {
                          const cell = grid[`${di}-${si}`];
                          const intensity = cell ? cell.games / maxGames : 0;
                          const winRate = cell && cell.games > 0 ? Math.round((cell.wins / cell.games) * 100) : 0;
                          const bg = intensity === 0 ? "#f4f4f7" : `rgba(26,138,125,${Math.max(0.15, intensity)})`;
                          return (
                            <div key={di} title={cell ? `${cell.games} game${cell.games === 1 ? '' : 's'} · ${winRate}% win` : 'No games'}
                              style={{ aspectRatio: "1 / 1", borderRadius: 5, background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", fontWeight: 800, color: intensity > 0.5 ? "#fff" : "#9aa0ab" }}>
                              {cell?.games || ''}
                            </div>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Prize eligibility banner */}
            <div style={{ background: "#101319", borderRadius: 16, border: "2px solid #fbbf24", padding: "20px 24px", marginBottom: 24 }}>
              <div style={{ fontWeight: 800, fontSize: "1rem", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}> Prizes <span style={{ fontSize: "0.72rem", color: "#9aa0ab", fontWeight: 500 }}>— Coming Soon</span></div>
              <div style={{ fontSize: "0.85rem", color: "#9aa0ab" }}>Prize categories and sponsors will be announced soon. Your stats are already being tracked!</div>
            </div>

            {/* Milestones — REAL — tiered by lifetime games played from liveMilestones */}
            {liveMilestones && liveMilestones.length > 0 && (
            <div style={{ background: "#101319", borderRadius: 16, padding: "24px", border: "1px solid rgba(255,255,255,0.09)", marginBottom: 24 }}>
              <h3 style={{ fontSize: "1rem", fontWeight: 800, margin: "0 0 4px" }}> Your Next Milestones</h3>
              <p style={{ fontSize: "0.78rem", color: "#9aa0ab", margin: "0 0 16px" }}>Lifetime games played — keep showing up</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {liveMilestones.map(m => {
                  const pct = Math.min(100, Math.round((m.progress / m.target) * 100));
                  const isNext = !m.complete && (liveMilestones.find(x => !x.complete) === m); // first incomplete
                  return (
                    <div key={m.name} style={{
                      display: "flex", alignItems: "center", gap: 14, padding: "12px 16px",
                      background: m.complete ? "rgba(16,185,129,0.06)" : (isNext ? "#fef3c7" : "#fafafe"),
                      borderRadius: 12,
                      border: m.complete ? "1px solid rgba(16,185,129,0.3)" : (isNext ? "1px solid #f59e0b" : "1px solid rgba(255,255,255,0.09)"),
                    }}>
                      <span style={{ fontSize: "1.4rem", opacity: m.complete ? 1 : 0.7 }}>{m.emoji}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "0.88rem", fontWeight: 700, color: m.complete ? C.pos : (isNext ? "#92400e" : "#1a1a2e") }}>{m.name}</div>
                        <div style={{ fontSize: "0.72rem", color: "#9aa0ab" }}>{m.desc}</div>
                      </div>
                      <div style={{ minWidth: 100 }}>
                        <div style={{ height: 6, borderRadius: 3, background: "#e0e0ea", overflow: "hidden" }}>
                          <div style={{ height: 6, background: m.complete ? "#10b981" : (isNext ? "#f59e0b" : P), width: `${pct}%`, transition: "width 0.5s" }} />
                        </div>
                        <div style={{ fontSize: "0.7rem", color: "#9aa0ab", textAlign: "right", marginTop: 3 }}>
                          {m.progress.toLocaleString()} / {m.target.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 }}>
              {/* Badges earned + next-to-chase */}
              <div style={{ background: "#101319", borderRadius: 16, padding: "24px", border: "1px solid rgba(255,255,255,0.09)" }}>
                <h3 style={{ fontSize: "1rem", fontWeight: 800, margin: "0 0 16px" }}> Badges Earned <span style={{ color: "#9aa0ab", fontWeight: 600, fontSize: "0.85rem" }}>({earned.length})</span></h3>
                {earned.length === 0 ? (
                  <div style={{ fontSize: "0.82rem", color: "#9aa0ab", padding: "10px 0 16px" }}>No badges yet — keep playing!</div>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                    {earned.slice(0, 12).map(b => (
                      <div key={b.id} title={`${b.name}${b.desc ? ' — ' + b.desc : ''}`}
                        style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 10, background: b.rarity === 'legendary' ? "#fff7ed" : b.rarity === 'rare' ? "#eff6ff" : "#f0fdf4", border: `1px solid ${b.rarity === 'legendary' ? "#fed7aa" : b.rarity === 'rare' ? "#bfdbfe" : "#bbf7d0"}`, fontSize: "0.78rem", fontWeight: 700, color: b.rarity === 'legendary' ? "#c2410c" : b.rarity === 'rare' ? "#1e40af" : "#166534" }}>
                        <span>{b.icon}</span>{b.name}
                      </div>
                    ))}
                  </div>
                )}
                {locked.length > 0 && (
                  <div style={{ borderTop: "1px solid rgba(255,255,255,0.09)", paddingTop: 14 }}>
                    <div style={{ fontSize: "0.78rem", color: "#9aa0ab", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 10 }}>Next to Chase</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {locked.map(b => (
                        <div key={b.key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: "1rem", opacity: 0.7 }}>{b.icon}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                              <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#f4f5f7" }}>{b.name}</div>
                              <div style={{ fontSize: "0.72rem", color: "#9aa0ab", fontWeight: 600 }}>{b.current} / {b.target}</div>
                            </div>
                            <div style={{ height: 5, borderRadius: 3, background: "rgba(255,255,255,0.14)", overflow: "hidden" }}>
                              <div style={{ width: `${b.pct}%`, height: 5, background: P, transition: "width 0.5s" }} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Venues visited */}
              <div style={{ background: "#101319", borderRadius: 16, padding: "24px", border: "1px solid rgba(255,255,255,0.09)" }}>
                <h3 style={{ fontSize: "1rem", fontWeight: 800, margin: "0 0 16px" }}> Venues Played ({p.venuesVisited.length})</h3>
                {p.venuesVisited.map((v, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < p.venuesVisited.length - 1 ? "1px solid rgba(255,255,255,0.09)" : "none" }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, #ff2e17${i === 0 ? '' : '88'}, #34d399${i === 0 ? '' : '88'})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <MapPin size={16} color="#fff" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: "0.88rem" }}>{v.name}</div>
                      <div style={{ fontSize: "0.75rem", color: "#9aa0ab" }}>{v.city} — since {v.firstVisit}</div>
                    </div>
                    <div style={{ fontWeight: 800, color: "#ff6a5f", fontSize: "0.95rem" }}>{v.games}</div>
                    <div style={{ fontSize: "0.72rem", color: "#9aa0ab" }}>games</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Season history — hidden until we wire per-season aggregation. */}
            {p.seasons.length > 0 && (
            <div style={{ background: "#101319", borderRadius: 16, padding: "24px", border: "1px solid rgba(255,255,255,0.09)", marginBottom: 24 }}>
              <h3 style={{ fontSize: "1rem", fontWeight: 800, margin: "0 0 16px" }}> Season History</h3>
              <div style={{ display: "grid", gridTemplateColumns: "80px 80px 80px 80px 80px 1fr 120px", padding: "10px 16px", background: "#0b0e13", borderRadius: 10, fontSize: "0.72rem", fontWeight: 700, color: "#9aa0ab", textTransform: "uppercase", marginBottom: 8 }}>
                <div>Year</div><div>Season</div><div style={{ textAlign: "center" }}>Games</div><div style={{ textAlign: "center" }}>Wins</div><div style={{ textAlign: "center" }}>Goals</div><div>Venue</div><div>Badge</div>
              </div>
              {p.seasons.map((s, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "80px 80px 80px 80px 80px 1fr 120px", padding: "10px 16px", borderBottom: "1px solid #f5f5f7", alignItems: "center", background: i === 0 ? "rgba(26,138,125,0.03)" : "transparent" }}>
                  <div style={{ fontWeight: 700 }}>{s.year}</div>
                  <div style={{ color: "#9aa0ab" }}>{s.season}</div>
                  <div style={{ textAlign: "center", fontWeight: 700 }}>{s.games}</div>
                  <div style={{ textAlign: "center", fontWeight: 700 }}>{s.wins}</div>
                  <div style={{ textAlign: "center", fontWeight: 700, color: "#ff6a5f" }}>{s.goals}</div>
                  <div style={{ fontSize: "0.82rem", color: "#9aa0ab" }}>{s.venue}</div>
                  <div>{s.badge ? <span style={{ background: "rgba(255,46,23,0.12)", color: "#ff6a5f", fontSize: "0.65rem", fontWeight: 700, padding: "3px 8px", borderRadius: 8 }}>{s.badge}</span> : "—"}</div>
                </div>
              ))}
            </div>
            )}

            {/* Non-WISA warning */}
            <div style={{ background: "rgba(245,158,11,0.1)", borderRadius: 16, padding: "24px 28px", border: "1px solid #f59e0b", display: "flex", alignItems: "center", gap: 16 }}>
              <AlertCircle size={28} color="#92400e" />
              <div>
                <div style={{ fontWeight: 800, color: "#fbbf24", marginBottom: 2 }}>Playing at a non-WISA venue?</div>
                <div style={{ fontSize: "0.85rem", color: "#fbbf24" }}>Those games won't appear on your profile and won't earn you badges. Ask your venue to join WISA so every game counts.</div>
              </div>
              <button onClick={() => setSection("venues")} style={{ background: "#92400e", color: "#fff", border: "none", borderRadius: 10, padding: "10px 20px", fontWeight: 700, fontSize: "0.82rem", cursor: "pointer", whiteSpace: "nowrap" }}>
                Request a Venue
              </button>
            </div>
          </section>
        );
      })()}

      {/* ═══════════════════════════════════════════════ */}
      {/* TEAMS                                          */}
      {/* ═══════════════════════════════════════════════ */}
      {section === "teams" && (
        <section style={{ maxWidth: 1000, margin: "0 auto", padding: "40px 32px" }}>
          <div style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: "1.6rem", fontWeight: 800, margin: "0 0 6px" }}>Teams</h2>
            <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: 0 }}>Team profiles, rosters, and season history across WISA venues</p>
          </div>

          {!selectedTeam ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 20 }}>
              {(liveTeams || []).map(team => {
                const totalGames = team.record.wins + team.record.losses + team.record.draws;
                const winPct = totalGames > 0 ? ((team.record.wins / totalGames) * 100).toFixed(0) : '—';
                return (
                  <div key={team.id} onClick={() => setSelectedTeam(team)}
                    style={{ background: "#101319", borderRadius: 16, padding: "24px", border: "1px solid rgba(255,255,255,0.09)", cursor: "pointer", transition: "all 0.2s", position: "relative" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = P; e.currentTarget.style.transform = "translateY(-3px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#f0f0f5"; e.currentTarget.style.transform = "none"; }}
                  >
                    {team.looking_for_players && (
                      <div style={{ position: "absolute", top: 12, right: 12, background: "rgba(245,158,11,0.1)", color: "#fbbf24", fontSize: "0.65rem", fontWeight: 700, padding: "3px 8px", borderRadius: 10 }}>RECRUITING</div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                      <div style={{ width: 48, height: 48, borderRadius: 12, background: `linear-gradient(135deg, #ff2e17, #ff5a45)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.3rem" }}>{sportEmoji[team.sport] || ""}</div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 800, fontSize: "1.1rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{team.name}</div>
                        <div style={{ fontSize: "0.78rem", color: "#9aa0ab", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{team.comp || team.sport}</div>
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, background: "#0b0e13", borderRadius: 12, padding: 16, marginBottom: 12 }}>
                      <div style={{ textAlign: "center" }}><div style={{ fontSize: "1.2rem", fontWeight: 800, color: "#10b981" }}>{team.record.wins}</div><div style={{ fontSize: "0.68rem", color: "#9aa0ab" }}>Wins</div></div>
                      <div style={{ textAlign: "center" }}><div style={{ fontSize: "1.2rem", fontWeight: 800, color: "#e74c3c" }}>{team.record.losses}</div><div style={{ fontSize: "0.68rem", color: "#9aa0ab" }}>Losses</div></div>
                      <div style={{ textAlign: "center" }}><div style={{ fontSize: "1.2rem", fontWeight: 800, color: "#9aa0ab" }}>{team.record.draws}</div><div style={{ fontSize: "0.68rem", color: "#9aa0ab" }}>Draws</div></div>
                      <div style={{ textAlign: "center" }}><div style={{ fontSize: "1.2rem", fontWeight: 800, color: "#ff6a5f" }}>{winPct === '—' ? '—' : winPct + '%'}</div><div style={{ fontSize: "0.68rem", color: "#9aa0ab" }}>Win Rate</div></div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ fontSize: "0.82rem", color: "#9aa0ab" }}>{team.roster.length} player{team.roster.length !== 1 ? 's' : ''}</div>
                      <ChevronRight size={16} color={P} />
                    </div>
                  </div>
                );
              })}
              {liveTeams && liveTeams.length === 0 && (
                <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "60px 20px", color: "#9aa0ab" }}>No teams found.</div>
              )}
              {!liveTeams && (
                <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "60px 20px", color: "#9aa0ab" }}>Loading teams…</div>
              )}
            </div>
          ) : (
            <div>
              <button onClick={() => setSelectedTeam(null)} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: "#ff6a5f", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer", padding: 0, marginBottom: 20 }}>
                ← Back to all teams
              </button>

              {/* Team header — defensive: real team rows may not have .record/.season/.country.
                  2026-05-31: sport-themed avatar gradient + auto title chip
                  matching the player profile treatment. */}
              <div style={{ background: `linear-gradient(135deg, #1a1a2e, #0d4a42)`, borderRadius: 20, padding: "32px", color: "#fff", marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                  {(() => {
                    const sp = (selectedTeam.sport || '').toLowerCase().replace(/^indoor\s+/, '');
                    const grad = SPORT_GRADIENTS[sp] || `linear-gradient(135deg, #ff2e17, #ff5a45)`;
                    return <div style={{ width: 64, height: 64, borderRadius: 16, background: grad, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2rem" }}>{sportEmoji[selectedTeam.sport] || ''}</div>;
                  })()}
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <h2 style={{ fontSize: "1.6rem", fontWeight: 900, margin: "0 0 4px" }}>{selectedTeam.name}</h2>
                    {(() => {
                      const t = teamTitle(selectedTeam.record);
                      if (!t) return null;
                      return (
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", background: "rgba(251,191,36,0.18)", border: "1px solid rgba(251,191,36,0.4)", borderRadius: 999, fontSize: "0.74rem", fontWeight: 700, color: "#fde68a", marginBottom: 6 }}>
                          <span>{t.icon}</span><span style={{ letterSpacing: 0.5 }}>{t.label}</span>
                        </div>
                      );
                    })()}
                    <div style={{ opacity: 0.7 }}>
                      {selectedTeam.venue || 'Wildcatz Indoor Sports'}
                      {selectedTeam.sport ? ` — ${selectedTeam.sport}` : ''}
                      {selectedTeam.comp ? ` — ${selectedTeam.comp}` : ''}
                    </div>
                  </div>
                  {selectedTeam.record && (
                    <div style={{ marginLeft: "auto", textAlign: "center" }}>
                      <div style={{ fontSize: "2rem", fontWeight: 900 }}>{selectedTeam.record.wins ?? 0}W-{selectedTeam.record.losses ?? 0}L</div>
                      {selectedTeam.season && <div style={{ opacity: 0.6, fontSize: "0.82rem" }}>{selectedTeam.season}</div>}
                    </div>
                  )}
                </div>
              </div>

              {/* Roster — defensive map for missing role/games */}
              <div style={{ background: "#101319", borderRadius: 16, padding: "24px", border: "1px solid rgba(255,255,255,0.09)", marginBottom: 24 }}>
                <h3 style={{ fontSize: "1rem", fontWeight: 800, margin: "0 0 16px" }}> Roster {Array.isArray(selectedTeam.roster) && <span style={{ color: "#9aa0ab", fontWeight: 600, fontSize: "0.85rem" }}>({selectedTeam.roster.length})</span>}</h3>
                {Array.isArray(selectedTeam.roster) && selectedTeam.roster.length > 0 ? (
                  selectedTeam.roster.map((player, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: i < selectedTeam.roster.length - 1 ? "1px solid rgba(255,255,255,0.09)" : "none" }}>
                      <div style={{ width: 40, height: 40, borderRadius: "50%", background: `linear-gradient(135deg, #ff2e17, #ff5a45)`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "0.72rem", fontWeight: 700 }}>{player.avatar || (player.name || '').split(' ').map(s => s[0]).join('').slice(0,2).toUpperCase()}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700 }}>{player.name}</div>
                        {player.role && <div style={{ fontSize: "0.78rem", color: "#9aa0ab" }}>{player.role}</div>}
                      </div>
                      {player.games != null && (
                        <div style={{ textAlign: "center" }}><div style={{ fontWeight: 700, color: "#ff6a5f" }}>{player.games}</div><div style={{ fontSize: "0.68rem", color: "#9aa0ab" }}>Games</div></div>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ color: "#9aa0ab", fontSize: "0.85rem", padding: "8px 0" }}>No roster on file yet — once they play their next game it'll appear here.</div>
                )}
              </div>

              {/* Season history — only render when present */}
              {Array.isArray(selectedTeam.seasonHistory) && selectedTeam.seasonHistory.length > 0 && (
                <div style={{ background: "#101319", borderRadius: 16, padding: "24px", border: "1px solid rgba(255,255,255,0.09)" }}>
                  <h3 style={{ fontSize: "1rem", fontWeight: 800, margin: "0 0 16px" }}> Season History</h3>
                  {selectedTeam.seasonHistory.map((s, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 16px", background: i === 0 ? `rgba(26,138,125,0.04)` : "transparent", borderRadius: 10, marginBottom: 4 }}>
                      <div style={{ fontWeight: 700, minWidth: 80 }}>{s.season}</div>
                      <div style={{ fontWeight: 800, color: s.result?.includes("1st") ? P : C.ink2 }}>{s.result}</div>
                      <div style={{ marginLeft: "auto", fontSize: "0.85rem", color: "#9aa0ab" }}>{s.record}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ textAlign: "center", marginTop: 32, padding: "28px", background: "rgba(245,158,11,0.1)", borderRadius: 16, border: "1px solid #f59e0b" }}>
            <div style={{ fontWeight: 800, color: "#fbbf24", marginBottom: 4 }}>Teams at non-WISA venues have no history</div>
            <div style={{ fontSize: "0.85rem", color: "#fbbf24" }}>Without WISA, your team's wins, records, and seasons just disappear. Tell your venue to get on WISA.</div>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/* MATCHES                                        */}
      {/* ═══════════════════════════════════════════════ */}
      {section === "matches" && (() => {
        // Use live data when loaded, otherwise fall back to the hardcoded demo set
        const reports = liveMatchReports || [];
        const filtered = reports.filter(m => !matchSportFilter || m.sport === matchSportFilter);
        const matchSports = [...new Set(reports.map(m => m.sport))];
        return (
          <section style={{ maxWidth: 1000, margin: "0 auto", padding: "40px 32px" }}>
            <div style={{ marginBottom: 32 }}>
              <h2 style={{ fontSize: "1.6rem", fontWeight: 800, margin: "0 0 6px" }}>Match Reports</h2>
              <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: "0 0 20px" }}>Latest results from across the WISA network</p>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setMatchSportFilter("")} style={{ background: !matchSportFilter ? P : "rgba(255,255,255,0.05)", color: !matchSportFilter ? "#fff" : "#555", border: !matchSportFilter ? "none" : "1px solid rgba(255,255,255,0.09)", borderRadius: 10, padding: "8px 16px", fontWeight: 600, fontSize: "0.82rem", cursor: "pointer" }}>All</button>
                {matchSports.map(s => (
                  <button key={s} onClick={() => setMatchSportFilter(s)} style={{ background: matchSportFilter === s ? P : "rgba(255,255,255,0.05)", color: matchSportFilter === s ? "#fff" : "#555", border: matchSportFilter === s ? "none" : "1px solid rgba(255,255,255,0.09)", borderRadius: 10, padding: "8px 16px", fontWeight: 600, fontSize: "0.82rem", cursor: "pointer" }}>
                    {sportEmoji[s]} {s}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {filtered.map(match => {
                // Match reports live on the WISA frontend (wisasports.com/wildcatz/match/{id}).
                // Only real DB-sourced matches have a UUID id; the hardcoded demo entries don't.
                const isLive = typeof match.id === 'string' && match.id.length === 36;
                const reportUrl = isLive ? `https://www.wisasports.com/wildcatz/match/${match.id}` : null;
                const cardInner = (
                  <>
                    <div style={{ background: "linear-gradient(135deg, #1a1a2e, #0d4a42)", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", color: "#fff" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: "1.2rem" }}>{sportEmoji[match.sport]}</span>
                        <span style={{ fontWeight: 700, fontSize: "0.85rem" }}>{match.sport}</span>
                        <span style={{ opacity: 0.5 }}>•</span>
                        <span style={{ fontSize: "0.82rem", opacity: 0.7 }}>{match.venue}</span>
                        <WisaBadge />
                      </div>
                      <span style={{ fontSize: "0.78rem", opacity: 0.7 }}>{match.date}</span>
                    </div>
                    <div style={{ padding: "24px", display: "flex", alignItems: "center", justifyContent: "center", gap: 32 }}>
                      <div style={{ textAlign: "center", flex: 1 }}>
                        <div style={{ fontWeight: 800, fontSize: "1.1rem", color: match.score1 >= match.score2 ? P : C.ink2 }}>{match.team1}</div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ fontSize: "2.2rem", fontWeight: 900, color: match.score1 > match.score2 ? P : match.score1 === match.score2 ? "#fbbf24" : "#ccc" }}>{match.score1}</div>
                        <div style={{ fontSize: "1.2rem", color: "#ccc" }}>–</div>
                        <div style={{ fontSize: "2.2rem", fontWeight: 900, color: match.score2 > match.score1 ? P : match.score1 === match.score2 ? "#fbbf24" : "#ccc" }}>{match.score2}</div>
                      </div>
                      <div style={{ textAlign: "center", flex: 1 }}>
                        <div style={{ fontWeight: 800, fontSize: "1.1rem", color: match.score2 > match.score1 ? P : C.ink2 }}>{match.team2}</div>
                      </div>
                    </div>
                    <div style={{ padding: "0 24px 20px", borderTop: "1px solid rgba(255,255,255,0.09)", paddingTop: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                      <div style={{ display: "flex", alignItems: "start", gap: 12, flex: 1 }}>
                        {match.mvp ? (
                          <span style={{ background: "rgba(245,158,11,0.1)", padding: "4px 12px", borderRadius: 10, fontSize: "0.78rem", fontWeight: 700, color: "#fbbf24", flexShrink: 0 }}> MVP: {match.mvp}</span>
                        ) : null}
                        <span style={{ fontSize: "0.85rem", color: "#9aa0ab" }}>{match.highlight}</span>
                      </div>
                      {reportUrl ? (
                        <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#ff6a5f", whiteSpace: "nowrap" }}>View report →</span>
                      ) : null}
                    </div>
                  </>
                );
                return reportUrl ? (
                  <a key={match.id} href={reportUrl} target="_blank" rel="noreferrer" style={{ background: "#101319", borderRadius: 16, overflow: "hidden", border: "1px solid rgba(255,255,255,0.09)", textDecoration: "none", color: "inherit", cursor: "pointer", display: "block" }}>
                    {cardInner}
                  </a>
                ) : (
                  <div key={match.id} style={{ background: "#101319", borderRadius: 16, overflow: "hidden", border: "1px solid rgba(255,255,255,0.09)" }}>
                    {cardInner}
                  </div>
                );
              })}
            </div>
            <div style={{ textAlign: "center", marginTop: 32, padding: "28px", background: "rgba(245,158,11,0.1)", borderRadius: 16, border: "1px solid #f59e0b" }}>
              <div style={{ fontWeight: 800, color: "#fbbf24", marginBottom: 4 }}>Non-WISA venues have no match reports</div>
              <div style={{ fontSize: "0.85rem", color: "#fbbf24" }}>That incredible comeback? That last-second goal? If it didn't happen at a WISA venue, there's no record of it.</div>
            </div>
          </section>
        );
      })()}

      {/* ═══════════════════════════════════════════════ */}
      {/* HEAD-TO-HEAD COMPARISON — live, real players    */}
      {/* ═══════════════════════════════════════════════ */}
      {/* ═══════════════════════════════════════════════ */}
      {/* WRAPPED — Spotify-Wrapped style season recap   */}
      {/* ═══════════════════════════════════════════════ */}
      {section === "wrapped" && (() => {
        if (!authUser) {
          return (
            <section style={{ maxWidth: 720, margin: "0 auto", padding: "60px 32px", textAlign: "center" }}>
              <div style={{ fontSize: "3rem", marginBottom: 16 }}></div>
              <h2 style={{ fontSize: "1.6rem", fontWeight: 900, margin: "0 0 8px" }}>Your WISA Wrapped</h2>
              <p style={{ color: "#9aa0ab", marginBottom: 24 }}>Sign in to see your season recap — every game, every stat, every milestone.</p>
              <button onClick={() => setSection('profile')} style={{ background: `linear-gradient(135deg, #ff2e17, #ff5a45)`, color: "#fff", border: "none", borderRadius: 12, padding: "12px 28px", fontSize: "0.95rem", fontWeight: 700, cursor: "pointer" }}>Sign in</button>
            </section>
          );
        }
        if (!liveWrapped) {
          return (
            <section style={{ maxWidth: 720, margin: "0 auto", padding: "100px 32px", textAlign: "center", color: "#9aa0ab" }}>
              <div style={{ fontSize: "2rem", marginBottom: 12 }}></div>
              Building your wrapped…
            </section>
          );
        }
        if (liveWrapped.empty) {
          return (
            <section style={{ maxWidth: 720, margin: "0 auto", padding: "80px 32px", textAlign: "center" }}>
              <div style={{ fontSize: "3rem", marginBottom: 16 }}></div>
              <h2 style={{ fontSize: "1.6rem", fontWeight: 900, margin: "0 0 8px" }}>No matches yet</h2>
              <p style={{ color: "#9aa0ab", marginBottom: 24 }}>Play some games and we'll wrap up your season here.</p>
            </section>
          );
        }
        const w = liveWrapped;
        const firstName = authProfile?.first_name || 'You';
        const sportEmojiOf = (sport) => sportEmoji[(sport || '').replace(/^Indoor\s+/i, '')] || '';
        // Slide content — keep simple plain objects so the renderer can paginate
        const slides = [
          {
            bg: "linear-gradient(135deg, #1a1a2e, #0d4a42, #ff2e17)",
            content: (
              <>
                <div style={{ fontSize: "0.85rem", letterSpacing: 4, opacity: 0.7, marginBottom: 14 }}>YOUR WISA WRAPPED</div>
                <div style={{ fontSize: "clamp(2.5rem, 8vw, 4.5rem)", fontWeight: 900, lineHeight: 1, margin: "0 0 14px" }}>{firstName}.</div>
                <div style={{ fontSize: "1.05rem", opacity: 0.85, maxWidth: 420, margin: "0 auto" }}>Let's look back at every match you played. Ready?</div>
              </>
            ),
          },
          {
            bg: "linear-gradient(135deg, #ff2e17, #ff5a45)",
            content: (
              <>
                <div style={{ fontSize: "0.85rem", letterSpacing: 3, opacity: 0.8, marginBottom: 18 }}>YOU PLAYED</div>
                <div style={{ fontSize: "clamp(5rem, 16vw, 9rem)", fontWeight: 900, lineHeight: 0.95, margin: "0 0 12px" }}>{w.totalGames}</div>
                <div style={{ fontSize: "1.4rem", fontWeight: 700, marginBottom: 14 }}>games of indoor sport.</div>
                <div style={{ fontSize: "1rem", opacity: 0.85 }}>That's <strong>{w.wins} wins</strong> · <strong>{w.winRate}% win rate</strong>.</div>
              </>
            ),
          },
          w.topSport ? {
            bg: "linear-gradient(135deg, #f59e0b, #dc2626)",
            content: (
              <>
                <div style={{ fontSize: "0.85rem", letterSpacing: 3, opacity: 0.85, marginBottom: 14 }}>YOUR TOP SPORT</div>
                <div style={{ fontSize: "5rem", marginBottom: 8 }}>{sportEmojiOf(w.topSport.name)}</div>
                <div style={{ fontSize: "clamp(2.4rem, 7vw, 3.6rem)", fontWeight: 900, lineHeight: 1, margin: "0 0 12px" }}>{w.topSport.name.replace(/^Indoor\s+/i, '')}</div>
                <div style={{ fontSize: "1.05rem", opacity: 0.9 }}>You showed up for <strong>{w.topSport.games}</strong> {w.topSport.games === 1 ? 'game' : 'games'}.</div>
                {Object.keys(w.sportCounts).length > 1 && (
                  <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 22, flexWrap: "wrap" }}>
                    {Object.entries(w.sportCounts).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([sp, n]) => (
                      <div key={sp} style={{ background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 14px", fontSize: "0.85rem", fontWeight: 700 }}>
                        {sportEmojiOf(sp)} {sp.replace(/^Indoor\s+/i, '')} <span style={{ opacity: 0.8 }}>· {n}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ),
          } : null,
          w.biggestWin ? {
            bg: "linear-gradient(135deg, #10b981, #065f46)",
            content: (
              <>
                <div style={{ fontSize: "0.85rem", letterSpacing: 3, opacity: 0.85, marginBottom: 14 }}>YOUR BIGGEST WIN</div>
                <div style={{ fontSize: "clamp(4rem, 12vw, 6.5rem)", fontWeight: 900, lineHeight: 1, marginBottom: 8 }}>{w.biggestWin.myScore}–{w.biggestWin.oppScore}</div>
                <div style={{ fontSize: "1.6rem", fontWeight: 700, marginBottom: 12 }}>+{w.biggestWin.margin} margin</div>
                <div style={{ fontSize: "0.95rem", opacity: 0.85 }}>{new Date(w.biggestWin.ts).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</div>
              </>
            ),
          } : null,
          (liveCareer?.longestStreak || 0) >= 2 ? {
            bg: "linear-gradient(135deg, #f59e0b, #92400e)",
            content: (
              <>
                <div style={{ fontSize: "0.85rem", letterSpacing: 3, opacity: 0.85, marginBottom: 14 }}>YOUR LONGEST STREAK</div>
                <div style={{ fontSize: "5rem", marginBottom: 4 }}></div>
                <div style={{ fontSize: "clamp(4rem, 11vw, 6rem)", fontWeight: 900, lineHeight: 1, margin: "0 0 8px" }}>{liveCareer.longestStreak}</div>
                <div style={{ fontSize: "1.5rem", fontWeight: 700, marginBottom: 6 }}>wins in a row.</div>
                {liveCareer.currentStreak > 0 && (
                  <div style={{ fontSize: "1rem", opacity: 0.9 }}>You're on <strong>{liveCareer.currentStreak}</strong> right now.</div>
                )}
              </>
            ),
          } : null,
          w.bestPartner ? {
            bg: "linear-gradient(135deg, #3b82f6, #1e40af)",
            content: (
              <>
                <div style={{ fontSize: "0.85rem", letterSpacing: 3, opacity: 0.85, marginBottom: 14 }}>YOUR BEST PARTNER</div>
                <div style={{ width: 92, height: 92, borderRadius: "50%", background: "rgba(255,255,255,0.2)", border: "3px solid rgba(255,255,255,0.4)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: "2rem", fontWeight: 900 }}>
                  {w.bestPartner.name.split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div style={{ fontSize: "clamp(2rem, 6vw, 3rem)", fontWeight: 900, lineHeight: 1, marginBottom: 10 }}>{w.bestPartner.name}</div>
                <div style={{ fontSize: "1.1rem", opacity: 0.9 }}>You played <strong>{w.bestPartner.with}</strong> together — won <strong>{w.bestPartner.wins}</strong> ({w.bestPartner.winRate}%).</div>
              </>
            ),
          } : null,
          w.topRival ? {
            bg: "linear-gradient(135deg, #e74c3c, #7f1d1d)",
            content: (
              <>
                <div style={{ fontSize: "0.85rem", letterSpacing: 3, opacity: 0.85, marginBottom: 14 }}>YOUR BIGGEST RIVAL</div>
                <div style={{ width: 92, height: 92, borderRadius: "50%", background: "rgba(255,255,255,0.2)", border: "3px solid rgba(255,255,255,0.4)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: "2rem", fontWeight: 900 }}>
                  {w.topRival.name.split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div style={{ fontSize: "clamp(2rem, 6vw, 3rem)", fontWeight: 900, lineHeight: 1, marginBottom: 10 }}>{w.topRival.name}</div>
                <div style={{ fontSize: "1.05rem", opacity: 0.95 }}>{w.topRival.against} meeting{w.topRival.against === 1 ? '' : 's'} on court.</div>
                <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 18 }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "2rem", fontWeight: 900, color: "#10b981" }}>{w.topRival.beat}</div>
                    <div style={{ fontSize: "0.7rem", opacity: 0.85, letterSpacing: 1 }}>BEAT THEM</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "2rem", fontWeight: 900, color: "#fff" }}>{w.topRival.lostTo}</div>
                    <div style={{ fontSize: "0.7rem", opacity: 0.85, letterSpacing: 1 }}>LOST TO</div>
                  </div>
                  {w.topRival.draws > 0 && (
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "2rem", fontWeight: 900, color: "#fbbf24" }}>{w.topRival.draws}</div>
                      <div style={{ fontSize: "0.7rem", opacity: 0.85, letterSpacing: 1 }}>DRAWS</div>
                    </div>
                  )}
                </div>
              </>
            ),
          } : null,
          (liveBadges?.length || 0) > 0 ? (() => {
            const rarest = (liveBadges || []).slice().sort((a, b) => {
              const order = { legendary: 4, rare: 3, uncommon: 2, common: 1 };
              return (order[b.rarity] || 0) - (order[a.rarity] || 0);
            })[0];
            return {
              bg: "linear-gradient(135deg, #a855f7, #6b21a8)",
              content: (
                <>
                  <div style={{ fontSize: "0.85rem", letterSpacing: 3, opacity: 0.85, marginBottom: 14 }}>BADGES EARNED</div>
                  <div style={{ fontSize: "clamp(5rem, 14vw, 8rem)", fontWeight: 900, lineHeight: 1, marginBottom: 8 }}>{liveBadges.length}</div>
                  {rarest && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: "0.78rem", opacity: 0.85, marginBottom: 6 }}>Rarest unlock:</div>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "10px 18px", borderRadius: 14, background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.35)" }}>
                        <span style={{ fontSize: "1.4rem" }}>{rarest.emoji || ''}</span>
                        <span style={{ fontWeight: 800 }}>{rarest.achievement_name}</span>
                        {rarest.rarity && rarest.rarity !== 'common' && (
                          <span style={{ fontSize: "0.65rem", letterSpacing: 1, padding: "2px 7px", borderRadius: 6, background: "rgba(255,255,255,0.25)", textTransform: "uppercase", fontWeight: 800 }}>{rarest.rarity}</span>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ),
            };
          })() : null,
          {
            bg: "linear-gradient(135deg, #1a1a2e, #ff2e17, #fbbf24)",
            content: (
              <>
                <div style={{ fontSize: "0.85rem", letterSpacing: 4, opacity: 0.85, marginBottom: 18 }}>THAT'S A WRAP</div>
                <div style={{ fontSize: "clamp(2rem, 5.5vw, 2.6rem)", fontWeight: 900, lineHeight: 1.15, marginBottom: 16 }}>
                  {w.totalGames} games. {w.wins} wins. {liveBadges?.length || 0} badges.
                </div>
                <div style={{ fontSize: "1.05rem", opacity: 0.9, marginBottom: 26 }}>Now go beat your record.</div>
                <button onClick={async () => {
                  const url = `${window.location.origin}/?player=${authUser.id}`;
                  const text = `My WISA Wrapped: ${w.totalGames} games · ${w.wins} wins · ${w.winRate}% win rate${liveCareer?.longestStreak >= 2 ? ` · ${liveCareer.longestStreak}-win streak` : ''}. Beat me at all4indoor.com`;
                  if (navigator.share) { try { await navigator.share({ title: 'My WISA Wrapped', text, url }); return; } catch {} }
                  try { await navigator.clipboard.writeText(`${text}\n${url}`); alert('Copied to clipboard!'); }
                  catch { window.prompt('Copy:', `${text}\n${url}`); }
                }}
                  style={{ background: "#101319", color: "#f4f5f7", border: "none", borderRadius: 14, padding: "14px 32px", fontSize: "1rem", fontWeight: 800, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Share2 size={16} /> Share my wrapped
                </button>
              </>
            ),
          },
        ].filter(Boolean);

        const safeIdx = Math.min(wrappedSlide, slides.length - 1);
        const slide = slides[safeIdx];
        return (
          <section style={{ minHeight: "calc(100vh - 64px)", display: "flex", alignItems: "center", justifyContent: "center", background: slide.bg, color: "#fff", padding: "60px 24px", position: "relative", overflow: "hidden", transition: "background 0.4s" }}>
            {/* Background decoration */}
            <div style={{ position: "absolute", top: -80, right: -80, width: 240, height: 240, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
            <div style={{ position: "absolute", bottom: -100, left: -60, width: 280, height: 280, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
            {/* Progress dots */}
            <div style={{ position: "absolute", top: 18, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 6, padding: "0 20px", zIndex: 2 }}>
              {slides.map((_, i) => (
                <button key={i} onClick={() => setWrappedSlide(i)}
                  style={{ flex: 1, maxWidth: 50, height: 3, borderRadius: 2, background: i <= safeIdx ? "#fff" : "rgba(255,255,255,0.3)", border: "none", cursor: "pointer", padding: 0 }} />
              ))}
            </div>
            {/* Content */}
            <div style={{ textAlign: "center", maxWidth: 640, position: "relative", zIndex: 1 }}>
              {slide.content}
            </div>
            {/* Nav controls */}
            <div style={{ position: "absolute", bottom: 32, left: 0, right: 0, display: "flex", justifyContent: "space-between", padding: "0 32px", zIndex: 2 }}>
              <button onClick={() => setWrappedSlide(Math.max(0, safeIdx - 1))} disabled={safeIdx === 0}
                style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", padding: "10px 18px", borderRadius: 12, fontSize: "0.85rem", fontWeight: 700, cursor: safeIdx === 0 ? "not-allowed" : "pointer", opacity: safeIdx === 0 ? 0.4 : 1, fontFamily: "inherit" }}>
                ← Previous
              </button>
              <div style={{ fontSize: "0.72rem", opacity: 0.6, alignSelf: "center" }}>{safeIdx + 1} / {slides.length}</div>
              <button onClick={() => setWrappedSlide(Math.min(slides.length - 1, safeIdx + 1))} disabled={safeIdx === slides.length - 1}
                style={{ background: "rgba(255,255,255,0.95)", border: "none", color: "#f4f5f7", padding: "10px 22px", borderRadius: 12, fontSize: "0.85rem", fontWeight: 800, cursor: safeIdx === slides.length - 1 ? "default" : "pointer", opacity: safeIdx === slides.length - 1 ? 0.3 : 1, fontFamily: "inherit" }}>
                {safeIdx === slides.length - 1 ? ' Done' : 'Next →'}
              </button>
            </div>
          </section>
        );
      })()}

      {/* ═══════════════════════════════════════════════ */}
      {/* ALL-TIME LEADERS — the ranked lists behind the Hall of Fame */}
      {section === "alltime" && (() => {
        if (!allTimeLeaders) {
          return <section style={{ maxWidth: 720, margin: "0 auto", padding: "100px 32px", textAlign: "center", color: "#9aa0ab" }}>Loading the all-time leaders…</section>;
        }
        if (!allTimeLeaders.length) {
          return <section style={{ maxWidth: 720, margin: "0 auto", padding: "100px 32px", textAlign: "center", color: "#9aa0ab" }}>No all-time records yet.</section>;
        }

        // Group into cards. Categories that carry a sport get one card each.
        const groups = [];
        const seen = {};
        for (const row of allTimeLeaders) {
          const key = row.category + "|" + (row.sport || "");
          if (!seen[key]) { seen[key] = { key, category: row.category, label: row.label, sport: row.sport, rows: [] }; groups.push(seen[key]); }
          seen[key].rows.push(row);
        }
        const ORDER = ["most_runs", "most_wickets", "highest_score", "most_sixes", "five_wicket_hauls", "most_goals", "most_wins_by_sport", "most_games", "most_wins", "best_win_rate"];
        groups.sort((a, b) => {
          const d = ORDER.indexOf(a.category) - ORDER.indexOf(b.category);
          return d !== 0 ? d : (a.sport || "").localeCompare(b.sport || "");
        });
        const SUFFIX = { most_runs: " runs", most_wickets: " wkts", most_sixes: "", five_wicket_hauls: "", most_goals: "", most_games: "", most_wins: "", most_wins_by_sport: "", best_win_rate: "%", highest_score: "" };

        return (
          <section style={{ maxWidth: 1180, margin: "0 auto", padding: "72px 24px 96px" }}>
            <div style={{ textAlign: "center", marginBottom: 44 }}>
              <div style={{ fontSize: "0.78rem", letterSpacing: 4, color: "#dc2626", fontWeight: 800, textTransform: "uppercase", marginBottom: 8 }}>ALL-TIME LEADERS</div>
              <h2 style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: "clamp(2rem, 5vw, 3rem)", letterSpacing: "-0.03em", color: "#f4f5f7", margin: 0 }}>Every record, every player</h2>
              <p style={{ color: "#9aa0ab", maxWidth: 560, margin: "14px auto 0", fontSize: "0.95rem" }}>
                Career totals across every season and every venue on the network. Junior players appear by first name and initial.
              </p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20 }}>
              {groups.map(g => (
                <div key={g.key} style={{ background: "#101319", borderRadius: 18, padding: "22px 24px", border: "1px solid rgba(255,255,255,0.09)" }}>
                  <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.18em", fontSize: "0.7rem", color: "#ff6a5f", marginBottom: 16 }}>
                    {g.label}{g.sport ? ` — ${g.sport}` : ""}
                  </div>
                  {g.rows.map((r, i) => (
                    <div key={r.user_id + "|" + i}
                         onClick={() => setSelectedPlayerId(r.user_id)}
                         style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", cursor: "pointer", borderTop: i === 0 ? "none" : "1px solid rgba(255,255,255,0.05)" }}>
                      <div style={{ width: 24, textAlign: "right", fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: "0.85rem", color: r.rank === 1 ? "#ffd166" : "#646a75" }}>{r.rank}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: "#f4f5f7", fontSize: "0.92rem", fontWeight: r.rank === 1 ? 700 : 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.player}</div>
                        {(r.venue || r.detail) && (
                          <div style={{ color: "#646a75", fontSize: "0.72rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {[r.venue, g.category === "most_goals" ? null : r.detail].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </div>
                      <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: "1.05rem", color: "#f4f5f7" }}>
                        {Number(r.value).toLocaleString("en-AU")}{SUFFIX[g.category] || ""}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </section>
        );
      })()}

      {/* HALL OF FAME — all-time records                */}
      {/* ═══════════════════════════════════════════════ */}
      {section === "hall" && (() => {
        const r = liveHallOfFame;
        if (!r) {
          return <section style={{ maxWidth: 720, margin: "0 auto", padding: "100px 32px", textAlign: "center", color: "#9aa0ab" }}>Loading the Hall of Fame…</section>;
        }
        const cardBase = { background: "#101319", borderRadius: 18, padding: "26px 28px", border: "1px solid rgba(255,255,255,0.09)", boxShadow: "0 2px 14px rgba(0,0,0,0.04)" };
        const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
        const Record = ({ icon, label, value, who, sub, color = "#ff2e17" }) => (
          <div style={cardBase}>
            <div style={{ fontSize: "0.7rem", letterSpacing: 2, fontWeight: 800, color: "#9aa0ab", textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: "2rem" }}>{icon}</span>
              <span style={{ fontFamily: "system-ui", fontSize: "clamp(2rem, 5vw, 2.8rem)", fontWeight: 900, color, lineHeight: 1 }}>{value}</span>
            </div>
            {who && <div style={{ fontSize: "1rem", fontWeight: 700, color: "#f4f5f7" }}>{who}</div>}
            {sub && <div style={{ fontSize: "0.78rem", color: "#9aa0ab", marginTop: 3 }}>{sub}</div>}
          </div>
        );
        return (
          <section style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 32px" }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <div style={{ fontSize: "0.78rem", letterSpacing: 4, color: "#dc2626", fontWeight: 800, textTransform: "uppercase", marginBottom: 8 }}> HALL OF FAME</div>
              <h2 style={{ fontSize: "2rem", fontWeight: 900, margin: "0 0 8px" }}>All-time records</h2>
              <p style={{ color: "#9aa0ab", fontSize: "0.95rem", margin: 0 }}>Beat one and your name lives here forever.</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 18 }}>
              {r.topMatchScore && <Record icon="" label="Highest Match Score" value={r.topMatchScore.score} who={r.topMatchScore.winningTeam} sub={`${r.topMatchScore.winningTeam} ${r.topMatchScore.score} – ${r.topMatchScore.otherScore} · ${fmtDate(r.topMatchScore.date)}`} color="#10b981" />}
              {r.biggestBlowout && <Record icon="" label="Biggest Blowout" value={`+${r.biggestBlowout.margin}`} who={`${r.biggestBlowout.winningTeam} beat ${r.biggestBlowout.losingTeam}`} sub={`${r.biggestBlowout.winScore}–${r.biggestBlowout.loseScore} · ${fmtDate(r.biggestBlowout.date)}`} color="#dc2626" />}
              {r.longestStreak && <Record icon="" label="Longest Win Streak" value={`${r.longestStreak.length}`} who={r.longestStreak.name} sub="Consecutive wins ever" color="#f59e0b" />}
              {r.topRuns && <Record icon="" label="Most Career Runs" value={r.topRuns.runs.toLocaleString()} who={r.topRuns.name} color="#ff2e17" />}
              {r.topWickets && <Record icon="" label="Most Career Wickets" value={r.topWickets.wickets.toLocaleString()} who={r.topWickets.name} color="#e74c3c" />}
              {r.topGoalsNetball && <Record icon="" label="Most Netball Goals" value={r.topGoalsNetball.goals.toLocaleString()} who={r.topGoalsNetball.name} color="#ec4899" />}
              {r.topGoalsSoccer && <Record icon="" label="Most Soccer Goals" value={r.topGoalsSoccer.goals.toLocaleString()} who={r.topGoalsSoccer.name} color="#3b82f6" />}
              {r.topGoalsFutsal && <Record icon="" label="Most Futsal Goals" value={r.topGoalsFutsal.goals.toLocaleString()} who={r.topGoalsFutsal.name} color="#0ea5e9" />}
              {r.topAces && <Record icon="" label="Most Volleyball Aces" value={r.topAces.aces.toLocaleString()} who={r.topAces.name} color="#f97316" />}
              {r.topSevens && <Record icon="" label="Most 7s" value={r.topSevens.sixes.toLocaleString()} who={r.topSevens.name} color="#fbbf24" />}
              {/* 2026-05-31: split into soccer + netball records. Fall back
                  to the lumped record only if neither sport-split entry
                  exists (e.g. all hat-tricks came from a sport we don't
                  bucket). */}
              {r.topHatTricksSoccer  && <Record icon="" label="Most Soccer Hat-tricks" value={r.topHatTricksSoccer.hatTricks.toLocaleString()}  who={r.topHatTricksSoccer.name}  color="#3b82f6" />}
              {r.topHatTricksNetball && <Record icon="" label="Most Netball Hat-tricks" value={r.topHatTricksNetball.hatTricks.toLocaleString()} who={r.topHatTricksNetball.name} color="#ec4899" />}
              {!r.topHatTricksSoccer && !r.topHatTricksNetball && r.topHatTricks && <Record icon="" label="Most Hat-tricks" value={r.topHatTricks.hatTricks.toLocaleString()} who={r.topHatTricks.name} color="#a855f7" />}
              {r.highestSingleScore?.highestScore > 0 && <Record icon="" label="Highest Single-Game Score" value={r.highestSingleScore.highestScore} who={r.highestSingleScore.name} color="#10b981" />}
            </div>
            {r.legendaryBadges?.length > 0 && (
              <div style={{ marginTop: 36 }}>
                <h3 style={{ fontSize: "1.2rem", fontWeight: 800, margin: "0 0 14px", textAlign: "center" }}> Legendary Badges Unlocked</h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                  {r.legendaryBadges.map((b, i) => (
                    <div key={i} style={{ background: "rgba(245,158,11,0.1)", border: "1px solid #fdba74", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: "1.6rem" }}>{b.emoji}</span>
                      <div>
                        <div style={{ fontWeight: 800, color: "#c2410c" }}>{b.name}</div>
                        <div style={{ fontSize: "0.78rem", color: "#9a3412" }}>{b.who} · {fmtDate(b.earned)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ textAlign: "center", marginTop: 36, padding: "22px", background: "rgba(26,138,125,0.04)", borderRadius: 14, border: `1px solid rgba(26,138,125,0.15)`, color: "#ff2e17", fontSize: "0.9rem", fontWeight: 700 }}>
              Beat a record. Get your name engraved here.
            </div>
          </section>
        );
      })()}

      {/* ═══════════════════════════════════════════════ */}
      {/* BADGE CATALOG — every achievement with rarity   */}
      {/* ═══════════════════════════════════════════════ */}
      {section === "badges" && (() => {
        const cat = liveBadgeCatalog;
        if (!cat) {
          return <section style={{ maxWidth: 720, margin: "0 auto", padding: "100px 32px", textAlign: "center", color: "#9aa0ab" }}>Loading badges…</section>;
        }
        const rarityColor = (r) => r === 'legendary' ? '#fbbf24' : r === 'rare' ? '#3b82f6' : r === 'uncommon' ? '#10b981' : '#9aa0ab';
        const rarityBg = (r) => r === 'legendary' ? '#fff7ed' : r === 'rare' ? '#eff6ff' : r === 'uncommon' ? '#f0fdf4' : '#f9fafb';
        const rarityBorder = (r) => r === 'legendary' ? '#fed7aa' : r === 'rare' ? '#bfdbfe' : r === 'uncommon' ? '#bbf7d0' : 'rgba(255,255,255,0.14)';
        return (
          <section style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 32px" }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <div style={{ fontSize: "0.78rem", letterSpacing: 4, color: "#a855f7", fontWeight: 800, textTransform: "uppercase", marginBottom: 8 }}> BADGE CATALOG</div>
              <h2 style={{ fontSize: "2rem", fontWeight: 900, margin: "0 0 8px" }}>Every badge, every rarity</h2>
              <p style={{ color: "#9aa0ab", fontSize: "0.95rem", margin: 0 }}>{cat.badges.length} unlocks earned across {cat.totalUsers.toLocaleString()} players. Rarest first.</p>
            </div>
            {cat.badges.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px", color: "#9aa0ab", background: "#101319", border: "1px dashed rgba(255,255,255,0.16)", borderRadius: 16 }}>
                <div style={{ fontSize: "3rem", marginBottom: 12 }}></div>
                No badges have been awarded yet — be the first to earn one.
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
                {cat.badges.map((b, i) => (
                  <div key={i} style={{ background: rarityBg(b.rarity), border: `1px solid ${rarityBorder(b.rarity)}`, borderRadius: 14, padding: "16px 18px", position: "relative" }}>
                    <div style={{ position: "absolute", top: 10, right: 12, fontSize: "0.6rem", fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase", color: rarityColor(b.rarity), padding: "2px 7px", borderRadius: 5, background: "rgba(255,255,255,0.7)" }}>{b.rarity}</div>
                    <div style={{ fontSize: "2.2rem", marginBottom: 8 }}>{b.emoji}</div>
                    <div style={{ fontWeight: 800, fontSize: "0.95rem", color: "#f4f5f7", marginBottom: 4 }}>{b.name}</div>
                    {b.description && <div style={{ fontSize: "0.75rem", color: "#9aa0ab", marginBottom: 10, minHeight: 30 }}>{b.description}</div>}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, paddingTop: 8, borderTop: `1px solid ${rarityBorder(b.rarity)}` }}>
                      <div style={{ fontSize: "0.78rem", fontWeight: 700, color: rarityColor(b.rarity) }}>{b.holdersCount} {b.holdersCount === 1 ? 'holder' : 'holders'}</div>
                      {b.rarityPct != null && <div style={{ fontSize: "0.7rem", color: "#9aa0ab" }}>{b.rarityPct}% of players</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })()}

      {section === "team-rankings" && (() => {
        if (!liveTeamRatings) {
          return (
            <section style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 32px" }}>
              <h2 style={{ fontSize: "1.6rem", fontWeight: 800, margin: "0 0 6px" }}> Team Rankings</h2>
              <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: "0 0 20px" }}>Loading…</p>
              <Skeleton w="100%" h={400} />
            </section>
          );
        }

        const source = teamRankingsScope === 'season' ? liveTeamRatings.currentSeason : liveTeamRatings.allTime;
        let rows = Object.values(source || {});
        if (teamRankingsSport) rows = rows.filter(r => r.sport === teamRankingsSport);
        rows = rankTeams([...rows]);

        // Available sport filters — only show sports that have teams
        const sportsAvailable = [...new Set(Object.values(liveTeamRatings.allTime || {}).map(r => r.sport))].filter(Boolean);
        const sportEmoji = { cricket: '', netball: '', soccer: '', volleyball: '' };
        const sportLabel = (s) => s ? (s[0].toUpperCase() + s.slice(1)) : 'Unknown';

        return (
          <section style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 32px" }}>
            <div style={{ marginBottom: 18 }}>
              <h2 style={{ fontSize: "1.6rem", fontWeight: 800, margin: "0 0 6px" }}> Team Rankings</h2>
              <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: "0 0 14px" }}>
                Teams ranked by our rating formula — result points (W/L/D/F) plus per-sport scoring contribution.
              </p>

              {/* Scoring legend — collapsible */}
              <details style={{ background: "#101319", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: "0.78rem" }}>
                <summary style={{ cursor: "pointer", fontWeight: 700, color: "#f4f5f7" }}>How the rating works</summary>
                <div style={{ marginTop: 10, color: "#c3c7ce", lineHeight: 1.6 }}>
                  <div><strong>Result:</strong> Win +{SCORING_RULES.result.win} · Draw +{SCORING_RULES.result.draw} · Loss {SCORING_RULES.result.loss} · Forfeit {SCORING_RULES.result.forfeit}</div>
                  <div><strong>Cricket:</strong> +1 per 10 runs scored · -1 per 20 runs conceded</div>
                  <div><strong>Netball:</strong> +2 per goal scored · -1 per goal conceded</div>
                  <div><strong>Soccer:</strong> +10 per goal scored · -5 per goal conceded</div>
                  <div><strong>Volleyball:</strong> +1 per 5 points scored · -1 per 10 points conceded</div>
                </div>
              </details>

              {/* Scope + sport filters */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                {[
                  { key: 'allTime', label: 'All-time' },
                  { key: 'season',  label: 'This season' },
                ].map(t => (
                  <button key={t.key} onClick={() => setTeamRankingsScope(t.key)}
                    style={{
                      padding: "6px 14px", borderRadius: 18, border: `1.5px solid ${teamRankingsScope === t.key ? P : C.line}`,
                      background: teamRankingsScope === t.key ? P : C.raised, color: teamRankingsScope === t.key ? '#fff' : C.ink2,
                      fontWeight: 700, fontSize: "0.78rem", cursor: "pointer", fontFamily: "inherit",
                    }}>{t.label}</button>
                ))}
              </div>

              {sportsAvailable.length > 1 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
                  <button onClick={() => setTeamRankingsSport(null)}
                    style={{
                      padding: "5px 12px", borderRadius: 16, border: `1.5px solid ${!teamRankingsSport ? P : C.line}`,
                      background: !teamRankingsSport ? P : C.raised, color: !teamRankingsSport ? '#fff' : C.ink2,
                      fontWeight: 700, fontSize: "0.72rem", cursor: "pointer", fontFamily: "inherit",
                    }}>All sports</button>
                  {sportsAvailable.map(s => (
                    <button key={s} onClick={() => setTeamRankingsSport(s)}
                      style={{
                        padding: "5px 12px", borderRadius: 16, border: `1.5px solid ${teamRankingsSport === s ? P : 'rgba(255,255,255,0.14)'}`,
                        background: teamRankingsSport === s ? P : C.raised, color: teamRankingsSport === s ? '#fff' : C.ink2,
                        fontWeight: 700, fontSize: "0.72rem", cursor: "pointer", fontFamily: "inherit",
                      }}>{sportEmoji[s] || ''} {sportLabel(s)}</button>
                  ))}
                </div>
              )}
            </div>

            {rows.length === 0 ? (
              <div style={{ background: "#101319", borderRadius: 14, border: "1px solid rgba(255,255,255,0.09)", padding: "40px 20px", textAlign: "center", color: "#9aa0ab" }}>
                No teams ranked for this filter yet.
              </div>
            ) : (
              <div style={{ background: "#101319", borderRadius: 14, border: "1px solid rgba(255,255,255,0.09)", overflow: "hidden" }}>
                {/* Header row */}
                <div style={{
                  display: "grid", gridTemplateColumns: "44px 1fr 70px 70px 90px 90px", alignItems: "center", gap: 12,
                  padding: "10px 16px", borderBottom: "1px solid rgba(255,255,255,0.12)", background: "#0b0e13",
                  fontSize: "0.62rem", color: "#9aa0ab", fontWeight: 800, letterSpacing: 1, textTransform: "uppercase",
                }}>
                  <div style={{ textAlign: 'center' }}>#</div>
                  <div>Team</div>
                  <div style={{ textAlign: 'right' }}>Played</div>
                  <div style={{ textAlign: 'right' }}>W-L-D</div>
                  <div style={{ textAlign: 'right' }}>Best Szn</div>
                  <div style={{ textAlign: 'right' }}>Rating</div>
                </div>
                {rows.map((t, i) => (
                  <div key={t.teamId} onClick={() => setSelectedTeamId(t.teamId)} style={{
                    display: "grid", gridTemplateColumns: "44px 1fr 70px 70px 90px 90px", alignItems: "center", gap: 12,
                    padding: "12px 16px",
                    // #f5f5f7 hairlines across a near-black table, and a hover of
                    // rgba(0,0,0,0.025) — a black wash on black, i.e. no hover at
                    // all. Both were light-theme leftovers.
                    borderBottom: i < rows.length - 1 ? `1px solid ${C.line}` : "none",
                    background: i === 0 ? "rgba(212,167,44,0.10)" : "transparent",
                    cursor: "pointer", transition: `background ${M.fast}`,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = i === 0 ? "rgba(212,167,44,0.16)" : C.raised; }}
                  onMouseLeave={e => { e.currentTarget.style.background = i === 0 ? "rgba(212,167,44,0.10)" : "transparent"; }}
                  >
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: "'Black Han Sans', sans-serif", fontWeight: 900, fontSize: "0.95rem",
                      background: i < 3 ? podium(i + 1).bg : C.raised,
                      color: i < 3 ? podium(i + 1).fg : C.ink2, ...NUM,
                    }}>{i + 1}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#f4f5f7", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</div>
                      <div style={{ fontSize: "0.7rem", color: "#9aa0ab", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {sportEmoji[t.sport] || ''} {t.sportName}{t.competitionName ? ` · ${t.competitionName}` : ''}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', fontWeight: 700, color: C.ink2, fontSize: '0.82rem', ...NUM }}>{t.played}</div>
                    <div style={{ textAlign: 'right', fontSize: '0.78rem', color: C.ink2, ...NUM }}>
                      <span style={{ color: '#10b981' }}>{t.wins}</span>-<span style={{ color: '#e74c3c' }}>{t.losses}</span>{t.draws ? `-${t.draws}` : ''}
                    </div>
                    <div style={{ textAlign: 'right', fontSize: '0.75rem' }}>
                      {t.bestSeason ? (
                        <>
                          <div style={{ fontWeight: 700, color: C.ink2, ...NUM }}>+{t.bestSeason.rating}</div>
                          <div style={{ fontSize: '0.62rem', color: '#9aa0ab' }}>{t.bestSeason.season}</div>
                        </>
                      ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                    </div>
                    <div style={{ fontFamily: "'Black Han Sans', sans-serif", fontSize: "1.4rem", color: t.rating > 0 ? "#ff2e17" : t.rating < 0 ? "#e74c3c" : "#64748b", fontWeight: 900, textAlign: 'right' }}>
                      {t.rating > 0 ? '+' : ''}{t.rating}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })()}

      {section === "h2h" && (() => {
        const a = compareIds.a && livePlayerIndex ? livePlayerIndex[compareIds.a] : null;
        const b = compareIds.b && livePlayerIndex ? livePlayerIndex[compareIds.b] : null;
        const c = compareIds.c && livePlayerIndex ? livePlayerIndex[compareIds.c] : null;
        const aRanked = liveRankedPlayers?.find(p => p.id === compareIds.a);
        const bRanked = liveRankedPlayers?.find(p => p.id === compareIds.b);
        const cRanked = liveRankedPlayers?.find(p => p.id === compareIds.c);
        const has3 = !!c;
        const h2hLoading = !!(compareIds.a && compareIds.b && !liveH2H);

        // Sport-adaptive — only show metrics that at least one player actually has
        const combinedSports = new Set([
          ...(a?.sports || []),
          ...(b?.sports || []),
          ...(c?.sports || []),
        ]);
        const playsCricket = combinedSports.has('Cricket') || combinedSports.has('Indoor Cricket');
        const playsBall = ['Netball','Indoor Netball','Soccer','Indoor Soccer','Futsal'].some(s => combinedSports.has(s));

        // Helper: lookup a pairwise H2H record from liveH2H.pairwise. Keys
        // are deterministic (sorted ids joined by ::), so order doesn't matter.
        // Returns the record from leftId's perspective (left.wins, right.wins).
        const pairRecord = (leftId, rightId) => {
          if (!leftId || !rightId || leftId === rightId || !liveH2H?.pairwise) return null;
          const key = [leftId, rightId].sort().join('::');
          const pair = liveH2H.pairwise[key];
          if (!pair) return null;
          // Pair was computed with computePairH2H(uidA, uidB) where uidA was
          // the first sorted id. If our leftId IS that first sorted id, we
          // can use the record as-is; otherwise we flip a/b.
          const sortedFirst = [leftId, rightId].sort()[0];
          if (sortedFirst === leftId) return pair;
          return { h2h: pair.h2h.map(g => ({ ...g, winner: g.winner === 'a' ? 'b' : g.winner === 'b' ? 'a' : 'draw', aTeam: g.bTeam, bTeam: g.aTeam, aScore: g.bScore, bScore: g.aScore })), record: { a: pair.record.b, b: pair.record.a, draws: pair.record.draws } };
        };

        const statKeys = [
          { key: 'games',        label: 'Games Played',  icon: '' },
          { key: 'wins',         label: 'Wins',          icon: '' },
          { key: 'winRate',      label: 'Win Rate',      icon: '', suffix: '%' },
          ...(playsCricket ? [
            { key: 'runs',        label: 'Runs',         icon: '' },
            { key: 'wickets',     label: 'Wickets',      icon: '' },
            { key: 'sixes',       label: 'Sevens',       icon: '' },
          ] : []),
          ...(playsBall ? [
            { key: 'goals',       label: 'Goals',        icon: '' },
            { key: 'hatTricks',   label: 'Hat-tricks',   icon: '' },
          ] : []),
          { key: 'highestScore', label: 'Highest Score', icon: '' },
          { key: 'badges',       label: 'Badges Earned', icon: '' },
          { key: 'sportsPlayed', label: 'Sports Played', icon: '' },
        ];

        const valueFor = (player, ranked, uid, key) => {
          if (!player) return 0;
          if (key === 'wins')     return liveH2H?.stats?.[uid]?.wins ?? ranked?.wins ?? 0;
          if (key === 'winRate')  return liveH2H?.stats?.[uid]?.winRate ?? (ranked && ranked.games ? Math.round((ranked.wins / ranked.games) * 100) : 0);
          if (key === 'games')    return liveH2H?.stats?.[uid]?.games ?? player.stats.games ?? 0;
          if (key === 'badges')   return liveH2H?.badges?.[uid]?.count ?? 0;
          if (key === 'sportsPlayed') return player.sports?.length || 0;
          return player.stats[key] || 0;
        };

        // "Stats won" tally — across every metric, count how many a beat b
        // (and vice versa). With 3 players, each stat has a unique winner
        // (or tie), so we tally per-player.
        const tallyStats = (uid, p, ranked) => {
          if (!p) return 0;
          return statKeys.filter(s => {
            const myVal = valueFor(p, ranked, uid, s.key);
            const competitors = [
              [compareIds.a, a, aRanked],
              [compareIds.b, b, bRanked],
              ...(has3 ? [[compareIds.c, c, cRanked]] : []),
            ].filter(([id]) => id && id !== uid);
            return competitors.every(([cid, cp, cr]) => myVal > valueFor(cp, cr, cid, s.key));
          }).length;
        };
        const aWonStats = tallyStats(compareIds.a, a, aRanked);
        const bWonStats = tallyStats(compareIds.b, b, bRanked);
        const cWonStats = has3 ? tallyStats(compareIds.c, c, cRanked) : 0;

        // Share button helper — builds the deep-link URL + a friendly
        // headline text. Uses navigator.share when available (mobile) and
        // falls back to copying the URL to clipboard otherwise.
        const shareH2H = async () => {
          const ids = [compareIds.a, compareIds.b, compareIds.c].filter(Boolean).join('::');
          const url = `${window.location.origin}/?h2h=${ids}`;
          let text = '';
          if (has3 && a && b && c) {
            text = `${a.firstName} vs ${b.firstName} vs ${c.firstName} — All4Indoor head-to-head`;
          } else if (a && b) {
            const ab = liveH2H?.h2hRecord;
            const scoreLine = ab ? ` (${ab.a}-${ab.b}${ab.draws ? ', ' + ab.draws + ' draws' : ''})` : '';
            text = `${a.firstName} vs ${b.firstName}${scoreLine} — All4Indoor head-to-head`;
          } else { text = 'All4Indoor head-to-head'; }
          if (navigator.share) {
            try { await navigator.share({ title: text, text, url }); return; } catch {}
          }
          try {
            await navigator.clipboard.writeText(url);
            alert('Link copied — paste anywhere to share this matchup');
          } catch {
            window.prompt('Copy this link:', url);
          }
        };

        return (
          <section style={{ maxWidth: 1000, margin: "0 auto", padding: "40px 24px" }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <h2 style={{ fontSize: "1.6rem", fontWeight: 800, margin: "0 0 6px" }}>Head-to-Head</h2>
              <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: 0 }}>
                {has3 ? "Comparing 3 players — pairwise H2H below" : "Pick any two real players and see who's actually better"}
              </p>
              {/* 2026-05-31: Share button surfaces once at least 2 players
                  are picked. Builds a ?h2h=<id>::<id>[::<id>] deep link the
                  recipient can paste anywhere to land on the same matchup. */}
              {a && b && (
                <div style={{ marginTop: 12 }}>
                  <button onClick={shareH2H}
                    style={{ padding: "6px 14px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.09)", background: "#101319", color: "#f4f5f7", fontWeight: 700, fontSize: "0.8rem", cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <Share2 size={13} /> Share this matchup
                  </button>
                </div>
              )}
            </div>

            {/* Player pickers — 2 columns when comparing 2, 3 columns when 3.
                The "+ Add 3rd" button only renders once 2 slots are filled. */}
            <div style={{ display: "grid", gridTemplateColumns: has3 ? "1fr 1fr 1fr" : "1fr 60px 1fr", gap: 12, marginBottom: 24, alignItems: "stretch" }}>
              <H2HPlayerSlot
                side="a" player={a} winsCount={aWonStats} oppWinsCount={Math.max(bWonStats, cWonStats)}
                color={P} altColor="#34d399"onPick={() => setH2hPickerSide('a')}
                onClear={() => setCompareIds(prev => ({ ...prev, a: null }))}
                badges={liveH2H?.badges?.[compareIds.a]}
              />
              {!has3 && (
                <div style={{ textAlign: "center", fontWeight: 900, fontSize: "1.5rem", color: "#f4f5f7", alignSelf: "center" }}>VS</div>
              )}
              <H2HPlayerSlot
                side="b" player={b} winsCount={bWonStats} oppWinsCount={Math.max(aWonStats, cWonStats)}
                color="#e74c3c" altColor="#f59e0b"onPick={() => setH2hPickerSide('b')}
                onClear={() => setCompareIds(prev => ({ ...prev, b: null }))}
                badges={liveH2H?.badges?.[compareIds.b]}
              />
              {has3 && (
                <H2HPlayerSlot
                  side="c" player={c} winsCount={cWonStats} oppWinsCount={Math.max(aWonStats, bWonStats)}
                  color="#7c3aed" altColor="#a78bfa"onPick={() => setH2hPickerSide('c')}
                  onClear={() => setCompareIds(prev => ({ ...prev, c: null }))}
                  badges={liveH2H?.badges?.[compareIds.c]}
                />
              )}
            </div>

            {/* "+ Add a 3rd player" button when both a and b are filled but c
                isn't. Tap it → opens picker on slot c. Removing the 3rd
                player just clears compareIds.c (the slot in the row already
                has its own × button). */}
            {a && b && !has3 && (
              <div style={{ textAlign: "center", marginBottom: 24 }}>
                <button
                  onClick={() => setH2hPickerSide('c')}
                  style={{ padding: "8px 16px", borderRadius: 999, border: "1.5px dashed #7c3aed", background: "rgba(124,58,237,0.06)", color: "#7c3aed", fontWeight: 700, fontSize: "0.82rem", cursor: "pointer", fontFamily: "inherit" }}
                >
                  + Add a 3rd player
                </button>
              </div>
            )}

            {/* Direct H2H records. For 2 players: a single record card with
                the existing layout. For 3 players: a stacked list of all
                three pairwise matchups (A vs B, A vs C, B vs C). */}
            {a && b && liveH2H && !has3 && liveH2H.h2h.length > 0 && (
              <div style={{ background: `linear-gradient(90deg, #ff2e1711, #e74c3c11)`, border: "1px solid rgba(255,255,255,0.09)", borderRadius: 14, padding: "14px 20px", marginBottom: 18, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div style={{ fontSize: "0.75rem", fontWeight: 800, letterSpacing: "1.5px", textTransform: "uppercase", color: "#9aa0ab" }}>Direct H2H</div>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "1.8rem", fontWeight: 900, color: liveH2H.h2hRecord.a > liveH2H.h2hRecord.b ? "#10b981" : C.ink2, lineHeight: 1 }}>{liveH2H.h2hRecord.a}</div>
                    <div style={{ fontSize: "0.7rem", color: "#9aa0ab", marginTop: 2 }}>{a.firstName}</div>
                  </div>
                  <div style={{ fontSize: "0.8rem", color: "#9aa0ab" }}>—</div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "1.8rem", fontWeight: 900, color: liveH2H.h2hRecord.b > liveH2H.h2hRecord.a ? "#10b981" : C.ink2, lineHeight: 1 }}>{liveH2H.h2hRecord.b}</div>
                    <div style={{ fontSize: "0.7rem", color: "#9aa0ab", marginTop: 2 }}>{b.firstName}</div>
                  </div>
                  {liveH2H.h2hRecord.draws > 0 && (
                    <>
                      <div style={{ fontSize: "0.8rem", color: "#9aa0ab" }}>·</div>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "1.2rem", fontWeight: 800, color: "#f59e0b" }}>{liveH2H.h2hRecord.draws}</div>
                        <div style={{ fontSize: "0.65rem", color: "#9aa0ab", marginTop: 2 }}>draws</div>
                      </div>
                    </>
                  )}
                </div>
                <div style={{ fontSize: "0.78rem", color: "#f4f5f7", fontWeight: 700 }}>{liveH2H.h2h.length} meeting{liveH2H.h2h.length === 1 ? '' : 's'} on court</div>
              </div>
            )}

            {/* 3-way pairwise H2H block — three rows, one per pair. */}
            {has3 && a && b && c && liveH2H && (() => {
              const pairs = [
                { left: a, right: b, leftId: compareIds.a, rightId: compareIds.b, leftColor: P, rightColor: '#e74c3c' },
                { left: a, right: c, leftId: compareIds.a, rightId: compareIds.c, leftColor: P, rightColor: '#7c3aed' },
                { left: b, right: c, leftId: compareIds.b, rightId: compareIds.c, leftColor: '#e74c3c', rightColor: '#7c3aed' },
              ];
              return (
                <div style={{ background: "#101319", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 14, padding: "14px 20px", marginBottom: 18 }}>
                  <div style={{ fontSize: "0.75rem", fontWeight: 800, letterSpacing: "1.5px", textTransform: "uppercase", color: "#9aa0ab", marginBottom: 12 }}>Pairwise H2H</div>
                  {pairs.map((p, i) => {
                    const rec = pairRecord(p.leftId, p.rightId);
                    if (!rec) return null;
                    const total = rec.h2h.length;
                    return (
                      <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 14, alignItems: "center", padding: "10px 0", borderBottom: i < pairs.length - 1 ? "1px solid #f5f5f7" : "none" }}>
                        <div style={{ textAlign: "right" }}>
                          <span style={{ fontWeight: 700, color: p.leftColor, fontSize: "0.9rem" }}>{p.left.firstName}</span>
                          <span style={{ display: "inline-block", marginLeft: 8, fontSize: "1.3rem", fontWeight: 900, color: rec.record.a > rec.record.b ? "#10b981" : C.ink2 }}>{rec.record.a}</span>
                        </div>
                        <div style={{ fontSize: "0.7rem", color: "#9aa0ab", whiteSpace: "nowrap" }}>{total ? `${total} game${total === 1 ? '' : 's'}` : 'never met'}</div>
                        <div style={{ textAlign: "left" }}>
                          <span style={{ fontSize: "1.3rem", fontWeight: 900, color: rec.record.b > rec.record.a ? "#10b981" : C.ink2, marginRight: 8 }}>{rec.record.b}</span>
                          <span style={{ fontWeight: 700, color: p.rightColor, fontSize: "0.9rem" }}>{p.right.firstName}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {a && b && !has3 && liveH2H && liveH2H.h2h.length === 0 && !h2hLoading && (
              <div style={{ background: "#0b0e13", border: "1px dashed #d1d5db", borderRadius: 12, padding: "14px 18px", marginBottom: 18, textAlign: "center", color: "#9aa0ab", fontSize: "0.85rem" }}>
                These two haven't played against each other yet — but you can still compare their career stats below.
              </div>
            )}

            {/* Stat comparison — 2-column bar layout for 2 players, 3-column
                row layout for 3 players (winner of each stat highlighted with
                their slot colour). */}
            {a && b ? (
              h2hLoading ? (
                <div style={{ background: "#101319", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 16, padding: 36, textAlign: "center", color: "#9aa0ab" }}>Loading head-to-head stats…</div>
              ) : has3 ? (
                <div style={{ background: "#101319", borderRadius: 16, padding: "24px", border: "1px solid rgba(255,255,255,0.09)" }}>
                  {statKeys.map((s, i) => {
                    const v1 = valueFor(a, aRanked, compareIds.a, s.key);
                    const v2 = valueFor(b, bRanked, compareIds.b, s.key);
                    const v3 = valueFor(c, cRanked, compareIds.c, s.key);
                    const maxVal = Math.max(v1, v2, v3);
                    const winner = (v1 === maxVal && v1 > v2 && v1 > v3) ? 1
                                 : (v2 === maxVal && v2 > v1 && v2 > v3) ? 2
                                 : (v3 === maxVal && v3 > v1 && v3 > v2) ? 3
                                 : 0;
                    const fmt = (v) => `${v.toLocaleString()}${s.suffix || ''}`;
                    return (
                      <div key={s.key} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, padding: i > 0 ? "12px 0" : "0 0 12px", borderTop: i > 0 ? "1px solid #f5f5f7" : "none", alignItems: "center" }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "1.15rem", fontWeight: 900, color: winner === 1 ? P : "#cbd5e1", lineHeight: 1 }}>{fmt(v1)}</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "#9aa0ab", whiteSpace: "nowrap" }}>{s.icon} {s.label}</div>
                          <div style={{ fontSize: "1.15rem", fontWeight: 900, color: winner === 2 ? "#e74c3c" : "#cbd5e1", lineHeight: 1, marginTop: 4 }}>{fmt(v2)}</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "1.15rem", fontWeight: 900, color: winner === 3 ? "#7c3aed" : "#cbd5e1", lineHeight: 1 }}>{fmt(v3)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ background: "#101319", borderRadius: 16, padding: "24px", border: "1px solid rgba(255,255,255,0.09)" }}>
                  {statKeys.map((s, i) => {
                    const v1 = valueFor(a, aRanked, compareIds.a, s.key);
                    const v2 = valueFor(b, bRanked, compareIds.b, s.key);
                    const max = Math.max(v1, v2, 1);
                    const w1 = (v1 / max) * 100;
                    const w2 = (v2 / max) * 100;
                    const winner = v1 > v2 ? 1 : v2 > v1 ? 2 : 0;
                    const fmt = (v) => `${v.toLocaleString()}${s.suffix || ''}`;
                    return (
                      <div key={s.key} style={{ marginBottom: i < statKeys.length - 1 ? 16 : 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 12 }}>
                          <div style={{ fontSize: "1.1rem", fontWeight: 900, color: winner === 1 ? P : "#cbd5e1", minWidth: 80, textAlign: "left" }}>
                            {fmt(v1)}
                          </div>
                          <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#9aa0ab", display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                            <span style={{ fontSize: "0.95rem" }}>{s.icon}</span> {s.label}
                          </div>
                          <div style={{ fontSize: "1.1rem", fontWeight: 900, color: winner === 2 ? "#e74c3c" : "#cbd5e1", minWidth: 80, textAlign: "right" }}>
                            {fmt(v2)}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 4, height: 8 }}>
                          <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                            <div style={{ width: `${w1}%`, height: 8, borderRadius: 4, background: winner === 1 ? P : "#d1fae5", transition: "width 0.5s" }} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ width: `${w2}%`, height: 8, borderRadius: 4, background: winner === 2 ? "#e74c3c" : "#fee2e2", transition: "width 0.5s" }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            ) : (
              <div style={{ background: "#101319", border: "1px dashed rgba(255,255,255,0.16)", borderRadius: 16, padding: 48, textAlign: "center", color: "#9aa0ab" }}>
                <div style={{ fontSize: "2.4rem", marginBottom: 12 }}></div>
                <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#f4f5f7", marginBottom: 4 }}>Pick two players to compare</div>
                <div style={{ fontSize: "0.85rem" }}>Click both slots above and search for the names you want.</div>
              </div>
            )}

            {/* Recent direct meetings */}
            {a && b && liveH2H && liveH2H.h2h.length > 0 && (
              <div style={{ marginTop: 24, background: "#101319", borderRadius: 16, padding: "20px 22px", border: "1px solid rgba(255,255,255,0.09)" }}>
                <div style={{ fontSize: "0.75rem", fontWeight: 800, letterSpacing: "2px", textTransform: "uppercase", color: "#9aa0ab", marginBottom: 14 }}>Recent Meetings</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {liveH2H.h2h.slice(0, 6).map(g => (
                    <div key={g.id} style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 12, alignItems: "center", padding: "10px 12px", background: "#fafafc", borderRadius: 10 }}>
                      <div style={{ textAlign: "right", fontSize: "0.85rem", fontWeight: g.winner === 'a' ? 800 : 500, color: g.winner === 'a' ? "#10b981" : C.ink2 }}>
                        {g.aTeam} <span style={{ fontWeight: 900, marginLeft: 4 }}>{g.aScore}</span>
                      </div>
                      <div style={{ fontSize: "0.7rem", color: "#9aa0ab", whiteSpace: "nowrap" }}>{g.when}</div>
                      <div style={{ textAlign: "left", fontSize: "0.85rem", fontWeight: g.winner === 'b' ? 800 : 500, color: g.winner === 'b' ? "#10b981" : C.ink2 }}>
                        <span style={{ fontWeight: 900, marginRight: 4 }}>{g.bScore}</span> {g.bTeam}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Player picker modal */}
            {h2hPickerSide && livePlayerIndex && (
              <PlayerSearchModal
                title={`Pick player ${h2hPickerSide.toUpperCase()}`}
                playerIndex={livePlayerIndex}
                excludeIds={[compareIds.a, compareIds.b, compareIds.c].filter(Boolean)}
                onPick={id => { setCompareIds(prev => ({ ...prev, [h2hPickerSide]: id })); setH2hPickerSide(null); }}
                onClose={() => setH2hPickerSide(null)}
              />
            )}
          </section>
        );
      })()}

      {/* ═══════════════════════════════════════════════ */}
      {/* RANKINGS — Points, Rivalries, Challenges, Wrap  */}
      {/* ═══════════════════════════════════════════════ */}
      {section === "rankings" && (() => {
        // REAL data — substitutes hardcoded rankedPlayers with live computation
        const rankedPlayers = liveRankedPlayers || [];
        const lb = liveLeaderboards;
        const leaderboardCard = (title, icon, data, metricLabel, getValue) => data && data.length > 0 ? (
          <div style={{ background: "#101319", borderRadius: 14, padding: "18px 20px", border: "1px solid rgba(255,255,255,0.09)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: "1.3rem" }}>{icon}</span>
              <h4 style={{ fontSize: "0.95rem", fontWeight: 800, margin: 0 }}>{title}</h4>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {data.slice(0, 5).map((p, i) => {
                // 2026-05-31: gold #1 row — slim left rail + crown rank +
                // soft amber gradient bg. Same hover behaviour, just with
                // a touch of crown energy so the leader visibly stands out.
                const isFirst = i === 0;
                return (
                <div key={p.id} onClick={() => setSelectedPlayerId(p.id)}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 8px",
                    background: isFirst ? "linear-gradient(90deg, rgba(251,191,36,0.16), rgba(251,191,36,0.04))" : "transparent",
                    borderLeft: isFirst ? "3px solid #f59e0b" : "3px solid transparent",
                    borderRadius: 6, cursor: "pointer", transition: "background 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = isFirst ? "linear-gradient(90deg, rgba(251,191,36,0.26), rgba(251,191,36,0.08))" : `rgba(255,46,23,0.12)`; }}
                  onMouseLeave={e => { e.currentTarget.style.background = isFirst ? "linear-gradient(90deg, rgba(251,191,36,0.16), rgba(251,191,36,0.04))" : "transparent"; }}>
                  <div style={{ width: 22, textAlign: "center", fontSize: isFirst ? "1rem" : "0.78rem", fontWeight: 700, color: isFirst ? "#b45309" : "#9aa0ab" }}>{isFirst ? '' : i+1}</div>
                  <div style={{ flex: 1, minWidth: 0, fontSize: "0.85rem", fontWeight: isFirst ? 700 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                  <div style={{ fontWeight: 800, color: isFirst ? "#b45309" : P, fontSize: "0.95rem" }}>{getValue(p)}</div>
                </div>
              );})}
            </div>
          </div>
        ) : null;
        return (
        <section style={{ maxWidth: 1000, margin: "0 auto", padding: "40px 32px" }}>
          {/* ─── LEADERBOARDS — top performers, filterable by sport ─── */}
          {!lb && (
            <div style={{ marginBottom: 40 }}>
              <Skeleton w={200} h={28} style={{ marginBottom: 12 }} />
              <Skeleton w="50%" h={14} style={{ marginBottom: 18 }} />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} style={{ background: "#101319", borderRadius: 14, padding: "18px 20px", border: "1px solid rgba(255,255,255,0.09)" }}>
                    <Skeleton w="60%" h={14} style={{ marginBottom: 14 }} />
                    <SkeletonRow count={4} h={28} gap={6} />
                  </div>
                ))}
              </div>
            </div>
          )}
          {lb && (
          <div style={{ marginBottom: 40 }} className="a4i-fadein">
            <h2 style={{ fontSize: "1.6rem", fontWeight: 800, margin: "0 0 6px" }}> Leaderboards</h2>
            <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: "0 0 14px" }}>Top performers across the venue — live from current season stats</p>
            {/* Sport filter pills */}
            {leaderboardSports.length > 1 && (
              <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
                <button onClick={() => setLeaderboardSport(null)}
                  style={{
                    padding: "6px 14px", borderRadius: 18, border: `1.5px solid ${!leaderboardSport ? P : 'rgba(255,255,255,0.14)'}`,
                    background: !leaderboardSport ? P : "rgba(255,255,255,0.05)", color: !leaderboardSport ? "#fff" : "#c3c7ce",
                    fontWeight: 700, fontSize: "0.78rem", cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s"}}>
                  All sports
                </button>
                {leaderboardSports.map(sp => (
                  <button key={sp} onClick={() => setLeaderboardSport(sp)}
                    style={{
                      padding: "6px 14px", borderRadius: 18, border: `1.5px solid ${leaderboardSport === sp ? P : C.line}`,
                      background: leaderboardSport === sp ? P : "rgba(255,255,255,0.05)", color: leaderboardSport === sp ? "#fff" : "#c3c7ce",
                      fontWeight: 700, fontSize: "0.78rem", cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s"}}>
                    {sp.replace(/^Indoor\s+/i, '')}
                  </button>
                ))}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
              {leaderboardCard('Most Runs', '', lb.topRuns, 'runs', p => p.runs)}
              {leaderboardCard('Most Wickets', '', lb.topWickets, 'wkts', p => p.wickets)}
              {/* 2026-05-31: split Goals + Hat-tricks by sport. A lumped"Most Goals" card was misleading once players were
                  earning goals across two different sports — Toby's
                  netball goals shouldn't outrank Brad's soccer goals on
                  the same board. Each card hides itself when its array
                  is empty (e.g. sport filter excludes that sport), so
                  filtered views still look clean. */}
              {leaderboardCard('Most Soccer Goals', '', lb.topGoalsSoccer, 'goals', p => p.soccerGoals)}
              {leaderboardCard('Most Netball Goals', '', lb.topGoalsNetball, 'goals', p => p.netballGoals)}
              {leaderboardCard('Most 7s', '', lb.topSixes, '7s', p => p.sixes)}
              {leaderboardCard('Most Games Played', '', lb.topGames, 'games', p => p.games)}
              {leaderboardCard('Most Soccer Hat-tricks', '', lb.topHatTricksSoccer, 'hat-tricks', p => p.soccerHatTricks)}
              {leaderboardCard('Most Netball Hat-tricks', '', lb.topHatTricksNetball, 'hat-tricks', p => p.netballHatTricks)}
            </div>
            {leaderboardSport && Object.values(lb).every(arr => arr.length === 0) && (
              <div style={{ textAlign: "center", padding: "30px 20px", color: "#9aa0ab", fontSize: "0.9rem", marginTop: 16 }}>
                No leaderboard data for {leaderboardSport.replace(/^Indoor\s+/i, '')} yet.
              </div>
            )}
          </div>
          )}

          {/* ELO Rankings — skeleton while loading, then real data */}
          {!liveElo && (
            <div style={{ marginBottom: 40 }}>
              <Skeleton w={220} h={28} style={{ marginBottom: 12 }} />
              <Skeleton w="60%" h={14} style={{ marginBottom: 18 }} />
              <SkeletonRow count={6} h={56} />
            </div>
          )}
          {liveElo && liveElo.length > 0 && (
          <div style={{ marginBottom: 40 }} className="a4i-fadein">
            <h2 style={{ fontSize: "1.6rem", fontWeight: 800, margin: "0 0 6px" }}> Player Rankings (ELO)</h2>
            <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: "0 0 10px" }}>
              Skill-based rankings — beating better-ranked players earns more rating. <strong style={{ color: "#ff6a5f" }}>Tap any name for their profile.</strong>
            </p>
            {/* 2026-06-02: How ELO works — collapsible explainer, matches the
                Team Rankings "How the rating works" pattern. Players new to
                ELO have no idea why their number is what it is; this gives
                them the plain-English version. */}
            <details style={{ background: "#101319", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: "0.82rem" }}>
              <summary style={{ cursor: "pointer", fontWeight: 700, color: "#f4f5f7" }}>How ELO works</summary>
              <div style={{ marginTop: 10, color: "#c3c7ce", lineHeight: 1.6 }}>
                <div><strong>Starting rating:</strong> Every player begins at <strong>1000</strong>. Your rating goes up when you win and down when you lose.</div>
                <div style={{ marginTop: 6 }}><strong>How much it moves:</strong> The system compares your team's average rating to the opposition's <em>before</em> the match starts. If you beat a team rated higher than yours, you gain more. If you lose to a team rated lower than yours, you lose more. Beating an evenly-matched team is worth ~12 points; an upset can be worth 20+.</div>
                <div style={{ marginTop: 6 }}><strong>Draws count:</strong> A draw against a higher-rated team still moves your rating up slightly. A draw against a lower-rated team moves it down slightly.</div>
                <div style={{ marginTop: 6 }}><strong>Min 3 games to appear:</strong> Stops one-off lucky wins from topping the table.</div>
                <div style={{ marginTop: 6 }}><strong>Tiers:</strong> <span style={{ color: '#fbbf24', fontWeight: 700 }}>ELITE</span> (1200+) · <span style={{ color: '#ff4d00', fontWeight: 700 }}>STRONG</span> (1100–1199) · <span style={{ color: '#3d9eff', fontWeight: 700 }}>STEADY</span> (1000–1099) · <span style={{ color: '#9aa0ab', fontWeight: 700 }}>RISING</span> (below 1000)</div>
                <div style={{ marginTop: 6 }}><strong>Form dots:</strong> The 5 coloured squares show your last 5 results, newest on the left. <span style={{ background: '#10b981', color: '#fff', padding: '0 5px', borderRadius: 3, fontWeight: 800 }}>W</span> = win, <span style={{ background: '#e74c3c', color: '#fff', padding: '0 5px', borderRadius: 3, fontWeight: 800 }}>L</span> = loss, <span style={{ background: '#f59e0b', color: '#fff', padding: '0 5px', borderRadius: 3, fontWeight: 800 }}>D</span> = draw.</div>
                <div style={{ marginTop: 6, fontSize: '0.76rem', color: '#888' }}>System: standard <em>Elo</em> with K=24 per match — used the same way Chess.com, FIDE, and most ranking ladders work.</div>
              </div>
            </details>
            <div style={{ background: "#101319", borderRadius: 14, border: "1px solid rgba(255,255,255,0.09)", overflow: "hidden" }}>
              {liveElo.map((p, i) => {
                const tierColor = p.elo >= 1200 ? '#fbbf24' : p.elo >= 1100 ? P : p.elo >= 1000 ? '#3d9eff' : '#9aa0ab';
                const tierLabel = p.elo >= 1200 ? 'ELITE' : p.elo >= 1100 ? 'STRONG' : p.elo >= 1000 ? 'STEADY' : 'RISING';
                return (
                  <div key={p.id} onClick={() => setSelectedPlayerId(p.id)} style={{
                    display: "grid", gridTemplateColumns: "44px 1fr 90px 110px 90px 70px",
                    padding: "12px 18px", borderBottom: "1px solid #f5f5f9", alignItems: "center", gap: 8,
                    background: i < 3 ? `${tierColor}12` : "transparent",
                    cursor: "pointer", transition: "background 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = `${tierColor}1f`; }}
                  onMouseLeave={e => { e.currentTarget.style.background = i < 3 ? `${tierColor}12` : "transparent"; }}>
                    <div style={{ fontWeight: 900, color: i < 3 ? tierColor : "#9aa0ab", fontSize: "1.05rem" }}>#{i + 1}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: `linear-gradient(135deg, ${tierColor}, #ff2e17)`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "0.7rem", fontWeight: 700, flexShrink: 0 }}>{p.avatar}</div>
                      <div style={{ fontWeight: 700, fontSize: "0.9rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                    </div>
                    <div style={{ fontSize: "0.7rem", fontWeight: 700, color: tierColor, textAlign: "center", letterSpacing: 1 }}>{tierLabel}</div>
                    <div style={{ display: "flex", justifyContent: "center", gap: 3 }} title={`Form (latest first): ${p.recentForm?.join(' ') || '—'}`}>
                      {(p.recentForm && p.recentForm.length > 0) ? p.recentForm.map((r, ri) => (
                        <span key={ri} style={{ width: 14, height: 14, borderRadius: 3, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "0.55rem", fontWeight: 800, color: "#fff", background: r === 'W' ? "#10b981" : r === 'L' ? "#e74c3c" : "#f59e0b" }}>{r}</span>
                      )) : <span style={{ fontSize: "0.7rem", color: "#cbd5e1" }}>—</span>}
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontFamily: "system-ui", fontWeight: 900, fontSize: "1.15rem", color: tierColor }}>{p.elo}</div>
                      <div style={{ fontSize: "0.62rem", color: "#9aa0ab" }}>ELO</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontWeight: 700, fontSize: "0.85rem" }}>{p.games}</div>
                      <div style={{ fontSize: "0.62rem", color: "#9aa0ab" }}>games</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ textAlign: "center", marginTop: 10, fontSize: "0.78rem", color: "#9aa0ab" }}>Showing all {liveElo.length.toLocaleString()} qualified players</div>
          </div>
          )}

          {/* Points Rankings */}
          <div style={{ marginBottom: 40 }}>
            <h2 style={{ fontSize: "1.6rem", fontWeight: 800, margin: "0 0 6px" }}>Player Points Leaderboard</h2>
            <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: "0 0 12px" }}>Points-based — turn up, get rewarded. 3 pts per win, 1 per draw, 1 per game.</p>
            <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
              <div style={{ background: "rgba(255,46,23,0.12)", borderRadius: 8, padding: "6px 12px", fontSize: "0.75rem", fontWeight: 700, color: "#ff6a5f" }}> 3 pts per win</div>
              <div style={{ background: "rgba(255,46,23,0.12)", borderRadius: 8, padding: "6px 12px", fontSize: "0.75rem", fontWeight: 700, color: "#ff6a5f" }}> 1 pt per draw</div>
              <div style={{ background: "rgba(255,46,23,0.12)", borderRadius: 8, padding: "6px 12px", fontSize: "0.75rem", fontWeight: 700, color: "#ff6a5f" }}> 1 pt per game</div>
              <div style={{ background: "rgba(245,158,11,0.1)", borderRadius: 8, padding: "6px 12px", fontSize: "0.75rem", fontWeight: 700, color: "#fbbf24" }}> Min 50 games to qualify</div>
              <div style={{ background: "rgba(239,68,68,0.12)", borderRadius: 8, padding: "6px 12px", fontSize: "0.75rem", fontWeight: 700, color: "#991b1b" }}> Rep comps excluded</div>
            </div>

            {/* Rank tiers legend */}
            <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
              {RANK_TIERS.map(t => (
                <div key={t.name} style={{ display: "flex", alignItems: "center", gap: 4, background: "#101319", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 8, padding: "4px 10px", fontSize: "0.72rem", fontWeight: 700 }}>
                  <span>{t.icon}</span> <span style={{ color: t.color }}>{t.name}</span> <span style={{ color: "#9aa0ab" }}>{t.min}+</span>
                </div>
              ))}
            </div>

            {/* Find My Ranking search */}
            <div style={{ background: "linear-gradient(135deg, #ecfdf5, #d1fae5)", borderRadius: 16, padding: "20px 24px", marginBottom: 20, border: "1px solid #a7f3d0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Search size={20} color={P} />
                <div style={{ flex: 1 }}>
                  <input value={rankSearch} onChange={e => setRankSearch(e.target.value)} placeholder="Find your ranking — type your name..." style={{ width: "100%", border: "none", outline: "none", padding: "10px 0", fontSize: "1rem", fontFamily: "inherit", fontWeight: 600, background: "transparent", color: "#f4f5f7" }} />
                </div>
                {rankSearch && <X size={18} color="#9aa0ab" style={{ cursor: "pointer" }} onClick={() => setRankSearch("")} />}
              </div>
            </div>

            {/* Searched player highlight */}
            {rankSearch && (() => {
              const q = rankSearch.toLowerCase();
              const matchedIdx = rankedPlayers.findIndex(p => p.name.toLowerCase().includes(q));
              if (matchedIdx === -1) return (
                <div style={{ background: "rgba(245,158,11,0.1)", borderRadius: 12, padding: "16px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10, border: "1px solid #f59e0b" }}>
                  <AlertCircle size={18} color="#92400e" />
                  <div style={{ fontSize: "0.9rem", color: "#fbbf24" }}>No player found matching "{rankSearch}". In the live version, this searches all {totalPlayers.toLocaleString()}+ players across every WISA venue.</div>
                </div>
              );
              const matched = rankedPlayers[matchedIdx];
              const tier = RANK_TIERS.find(t => matched.pts >= t.min && matched.pts <= t.max);
              const ptsToNext = RANK_TIERS.find(t => t.min > matched.pts);
              return (
                <div style={{ background: `linear-gradient(135deg, #ff2e1708, #ff2e1712)`, borderRadius: 16, padding: "24px", marginBottom: 20, border: `2px solid #ff2e1730` }}>
                  <div style={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, color: "#ff6a5f", marginBottom: 12 }}>Your Ranking</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <div style={{ fontSize: "2.2rem", fontWeight: 900, color: "#ff6a5f" }}>#{matchedIdx + 1}</div>
                    <div style={{ width: 52, height: 52, borderRadius: "50%", background: `linear-gradient(135deg, ${tier?.color || P}, #ff2e17)`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: "0.9rem" }}>{matched.avatar}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 800, fontSize: "1.15rem" }}>{matched.name}</div>
                      <div style={{ fontSize: "0.85rem", color: "#9aa0ab" }}>{matched.venue}</div>
                    </div>
                    <div style={{ display: "flex", gap: 20 }}>
                      <div style={{ textAlign: "center" }}><div style={{ fontWeight: 900, fontSize: "1.3rem", color: "#ff6a5f" }}>{matched.pts}</div><div style={{ fontSize: "0.68rem", color: "#9aa0ab" }}>Points</div></div>
                      <div style={{ textAlign: "center" }}><div style={{ fontWeight: 800, fontSize: "1.3rem" }}>{matched.wins}</div><div style={{ fontSize: "0.68rem", color: "#9aa0ab" }}>Wins</div></div>
                      <div style={{ textAlign: "center" }}><div style={{ fontWeight: 800, fontSize: "1.3rem" }}>{matched.games}</div><div style={{ fontSize: "0.68rem", color: "#9aa0ab" }}>Games</div></div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
                    <div style={{ background: "#101319", borderRadius: 10, padding: "8px 14px", fontSize: "0.82rem", display: "flex", alignItems: "center", gap: 6 }}>
                      <span>{tier?.icon}</span> <span style={{ fontWeight: 700, color: tier?.color }}>{tier?.name}</span> Tier
                    </div>
                    {!matched.qualified && <div style={{ background: "rgba(239,68,68,0.12)", borderRadius: 10, padding: "8px 14px", fontSize: "0.82rem", fontWeight: 600, color: "#991b1b" }}> {50 - matched.games} more games needed to qualify for leaderboards</div>}
                    {matched.qualified && <div style={{ background: "#d1fae5", borderRadius: 10, padding: "8px 14px", fontSize: "0.82rem", fontWeight: 600, color: "#065f46" }}> Qualified for global leaderboards</div>}
                    {ptsToNext && <div style={{ background: "#101319", borderRadius: 10, padding: "8px 14px", fontSize: "0.82rem", color: "#c3c7ce" }}>{ptsToNext.min - matched.pts} pts to reach <span style={{ fontWeight: 700, color: ptsToNext.color }}>{ptsToNext.name}</span></div>}
                    {matchedIdx > 0 && <div style={{ background: "#101319", borderRadius: 10, padding: "8px 14px", fontSize: "0.82rem", color: "#c3c7ce" }}>{rankedPlayers[matchedIdx - 1].pts - matched.pts} pts behind #{matchedIdx} {rankedPlayers[matchedIdx - 1].name}</div>}
                  </div>
                </div>
              );
            })()}

            <div style={{ background: "#101319", borderRadius: 16, border: "1px solid rgba(255,255,255,0.09)", overflow: "hidden" }}>
              {rankedPlayers.map((p, i) => {
                const tier = RANK_TIERS.find(t => p.pts >= t.min && p.pts <= t.max);
                const isSearched = rankSearch && p.name.toLowerCase().includes(rankSearch.toLowerCase());
                return (
                  <div key={i} onClick={() => setSelectedPlayerId(p.id)} style={{
                    display: "grid", gridTemplateColumns: "40px 1fr 100px 80px 80px 90px",
                    padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.09)", alignItems: "center",
                    background: isSearched ? "rgba(255,46,23,0.08)" : i < 3 ? "rgba(255,255,255,0.028)" : "transparent",
                    border: isSearched ? `2px solid rgba(255,46,23,0.25)` : undefined,
                    borderRadius: isSearched ? R.md : 0,
                    cursor: "pointer", transition: `background ${M.fast}`,
                  }}
                  onMouseEnter={e => { if (!isSearched) e.currentTarget.style.background = C.raised; }}
                  onMouseLeave={e => { if (!isSearched) e.currentTarget.style.background = i < 3 ? "rgba(255,255,255,0.028)" : "transparent"; }}>
                    <div style={{ fontWeight: 900, color: i < 3 ? podium(i + 1).bg : C.ink3, ...NUM }}>#{i + 1}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 40, height: 40, borderRadius: R.sm, background: podium(i + 1).bg, color: podium(i + 1).fg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.72rem", fontWeight: 700, boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.10)" }}>{p.avatar}</div>
                      <div>
                        <div style={{ fontWeight: 700 }}>{p.name} {isSearched && <span style={{ fontSize: "0.68rem", background: P, color: "#fff", padding: "2px 6px", borderRadius: 4, marginLeft: 4 }}>YOU</span>}</div>
                        <div style={{ fontSize: "0.75rem", color: "#9aa0ab" }}>{p.venue}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span>{tier?.icon}</span>
                      <span style={{ fontWeight: 700, color: tier?.color, fontSize: "0.85rem" }}>{tier?.name}</span>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontWeight: 900, fontSize: "1.1rem", color: C.ink, ...NUM }}>{p.pts}</div>
                      <div style={{ fontSize: "0.65rem", color: "#9aa0ab" }}>Points</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontWeight: 700, fontSize: "0.9rem", ...NUM }}>{p.wins}</div>
                      <div style={{ fontSize: "0.65rem", color: "#9aa0ab" }}>Wins</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontWeight: 700, fontSize: "0.9rem", ...NUM }}>{p.games}</div>
                      <div style={{ fontSize: "0.65rem", color: "#9aa0ab" }}>Games</div>
                      {!p.qualified && <div style={{ fontSize: "0.55rem", color: "#e74c3c", fontWeight: 600 }}>Not qualified</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ textAlign: "center", marginTop: 12, fontSize: "0.82rem", color: "#9aa0ab" }}>Showing top {rankedPlayers.length} players — live version shows all players across every WISA venue worldwide</div>
          </div>

          {/* Rivalry Tracker — REAL — team-vs-team head-to-heads from match history */}
          {liveRivalries && liveRivalries.length > 0 && (
          <div style={{ marginBottom: 40 }}>
            <h3 style={{ fontSize: "1.3rem", fontWeight: 800, margin: "0 0 6px" }}> Your Rivalries</h3>
            <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: "0 0 20px" }}>Auto-detected from match history — who's been giving you a run for your money</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              {liveRivalries.map((r, i) => {
                const total = r.played;
                const intensityKey = total >= 8 ? 'legendary' : total >= 5 ? 'fierce' : total >= 3 ? 'heated' : 'budding';
                const intensity = intensityKey === "legendary" ? "#e74c3c" : intensityKey === "fierce" ? "#f59e0b" : intensityKey === "heated" ? P : "#9aa0ab";
                const lastMet = r.lastMet ? new Date(r.lastMet).toLocaleDateString('en-AU', { day:'numeric', month:'short', year:'numeric' }) : '—';
                return (
                  <div key={i} style={{ background: "#101319", borderRadius: 16, padding: "20px", border: "1px solid rgba(255,255,255,0.09)", position: "relative", overflow: "hidden" }}>
                    <div style={{ position: "absolute", top: 10, right: 12, background: intensity + "18", color: intensity, fontSize: "0.65rem", fontWeight: 700, padding: "3px 8px", borderRadius: 8, textTransform: "uppercase" }}>{intensityKey}</div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginBottom: 12 }}>
                      <div style={{ textAlign: "center", flex: 1, minWidth: 0 }}>
                        <div style={{ width: 44, height: 44, borderRadius: "50%", background: `linear-gradient(135deg, #ff2e17, #ff5a45)`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "0.75rem", fontWeight: 700, margin: "0 auto 4px" }}>{(r.myTeam || '?').slice(0,2).toUpperCase()}</div>
                        <div style={{ fontWeight: 700, fontSize: "0.82rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.myTeam}</div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "1.5rem", fontWeight: 900, whiteSpace: "nowrap" }}>
                          <span style={{ color: r.wins > r.losses ? "#10b981" : "#9aa0ab" }}>{r.wins}</span>
                          <span style={{ color: "#ccc" }}> – </span>
                          <span style={{ color: r.losses > r.wins ? "#e74c3c" : "#9aa0ab" }}>{r.losses}</span>
                        </div>
                        <div style={{ fontSize: "0.68rem", color: "#9aa0ab" }}>{r.draws} draws • {total} games</div>
                      </div>
                      <div style={{ textAlign: "center", flex: 1, minWidth: 0 }}>
                        <div style={{ width: 44, height: 44, borderRadius: "50%", background: "linear-gradient(135deg, #e74c3c, #f59e0b)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "0.75rem", fontWeight: 700, margin: "0 auto 4px" }}>{(r.oppTeam || '?').slice(0,2).toUpperCase()}</div>
                        <div style={{ fontWeight: 700, fontSize: "0.82rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.oppTeam}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#9aa0ab", textAlign: "center" }}>Last met: {lastMet}</div>
                  </div>
                );
              })}
            </div>
          </div>
          )}

          {/* Weekly Challenges — hidden for authenticated users (no challenge system yet).
              Marketing visitors see the demo. Will return when we build a real challenges feature. */}
          {!authUser && (
          <div style={{ marginBottom: 40 }}>
            <h3 style={{ fontSize: "1.3rem", fontWeight: 800, margin: "0 0 6px" }}> Weekly Challenges <span style={{ fontSize: "0.7rem", fontWeight: 600, background: "#101319", color: "#9aa0ab", padding: "2px 8px", borderRadius: 8, marginLeft: 8, verticalAlign: "middle" }}>COMING SOON</span></h3>
            <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: "0 0 20px" }}>New challenges every Monday — complete them for XP and limited-edition badges</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {WEEKLY_CHALLENGES.map(c => (
                <div key={c.id} style={{ background: "#101319", borderRadius: 14, padding: "18px 20px", border: c.progress >= c.target ? "2px solid #10b981" : "1px solid rgba(255,255,255,0.09)", position: "relative" }}>
                  {c.progress >= c.target && <div style={{ position: "absolute", top: 10, right: 12, background: "#10b981", color: "#fff", fontSize: "0.65rem", fontWeight: 700, padding: "3px 8px", borderRadius: 8 }}>COMPLETE</div>}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: "1.5rem" }}>{c.icon}</span>
                    <div>
                      <div style={{ fontWeight: 800 }}>{c.title}</div>
                      <div style={{ fontSize: "0.78rem", color: "#9aa0ab" }}>{c.desc}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ flex: 1, height: 8, borderRadius: 4, background: "#101319" }}>
                      <div style={{ height: 8, borderRadius: 4, background: c.progress >= c.target ? "#10b981" : P, width: `${Math.min(100, (c.progress / c.target) * 100)}%` }} />
                    </div>
                    <div style={{ fontSize: "0.78rem", fontWeight: 700, color: c.progress >= c.target ? "#10b981" : P }}>{c.progress}/{c.target}</div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: "0.72rem", color: "#9aa0ab" }}>
                    <span> {c.reward}</span>
                    <span> {c.expires}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* Weekly Wrap-Up — for authenticated users we show a "Coming soon" placeholder
              instead of the fake demo numbers. Marketing/demo numbers only appear for
              unauthenticated visitors so real users (e.g. Graham Craig) don't see fake stats
              attached to their name. */}
          {!!authUser ? (
            // REAL weekly wrap-up — last 7 days of the logged-in user's activity
            liveWeeklyWrap && (
              <div style={{ background: "linear-gradient(135deg, #1a1a2e, #0d4a42)", borderRadius: 20, padding: "32px", color: "#fff", marginBottom: 40 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: "1.4rem" }}></span>
                  <h3 style={{ fontSize: "1.15rem", fontWeight: 800, margin: 0 }}>Your Weekly Wrap-Up</h3>
                </div>
                <div style={{ fontSize: "0.75rem", opacity: 0.6, marginBottom: 18 }}>{liveWeeklyWrap.period}</div>
                {liveWeeklyWrap.hasActivity ? (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 16 }}>
                      <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "16px 12px", textAlign: "center" }}>
                        <div style={{ fontSize: "1.9rem", fontWeight: 900 }}>{liveWeeklyWrap.games}</div>
                        <div style={{ fontSize: "0.7rem", opacity: 0.6, letterSpacing: 1, textTransform: "uppercase" }}>Games</div>
                      </div>
                      <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "16px 12px", textAlign: "center" }}>
                        <div style={{ fontSize: "1.9rem", fontWeight: 900, color: "#34d399" }}>{liveWeeklyWrap.wins}</div>
                        <div style={{ fontSize: "0.7rem", opacity: 0.6, letterSpacing: 1, textTransform: "uppercase" }}>Wins</div>
                      </div>
                      <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "16px 12px", textAlign: "center" }}>
                        <div style={{ fontSize: "1.9rem", fontWeight: 900, color: "#fbbf24" }}>{liveWeeklyWrap.motms}</div>
                        <div style={{ fontSize: "0.7rem", opacity: 0.6, letterSpacing: 1, textTransform: "uppercase" }}>MVPs</div>
                      </div>
                      <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "16px 12px", textAlign: "center" }}>
                        <div style={{ fontSize: "1.9rem", fontWeight: 900, color: "#10b981" }}>+{liveWeeklyWrap.ptsEarned}</div>
                        <div style={{ fontSize: "0.7rem", opacity: 0.6, letterSpacing: 1, textTransform: "uppercase" }}>Points</div>
                      </div>
                    </div>
                    {liveWeeklyWrap.newBadges.length > 0 && (
                      <div style={{ background: "rgba(251,191,36,0.12)", borderRadius: 10, padding: "10px 14px", border: "1px solid rgba(251,191,36,0.25)" }}>
                        <div style={{ fontSize: "0.65rem", letterSpacing: 2, color: "#fbbf24", fontWeight: 700, marginBottom: 6 }}>★ NEW BADGES THIS WEEK</div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          {liveWeeklyWrap.newBadges.map((b, i) => (
                            <span key={i} style={{ background: "rgba(255,255,255,0.1)", padding: "4px 10px", borderRadius: 8, fontSize: "0.78rem" }}>
                              {b.emoji || ''} {b.achievement_name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ textAlign: "center", padding: "24px 12px", fontSize: "0.9rem", opacity: 0.7 }}>
                    No games this week — head back to the court and your stats will start showing here.
                  </div>
                )}
              </div>
            )
          ) : (
          <div style={{ background: "linear-gradient(135deg, #1a1a2e, #0d4a42)", borderRadius: 20, padding: "32px", color: "#fff", marginBottom: 40 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: "1.3rem" }}></span>
              <h3 style={{ fontSize: "1.1rem", fontWeight: 800, margin: 0 }}>Your Weekly Wrap-Up</h3>
              <span style={{ fontSize: "0.7rem", opacity: 0.5, background: "rgba(255,255,255,0.12)", padding: "2px 8px", borderRadius: 8, marginLeft: 8 }}>EXAMPLE</span>
            </div>
            <div style={{ fontSize: "0.72rem", opacity: 0.5, marginBottom: 20 }}>{WEEKLY_WRAP.period}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16, marginBottom: 20 }}>
              <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "16px", textAlign: "center" }}>
                <div style={{ fontSize: "1.8rem", fontWeight: 900 }}>{WEEKLY_WRAP.games}</div><div style={{ fontSize: "0.72rem", opacity: 0.6 }}>Games</div>
              </div>
              <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "16px", textAlign: "center" }}>
                <div style={{ fontSize: "1.8rem", fontWeight: 900 }}>{WEEKLY_WRAP.wins}</div><div style={{ fontSize: "0.72rem", opacity: 0.6 }}>Wins</div>
              </div>
              <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "16px", textAlign: "center" }}>
                <div style={{ fontSize: "1.8rem", fontWeight: 900 }}>{WEEKLY_WRAP.goals}</div><div style={{ fontSize: "0.72rem", opacity: 0.6 }}>Goals</div>
              </div>
              <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "16px", textAlign: "center" }}>
                <div style={{ fontSize: "1.8rem", fontWeight: 900, color: WEEKLY_WRAP.ptsEarned > 0 ? "#10b981" : "#e74c3c" }}>+{WEEKLY_WRAP.ptsEarned}</div><div style={{ fontSize: "0.72rem", opacity: 0.6 }}>Points</div>
              </div>
            </div>
            <div style={{ textAlign: "center", marginTop: 16, fontSize: "0.78rem", opacity: 0.5 }}>Sign in to get your real weekly wrap-up</div>
          </div>
          )}

          {/* Shareable Stats Card Preview — hide entirely for authenticated users since
              the cards contain fake demo content (Jake R., Thunder FC, hat-tricks etc.).
              Marketing visitors still see them as examples. */}
          {!authUser && (
          <div style={{ marginBottom: 32 }}>
            <h3 style={{ fontSize: "1.3rem", fontWeight: 800, margin: "0 0 6px" }}> Share Your Game <span style={{ fontSize: "0.7rem", fontWeight: 600, background: "#101319", color: "#9aa0ab", padding: "2px 8px", borderRadius: 8, marginLeft: 8, verticalAlign: "middle" }}>EXAMPLES</span></h3>
            <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: "0 0 20px" }}>Every game will generate a shareable card — perfect for Instagram, Facebook, and X</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              {[
                { title: "Match Result", bg: `linear-gradient(135deg, #ff2e17, #ff5a45)`, content: (
                  <div style={{ textAlign: "center", color: "#fff" }}>
                    <div style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, opacity: 0.6 }}>Match Result</div>
                    <div style={{ fontSize: "1.5rem", fontWeight: 900, margin: "8px 0" }}>Thunder FC 7 – 4 Lightning</div>
                    <div style={{ fontSize: "0.82rem", opacity: 0.7 }}>Wildcatz Indoor Sports • Apr 8</div>
                    <div style={{ marginTop: 10, background: "rgba(255,255,255,0.2)", display: "inline-block", padding: "4px 12px", borderRadius: 8, fontSize: "0.78rem", fontWeight: 700 }}> MVP: Jake R. (Hat-trick)</div>
                  </div>
                )},
                { title: "Milestone Badge", bg: "linear-gradient(135deg, #fbbf24, #f59e0b)", content: (
                  <div style={{ textAlign: "center", color: "#fff" }}>
                    <div style={{ fontSize: "3rem", marginBottom: 8 }}></div>
                    <div style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, opacity: 0.7 }}>Badge Unlocked</div>
                    <div style={{ fontSize: "1.3rem", fontWeight: 900, margin: "4px 0" }}>Sharp Shooter</div>
                    <div style={{ fontSize: "0.85rem", opacity: 0.8 }}>Jake R. scored 50 career goals</div>
                    <div style={{ marginTop: 6, fontSize: "0.72rem", opacity: 0.6 }}>all4indoor.com</div>
                  </div>
                )},
                { title: "Season Summary", bg: "linear-gradient(135deg, #10b981, #059669)", content: (
                  <div style={{ textAlign: "center", color: "#fff" }}>
                    <div style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, opacity: 0.6 }}>Season 2026 S1</div>
                    <div style={{ fontSize: "1.6rem", fontWeight: 900, margin: "8px 0" }}>Jake R.</div>
                    <div style={{ display: "flex", justifyContent: "center", gap: 16, fontSize: "0.85rem" }}>
                      <span>42 Games</span><span>31 Wins</span><span>47 Goals</span>
                    </div>
                    <div style={{ marginTop: 8, fontSize: "0.78rem", opacity: 0.7 }}>Wildcatz Indoor Sports</div>
                  </div>
                )},
                { title: "Rank Up", bg: "linear-gradient(135deg, #1a1a2e, #ff2e17)", content: (
                  <div style={{ textAlign: "center", color: "#fff" }}>
                    <div style={{ fontSize: "2.5rem", marginBottom: 4 }}></div>
                    <div style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, opacity: 0.6 }}>Rank Up!</div>
                    <div style={{ fontSize: "1.3rem", fontWeight: 900 }}>Gold → Platinum</div>
                    <div style={{ fontSize: "0.85rem", opacity: 0.7, marginTop: 4 }}>Jake R. — 710 pts</div>
                    <div style={{ marginTop: 6, fontSize: "0.72rem", opacity: 0.5 }}>all4indoor.com</div>
                  </div>
                )},
              ].map((card, i) => (
                <div key={i} style={{ background: card.bg, borderRadius: 16, padding: "32px 24px", boxShadow: "0 4px 24px rgba(0,0,0,0.15)" }}>
                  {card.content}
                  <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
                    {[" Facebook", " X", " Instagram"].map(s => (
                      <div key={s} style={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: "0.68rem", fontWeight: 600, padding: "4px 10px", borderRadius: 8, cursor: "pointer" }}>{s}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* Venue Power Rankings — removed. Was hardcoded fake data ("96.2 Power Score" etc).
              Will return when we have a real venue rankings algorithm fed from live data. */}
        </section>
        );
      })()}

      {/* ═══════════════════════════════════════════════ */}
      {/* PLAY — Looking for Players & Pickup Games      */}
      {/* ═══════════════════════════════════════════════ */}
      {section === "play" && (
        <section style={{ maxWidth: 1000, margin: "0 auto", padding: "40px 32px" }}>
          <div style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: "1.6rem", fontWeight: 800, margin: "0 0 6px" }}>Find a Game</h2>
            <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: 0 }}>Teams need players. Players need games. WISA connects them — stats count for everything.</p>
          </div>

          {/* Post-game reactions demo */}
          <div style={{ background: "linear-gradient(135deg, #1a1a2e, #0d4a42)", borderRadius: 16, padding: "24px", color: "#fff", marginBottom: 32 }}>
            <div style={{ fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, opacity: 0.5, marginBottom: 12 }}>Post-Game Reactions (positive only — no comments, no abuse)</div>
            <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "16px", marginBottom: 12 }}>
              <div style={{ fontSize: "0.85rem", marginBottom: 10 }}> <strong>Thunder FC 7 – 4 Lightning Strikers</strong> — Wildcatz Indoor Sports</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {REACTION_OPTIONS.map((r, i) => (
                  <div key={i} style={{ background: i < 3 ? "rgba(26,138,125,0.3)" : "rgba(255,255,255,0.1)", borderRadius: 20, padding: "6px 14px", fontSize: "0.82rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                    <span>{r.emoji}</span> <span style={{ fontWeight: 600 }}>{r.label}</span>
                    {i < 3 && <span style={{ background: "rgba(255,255,255,0.2)", borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.6rem", fontWeight: 700 }}>{[24, 18, 12][i]}</span>}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ fontSize: "0.75rem", opacity: 0.5 }}>Players react after each game — no text, no comments, just positive vibes</div>
          </div>

          {/* Looking for Players — REAL — from teams.looking_for_players flag */}
          <h3 style={{ fontSize: "1.2rem", fontWeight: 800, margin: "0 0 16px" }}> Looking for Players</h3>
          {liveLookingForPlayers && liveLookingForPlayers.length === 0 && (
            <div style={{ padding: "20px", background: "#0b0e13", borderRadius: 12, color: "#9aa0ab", textAlign: "center", marginBottom: 40 }}>
              No teams are currently recruiting. Check back later or ask your captain to flag your team as looking for players.
            </div>
          )}
          {liveLookingForPlayers && liveLookingForPlayers.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 40 }}>
            {liveLookingForPlayers.map(g => (
              <div key={g.id} style={{ background: "#101319", borderRadius: 14, padding: "18px 22px", border: "1px solid rgba(255,255,255,0.09)", display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ width: 50, height: 50, borderRadius: 12, background: `linear-gradient(135deg, #ff2e17, #ff5a45)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.5rem", flexShrink: 0 }}>{g.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: "1rem" }}>{g.team}</div>
                  <div style={{ fontSize: "0.82rem", color: "#9aa0ab" }}>{g.sport}{g.comp ? ` · ${g.comp}` : ''}{g.day ? ` · ${g.day}` : ''}{g.time ? ` ${g.time}` : ''}</div>
                  <div style={{ fontSize: "0.82rem", color: "#f4f5f7", marginTop: 6, fontStyle: "italic" }}>"{g.message}"</div>
                </div>
                <button style={{ background: P, color: "#fff", border: "none", borderRadius: 10, padding: "10px 18px", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer", whiteSpace: "nowrap" }}>
                  I'm Keen
                </button>
              </div>
            ))}
          </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div style={{ background: "rgba(26,138,125,0.04)", borderRadius: 16, padding: "28px", border: `1px solid rgba(26,138,125,0.1)` }}>
              <Zap size={24} color={P} style={{ marginBottom: 8 }} />
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Never cancel a game again</div>
              <div style={{ color: "#9aa0ab", fontSize: "0.85rem", lineHeight: 1.5 }}>Short a player? Post it on all4indoor. Players nearby get notified. The game goes ahead. The venue keeps the booking. Everyone wins.</div>
            </div>
            <div style={{ background: "rgba(245,158,11,0.1)", borderRadius: 16, padding: "28px", border: "1px solid #f59e0b" }}>
              <AlertCircle size={24} color="#92400e" style={{ marginBottom: 8 }} />
              <div style={{ fontWeight: 700, color: "#fbbf24", marginBottom: 4 }}>Non-WISA venues can't do this</div>
              <div style={{ fontSize: "0.85rem", color: "#fbbf24", lineHeight: 1.5 }}>Short a player at a non-WISA venue? You're on your own. Game cancelled. Booking lost. Revenue gone.</div>
            </div>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/* SEASON AWARDS                                  */}
      {/* ═══════════════════════════════════════════════ */}
      {section === "awards" && (() => {
        // Use real season awards computed from player_stats
        const awardsList = liveSeasonAwards || [];
        // Auto-select first season if none selected and we have data
        const activeSeason = selectedAwardSeason && awardsList.find(s => s.season === selectedAwardSeason)
          ? selectedAwardSeason
          : (awardsList[0]?.season || null);
        return (
        <section style={{ maxWidth: 1000, margin: "0 auto", padding: "40px 32px" }}>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <h2 style={{ fontSize: "1.6rem", fontWeight: 800, margin: "0 0 6px" }}>Season Awards</h2>
            <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: "0 0 20px" }}>Best performers per season — computed live from match stats</p>
            {awardsList.length === 0 && (
              <div style={{ background: "#0b0e13", padding: "40px 20px", borderRadius: 12, color: "#9aa0ab" }}>
                No season stats recorded yet. Awards will appear as players accumulate stats across each season.
              </div>
            )}
            {awardsList.length > 0 && (
            <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
              {awardsList.map(sa => {
                // 2026-05-31: show date range under the season name so admins
                // can tell at a glance which season they're looking at when
                // multiple seasons are active (e.g. Winter Season 2026 vs
                // Term 2 Junior Sports).
                const fmt = (d) => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
                const startLabel = fmt(sa.startDate);
                const endLabel = fmt(sa.endDate);
                const dateLine = startLabel && endLabel ? `${startLabel} → ${endLabel}` : (endLabel ? `Ends ${endLabel}` : null);
                const isActive = activeSeason === sa.season;
                return (
                  <button key={sa.season} onClick={() => setSelectedAwardSeason(sa.season)}
                    style={{ background: isActive ? P : "rgba(255,255,255,0.05)", color: isActive ? "#fff" : "#555", border: isActive ? "none" : "1px solid rgba(255,255,255,0.09)", borderRadius: 10, padding: "10px 20px", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer", textAlign: "center", lineHeight: 1.3 }}>
                    <div>{sa.season}</div>
                    {dateLine && (
                      <div style={{ fontSize: "0.7rem", fontWeight: 500, opacity: isActive ? 0.85 : 0.6, marginTop: 2 }}>
                        {dateLine}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            )}
          </div>

          {(() => {
            const sa = awardsList.find(s => s.season === activeSeason);
            if (!sa) return null;
            return (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
                {sa.awards.map((award, i) => (
                  <div key={i} style={{
                    background: "#101319", borderRadius: 16, padding: "28px 24px", textAlign: "center",
                    border: "1px solid rgba(255,255,255,0.09)", boxShadow: "0 2px 16px rgba(0,0,0,0.04)",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 8px 32px rgba(26,138,125,0.12)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 2px 16px rgba(0,0,0,0.04)"; }}
                  >
                    <div style={{ fontSize: "2.4rem", marginBottom: 6 }}>{award.emoji}</div>
                    <div style={{ fontSize: "0.7rem", letterSpacing: 2, color: "#ff6a5f", fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>{award.title}</div>
                    <div style={{ fontWeight: 900, fontSize: "1.15rem", marginBottom: 4 }}>{award.winner.name}</div>
                    <div style={{ fontSize: "0.95rem", color: "#ff6a5f", fontWeight: 700 }}>{award.value(award.winner)}</div>
                    {award.winner.sport && <div style={{ fontSize: "0.72rem", color: "#9aa0ab", marginTop: 6 }}>{award.winner.sport}</div>}
                  </div>
                ))}
              </div>
            );
          })()}

          <div style={{ textAlign: "center", marginTop: 32, padding: "28px", background: "rgba(26,138,125,0.04)", borderRadius: 16, border: `1px solid rgba(26,138,125,0.1)` }}>
            <Award size={24} color={P} style={{ marginBottom: 8 }} />
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Awards live on your profile forever</div>
            <div style={{ color: "#9aa0ab", fontSize: "0.85rem" }}>Win a season award and it's permanently displayed on your player profile. Build your legacy across every WISA venue you play at.</div>
          </div>

          <div style={{ textAlign: "center", marginTop: 16, padding: "24px", background: "rgba(245,158,11,0.1)", borderRadius: 16, border: "1px solid #f59e0b" }}>
            <div style={{ fontWeight: 800, color: "#fbbf24", marginBottom: 4 }}>Non-WISA venues have no season awards</div>
            <div style={{ fontSize: "0.85rem", color: "#fbbf24" }}>Had the best season of your life at a non-WISA venue? Nobody will ever know. No awards. No recognition. No legacy.</div>
          </div>
        </section>
        );
      })()}

      {/* ═══════════════════════════════════════════════ */}
      {/* REP PATHWAYS                                    */}
      {/* ═══════════════════════════════════════════════ */}
      {section === "rep" && (
        <section style={{ maxWidth: 1000, margin: "0 auto", padding: "40px 32px" }}>
          {/* Hero */}
          <div style={{ background: "linear-gradient(135deg, #1a1a2e, #0f172a)", borderRadius: 20, padding: "48px 36px", color: "#fff", marginBottom: 32, position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: -60, right: -60, width: 250, height: 250, borderRadius: "50%", background: "rgba(26,138,125,0.15)" }} />
            <div style={{ position: "absolute", bottom: -40, left: -40, width: 180, height: 180, borderRadius: "50%", background: "rgba(168,85,247,0.1)" }} />
            <div style={{ position: "relative" }}>
              <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 3, color: "#34d399", marginBottom: 12 }}>Representative Pathways</div>
              <h2 style={{ fontSize: "2rem", fontWeight: 900, margin: "0 0 10px", lineHeight: 1.2 }}>From weekly comps to <span style={{ color: "#34d399" }}>representative honours</span></h2>
              <p style={{ fontSize: "1rem", opacity: 0.7, margin: 0, maxWidth: 600 }}>WISA tracks your journey from grassroots to elite. Rep selections are based on weekly comp performance — your stats build your case automatically.</p>
            </div>
          </div>

          {/* How rep selection works */}
          <div style={{ marginBottom: 40 }}>
            <h3 style={{ fontSize: "1.3rem", fontWeight: 800, margin: "0 0 6px" }}> How Rep Selection Works</h3>
            <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: "0 0 20px" }}>Your weekly comp stats are your audition — no politics, just performance</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16 }}>
              {[
                { step: "1", title: "Play Weekly Comps", desc: "Every game tracked across all WISA venues. Goals, wins, attendance — it all counts.", icon: "" },
                { step: "2", title: "Build Your Profile", desc: "Your stats, consistency, and sportsmanship build a rep-ready profile over time.", icon: "" },
                { step: "3", title: "Get Noticed", desc: "Venue managers and selectors see top performers by sport, age group, and region.", icon: "" },
                { step: "4", title: "Rep Selection", desc: "Selected for venue, regional, state, or national representative teams.", icon: "" },
              ].map(s => (
                <div key={s.step} style={{ background: "#101319", borderRadius: 16, padding: "24px 20px", border: "1px solid rgba(255,255,255,0.09)", textAlign: "center" }}>
                  <div style={{ fontSize: "2rem", marginBottom: 8 }}>{s.icon}</div>
                  <div style={{ background: P, color: "#fff", width: 28, height: 28, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "0.8rem", marginBottom: 8 }}>{s.step}</div>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>{s.title}</div>
                  <div style={{ fontSize: "0.82rem", color: "#9aa0ab", lineHeight: 1.5 }}>{s.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Pathway tiers */}
          <div style={{ marginBottom: 40 }}>
            <h3 style={{ fontSize: "1.3rem", fontWeight: 800, margin: "0 0 6px" }}> The Pathway</h3>
            <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: "0 0 20px" }}>Every level of representative indoor sport — tracked and visible on your profile</p>
            {[
              { level: "Venue Rep", color: "#ff2e17", desc: "Represent your home venue against other WISA venues in your area", badge: "", example: "Wildcatz Netball Rep Team", criteria: "Top performers from weekly comps selected by venue managers" },
              { level: "Regional Rep", color: "#3b82f6", desc: "Represent your region in inter-regional tournaments and carnivals", badge: "", example: "North QLD Indoor Soccer", criteria: "Best venue rep players selected for regional squads" },
              { level: "State Rep", color: "#10b981", desc: "Represent your state in national championships and inter-state events", badge: "", example: "Queensland Indoor Netball", criteria: "Top regional performers compete for state selection" },
              { level: "National Rep", color: "#f59e0b", desc: "Represent your country on the international stage", badge: "", example: "Australia Indoor Soccer", criteria: "State champions form the national squad" },
              { level: "International", color: "#e74c3c", desc: "World championships, international invitational tournaments, and global events", badge: "", example: "WISA World Indoor Games", criteria: "National teams competing at the highest level" },
            ].map((tier, i) => (
              <div key={i} style={{
                display: "flex", gap: 20, alignItems: "stretch", marginBottom: 12,
                background: "#101319", borderRadius: 16, border: "1px solid rgba(255,255,255,0.09)", overflow: "hidden",
              }}>
                <div style={{ width: 6, background: tier.color, flexShrink: 0 }} />
                <div style={{ padding: "20px 24px 20px 16px", flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <span style={{ fontSize: "1.5rem" }}>{tier.badge}</span>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: "1.05rem" }}>{tier.level}</div>
                      <div style={{ fontSize: "0.82rem", color: "#9aa0ab" }}>{tier.desc}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 16, fontSize: "0.8rem" }}>
                    <div style={{ background: `${tier.color}10`, padding: "6px 12px", borderRadius: 8, color: tier.color, fontWeight: 600 }}>e.g. {tier.example}</div>
                    <div style={{ color: "#9aa0ab", padding: "6px 0" }}>{tier.criteria}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Rep vs Weekly — the distinction */}
          <div style={{ background: "linear-gradient(135deg, #ecfdf5, #d1fae5)", borderRadius: 20, padding: "32px", marginBottom: 40, border: "1px solid #a7f3d0" }}>
            <h3 style={{ fontSize: "1.2rem", fontWeight: 800, margin: "0 0 16px", color: "#ff6a5f" }}> Rep vs Weekly Competitions</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 8, fontSize: "0.95rem" }}>Weekly Comps</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: "0.85rem", color: "#065f46" }}>
                  {[" Count towards global leaderboard", " Earn points (3 per win, 1 draw, 1 per game)", " Build your ranking tier", " Feed into rep selection criteria", " Available at every WISA venue"].map((t, i) => <div key={i}>{t}</div>)}
                </div>
              </div>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 8, fontSize: "0.95rem" }}>Rep Competitions</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: "0.85rem", color: "#065f46" }}>
                  {[" Separate honour — displayed on your profile", " Rep stats tracked independently", " Do NOT count towards prize leaderboard", " Show selectors and scouts your ability", " Build your pathway to state & national"].map((t, i) => <div key={i}>{t}</div>)}
                </div>
              </div>
            </div>
          </div>

          {/* Demo: player rep profile */}
          <div style={{ marginBottom: 40 }}>
            <h3 style={{ fontSize: "1.3rem", fontWeight: 800, margin: "0 0 16px" }}> Your Rep Profile</h3>
            <div style={{ background: "#101319", borderRadius: 16, border: "1px solid rgba(255,255,255,0.09)", padding: "24px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
                <div style={{ width: 56, height: 56, borderRadius: "50%", background: `linear-gradient(135deg, #ff2e17, #ff5a45)`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: "1.1rem" }}>JR</div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: "1.1rem" }}>Jake R.</div>
                  <div style={{ fontSize: "0.85rem", color: "#9aa0ab" }}>Wildcatz Indoor Sports — Soccer, Cricket</div>
                </div>
              </div>
              <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#9aa0ab", marginBottom: 12 }}>Representative History</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[
                  { year: "2026", team: "Wildcatz Soccer Rep", level: "Venue", sport: "Soccer", result: "Regional Carnival — 3rd Place", color: "#ff2e17" },
                  { year: "2025", team: "North QLD Indoor Soccer", level: "Regional", sport: "Soccer", result: "State Championships — Qualified", color: "#3b82f6" },
                  { year: "2025", team: "Wildcatz Cricket Rep", level: "Venue", sport: "Cricket", result: "Inter-venue T20 — Runner Up", color: "#ff2e17" },
                ].map((r, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: "#0b0e13", borderRadius: 12, borderLeft: `3px solid ${r.color}` }}>
                    <div style={{ fontWeight: 800, fontSize: "0.85rem", color: "#9aa0ab", minWidth: 40 }}>{r.year}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>{r.team}</div>
                      <div style={{ fontSize: "0.78rem", color: "#9aa0ab" }}>{r.sport} · {r.level} Rep · {r.result}</div>
                    </div>
                    <div style={{ background: `${r.color}15`, color: r.color, padding: "4px 10px", borderRadius: 8, fontSize: "0.72rem", fontWeight: 700 }}>{r.level}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 16, padding: "12px 16px", background: "rgba(255,46,23,0.12)", borderRadius: 10, fontSize: "0.82rem", color: "#ff6a5f", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                <Star size={14} /> Rep history is permanently displayed on your profile — build your legacy
              </div>
            </div>
          </div>

          {/* For juniors */}
          <div style={{ marginBottom: 40 }}>
            <h3 style={{ fontSize: "1.3rem", fontWeight: 800, margin: "0 0 6px" }}> Junior Pathways</h3>
            <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: "0 0 20px" }}>Age-group representative pathways from Under 10s all the way through to Open</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              {["Under 10s", "Under 12s", "Under 14s", "Under 16s", "Under 18s", "Open"].map((age, i) => (
                <div key={i} style={{ background: "#101319", borderRadius: 12, padding: "16px", border: "1px solid rgba(255,255,255,0.09)", textAlign: "center" }}>
                  <div style={{ fontSize: "1.5rem", marginBottom: 6 }}>{i < 5 ? "" : ""}</div>
                  <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>{age}</div>
                  <div style={{ fontSize: "0.78rem", color: "#9aa0ab", marginTop: 4 }}>Venue → Regional → State → National</div>
                </div>
              ))}
            </div>
          </div>

          {/* For venues / selectors */}
          <div style={{ background: "#101319", borderRadius: 20, border: "1px solid rgba(255,255,255,0.09)", padding: "32px", marginBottom: 32 }}>
            <h3 style={{ fontSize: "1.2rem", fontWeight: 800, margin: "0 0 16px" }}> For Selectors & Venue Managers</h3>
            <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: "0 0 16px" }}>WISA gives you the data to make fair, transparent rep selections</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                { title: "Player Comparison", desc: "Compare any players head-to-head across all stats — games, wins, consistency, attendance" },
                { title: "Attendance Records", desc: "See who turns up every week. Rep selection should reward commitment." },
                { title: "Multi-venue Stats", desc: "Players who compete at multiple venues get recognised for their breadth of experience" },
                { title: "Transparent Selection", desc: "Share selection criteria publicly so players know exactly what they need to aim for" },
              ].map((f, i) => (
                <div key={i} style={{ background: "#0b0e13", borderRadius: 12, padding: "16px" }}>
                  <div style={{ fontWeight: 700, fontSize: "0.9rem", marginBottom: 4 }}>{f.title}</div>
                  <div style={{ fontSize: "0.82rem", color: "#9aa0ab", lineHeight: 1.5 }}>{f.desc}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ textAlign: "center", padding: "24px", background: "rgba(245,158,11,0.1)", borderRadius: 16, border: "1px solid #f59e0b" }}>
            <div style={{ fontWeight: 800, color: "#fbbf24", marginBottom: 4 }}>Non-WISA venues can't offer rep pathways</div>
            <div style={{ fontSize: "0.85rem", color: "#fbbf24" }}>No tracked stats. No rep profile. No pathway from weekly comps to state selection. Your players deserve better.</div>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/* PRIZES                                         */}
      {/* ═══════════════════════════════════════════════ */}
      {section === "prizes" && (
        <section style={{ maxWidth: 1000, margin: "0 auto", padding: "40px 32px" }}>
          <div style={{ background: "linear-gradient(135deg, #1a1a2e, #0d4a42, #ff2e17)", borderRadius: 20, padding: "40px 36px", color: "#fff", marginBottom: 32, position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: -50, right: -50, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
            <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 3, opacity: 0.6, marginBottom: 8 }}>all4indoor Global Leaderboard</div>
                <h2 style={{ fontSize: "2rem", fontWeight: 900, margin: "0 0 8px" }}>Play. Compete. Win Big.</h2>
                <p style={{ fontSize: "1rem", opacity: 0.7, margin: 0 }}>Only games at WISA-powered venues count towards global leaderboards.</p>
              </div>
              <div style={{ textAlign: "center", background: "rgba(255,255,255,0.1)", padding: "20px 32px", borderRadius: 16 }}>
                <div style={{ fontSize: "3rem", fontWeight: 900, lineHeight: 1 }}>{daysLeft}</div>
                <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", opacity: 0.7, marginTop: 4 }}>Days Left</div>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 32, background: "#101319", borderRadius: 16, padding: "24px", marginBottom: 32, border: "1px solid rgba(255,255,255,0.09)" }}>
            <div style={{ textAlign: "center" }}><div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", color: "#9aa0ab" }}>Prize Pool</div><div style={{ fontSize: "2.2rem", fontWeight: 900, color: "#ff6a5f" }}>Coming Soon</div></div>
            <div style={{ width: 1, height: 40, background: "#101319" }} />
            <div style={{ textAlign: "center" }}><div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", color: "#9aa0ab" }}>Categories</div><div style={{ fontSize: "2.2rem", fontWeight: 900 }}>TBA</div></div>
            <div style={{ width: 1, height: 40, background: "#101319" }} />
            <div style={{ textAlign: "center" }}><div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", color: "#9aa0ab" }}>Eligible Venues</div><div style={{ fontSize: "2.2rem", fontWeight: 900 }}>{VENUES.length}</div></div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 24, marginBottom: 32 }}>
            <div style={{ background: "linear-gradient(135deg, #1a1a2e, #16213e)", borderRadius: 20, padding: "48px 32px", textAlign: "center", color: "#fff" }}>
              <div style={{ fontSize: "3rem", marginBottom: 16 }}></div>
              <div style={{ fontSize: "1.5rem", fontWeight: 900, marginBottom: 12 }}>Prizes Coming Soon</div>
              <p style={{ fontSize: "1rem", opacity: 0.7, maxWidth: 500, margin: "0 auto 24px" }}>We're finalising partnerships with major sponsors. Your games are already being tracked — when prizes launch, you'll be ready.</p>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.1)", padding: "12px 24px", borderRadius: 12, fontSize: "0.9rem", fontWeight: 600 }}>Download the WISA App to stay updated</div>
            </div>
          </div>

          <div style={{ background: "#101319", borderRadius: 20, padding: "36px", border: "1px solid rgba(255,255,255,0.09)" }}>
            <h3 style={{ fontSize: "1.2rem", fontWeight: 800, margin: "0 0 24px", textAlign: "center" }}>How It Works</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24 }}>
              {[
                { icon: "", title: "Play at Any WISA Venue", desc: "Stats tracked automatically at every WISA venue worldwide" },
                { icon: "", title: "Stats Sync Globally", desc: "All games, wins, and rankings sync across all WISA venues" },
                { icon: "", title: "Prize Leaderboard", desc: "Prizes coming soon — your games are already being tracked." },
              ].map((s, i) => (
                <div key={i} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "2.5rem", marginBottom: 12 }}>{s.icon}</div>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>{s.title}</div>
                  <div style={{ fontSize: "0.85rem", color: "#9aa0ab", lineHeight: 1.5 }}>{s.desc}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 24, background: "rgba(255,46,23,0.12)", borderRadius: 16, padding: "28px 32px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: "1.1rem" }}>Want to Sponsor a Prize? Get in Touch</div>
              <div style={{ fontSize: "0.85rem", color: "#9aa0ab" }}>Get your brand in front of thousands of active indoor sports players worldwide</div>
            </div>
            {/* 2026-05-31: was linking to wisasports.com home — useless for
                a prospective sponsor. mailto: opens their email client with
                subject/body pre-filled so they just hit send. */}
            <a href={'mailto:info@wildcatzindoorsports.com.au?subject=' + encodeURIComponent('Prize sponsorship enquiry — All4Indoor') + '&body=' + encodeURIComponent('Hi Jake,\n\nWe\'d like to chat about sponsoring a prize on the All4Indoor leaderboards.\n\n• Company / brand:\n• What we\'d like to sponsor (sport / leaderboard / event):\n• Budget range:\n• Best contact number:\n\nThanks,\n')}
              style={{ background: `linear-gradient(135deg, #ff2e17, #ff5a45)`, color: "#fff", padding: "12px 24px", borderRadius: 10, fontWeight: 700, fontSize: "0.85rem", textDecoration: "none", whiteSpace: "nowrap" }}>
              Register Interest →
            </a>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/* FOR VENUES — The killer sales page              */}
      {/* ═══════════════════════════════════════════════ */}
      {section === "for-venues" && (
        <section style={{ maxWidth: 1000, margin: "0 auto", padding: "40px 32px" }}>
          {/* Hero */}
          <div style={{ background: "linear-gradient(135deg, #1a1a2e, #0d4a42, #ff2e17)", borderRadius: 20, padding: "48px 40px", color: "#fff", marginBottom: 32, textAlign: "center", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: -60, right: -60, width: 250, height: 250, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
            <div style={{ position: "relative" }}>
              <div style={{ fontSize: "0.78rem", fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", opacity: 0.6, marginBottom: 16 }}>For Venue Owners</div>
              <h2 style={{ fontSize: "2rem", fontWeight: 900, margin: "0 0 12px" }}>Your Players Are Already Asking For WISA</h2>
              <p style={{ fontSize: "1.1rem", opacity: 0.7, maxWidth: 600, margin: "0 auto" }}>Players are choosing WISA venues over yours because their games count. Their stats are tracked. They earn badges and climb leaderboards. Don't lose them.</p>
            </div>
          </div>

          {/* Player demand */}
          <div style={{ background: "#101319", borderRadius: 16, padding: "32px", border: "1px solid rgba(255,255,255,0.09)", marginBottom: 24 }}>
            <h3 style={{ fontSize: "1.2rem", fontWeight: 800, margin: "0 0 20px" }}> Player Demand Near You</h3>
            <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: "0 0 20px" }}>These venues have players actively requesting WISA. Once they hit 50 requests, we send the venue an email.</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              {NON_WISA_VENUES.sort((a, b) => b.requests - a.requests).map(v => (
                <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px", background: "#0b0e13", borderRadius: 12, border: v.requests >= 50 ? "2px solid #f59e0b" : "1px solid rgba(255,255,255,0.09)" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>{v.name}</div>
                    <div style={{ fontSize: "0.78rem", color: "#9aa0ab" }}>{v.city}, {v.state || v.country}</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "1.5rem", fontWeight: 900, color: v.requests >= 50 ? "#e74c3c" : P }}>{v.requests}</div>
                    <div style={{ fontSize: "0.68rem", color: "#9aa0ab" }}>requests</div>
                  </div>
                  <div style={{ width: 60 }}>
                    <div style={{ height: 6, borderRadius: 3, background: "#e0e0ea" }}>
                      <div style={{ height: 6, borderRadius: 3, background: v.requests >= 50 ? "#e74c3c" : P, width: `${Math.min(100, (v.requests / 50) * 100)}%` }} />
                    </div>
                    <div style={{ fontSize: "0.6rem", color: "#9aa0ab", textAlign: "center", marginTop: 2 }}>{v.requests >= 50 ? "NOTIFIED" : `${50 - v.requests} to go`}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* What WISA gives you */}
          <div style={{ background: "#101319", borderRadius: 16, padding: "32px", border: "1px solid rgba(255,255,255,0.09)", marginBottom: 24 }}>
            <h3 style={{ fontSize: "1.2rem", fontWeight: 800, margin: "0 0 24px", textAlign: "center" }}>What WISA Gives Your Venue</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20 }}>
              {[
                { icon: "", title: "34% More Bookings", desc: "WISA venues see 34% more bookings from players who find them on all4indoor" },
                { icon: "", title: "Player Retention", desc: "Players stay when their stats matter. No one wants to lose their season history." },
                { icon: "", title: "Prize Eligibility", desc: "Your players will be eligible for prizes when they launch. That's a selling point." },
                { icon: "", title: "Full Analytics", desc: "See which sports are growing, peak hours, player demographics, and court utilization" },
                { icon: "", title: "Global Discovery", desc: "Players worldwide find your venue on all4indoor. Free marketing that never stops." },
                { icon: "", title: "Revenue Tools", desc: "Online bookings, membership management, payment processing — all built in." },
              ].map((f, i) => (
                <div key={i} style={{ textAlign: "center", padding: "16px" }}>
                  <div style={{ fontSize: "2rem", marginBottom: 8 }}>{f.icon}</div>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{f.title}</div>
                  <div style={{ fontSize: "0.82rem", color: "#9aa0ab", lineHeight: 1.5 }}>{f.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* The pressure section — what you're losing */}
          <div style={{ background: "rgba(239,68,68,0.1)", borderRadius: 16, padding: "32px", border: "1px solid rgba(239,68,68,0.3)", marginBottom: 24 }}>
            <h3 style={{ fontSize: "1.2rem", fontWeight: 800, margin: "0 0 16px", color: "#991b1b" }}> What You're Losing Without WISA</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              {[
                { stat: "47", label: "players near your venue searched all4indoor this week", icon: "" },
                { stat: "34%", label: "of players choose WISA venues over non-WISA when both are nearby", icon: "" },
                { stat: "2.3x", label: "higher retention rate at WISA venues vs industry average", icon: "" },
                { stat: "Prizes", label: "coming soon — your players are missing out", icon: "" },
              ].map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, background: "#101319", borderRadius: 12, padding: "16px" }}>
                  <div style={{ fontSize: "1.5rem" }}>{s.icon}</div>
                  <div>
                    <span style={{ fontWeight: 900, fontSize: "1.3rem", color: "#991b1b" }}>{s.stat}</span>
                    <span style={{ fontSize: "0.85rem", color: "#c3c7ce", marginLeft: 6 }}>{s.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          {/* How Far Would You Drive? */}
          <div style={{ background: "#101319", borderRadius: 16, padding: "32px", border: "1px solid rgba(255,255,255,0.09)", marginBottom: 24 }}>
            <h3 style={{ fontSize: "1.2rem", fontWeight: 800, margin: "0 0 8px" }}> "How Far Would You Drive?"</h3>
            <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: "0 0 20px" }}>Players are already driving past non-WISA venues to get to WISA ones.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                { player: "Emma W.", from: "Drouin (non-WISA venue)", to: "Wildcatz Indoor Sports", distance: "22 min drive", reason: "My stats count and I'm going for the Iron Player badge" },
                { player: "Tyler M.", from: "Local centre in Aitkenvale", to: "Wildcatz Indoor Sports", distance: "15 min drive", reason: "Switched because I want my games on the global leaderboard" },
                { player: "Marcus T.", from: "Gym basketball court", to: "Wildcatz Indoor Sports", distance: "20 min drive", reason: "I'm competing for the gear package — every game matters" },
              ].map((q, i) => (
                <div key={i} style={{ background: "#0b0e13", borderRadius: 12, padding: "16px 20px", border: "1px solid rgba(255,255,255,0.09)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: `linear-gradient(135deg, #ff2e17, #ff5a45)`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "0.68rem", fontWeight: 700 }}>{q.player.split(" ").map(n => n[0]).join("")}</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "0.88rem" }}>{q.player}</div>
                      <div style={{ fontSize: "0.75rem", color: "#9aa0ab" }}>Drives past: {q.from} → {q.to}</div>
                    </div>
                    <div style={{ marginLeft: "auto", background: "rgba(245,158,11,0.1)", color: "#fbbf24", fontSize: "0.72rem", fontWeight: 700, padding: "4px 10px", borderRadius: 8 }}>
                      +{q.distance}
                    </div>
                  </div>
                  <div style={{ fontSize: "0.85rem", color: "#c3c7ce", fontStyle: "italic" }}>"{q.reason}"</div>
                </div>
              ))}
            </div>
          </div>

          {/* 2026-07-27 (Jake): was "Venue Success Stories — Real results from
              venues that joined WISA", a before/after table of business metrics
              nobody could source, from a network with one venue in it. This is
              what is actually on the system, which is checkable and is the
              stronger argument regardless. */}
          <div style={{ marginBottom: 32 }}>
            <h3 style={{ fontSize: "1.2rem", fontWeight: 800, margin: "0 0 8px" }}>What's on the system today</h3>
            <p style={{ color: "#9aa0ab", fontSize: "0.9rem", margin: "0 0 20px" }}>One venue live. Everything below came off it.</p>
            <div style={{ background: "#0b0e13", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 16, padding: "26px 28px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(0,230,118,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Shield size={22} color="#00e676" />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: "1.02rem", color: "#f4f5f7" }}>{VENUE_ON_SYSTEM.venue}</div>
                  <div style={{ fontSize: "0.8rem", color: "#9aa0ab" }}>{VENUE_ON_SYSTEM.city} — on WISA since {VENUE_ON_SYSTEM.since}</div>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 18 }}>
                {VENUE_ON_SYSTEM.facts.map((f, j) => (
                  <div key={j}>
                    <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: "1.6rem", fontWeight: 900, color: "#00e676", lineHeight: 1.1 }}>{f.value}</div>
                    <div style={{ fontSize: "0.82rem", color: "#9aa0ab", marginTop: 3 }}>{f.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 20, paddingTop: 18, borderTop: "1px solid rgba(255,255,255,0.08)", fontSize: "0.88rem", color: "#c3c7ce", lineHeight: 1.55 }}>
                {VENUE_ON_SYSTEM.note}
              </div>
            </div>
          </div>

          {/* 2026-07-27 (Jake): removed. This said "Players Lost to WISA
              Venues This Month — estimated players in your area who chose a
              WISA venue over a non-WISA venue" over three invented figures
              (342 / 278 / 456, all Townsville, all Wildcatz) and closed with
              "That's real revenue walking out the door every month."
              Nothing measures that, the three cards were the same city three
              times, and telling a prospect they are losing a number you made
              up is the fastest way to lose them when they check. */}

          {/* CTA — 2026-06-02: dropped "It's Free to List" (WISA is a
              licensed SaaS, not a free directory). Also handle singular/
              plural so we don't say "1 venues across 1 countries" while
              there's only one venue live. */}
          <div style={{ background: `linear-gradient(135deg, #ff2e17, #ff5a45)`, borderRadius: 20, padding: "48px 40px", textAlign: "center", color: "#fff" }}>
            <h2 style={{ fontSize: "1.8rem", fontWeight: 900, margin: "0 0 12px" }}>Get WISA for Your Venue</h2>
            <p style={{ opacity: 0.8, margin: "0 0 28px", maxWidth: 500, marginLeft: "auto", marginRight: "auto" }}>
              {VENUES.length === 1
                ? `Join the network — players are waiting. Don't let them drive past you.`
                : `Join ${VENUES.length} venues across ${ALL_COUNTRIES.length} ${ALL_COUNTRIES.length === 1 ? 'country' : 'countries'}. Players are waiting — don't let them drive past you.`}
            </p>
            <a href="https://www.wisasports.com" target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#101319", color: "#ff6a5f", padding: "16px 40px", borderRadius: 14, fontSize: "1.1rem", fontWeight: 800, textDecoration: "none" }}>
              Get Started — Talk to Us <ArrowRight size={20} />
            </a>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/* PLAYER PROFILE MODAL — opens when any player name is clicked */}
      {/* ═══════════════════════════════════════════════ */}
      {selectedPlayerId && livePlayerIndex && (() => {
        const player = livePlayerIndex[selectedPlayerId];
        if (!player) return null;
        const eloRow = (liveElo || []).find(e => e.id === selectedPlayerId);
        const eloRank = eloRow ? (liveElo || []).findIndex(e => e.id === selectedPlayerId) + 1 : null;
        const ptsRow = (liveRankedPlayers || []).find(p => p.id === selectedPlayerId);
        const ptsRank = ptsRow ? (liveRankedPlayers || []).findIndex(p => p.id === selectedPlayerId) + 1 : null;
        const detail = livePlayerDetail || { teams: [], badges: [], recentMatches: [] };
        // 2026-05-27: derive games/wins/winRate from the player's actual
        // match list (livePlayerDetail.allMatches) instead of ptsRow.
        // ptsRow only exists for the top 50 ranked players — for everyone
        // else it's undefined, which made the Wins tile read 0 even when the
        // recent-match list clearly showed W badges. allMatches comes from
        // the same player_match_confirmations + match_attendance dedup we use
        // to render the match cards, so the numbers stay consistent.
        const matchStats = (() => {
          const ms = detail.allMatches || [];
          if (!ms.length) {
            return ptsRow ? { games: ptsRow.games, wins: ptsRow.wins, winRate: ptsRow.games > 0 ? Math.round((ptsRow.wins / ptsRow.games) * 1000) / 10 : null } : { games: 0, wins: 0, winRate: null };
          }
          const games = ms.length;
          const wins  = ms.filter(m => m.result === 'W').length;
          return { games, wins, winRate: games > 0 ? Math.round((wins / games) * 1000) / 10 : null };
        })();
        const winRate = matchStats.winRate;
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}
            onClick={() => setSelectedPlayerId(null)}>
            <div style={{ background: "#101319", borderRadius: 20, maxWidth: 720, width: "100%", maxHeight: "90vh", overflow: "auto" }} onClick={e => e.stopPropagation()}>
              {/* Hero header */}
              <div style={{ background: "linear-gradient(135deg, #1a1a2e, #0d4a42, #ff2e17)", color: "#fff", padding: "28px 32px", position: "relative" }}>
                <button onClick={() => setSelectedPlayerId(null)} style={{ position: "absolute", top: 16, right: 16, background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", width: 32, height: 32, borderRadius: "50%", cursor: "pointer", fontSize: "1.1rem" }} aria-label="Close"><X size={16} /></button>
                <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                  {(() => {
                    // 2026-05-31: sport-themed avatar gradient — cricket/
                    // netball/soccer/volleyball each get their own colour
                    // so the same player feels distinct from page to page.
                    const sp = primarySportSlug(player.stats, player.sports);
                    const grad = (sp && SPORT_GRADIENTS[sp]) || `linear-gradient(135deg, #ff2e17, #ff5a45)`;
                    return <div style={{ width: 76, height: 76, borderRadius: "50%", background: grad, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: "1.6rem", border: "3px solid rgba(255,255,255,0.3)" }}>{player.avatar}</div>;
                  })()}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h2 style={{ fontSize: "1.55rem", fontWeight: 900, margin: "0 0 4px" }}>{player.name}</h2>
                    {/* 2026-05-31: auto-derived player title chip — adds a
                        bit of personality to each profile. Threshold-based
                        on career stats (see playerTitle helper). */}
                    {(() => {
                      const t = playerTitle(player.stats, matchStats, eloRow);
                      if (!t) return null;
                      return (
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", background: "rgba(251,191,36,0.18)", border: "1px solid rgba(251,191,36,0.4)", borderRadius: 999, fontSize: "0.74rem", fontWeight: 700, color: "#fde68a", marginBottom: 4 }}>
                          <span>{t.icon}</span><span style={{ letterSpacing: 0.5 }}>{t.label}</span>
                        </div>
                      );
                    })()}
                    <div style={{ opacity: 0.75, fontSize: "0.9rem" }}>Wildcatz Indoor Sports{player.sports.length ? ' · ' + player.sports.join(', ') : ''}</div>
                    {/* 2026-05-31: recent form dots — last 5 results
                        newest-first. Tiny but signals "active player" at a
                        glance, the way Strava / 5stars do. */}
                    {(() => {
                      const last = (detail.allMatches || []).slice(0, 5);
                      if (!last.length) return null;
                      const colorOf = (r) => r === 'W' ? '#10b981' : r === 'L' ? '#ef4444' : '#a3a3a3';
                      return (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
                          <span style={{ fontSize: "0.6rem", letterSpacing: 2, fontWeight: 800, opacity: 0.6, marginRight: 2 }}>FORM</span>
                          {last.map((m, i) => (
                            <span key={i} title={`${m.result} ${m.myScore}–${m.oppScore} vs ${m.opp || '—'}`} style={{ width: 10, height: 10, borderRadius: "50%", background: colorOf(m.result), border: "1px solid rgba(255,255,255,0.4)" }} />
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                {/* Rank chips */}
                {(eloRank || ptsRank) && (
                  <div style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
                    {eloRank && <div style={{ background: "rgba(251,191,36,0.18)", border: "1px solid rgba(251,191,36,0.35)", borderRadius: 8, padding: "6px 12px", fontSize: "0.8rem" }}> <strong>{eloRow.elo} ELO</strong> · Rank #{eloRank}</div>}
                    {ptsRank && <div style={{ background: "rgba(52,211,153,0.15)", border: "1px solid rgba(52,211,153,0.35)", borderRadius: 8, padding: "6px 12px", fontSize: "0.8rem" }}> <strong>{ptsRow.pts} pts</strong> · Rank #{ptsRank}</div>}
                  </div>
                )}
                {/* 2026-05-31: inline "Vs You" panel. Shows the head-to-head
                    record between the signed-in user and this profile as the
                    first thing the eye lands on — no button-tap needed. Click
                    the panel to jump straight to the full H2H view. */}
                {authUser && authUser.id !== selectedPlayerId && liveVsYou && liveVsYou.total > 0 && (
                  <div
                    onClick={() => {
                      setCompareIds({ a: authUser.id, b: selectedPlayerId, c: null });
                      setSelectedPlayerId(null);
                      setSection('h2h');
                    }}
                    style={{ marginTop: 16, padding: "12px 16px", background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.18)"}
                    onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.12)"}
                  >
                    <div>
                      <div style={{ fontSize: "0.65rem", letterSpacing: 2, fontWeight: 700, opacity: 0.7 }}>VS YOU</div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 4 }}>
                        <span style={{ fontSize: "1.6rem", fontWeight: 900, color: liveVsYou.record.wins > liveVsYou.record.losses ? "#10b981" : "#fff" }}>{liveVsYou.record.wins}</span>
                        <span style={{ fontSize: "0.85rem", opacity: 0.7 }}>W</span>
                        <span style={{ fontSize: "0.65rem", opacity: 0.5, margin: "0 4px" }}>·</span>
                        <span style={{ fontSize: "1.6rem", fontWeight: 900, color: liveVsYou.record.losses > liveVsYou.record.wins ? "#ef4444" : "#fff" }}>{liveVsYou.record.losses}</span>
                        <span style={{ fontSize: "0.85rem", opacity: 0.7 }}>L</span>
                        {liveVsYou.record.draws > 0 && (
                          <>
                            <span style={{ fontSize: "0.65rem", opacity: 0.5, margin: "0 4px" }}>·</span>
                            <span style={{ fontSize: "1.2rem", fontWeight: 700, color: "#f59e0b" }}>{liveVsYou.record.draws}</span>
                            <span style={{ fontSize: "0.75rem", opacity: 0.7 }}>D</span>
                          </>
                        )}
                      </div>
                      <div style={{ fontSize: "0.72rem", opacity: 0.65, marginTop: 4 }}>
                        {liveVsYou.total} meeting{liveVsYou.total === 1 ? '' : 's'}{liveVsYou.last ? ` · last: ${liveVsYou.last.outcome === 'W' ? 'you won' : liveVsYou.last.outcome === 'L' ? 'you lost' : 'drew'} ${liveVsYou.last.youScore}-${liveVsYou.last.themScore} (${liveVsYou.last.when})` : ''}
                      </div>
                    </div>
                    <div style={{ fontSize: "1.4rem", opacity: 0.5 }}>›</div>
                  </div>
                )}

                {/* Action row — Compare + Share */}
                <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                  {authUser && authUser.id !== selectedPlayerId && (
                    <button onClick={() => {
                      setCompareIds({ a: authUser.id, b: selectedPlayerId, c: null });
                      setSelectedPlayerId(null);
                      setSection('h2h');
                    }}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 10, background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", fontSize: "0.8rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", transition: "background 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.28)"}
                      onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.18)"}>
                       Compare to me
                    </button>
                  )}
                  <button onClick={async () => {
                    const url = `${window.location.origin}/?player=${selectedPlayerId}`;
                    const text = `Check out ${player.name} on All4Indoor — ${player.stats.games} games${winRate != null ? `, ${winRate}% win rate` : ''}.`;
                    if (navigator.share) {
                      try { await navigator.share({ title: `${player.name} on All4Indoor`, text, url }); return; } catch {}
                    }
                    try {
                      await navigator.clipboard.writeText(url);
                      alert('Profile link copied!');
                    } catch {
                      window.prompt('Copy this link:', url);
                    }
                  }}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 10, background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", fontSize: "0.8rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                    <Share2 size={14} /> Share
                  </button>
                  {/* Trading-card share — the centrepiece. Renders a 1080x1080
                      SVG of the player's headline stats and offers PNG export
                      via canvas + native share. Pulls from the same detail
                      object the rest of the modal uses, so no extra fetch. */}
                  <button onClick={() => setTradingCardPlayer({
                    id: selectedPlayerId,
                    name: player.name,
                    avatar: player.avatar,
                    games: player.stats.games,
                    winRate,
                    eloRank, ptsRank,
                    elo: eloRow?.elo,
                    pts: ptsRow?.pts,
                    careerContribution: detail.careerContribution,
                    avgContribution: detail.avgContribution,
                    cricketGamesPlayed: detail.cricketGamesPlayed,
                    skillBadges: detail.skillBadges || [],
                    sparkline: detail.sparkline,
                    sports: player.sports || [],
                    careerRuns: detail.career?.runs,
                    careerWickets: detail.career?.wickets,
                  })}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 10, background: "linear-gradient(135deg, #fbbf24, #f97316)", border: "1px solid rgba(255,255,255,0.4)", color: "#f4f5f7", fontSize: "0.8rem", fontWeight: 800, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 2px 8px rgba(251,191,36,0.4)" }}>
                     Player Card
                  </button>
                </div>
              </div>

              {/* Stat tiles */}
              <div style={{ padding: "20px 24px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 24 }}>
                  <div style={{ background: "#0b0e13", borderRadius: 10, padding: "14px 8px", textAlign: "center" }}>
                    <div style={{ fontSize: "1.4rem", fontWeight: 900 }}>{matchStats.games || player.stats.games || 0}</div>
                    <div style={{ fontSize: "0.65rem", color: "#9aa0ab", letterSpacing: 1, textTransform: "uppercase" }}>Games</div>
                  </div>
                  <div style={{ background: "#0b0e13", borderRadius: 10, padding: "14px 8px", textAlign: "center" }}>
                    <div style={{ fontSize: "1.4rem", fontWeight: 900, color: "#10b981" }}>{matchStats.wins}</div>
                    <div style={{ fontSize: "0.65rem", color: "#9aa0ab", letterSpacing: 1, textTransform: "uppercase" }}>Wins</div>
                  </div>
                  <div style={{ background: "#0b0e13", borderRadius: 10, padding: "14px 8px", textAlign: "center" }}>
                    <div style={{ fontSize: "1.4rem", fontWeight: 900, color: "#ff6a5f" }}>{winRate != null ? winRate + '%' : '—'}</div>
                    <div style={{ fontSize: "0.65rem", color: "#9aa0ab", letterSpacing: 1, textTransform: "uppercase" }}>Win Rate</div>
                  </div>
                  <div style={{ background: "#0b0e13", borderRadius: 10, padding: "14px 8px", textAlign: "center" }}>
                    <div style={{ fontSize: "1.4rem", fontWeight: 900, color: "#fbbf24" }}>{detail.badges.length}</div>
                    <div style={{ fontSize: "0.65rem", color: "#9aa0ab", letterSpacing: 1, textTransform: "uppercase" }}>Badges</div>
                  </div>
                </div>

                {/* WEEKLY PERCENTILE — find this player's position in the
                    last-14-days cricket leaderboard. Pure derived render
                    using liveSportLeaderboards.cricketAll (full ranked list,
                    not just top 10). Banner only shows if the player
                    actually played in the window. */}
                {(() => {
                  const all = liveSportLeaderboards?.cricketAll || [];
                  if (!all.length) return null;
                  const myKey = (player.name || '').trim().toLowerCase();
                  const idx = all.findIndex(p => (p.name || '').trim().toLowerCase() === myKey);
                  if (idx < 0) return null;
                  const total = all.length;
                  const me = all[idx];
                  const rank = idx + 1;
                  const percentile = Math.max(1, Math.round((rank / total) * 100));
                  const tier =
                    percentile <= 2  ? { label: 'ELITE',     color: '#fbbf24', bg: 'linear-gradient(135deg, #fef3c7, #fbbf24)', textColor:'#92400e' } :
                    percentile <= 10 ? { label: 'TOP 10%',   color: '#a855f7', bg: 'linear-gradient(135deg, #faf5ff, #ddd6fe)', textColor:'#6b21a8' } :
                    percentile <= 25 ? { label: 'TOP 25%',   color: '#10b981', bg: 'linear-gradient(135deg, #ecfdf5, #a7f3d0)', textColor:'#065f46' } :
                    percentile <= 50 ? { label: 'ABOVE AVG', color: "#ff6a5f",         bg: 'linear-gradient(135deg, #eff6ff, #bfdbfe)', textColor:'#1e40af' } :
                                       { label: 'CLIMBING',  color: '#6b7280', bg: '#f3f4f6', textColor:'#374151' };
                  return (
                    <div style={{ marginBottom: 24, padding: '14px 16px', borderRadius: 12, background: tier.bg, border: `1.5px solid ${tier.color}40`, display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 50, height: 50, borderRadius: 12, background: tier.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '0.95rem', flexShrink: 0 }}>
                        TOP {percentile}%
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.6rem', letterSpacing: 1.5, color: tier.textColor, fontWeight: 800, textTransform: 'uppercase', marginBottom: 2 }}>
                          {tier.label} · last 14 days
                        </div>
                        <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#3b3b4f' }}>
                          #{rank} of {total} cricketers
                        </div>
                        <div style={{ fontSize: '0.72rem', color: tier.textColor, marginTop: 1, opacity: 0.85 }}>
                          {me.contribution >= 0 ? '+' : ''}{me.contribution} contribution · {me.games} game{me.games === 1 ? '' : 's'}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Sport stats. 2026-05-31:
                    - Cricket runs/wickets sourced from detail.career (computed
                      live from balls_data) rather than player_stats, because
                      cricket lifetime stats writeback hasn't always created
                      player_stats rows — a player can have +39 contribution
                      on the leaderboard but 0 runs in the chip strip.
                    - Goals split by sport (soccer / netball) instead of lumped
                      together so a player who plays both can see them
                      separately. */}
                {(() => {
                  const cricketRuns     = detail.career?.runs    ?? player.stats.runs    ?? 0;
                  const cricketWickets  = detail.career?.wickets ?? player.stats.wickets ?? 0;
                  const soccerGoals     = player.stats.goalsBySport?.soccer  || 0;
                  const netballGoals    = player.stats.goalsBySport?.netball || 0;
                  const otherGoals      = (player.stats.goals || 0) - soccerGoals - netballGoals;
                  // 2026-05-31: split hat-tricks the same way goals are
                  // split — soccer-only and netball-only chips instead of
                  // one lumped count. "Soccer" includes futsal slug too.
                  const soccerHats      = (player.stats.hatTricksBySport?.soccer  || 0) + (player.stats.hatTricksBySport?.futsal || 0);
                  const netballHats     = player.stats.hatTricksBySport?.netball || 0;
                  const cricketHats     = (player.stats.hatTricksBySport?.cricket || 0) + (player.stats.hatTricksBySport?.['indoor-cricket'] || 0);
                  const otherHats       = (player.stats.hatTricks || 0) - soccerHats - netballHats - cricketHats;
                  const hasAnyStat = cricketRuns > 0 || cricketWickets > 0 || soccerGoals > 0 || netballGoals > 0 || otherGoals > 0 || (detail.careerContribution != null);
                  if (!hasAnyStat) return null;
                  return (
                    <div style={{ marginBottom: 24 }}>
                      <div style={{ fontSize: "0.7rem", letterSpacing: 2, color: "#9aa0ab", fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}>Career Stats</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {/* Career contribution — the killer Spawtz-era stat,
                            surfaced first. Gold accent so it grabs the eye. */}
                        {detail.careerContribution != null && (
                          <div style={{ background: "rgba(245,158,11,0.1)", border: "1.5px solid #fbbf24", color: "#fbbf24", padding: "8px 14px", borderRadius: 10, fontSize: "0.88rem", fontWeight: 800 }}>
                             {detail.careerContribution >= 0 ? '+' : ''}{detail.careerContribution} contribution
                            {detail.avgContribution != null && <span style={{ marginLeft:6, fontSize:'0.72rem', fontWeight:600, color:'#92400e99' }}>· {detail.avgContribution >= 0 ? '+' : ''}{detail.avgContribution}/game</span>}
                          </div>
                        )}
                        {cricketRuns > 0 && <div style={{ background: `#ff2e1710`, color: "#ff6a5f", padding: "8px 14px", borderRadius: 10, fontSize: "0.85rem", fontWeight: 600 }}> {cricketRuns} runs</div>}
                        {cricketWickets > 0 && <div style={{ background: "#e74c3c10", color: "#e74c3c", padding: "8px 14px", borderRadius: 10, fontSize: "0.85rem", fontWeight: 600 }}> {cricketWickets} wkts</div>}
                        {soccerGoals > 0 && <div style={{ background: "#3b82f610", color: "#3b82f6", padding: "8px 14px", borderRadius: 10, fontSize: "0.85rem", fontWeight: 600 }}> {soccerGoals} soccer goals</div>}
                        {netballGoals > 0 && <div style={{ background: "#e8439310", color: "#e84393", padding: "8px 14px", borderRadius: 10, fontSize: "0.85rem", fontWeight: 600 }}> {netballGoals} netball goals</div>}
                        {otherGoals > 0 && <div style={{ background: "#3b82f610", color: "#3b82f6", padding: "8px 14px", borderRadius: 10, fontSize: "0.85rem", fontWeight: 600 }}> {otherGoals} goals</div>}
                        {player.stats.sixes > 0 && <div style={{ background: "#fbbf2410", color: "#fbbf24", padding: "8px 14px", borderRadius: 10, fontSize: "0.85rem", fontWeight: 600 }}> {player.stats.sixes}×7s</div>}
                        {soccerHats  > 0 && <div style={{ background: "#3b82f610", color: "#3b82f6", padding: "8px 14px", borderRadius: 10, fontSize: "0.85rem", fontWeight: 600 }}> {soccerHats} soccer hat-trick{soccerHats === 1 ? '' : 's'}</div>}
                        {netballHats > 0 && <div style={{ background: "#e8439310", color: "#e84393", padding: "8px 14px", borderRadius: 10, fontSize: "0.85rem", fontWeight: 600 }}> {netballHats} netball hat-trick{netballHats === 1 ? '' : 's'}</div>}
                        {otherHats   > 0 && <div style={{ background: "#a855f710", color: "#a855f7", padding: "8px 14px", borderRadius: 10, fontSize: "0.85rem", fontWeight: 600 }}> {otherHats} hat-trick{otherHats === 1 ? '' : 's'}</div>}
                        {player.stats.highestScore > 0 && <div style={{ background: "#10b98110", color: "#10b981", padding: "8px 14px", borderRadius: 10, fontSize: "0.85rem", fontWeight: 600 }}> HS {player.stats.highestScore}</div>}
                      </div>
                    </div>
                  );
                })()}

                {/* Teams */}
                {detail.teams.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: "0.7rem", letterSpacing: 2, color: "#9aa0ab", fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}>Plays For</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {detail.teams.map(t => (
                        <div key={t.id}
                          onClick={() => { setSelectedPlayerId(null); setSelectedTeamId(t.id); }}
                          /* 2026-08-13 (Jake): "can barely read the white" — these hover
                             colours were pre-redesign LIGHT theme leftovers: one hover and
                             the chip stayed #fafafe with near-white text forever. Dark in,
                             dark out.

                             2026-08-20: that fix landed HERE ONLY, and four more of the
                             identical leftover sat further down this same modal -- both
                             match lists restored #fafafe on mouse-leave, so every row the
                             cursor crossed went white and stayed white with white text on
                             it, and two neutral labels were #3b3b4f on #0b0e13. Jake:
                             "I also thought this was actually fixed."

                             check-dark-contrast now fails the build on any of the three
                             shapes, so the next redesign cannot leave stragglers behind. */
                          onMouseEnter={e => { e.currentTarget.style.background = '#151a22'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.22)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = '#0b0e13'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; }}
                          style={{ background: "#0b0e13", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 10, padding: "8px 14px", fontSize: "0.85rem", cursor: "pointer", transition: "all 0.15s" }}>
                          {sportEmoji[t.sport] || ''} <strong>{t.name}</strong> <span style={{ color: "#9aa0ab" }}>· {t.comp}</span> <span style={{ color: "#ff6a5f", fontWeight: 700, marginLeft: 4 }}>›</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Badges — 2026-06-02: dedupe repeat earns. Lilly Stevens
                    had 9 OG rows showing as 9 separate chips because every"first match" type achievement gets re-inserted per
                    qualifying match. Group by achievement_name and show a"×N" count chip when earned multiple times. Total
                    count in the heading still reflects raw earns. */}
                {detail.badges.length > 0 && (() => {
                  const grouped = [];
                  const seenIdx = new Map();
                  detail.badges.forEach(b => {
                    const key = b.achievement_name || b.achievement_key || 'unknown';
                    const at = seenIdx.get(key);
                    if (at != null) {
                      grouped[at].count += 1;
                    } else {
                      seenIdx.set(key, grouped.length);
                      grouped.push({ ...b, count: 1 });
                    }
                  });
                  return (
                    <div style={{ marginBottom: 24 }}>
                      <div style={{ fontSize: "0.7rem", letterSpacing: 2, color: "#9aa0ab", fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}>Achievements ({grouped.length}{detail.badges.length !== grouped.length ? ` · ${detail.badges.length} earns` : ''})</div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {grouped.slice(0, 12).map((b, i) => {
                          const rarityColor = b.rarity === 'legendary' ? '#fbbf24' : b.rarity === 'rare' ? '#a855f7' : b.rarity === 'uncommon' ? '#3b82f6' : '#10b981';
                          return (
                            <div key={i} style={{ background: `${rarityColor}10`, border: `1px solid ${rarityColor}40`, borderRadius: 10, padding: "6px 12px", display: "flex", alignItems: "center", gap: 6, fontSize: "0.8rem" }}>
                              <span>{b.emoji || ''}</span> <strong>{b.achievement_name}</strong>
                              {b.count > 1 && (
                                <span style={{ fontSize: "0.68rem", fontWeight: 800, color: rarityColor, background: `${rarityColor}25`, padding: "1px 6px", borderRadius: 6 }}>×{b.count}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* CAREER HIGHS — personal records. Spawtz-quality "career
                    PBs" panel. Surfaces the headline numbers players brag
                    about: biggest contribution, top batting score, peak
                    wickets in a single match. */}
                {detail.bestContribution != null && detail.cricketGamesPlayed > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: "0.7rem", letterSpacing: 2, color: "#9aa0ab", fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}> Career Highs</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(95px, 1fr))", gap: 8 }}>
                      <div style={{ background: "rgba(245,158,11,0.1)", border: "1.5px solid #fbbf24", borderRadius: 10, padding: "10px 8px", textAlign: "center" }}>
                        <div style={{ fontSize: "1.3rem", fontWeight: 900, color: "#fbbf24", lineHeight: 1 }}>
                          {detail.bestContribution >= 0 ? '+' : ''}{detail.bestContribution}
                        </div>
                        <div style={{ fontSize: "0.55rem", color: "#fbbf24", letterSpacing: 1, marginTop: 4, textTransform: "uppercase", fontWeight: 700 }}>Best contrib</div>
                      </div>
                      {detail.mostRunsInMatch > 0 && (
                        <div style={{ background: "#0b0e13", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 10, padding: "10px 8px", textAlign: "center" }}>
                          <div style={{ fontSize: "1.3rem", fontWeight: 900, color: "#ff6a5f", lineHeight: 1 }}>{detail.mostRunsInMatch}</div>
                          <div style={{ fontSize: "0.55rem", color: "#9aa0ab", letterSpacing: 1, marginTop: 4, textTransform: "uppercase", fontWeight: 700 }}>Top runs</div>
                        </div>
                      )}
                      {detail.mostWicketsInMatch > 0 && (
                        <div style={{ background: "#0b0e13", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 10, padding: "10px 8px", textAlign: "center" }}>
                          <div style={{ fontSize: "1.3rem", fontWeight: 900, color: "#e74c3c", lineHeight: 1 }}>{detail.mostWicketsInMatch}</div>
                          <div style={{ fontSize: "0.55rem", color: "#9aa0ab", letterSpacing: 1, marginTop: 4, textTransform: "uppercase", fontWeight: 700 }}>Top wkts</div>
                        </div>
                      )}
                      <div style={{ background: "#0b0e13", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 10, padding: "10px 8px", textAlign: "center" }}>
                        <div style={{ fontSize: "1.3rem", fontWeight: 900, color: "#e8eaee", lineHeight: 1 }}>{detail.cricketGamesPlayed}</div>
                        <div style={{ fontSize: "0.55rem", color: "#9aa0ab", letterSpacing: 1, marginTop: 4, textTransform: "uppercase", fontWeight: 700 }}>Cricket gms</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ─── PHASE 8: QUIRKY STATS ──────────────────────────── */}

                {/* FUN FACT one-liner — picks a random sting from the
                    player's stats. Group-chat-screenshot bait. */}
                {detail.funFact && (
                  <div style={{ marginBottom: 16, padding: '12px 14px', borderRadius: 10, background: 'linear-gradient(135deg, #fef3c7, #fed7aa)', border: '1.5px solid #fbbf24', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontSize: '1.4rem', flexShrink: 0 }}></div>
                    <div style={{ flex: 1, fontSize: '0.82rem', color: '#78350f', fontWeight: 600, lineHeight: 1.4 }}>{detail.funFact}</div>
                  </div>
                )}

                {/* STREAK + CLUTCH chips — sit side by side */}
                {(detail.streakKind || detail.clutchAvg != null) && (
                  <div style={{ marginBottom: 18, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                    {detail.streakKind && (
                      <div style={{ padding: '12px 14px', borderRadius: 10, border: `1.5px solid ${detail.streakKind === 'hot' ? '#10b981' : '#3b82f6'}40`, background: detail.streakKind === 'hot' ? 'rgba(16,185,129,0.06)' : 'rgba(59,130,246,0.06)' }}>
                        <div style={{ fontSize: '0.6rem', letterSpacing: 1.5, color: detail.streakKind === 'hot' ? '#065f46' : '#1e40af', fontWeight: 800, textTransform: 'uppercase', marginBottom: 2 }}>
                          {detail.streakKind === 'hot' ? ' Hot streak' : ' Cold streak'}
                        </div>
                        <div style={{ fontSize: '1.4rem', fontWeight: 900, color: detail.streakKind === 'hot' ? '#10b981' : '#3b82f6', lineHeight: 1 }}>
                          {detail.streakCount} game{detail.streakCount === 1 ? '' : 's'}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#9aa0ab', marginTop: 4 }}>
                          {detail.streakKind === 'hot' ? 'in a row in the green' : 'in a row in the red'}
                        </div>
                      </div>
                    )}
                    {detail.clutchAvg != null && (
                      <div style={{ padding: '12px 14px', borderRadius: 10, border: '1.5px solid #a855f740', background: 'rgba(168,85,247,0.06)' }}>
                        <div style={{ fontSize: '0.6rem', letterSpacing: 1.5, color: '#6b21a8', fontWeight: 800, textTransform: 'uppercase', marginBottom: 2 }}>
                           Clutch rating
                        </div>
                        <div style={{ fontSize: '1.4rem', fontWeight: 900, color: detail.clutchAvg >= 0 ? '#a855f7' : '#dc2626', lineHeight: 1 }}>
                          {detail.clutchAvg >= 0 ? '+' : ''}{detail.clutchAvg}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#9aa0ab', marginTop: 4 }}>
                          avg in {detail.clutchGames} close game{detail.clutchGames === 1 ? '' : 's'} (margin {'<'} 10)
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* SKILL AUTO-BADGES — auto-derived flexes anyone can earn.
                    Rendered as a horizontal chip grid above rivalries so
                    it's the first thing screenshotted. Each badge tells
                    you exactly WHY it triggered. */}
                {Array.isArray(detail.skillBadges) && detail.skillBadges.length > 0 && (
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: "0.7rem", letterSpacing: 2, color: "#9aa0ab", fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}> Skill badges · auto-earned</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8 }}>
                      {detail.skillBadges.map((b, i) => (
                        <div key={i} style={{
                          background: '#fff',
                          border: `1.5px solid ${b.color}33`,
                          borderLeft: `4px solid ${b.color}`,
                          borderRadius: 10,
                          padding: '8px 10px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}>
                          <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>{b.emoji}</div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: '0.78rem', fontWeight: 900, color: b.color, lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</div>
                            <div style={{ fontSize: '0.62rem', color: '#9aa0ab', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* BUNNY / NEMESIS / PINATA — the bowler banter trifecta.
                    Wired so any of the three render only when the data
                    actually supports them (min ball/out thresholds). */}
                {(detail.bunny || detail.nemesis || detail.pinata) && (
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: "0.7rem", letterSpacing: 2, color: "#9aa0ab", fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}> Rivalries · bunny / nemesis / pinata</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8 }}>
                      {detail.bunny && (
                        <div style={{ background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)', border: '1.5px solid #10b98140', borderRadius: 10, padding: '10px 12px' }}>
                          <div style={{ fontSize: '0.6rem', letterSpacing: 1.5, color: '#065f46', fontWeight: 800, textTransform: 'uppercase', marginBottom: 3 }}>
                             Bunny
                          </div>
                          <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#3b3b4f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{detail.bunny.name}</div>
                          <div style={{ fontSize: '0.7rem', color: '#065f46', marginTop: 2 }}>
                            <strong>{detail.bunny.runs} runs</strong> off {detail.bunny.ballsFaced} balls
                          </div>
                        </div>
                      )}
                      {detail.nemesis && (
                        <div style={{ background: 'linear-gradient(135deg, #fef2f2, #fee2e2)', border: '1.5px solid #dc262640', borderRadius: 10, padding: '10px 12px' }}>
                          <div style={{ fontSize: '0.6rem', letterSpacing: 1.5, color: '#991b1b', fontWeight: 800, textTransform: 'uppercase', marginBottom: 3 }}>
                             Nemesis
                          </div>
                          <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#3b3b4f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{detail.nemesis.name}</div>
                          <div style={{ fontSize: '0.7rem', color: '#991b1b', marginTop: 2 }}>
                            Got you out <strong>{detail.nemesis.outs}×</strong>
                          </div>
                        </div>
                      )}
                      {detail.pinata && (
                        <div style={{ background: 'linear-gradient(135deg, #fef3c7, #fde68a)', border: '1.5px solid #d9770640', borderRadius: 10, padding: '10px 12px' }}>
                          <div style={{ fontSize: '0.6rem', letterSpacing: 1.5, color: '#92400e', fontWeight: 800, textTransform: 'uppercase', marginBottom: 3 }}>
                             Pinata
                          </div>
                          <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#3b3b4f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{detail.pinata.name}</div>
                          <div style={{ fontSize: '0.7rem', color: '#92400e', marginTop: 2 }}>
                            Smashed <strong>{detail.pinata.runs} runs</strong> off you
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* CAREER BATTING / BOWLING — Spawtz-style career counter
                    panel. Cumulative across every cricket match played:
                    balls faced, dot balls, 4s, 7s, times out, strike rate
                    on the batting side; balls bowled, dot balls bowled,
                    wides, no-balls, economy on the bowling side. Each
                    number builds with every match. */}
                {detail.career && detail.cricketGamesPlayed > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: "0.7rem", letterSpacing: 2, color: "#9aa0ab", fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}> Career cricket · all-time</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      {/* Batting */}
                      <div style={{ background: "#0b0e13", borderRadius: 10, padding: "12px 14px", border: "1px solid rgba(255,255,255,0.09)" }}>
                        <div style={{ fontSize: "0.65rem", color: "#ff6a5f", letterSpacing: 1.5, fontWeight: 800, textTransform: "uppercase", marginBottom: 8 }}>Batting</div>
                        <CareerRow label="Runs" value={detail.career.runs} bold />
                        <CareerRow label="Balls faced" value={detail.career.ballsFaced} />
                        <CareerRow label="Dot balls" value={detail.career.dotBallsFaced} muted />
                        <CareerRow label="4s" value={detail.career.fours} />
                        <CareerRow label="7s" value={detail.career.sevens} />
                        <CareerRow label="Times out" value={detail.career.timesOut} />
                        <CareerRow label="Strike rate" value={detail.career.strikeRate != null ? detail.career.strikeRate.toFixed(1) : '—'} bold />
                        {/* RA — Spawtz batting average. Runs per dismissal. */}
                        <CareerRow label="Runs avg" value={detail.career.runsAverage != null ? detail.career.runsAverage.toFixed(1) : '—'} bold />
                      </div>
                      {/* Bowling */}
                      <div style={{ background: "#0b0e13", borderRadius: 10, padding: "12px 14px", border: "1px solid rgba(255,255,255,0.09)" }}>
                        <div style={{ fontSize: "0.65rem", color: "#e74c3c", letterSpacing: 1.5, fontWeight: 800, textTransform: "uppercase", marginBottom: 8 }}>Bowling</div>
                        <CareerRow label="Wickets" value={detail.career.wickets} bold />
                        <CareerRow label="Runs conceded" value={detail.career.runsConceded} />
                        <CareerRow label="Balls bowled" value={detail.career.ballsBowled} />
                        <CareerRow label="Dot balls" value={detail.career.dotBallsBowled} muted />
                        <CareerRow label="Wides" value={detail.career.wides} />
                        <CareerRow label="No-balls" value={detail.career.noBalls} />
                        <CareerRow label="Economy" value={detail.career.economy != null ? detail.career.economy.toFixed(1) : '—'} bold />
                        {/* WA — Spawtz bowling average. Runs conceded per wicket. Lower=better. */}
                        <CareerRow label="Wickets avg" value={detail.career.wicketsAverage != null ? detail.career.wicketsAverage.toFixed(1) : '—'} bold />
                        {/* 2026-05-28: career best bowling spell (X/Y). */}
                        <CareerRow
                          label="Best figures" value={detail.career.bestBowlingFigures
                            ? `${detail.career.bestBowlingFigures.wickets}/${detail.career.bestBowlingFigures.runs}`
                            : '—'}
                          bold
                        />
                      </div>
                    </div>

                    {/* 2026-05-28: dismissal-type breakdowns. Both panels
                        only render if there's at least one tallied dismissal
                        on that side, so a player who's only batted (or only
                        bowled) doesn't see an empty panel. */}
                    {(() => {
                      const dByType = detail.career.dismissalsByType || {};
                      const wByType = detail.career.wicketsByType || {};
                      const totalD = Object.values(dByType).reduce((s, n) => s + n, 0);
                      const totalW = Object.values(wByType).reduce((s, n) => s + n, 0);
                      if (totalD === 0 && totalW === 0) return null;
                      const order = ['bowled', 'caught', 'lbw', 'stumped', 'run_out', 'other'];
                      const labels = { bowled: 'Bowled', caught: 'Caught', lbw: 'LBW', stumped: 'Stumped', run_out: 'Run out', other: 'Other' };
                      const Bar = ({ count, total, color }) => {
                        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                        return (
                          <div style={{ height: 6, background: '#eef0f5', borderRadius: 3, overflow: 'hidden', marginTop: 4 }}>
                            <div style={{ width: pct + '%', height: '100%', background: color }} />
                          </div>
                        );
                      };
                      const Row = ({ k, count, total, color }) => (
                        <div style={{ marginBottom: 6 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                            <span style={{ color: '#3b3b4f' }}>{labels[k]}</span>
                            <span style={{ color: '#9aa0ab', fontWeight: 700 }}>{count}</span>
                          </div>
                          <Bar count={count} total={total} color={color} />
                        </div>
                      );
                      return (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                          {/* HOW I GOT OUT (batting view) */}
                          <div style={{ background: '#fafafe', borderRadius: 10, padding: '12px 14px', border: '1px solid rgba(255,255,255,0.09)' }}>
                            <div style={{ fontSize: '0.65rem', color: "#ff6a5f", letterSpacing: 1.5, fontWeight: 800, textTransform: 'uppercase', marginBottom: 8 }}>How I got out</div>
                            {totalD === 0 ? (
                              <div style={{ fontSize: '0.78rem', color: '#9aa0ab' }}>Never been dismissed.</div>
                            ) : order.filter(k => dByType[k] > 0).map(k => (
                              <Row key={k} k={k} count={dByType[k]} total={totalD} color={P} />
                            ))}
                          </div>
                          {/* HOW I TOOK WICKETS (bowling view) */}
                          <div style={{ background: '#fafafe', borderRadius: 10, padding: '12px 14px', border: '1px solid rgba(255,255,255,0.09)' }}>
                            <div style={{ fontSize: '0.65rem', color: '#e74c3c', letterSpacing: 1.5, fontWeight: 800, textTransform: 'uppercase', marginBottom: 8 }}>How I took wickets</div>
                            {totalW === 0 ? (
                              <div style={{ fontSize: '0.78rem', color: '#9aa0ab' }}>No wickets yet.</div>
                            ) : order.filter(k => wByType[k] > 0).map(k => (
                              <Row key={k} k={k} count={wByType[k]} total={totalW} color="#e74c3c" />
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* CONTRIBUTION TREND — small sparkline showing per-match
                    contribution across the season, oldest left → newest
                    right. Visual proof of form. Pure SVG, no libs. */}
                {detail.sparkline && detail.sparkline.length >= 2 && (() => {
                  const data = detail.sparkline;
                  const maxAbs = Math.max(1, ...data.map(v => Math.abs(v)));
                  const W = 320, H = 60, pad = 4;
                  const dx = (W - pad * 2) / Math.max(1, data.length - 1);
                  const yFor = (v) => (H / 2) - (v / maxAbs) * (H / 2 - pad);
                  const path = data.map((v, i) => `${i === 0 ? 'M' : 'L'} ${pad + i * dx} ${yFor(v)}`).join(' ');
                  const lastIdx = data.length - 1;
                  return (
                    <div style={{ marginBottom: 24 }}>
                      <div style={{ fontSize: "0.7rem", letterSpacing: 2, color: "#9aa0ab", fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}> Contribution trend · last {data.length} games</div>
                      <div style={{ background: "#0b0e13", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 10, padding: "12px 14px" }}>
                        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: "block", width: "100%", height: 60 }}>
                          {/* Zero line */}
                          <line x1={pad} y1={H / 2} x2={W - pad} y2={H / 2} stroke="rgba(255,255,255,0.14)" strokeWidth="1" strokeDasharray="3 3" />
                          {/* Trend line */}
                          <path d={path} fill="none" stroke={P} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                          {/* Points */}
                          {data.map((v, i) => (
                            <circle key={i} cx={pad + i * dx} cy={yFor(v)} r={i === lastIdx ? 4 : 2.5} fill={v >= 0 ? "#10b981" : "#dc2626"} />
                          ))}
                        </svg>
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: "0.62rem", color: "#9aa0ab" }}>
                          <span>← Oldest</span>
                          <span>Latest: <strong style={{ color: data[lastIdx] >= 0 ? '#10b981' : '#dc2626' }}>{data[lastIdx] >= 0 ? '+' : ''}{data[lastIdx]}</strong></span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* BEST MATCHES — top 5 by contribution, clickable. The
                    highlight reel. Each row links into the recap modal so
                    the player can re-live their best games. */}
                {detail.topMatches && detail.topMatches.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: "0.7rem", letterSpacing: 2, color: "#9aa0ab", fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}> Best matches · tap for recap</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {detail.topMatches.map((m, idx) => (
                        <div key={m.matchId}
                          onClick={() => setSelectedMatchId(m.matchId)}
                          onMouseEnter={e => { e.currentTarget.style.background = '#151a22'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = '#0b0e13'; }}
                          style={{ display: "grid", gridTemplateColumns: "26px 1fr auto 18px", alignItems: "center", padding: "8px 12px", background: "#0b0e13", borderRadius: 8, gap: 10, cursor: "pointer", transition: "background 0.15s" }}>
                          <div style={{ fontSize: "0.78rem", fontWeight: 800, color: idx === 0 ? "#92400e" : "#9aa0ab", textAlign: "center" }}>
                            {idx === 0 ? '' : idx === 1 ? '' : idx === 2 ? '' : '#' + (idx + 1)}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#e8eaee", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>vs {m.opp || 'Opponent'}</div>
                            <div style={{ fontSize: "0.65rem", color: "#9aa0ab", marginTop: 1 }}>
                              {m.batRuns} runs{m.wickets > 0 ? ` · ${m.wickets} wkts` : ''}{m.when ? ' · ' + m.when : ''}
                            </div>
                          </div>
                          <div style={{ fontSize: "1.05rem", fontWeight: 900, color: m.contribution >= 0 ? "#10b981" : "#dc2626", textAlign: "right" }}>
                            {m.contribution >= 0 ? '+' : ''}{m.contribution}
                          </div>
                          <div style={{ fontSize: "0.85rem", color: "#9aa0ab", textAlign: "right" }}>›</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* PARTNERSHIPS — Spawtz-tier 'who do you bat best with'
                    flex. For each batting partner across the season, show
                    partnerships count, total runs, average runs per pair,
                    and personal best partnership. Sorted by avg desc. */}
                {detail.partnerships && detail.partnerships.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: "0.7rem", letterSpacing: 2, color: "#9aa0ab", fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}> Best partnerships</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {detail.partnerships.slice(0, 6).map((p, idx) => {
                        const tone = idx === 0 ? '#fef3c7' : '#fafafe';
                        return (
                          <div key={p.partner} style={{ display: "grid", gridTemplateColumns: "26px 1fr auto auto", alignItems: "center", padding: "10px 12px", background: tone, borderRadius: 8, gap: 10 }}>
                            <div style={{ fontSize: "0.75rem", fontWeight: 800, color: idx === 0 ? "#92400e" : "#9aa0ab", textAlign: "center" }}>
                              {idx === 0 ? '' : idx === 1 ? '' : idx === 2 ? '' : '#' + (idx + 1)}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: "0.88rem", fontWeight: 700, color: "#3b3b4f", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.partner}</div>
                              <div style={{ fontSize: "0.62rem", color: "#9aa0ab", marginTop: 2 }}>
                                {p.partnerships} pairing{p.partnerships === 1 ? '' : 's'} · {p.totalRuns} runs total{p.best !== -Infinity ? ` · best ${p.best}` : ''}
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: "1.05rem", fontWeight: 900, color: idx === 0 ? "#92400e" : "#3b3b4f", lineHeight: 1 }}>
                                {p.avg}
                              </div>
                              <div style={{ fontSize: "0.55rem", color: "#9aa0ab", letterSpacing: 0.5, marginTop: 2, textTransform: "uppercase", fontWeight: 700 }}>Avg / pair</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Recent matches — now clickable. Tapping a row opens the
                    full match recap modal with pair-by-pair, bowling figures
                    and ball-by-ball (for cricket). */}
                {detail.recentMatches.length > 0 && (
                  <div>
                    <div style={{ fontSize: "0.7rem", letterSpacing: 2, color: "#9aa0ab", fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}>Recent Matches · tap for recap</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {detail.recentMatches.map(m => {
                        const resColor = m.result === 'W' ? '#10b981' : m.result === 'L' ? '#e74c3c' : '#9aa0ab';
                        return (
                          <div key={m.id}
                            onClick={() => setSelectedMatchId(m.id)}
                            onMouseEnter={e => { e.currentTarget.style.background = '#151a22'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#0b0e13'; }}
                            style={{ display: "grid", gridTemplateColumns: "30px 1fr 80px 70px 18px", alignItems: "center", padding: "8px 12px", background: "#0b0e13", borderRadius: 8, gap: 10, cursor: "pointer", transition: "background 0.15s" }}>
                            <div style={{ background: resColor, color: "#fff", borderRadius: 6, width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "0.78rem" }}>{m.result}</div>
                            <div style={{ minWidth: 0, fontSize: "0.85rem", color: "#e8eaee", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.myTeam} vs {m.opp}</div>
                            <div style={{ fontFamily: "system-ui", fontWeight: 700, fontSize: "0.9rem", color: resColor, textAlign: "center" }}>{m.myScore}–{m.oppScore}</div>
                            <div style={{ fontSize: "0.72rem", color: "#9aa0ab", textAlign: "right" }}>{m.when}</div>
                            <div style={{ fontSize: "0.85rem", color: "#9aa0ab", textAlign: "right" }}>›</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {detail.recentMatches.length === 0 && detail.badges.length === 0 && detail.teams.length === 0 && (
                  <div style={{ textAlign: "center", padding: "20px", color: "#9aa0ab", fontSize: "0.9rem" }}>Loading detail…</div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Team profile modal — opens when a team name is clicked or via
          ?team=<id> deep-link. Public, shareable. Roster ranked by avg
          contribution + recent form + ladder + next fixture. */}
      {selectedTeamId && (
        <TeamProfileModal
          data={liveTeamDetail}
          accent={P}
          onClose={() => setSelectedTeamId(null)}
          onOpenPlayer={(pid) => { setSelectedTeamId(null); setSelectedPlayerId(pid); }}
          onOpenMatch={(mid) => setSelectedMatchId(mid)}
        />
      )}

      {/* Match recap modal — opens when a Recent Matches row is tapped.
          Shows the data Spawtz never gave players: pair-by-pair innings
          breakdown, bowling figures, and ball-by-ball log (for cricket).
          Non-cricket sports get a slimmer summary. Click outside to close.
          viewerPlayerName is the name of the player whose profile we came
          from — used to highlight their row in the contribution list with
          a 'YOU' badge so opening the modal feels personal. */}
      {selectedMatchId && (
        <MatchRecapModal
          data={selectedMatchData}
          career={liveCareer}
          accent={P}
          viewerPlayerName={livePlayerDetail?.playerName || (livePlayerIndex && selectedPlayerId ? livePlayerIndex[selectedPlayerId]?.name : null)}
          onClose={() => setSelectedMatchId(null)}
        />
      )}

      {/* ═══ TRADING CARD MODAL ════════════════════════════════════════
          Renders an SVG card of the player's headline stats, gives them
          one-tap PNG download + native share. The card is the highest-ROI
          marketing asset we've built — every player who flexes one of
          these to their group chat is free reach for Wildcatz / WISA. */}
      {tradingCardPlayer && (
        <TradingCardModal player={tradingCardPlayer} onClose={() => setTradingCardPlayer(null)} />
      )}

      {/* ═══ YOUR WEEK · SUNDAY WRAP ════════════════════════════════════
          Reach via /?wrap=<playerId>. Spotify-Wrapped style swipeable
          recap of the last 7 days. Last panel hands off to the trading
          card share for one-tap export. Designed to be deep-linked from
          a weekly push notification once the Play Store app lands. */}
      {weekWrapPlayer && (
        <WeekWrapModal
          playerId={weekWrapPlayer}
          onClose={() => {
            setWeekWrapPlayer(null);
            const params = new URLSearchParams(window.location.search);
            if (params.get('wrap')) { params.delete('wrap'); const qs = params.toString(); window.history.pushState({}, '', window.location.pathname + (qs ? '?' + qs : '')); }
          }}
          onShareCard={(cardData) => setTradingCardPlayer(cardData)}
        />
      )}

      {/* 2026-05-31: Konami easter egg overlay — appears briefly after
          ↑↑↓↓←→←→BA. Pure delight, no functional impact. Trophy bounce. */}
      {konamiHit && (
        <div onClick={() => setKonamiHit(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, animation: "fadein 0.3s" }}>
          <div style={{ textAlign: "center", color: "#fff" }}>
            <div style={{ fontSize: "5rem", marginBottom: 10, animation: "pulse 1s infinite" }}></div>
            <div style={{ fontFamily: "ui-monospace, monospace", fontSize: "0.7rem", letterSpacing: 4, fontWeight: 700, color: "#fbbf24", marginBottom: 8 }}>ACHIEVEMENT UNLOCKED</div>
            <div style={{ fontSize: "1.4rem", fontWeight: 900, marginBottom: 4 }}>The Cheat Code Crusader</div>
            <div style={{ fontSize: "0.9rem", opacity: 0.7 }}>You found the secret. Stats nerd certified.</div>
          </div>
        </div>
      )}

      {/* Global Find search (triggered from nav button) — players + teams */}
      {globalSearchOpen && livePlayerIndex && (
        <PlayerSearchModal
          title="Find a player or team"playerIndex={livePlayerIndex}
          teamIndex={liveTeamIndex}
          onPickPlayer={id => { setSelectedPlayerId(id); setGlobalSearchOpen(false); }}
          onPickTeam={id => { setSelectedTeamId(id); setGlobalSearchOpen(false); }}
          onClose={() => setGlobalSearchOpen(false)}
        />
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/* VENUE DETAIL MODAL                             */}
      {/* ═══════════════════════════════════════════════ */}
      {selectedVenue && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}
          onClick={() => setSelectedVenue(null)}>
          <div style={{ background: "#101319", borderRadius: 20, maxWidth: 560, width: "100%", maxHeight: "85vh", overflow: "auto" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ position: "relative" }}>
              {selectedVenue.logo ? (
                <div style={{ width: "100%", height: 220, background: selectedVenue.brandColor || "#0c1215", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "20px 20px 0 0", position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 30% 50%, ${selectedVenue.brandAccent || "#ff2e17"}22 0%, transparent 60%)` }} />
                  <img src={selectedVenue.logo} alt={selectedVenue.name} style={{ height: 90, objectFit: "contain", position: "relative", zIndex: 1 }} />
                </div>
              ) : (
                <img src={selectedVenue.img} alt={selectedVenue.name} style={{ width: "100%", height: 220, objectFit: "cover", borderRadius: "20px 20px 0 0" }} />
              )}
              <button onClick={() => setSelectedVenue(null)} style={{ position: "absolute", top: 12, right: 12, width: 36, height: 36, borderRadius: "50%", background: "rgba(0,0,0,0.5)", border: "none", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><X size={18} /></button>
            </div>
            <div style={{ padding: "24px 28px 28px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <h2 style={{ fontSize: "1.4rem", fontWeight: 800, margin: 0 }}>{selectedVenue.name}</h2>
                <WisaBadge />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#9aa0ab", marginBottom: 16 }}>
                <MapPin size={15} /> {selectedVenue.city}, {selectedVenue.state ? selectedVenue.state + ", " : ""}{selectedVenue.country}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
                {selectedVenue.sports.map(s => (
                  <span key={s} style={{ background: "rgba(255,46,23,0.12)", color: "#ff6a5f", fontSize: "0.8rem", fontWeight: 600, padding: "5px 14px", borderRadius: 20 }}>{s}</span>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, background: "#0b0e13", borderRadius: 12, padding: 20, marginBottom: 20 }}>
                <div style={{ textAlign: "center" }}><div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#ff6a5f" }}>{selectedVenue.players.toLocaleString()}</div><div style={{ fontSize: "0.78rem", color: "#9aa0ab" }}>Players</div></div>
                <div style={{ textAlign: "center" }}><div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#ff6a5f" }}>{selectedVenue.teams}</div><div style={{ fontSize: "0.78rem", color: "#9aa0ab" }}>Teams</div></div>
                <div style={{ textAlign: "center" }}><div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#ff6a5f" }}>{selectedVenue.sports.length}</div><div style={{ fontSize: "0.78rem", color: "#9aa0ab" }}>Sports</div></div>
              </div>
              <div style={{ background: "rgba(255,46,23,0.12)", borderRadius: 12, padding: "14px 16px", marginBottom: 16, fontSize: "0.85rem", color: "#ff6a5f", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                <Check size={16} /> Games here count towards all global leaderboards and upcoming prizes
              </div>
              {/* Reviews */}
              {VENUE_REVIEWS[selectedVenue.name] && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: "0.88rem", marginBottom: 10 }}> Player Reviews</div>
                  {VENUE_REVIEWS[selectedVenue.name].slice(0, 2).map((r, i) => (
                    <div key={i} style={{ background: "#0b0e13", borderRadius: 10, padding: "12px 14px", marginBottom: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: `linear-gradient(135deg, #ff2e17, #ff5a45)`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "0.6rem", fontWeight: 700 }}>{r.avatar}</div>
                        <div style={{ fontWeight: 700, fontSize: "0.82rem" }}>{r.reviewer}</div>
                        <div style={{ marginLeft: "auto", fontSize: "0.72rem", color: "#fbbf24" }}>{"★".repeat(r.rating)}</div>
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#c3c7ce", lineHeight: 1.5 }}>{r.text}</div>
                    </div>
                  ))}
                </div>
              )}
              {/* Outbound to the venue's OWN marketing site, not wisasports.
                  Each VENUES row defines its own `website`. Fall back to the
                  WISA platform site only if the venue hasn't filled one in. */}
              <a href={selectedVenue.website || "https://www.wisasports.com"} target="_blank" rel="noopener noreferrer" style={{ display: "block", textAlign: "center", background: `linear-gradient(135deg, #ff2e17, #ff5a45)`, color: "#fff", padding: "14px", borderRadius: 12, fontWeight: 700, textDecoration: "none" }}>
                Visit Venue →
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ── Footer ────────────────────────────────────── */}
      <footer style={{ background: C.surface, color: "rgba(255,255,255,0.5)", padding: "40px 32px", textAlign: "center", fontSize: "0.82rem" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 12 }}>
          <div style={{ width: 24, height: 24, borderRadius: 6, background: "#00e676", display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "#07080b", fontWeight: 900, fontSize: "0.8rem" }}>4</span></div>
          <span style={{ fontWeight: 700, color: "#fff" }}>all4indoor</span>
        </div>
        <div>Indoor sports stats and rankings — powered by <a href="https://www.wisasports.com" target="_blank" rel="noopener noreferrer" style={{ color: "#ff6a5f", textDecoration: "none" }}>WISA</a></div>
        <div style={{ marginTop: 8 }}>© {new Date().getFullYear()} all4indoor. All rights reserved.</div>
      </footer>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes shimmer { 0% { background-position: -800px 0; } 100% { background-position: 800px 0; } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .a4i-skeleton {
          background: linear-gradient(90deg, rgba(140,140,150,0.10) 0%, rgba(140,140,150,0.20) 50%, rgba(140,140,150,0.10) 100%);
          background-size: 800px 100%;
          animation: shimmer 1.4s infinite linear;
          border-radius: 8px;
        }
        .a4i-fadein { animation: fadeIn 0.35s ease-out both; }

        /* 2026-07-25: tasteful scroll-reveal + hover lift so the site feels
           alive, not static. Guarded by .a4i-anim (added by JS) so if JS is
           off, everything stays visible. Respects reduced-motion. */
        .a4i-reveal { opacity: 0; transform: translateY(26px); transition: opacity .6s cubic-bezier(.16,.8,.3,1), transform .6s cubic-bezier(.16,.8,.3,1); will-change: opacity, transform; }
        .a4i-reveal.a4i-in { opacity: 1; transform: none; }
        @media (prefers-reduced-motion: reduce) {
          .a4i-reveal { opacity: 1 !important; transform: none !important; transition: none !important; }
        }

        /* ──────────────────────────────────────────────────────────────────
           2026-05-27: MOBILE RESPONSIVE PASS
           The page is built with inline styles, so we override common
           inline-style patterns via attribute selectors with !important.
           This collapses padding, grids, and font sizes for phone widths
           without having to touch ~7000 lines of inline styles individually.
           ────────────────────────────────────────────────────────────────── */
        .a4i-root, .a4i-root * { box-sizing: border-box; }
        .a4i-root input:not([type="checkbox"]):not([type="radio"]):not([type="range"]), .a4i-root textarea, .a4i-root select {
          background: #101319; color: #f4f5f7; border-color: rgba(255,255,255,0.14);
        }
        .a4i-root input::placeholder, .a4i-root textarea::placeholder { color: #646a75; }
        .a4i-root select option { background: #101319; color: #f4f5f7; }
        html, body { overflow-x: hidden; }

        @media (max-width: 768px) {
          /* Stop horizontal scroll across the whole app */
          .a4i-root { overflow-x: hidden !important; }

          /* ── Reduce horizontal padding on every section ───────────────── */
          .a4i-root [style*="padding: 80px 32px"] { padding: 56px 16px !important; }
          .a4i-root [style*="padding: 60px 32px"] { padding: 40px 16px !important; }
          .a4i-root [style*="padding: 40px 32px"] { padding: 28px 16px !important; }
          .a4i-root [style*="padding: 30px 32px"] { padding: 20px 16px !important; }
          .a4i-root [style*="padding: 30px 32px 0"] { padding: 20px 16px 0 !important; }
          .a4i-root [style*="padding: 30px 32px 10px"] { padding: 20px 16px 8px !important; }
          .a4i-root [style*="padding: 40px 32px 20px"] { padding: 28px 16px 16px !important; }
          .a4i-root [style*="padding: 80px 32px 60px"] { padding: 48px 16px 36px !important; }
          .a4i-root [style*="padding: 60px 24px"] { padding: 40px 16px !important; }
          .a4i-root [style*="padding: 50px 32px"] { padding: 32px 16px !important; }
          .a4i-root [style*="padding: \\"0 32px\\""] { padding: 0 16px !important; }
          .a4i-root .a4i-nav { padding: 0 16px !important; }

          /* ── Collapse big multi-column grids ─────────────────────────── */
          .a4i-root [style*="repeat(4, 1fr)"] { grid-template-columns: repeat(2, 1fr) !important; }
          .a4i-root [style*="repeat(3, 1fr)"] { grid-template-columns: 1fr !important; }
          .a4i-root [style*="1fr 1fr 1fr 1fr"] { grid-template-columns: 1fr 1fr !important; }
          .a4i-root [style*="1fr 1fr 1fr"] { grid-template-columns: 1fr !important; }

          /* Side-by-side 2-col sections collapse to 1 column */
          .a4i-root [style*="grid-template-columns: 1fr 1fr"][style*="gap"] { grid-template-columns: 1fr !important; }

          /* The hero "VS" 1fr 80px 1fr layout — keep but tighten */
          .a4i-root [style*="1fr 80px 1fr"] { grid-template-columns: 1fr 60px 1fr !important; }
          .a4i-root [style*="1fr 60px 1fr"] { grid-template-columns: 1fr 50px 1fr !important; }

          /* Table-like grids with fixed-px first columns scroll horizontally */
          .a4i-root [style*="grid-template-columns: 80px 80px 80px"],
          .a4i-root [style*="grid-template-columns: 44px 1fr 90px"],
          .a4i-root [style*="grid-template-columns: 40px 1fr 100px"],
          .a4i-root [style*="grid-template-columns: 70px repeat(7"] {
            overflow-x: auto !important;
            min-width: 100%;
          }
          /* Wrap these table-grids in a scroll container by making them
             scrollable themselves */
          .a4i-root [style*="grid-template-columns: 80px 80px 80px 80px 80px"] {
            display: block !important;
            white-space: nowrap;
            overflow-x: auto;
          }

          /* ── Hero typography ──────────────────────────────────────────── */
          .a4i-root h1 { font-size: clamp(1.6rem, 7vw, 2.4rem) !important; line-height: 1.15 !important; }
          .a4i-root h2 { font-size: clamp(1.3rem, 5vw, 1.8rem) !important; }

          /* ── Nav: trim padding + button sizes ─────────────────────────── */
          .a4i-root nav { padding: 0 12px !important; }
          .a4i-root nav button { padding: 6px 10px !important; font-size: 0.72rem !important; }
          /* Hide the secondary "Find player" button on mobile — the
             hamburger drawer has its own Find Player CTA */
          .a4i-root nav button[title^="Find a player"] { display: none !important; }
          /* Trim sign-in chip */
          .a4i-root nav [style*="rgba(26,138,125,0.08)"] span {
            max-width: 60px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          }

          /* ── Modals: fit phone viewport ──────────────────────────────── */
          .a4i-root [style*="position: fixed"][style*="inset: 0"] {
            padding: 12px !important;
            align-items: flex-start !important;
            overflow-y: auto !important;
          }
          .a4i-root [style*="position: fixed"][style*="inset: 0"] > div {
            max-width: 100% !important;
            max-height: calc(100vh - 24px) !important;
            margin: 0 !important;
          }

          /* ── Search bars / inputs: full-width ─────────────────────────── */
          .a4i-root input[type="text"], .a4i-root input:not([type]) {
            font-size: 16px !important; /* iOS won't zoom-in on focus */
          }

          /* ── Card hover transforms can knock things off-canvas ───────── */
          .a4i-root [style*="hover"] { transform: none !important; }

          /* ── Footer ──────────────────────────────────────────────────── */
          .a4i-root footer { padding: 28px 16px !important; }

          /* ── Buttons: meet 44px touch target ─────────────────────────── */
          .a4i-root button { min-height: 40px; }

          /* ── Tab strips that overflow: allow horizontal scroll ───────── */
          .a4i-root [style*="overflowX: auto"], .a4i-root [style*="overflow-x: auto"] {
            -webkit-overflow-scrolling: touch;
          }

          /* ── Trim huge stat numbers ──────────────────────────────────── */
          .a4i-root [style*="fontSize: \\"3rem\\""],
          .a4i-root [style*="font-size: 3rem"] { font-size: 2rem !important; }
          .a4i-root [style*="fontSize: \\"2.5rem\\""],
          .a4i-root [style*="font-size: 2.5rem"] { font-size: 1.8rem !important; }
          .a4i-root [style*="fontSize: \\"2.2rem\\""],
          .a4i-root [style*="font-size: 2.2rem"] { font-size: 1.6rem !important; }

          /* ── Avatars/badges: prevent oversized circles ───────────────── */
          .a4i-root [style*="width: 200px"][style*="height: 200px"] { width: 140px !important; height: 140px !important; }
          .a4i-root [style*="width: 160px"][style*="height: 160px"] { width: 120px !important; height: 120px !important; }
          .a4i-root [style*="width: 120px"][style*="height: 120px"] { width: 96px !important; height: 96px !important; }
        }

        /* Extra-narrow phones (≤380px — iPhone SE / mini) */
        @media (max-width: 380px) {
          .a4i-root [style*="repeat(4, 1fr)"],
          .a4i-root [style*="1fr 1fr 1fr 1fr"] { grid-template-columns: 1fr !important; }
          .a4i-root [style*="padding: 80px 32px"] { padding: 40px 12px !important; }
          .a4i-root nav { padding: 0 10px !important; }
        }
      `}</style>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ─── MATCH RECAP MODAL ──────────────────────────────────────────────────────
// The "stats that blow former-Spawtz-players' minds" piece. Opens when a
// recent-match row on the player profile is tapped. For cricket: pair-by-pair
// innings, bowling figures, and ball-by-ball log. For other sports: a slim
// score summary. The career strip at the top reminds the viewer of THEIR
// all-time line whenever they open any match — keeps the experience personal.
// ═════════════════════════════════════════════════════════════════════════════
function MatchRecapModal({ data, career, accent, viewerPlayerName, onClose }) {
  const [expandedPair, setExpandedPair] = React.useState({ home: -1, away: -1 });
  const viewerKey = (viewerPlayerName || '').trim().toLowerCase();
  if (!data) {
    return (
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
        <div style={{ background:'#fff', borderRadius:14, padding:'24px 28px', fontSize:'0.9rem', color:'#555' }}>Loading recap…</div>
      </div>
    );
  }
  if (data.error) {
    return (
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
        <div style={{ background:'#fff', borderRadius:14, padding:'24px 28px', fontSize:'0.9rem', color:'#dc2626' }}>Could not load this match: {data.error}</div>
      </div>
    );
  }
  const { match: m, cricket } = data;
  const sportSlug = (m?.competition?.sport?.slug || '').toLowerCase();
  const sportName = m?.competition?.sport?.name || 'Match';
  const isCricket = sportSlug === 'cricket';
  const homeName = m?.home_team?.name || 'Home';
  const awayName = m?.away_team?.name || 'Away';
  const homeScore = m?.home_score ?? 0;
  const awayScore = m?.away_score ?? 0;
  const winningSide = homeScore > awayScore ? 'home' : awayScore > homeScore ? 'away' : null;
  const margin = Math.abs(homeScore - awayScore);
  const dateStr = m?.scheduled_at ? new Date(m.scheduled_at).toLocaleDateString('en-AU', { weekday:'short', day:'numeric', month:'short', year:'numeric' }) : '';

  // Pair + bowling computations, mirroring the WISA MatchReport logic.
  function calcPairStats(balls) {
    let runs = 0, wickets = 0;
    for (const b of (balls || [])) {
      if (b.type === 'wicket') wickets++;
      else if (b.type === 'run') runs += (b.runs || 0);
      else if (b.type === 'wide' || b.type === 'noball') runs += (b.runs || 2);
      else if (b.type === 'penalty') runs += (b.runs || 0);
    }
    return { runs, wickets };
  }
  function calcBatterRuns(balls, idx) {
    let r = 0;
    for (const b of (balls || [])) {
      if (b.batterIndex !== idx) continue;
      if (b.type === 'run') r += (b.runs || 0);
    }
    return r;
  }

  function buildInnings(team) {
    if (!cricket) return null;
    const pairs = [];
    const totalPairs = cricket.total_pairs || 4;
    for (let p = 0; p < totalPairs; p++) {
      const key = team + '-' + p;
      const pBalls = (cricket.balls_data || {})[key] || [];
      const setup = (cricket.batter_setup || {})[key] || {};
      const stats = calcPairStats(pBalls);
      pairs.push({
        pairNum: p + 1,
        batter1: setup.batter1 || `Batter ${p*2+1}`,
        batter2: setup.batter2 || `Batter ${p*2+2}`,
        runs1: calcBatterRuns(pBalls, 0),
        runs2: calcBatterRuns(pBalls, 1),
        pairRuns: stats.runs,
        pairWickets: stats.wickets,
        balls: pBalls,
      });
    }
    return pairs;
  }
  function buildBowling(battingTeam) {
    if (!cricket) return null;
    const stats = {};
    const totalPairs = cricket.total_pairs || 4;
    const OVERS_PER_PAIR = 4;
    const BALLS_PER_OVER = 6;
    // Indoor cricket "slot" rules — the bowler's count of balls bowled
    // (which drives overs.balls notation) is NOT simply len(balls). Some
    // deliveries consume a slot, others don't:
    //   - run, wicket, rebowl → always consume a slot (a "hard" ball)
    //   - wide, noball → consume a slot in overs 1..3 of a pair; in the
    //     final over (4) they only consume a slot if b.legal === true
    //     (umpire declined the rebowl). This matches the WISA scorer.
    // Without this logic the recap shows 1.3 / 1.4 overs instead of 2.0
    // and ex-Spawtz players spot the discrepancy immediately.
    for (let p = 0; p < totalPairs; p++) {
      const key = battingTeam + '-' + p;
      const pBalls = (cricket.balls_data || {})[key] || [];
      let slotsInOver = 0;
      let overIdx = 0; // 0..(OVERS_PER_PAIR-1)
      for (const b of pBalls) {
        if (!b.bowler) continue;
        if (!stats[b.bowler]) stats[b.bowler] = { wickets: 0, runs: 0, legalBalls: 0 };
        if (b.type === 'wicket') stats[b.bowler].wickets++;
        if (b.type === 'run') stats[b.bowler].runs += (b.runs || 0);
        if (b.type === 'wide' || b.type === 'noball') stats[b.bowler].runs += (b.runs || 2);
        const isHard = b.type === 'run' || b.type === 'wicket' || b.type === 'rebowl';
        const isExtra = b.type === 'wide' || b.type === 'noball';
        const isLastOver = overIdx === OVERS_PER_PAIR - 1;
        // consumesSlot — used for over advancement (matches live scorer).
        const consumesSlot = isHard || (!isLastOver && isExtra) || (isLastOver && isExtra && b.legal);
        // 2026-06-04: bowler's "legal balls" count = balls that consumed a
        // slot, which is the same rule the over uses to close. Previously
        // (2026-05-28 fix) we only counted "hard" balls in the last over to
        // stop rebowled extras inflating the count past 1.0. But that also
        // dropped the LEGITIMATE wide/noball with b.legal=true (batter
        // declined the rebowl) — that ball really did close the slot, so
        // the bowler bowled it and the over advanced because of it. Without
        // counting it, the recap shows 1.5 instead of 2.0 for any bowler
        // whose last over had a declined-rebowl extra (Chris Horn, Scott
        // Hunter — 4 Jun match). Using consumesSlot for both purposes keeps
        // rebowled extras out (no slot consumed, no bowler credit) AND
        // keeps declined-rebowl extras in (slot consumed, bowler credit),
        // which is the actual correct behaviour.
        const countsForBowler = consumesSlot;
        if (countsForBowler) stats[b.bowler].legalBalls++;
        if (consumesSlot) {
          slotsInOver++;
          if (slotsInOver >= BALLS_PER_OVER) { slotsInOver = 0; overIdx++; }
        }
      }
    }
    return Object.entries(stats).map(([name, s]) => ({
      name,
      overs: (Math.floor(s.legalBalls / BALLS_PER_OVER)) + '.' + (s.legalBalls % BALLS_PER_OVER),
      wickets: s.wickets,
      runs: s.runs,
    })).sort((a, b) => b.wickets - a.wickets || a.runs - b.runs);
  }

  const homeInnings = isCricket ? buildInnings('home') : null;
  const awayInnings = isCricket ? buildInnings('away') : null;
  const homeBowling = isCricket ? buildBowling('away') : null; // home bowled to away batters
  const awayBowling = isCricket ? buildBowling('home') : null;

  // Top run-scorers / wicket-takers across the whole match — fun headline stats
  let topScorer = null, topWicketTaker = null;
  // PLAYER CONTRIBUTIONS — the killer Spawtz-style stat. For each player,
  // contribution = runs they batted FOR minus runs they conceded WHILE
  // bowling. Net contribution > 0 means they helped the team beat the
  // average. This is what indoor cricketers quote to each other — the
  // single number that tells you "did I pull my weight this match?".
  // Surface it as a ranked list so the recap mirrors Spawtz's per-match
  // contribution column but with cleaner ranking and team colours.
  let contributions = []; // [{ name, team, batRuns, bowlRuns, contribution }]
  if (isCricket && cricket?.balls_data) {
    const batterRuns = {};
    const batterTeam = {}; // name → home|away (the team they batted for)
    const bowlerRuns = {}; // bowler → runs conceded
    const bowlerTeam = {}; // bowler → home|away (the team they bowled for)
    const bowlerWickets = {};
    for (const key of Object.keys(cricket.balls_data)) {
      const battingTeam = key.startsWith('home-') ? 'home' : 'away';
      const bowlingTeam = battingTeam === 'home' ? 'away' : 'home';
      for (const b of cricket.balls_data[key]) {
        if (b.type === 'run' && b.batter) {
          batterRuns[b.batter] = (batterRuns[b.batter] || 0) + (b.runs || 0);
          batterTeam[b.batter] = battingTeam;
        }
        if (b.type === 'wicket' && b.bowler) {
          bowlerWickets[b.bowler] = (bowlerWickets[b.bowler] || 0) + 1;
          bowlerTeam[b.bowler] = bowlingTeam;
        }
        if (b.bowler && (b.type === 'run' || b.type === 'wide' || b.type === 'noball')) {
          bowlerRuns[b.bowler] = (bowlerRuns[b.bowler] || 0) + (b.runs || 0);
          bowlerTeam[b.bowler] = bowlingTeam;
        }
      }
    }
    const batSorted = Object.entries(batterRuns).sort((a, b) => b[1] - a[1]);
    const bowlSorted = Object.entries(bowlerWickets).sort((a, b) => b[1] - a[1]);
    if (batSorted[0]) topScorer = { name: batSorted[0][0], runs: batSorted[0][1] };
    if (bowlSorted[0]) topWicketTaker = { name: bowlSorted[0][0], wickets: bowlSorted[0][1] };

    // Build per-player contribution rows.
    // 2026-07-07: contribution now includes the indoor-cricket -5-per-wicket
    // bonus. The formula matches what the match report page uses:
    //   contribution = batRuns - (bowlRuns - 5 * wickets)
    //                = batRuns - bowlRuns + 5 * wickets
    // Previously we were showing (batRuns - bowlRuns) which under-credits
    // wicket-takers by 5 per wicket. Chris Debney 33/13/3w was showing +20
    // here but +35 on the match page — this makes both agree at +35.
    const allNames = new Set([...Object.keys(batterRuns), ...Object.keys(bowlerRuns)]);
    contributions = [...allNames].map(name => {
      const team = batterTeam[name] || bowlerTeam[name] || 'home';
      const batRuns = batterRuns[name] || 0;
      const bowlRuns = bowlerRuns[name] || 0;
      const wickets = bowlerWickets[name] || 0;
      return { name, team, batRuns, bowlRuns, wickets,
               contribution: batRuns - bowlRuns + (5 * wickets) };
    }).sort((a, b) => b.contribution - a.contribution);
  }
  const topContributor = contributions[0] || null;

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'flex-end', justifyContent:'center', padding:0 }}>
      <div onClick={e => e.stopPropagation()} className="a4i-fadein" style={{ background:'#fff', width:'100%', maxWidth:560, borderTopLeftRadius:18, borderTopRightRadius:18, maxHeight:'94vh', overflowY:'auto', boxShadow:'0 -10px 40px rgba(0,0,0,0.3)' }}>

        {/* Sticky header — match identity */}
        <div style={{ position:'sticky', top:0, background:`linear-gradient(135deg, ${accent}, #34d399)`, color:'#fff', padding:'18px 20px 16px', display:'flex', alignItems:'center', gap:12, zIndex:2 }}>
          <button onClick={onClose} aria-label="Close" style={{ width:32, height:32, borderRadius:'50%', background:'rgba(255,255,255,0.18)', border:'none', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1rem', flexShrink:0 }}>‹</button>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:'0.65rem', letterSpacing:2, opacity:0.85, textTransform:'uppercase' }}>{sportName} · {m?.competition?.name}</div>
            <div style={{ fontSize:'1rem', fontWeight:800, marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {homeName} <span style={{ opacity:0.7 }}>vs</span> {awayName}
            </div>
            <div style={{ fontSize:'0.7rem', opacity:0.85, marginTop:2 }}>{dateStr}{m?.court?.name ? ' · ' + m.court.name : ''}</div>
          </div>
        </div>

        {/* Scoreline strip */}
        <div style={{ padding:'18px 20px', borderBottom:'1px solid rgba(255,255,255,0.09)' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr', alignItems:'center', gap:14 }}>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:'0.7rem', color:'#9aa0ab', letterSpacing:1, textTransform:'uppercase', marginBottom:2 }}>{homeName}</div>
              <div style={{ fontSize:'2rem', fontWeight:900, color: winningSide === 'home' ? accent : '#3b3b4f' }}>{homeScore}</div>
            </div>
            <div style={{ fontSize:'0.75rem', color:'#9aa0ab', fontWeight:700 }}>vs</div>
            <div style={{ textAlign:'left' }}>
              <div style={{ fontSize:'0.7rem', color:'#9aa0ab', letterSpacing:1, textTransform:'uppercase', marginBottom:2 }}>{awayName}</div>
              <div style={{ fontSize:'2rem', fontWeight:900, color: winningSide === 'away' ? accent : '#3b3b4f' }}>{awayScore}</div>
            </div>
          </div>
          {winningSide && (
            <div style={{ marginTop:10, textAlign:'center', fontSize:'0.78rem', color: accent, fontWeight:700 }}>
              {winningSide === 'home' ? homeName : awayName} won by {margin} {isCricket ? 'runs' : 'points'}
            </div>
          )}
        </div>

        {/* "Your career so far" strip — context-grabbing personal stats so
            every match recap reminds the player how their whole journey looks.
            Spawtz never wrapped match data + career data into one view. */}
        {career && (career.totalGames > 0 || career.totalRuns > 0 || career.totalWickets > 0 || career.totalGoals > 0) && (
          <div style={{ padding:'14px 20px', background:'#fafafe', borderBottom:'1px solid rgba(255,255,255,0.09)' }}>
            <div style={{ fontSize:'0.65rem', color:'#9aa0ab', letterSpacing:2, textTransform:'uppercase', fontWeight:700, marginBottom:8 }}>Your career so far</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(74px, 1fr))', gap:8 }}>
              <CareerChip value={career.totalGames || 0} label="Games" />
              <CareerChip value={career.totalWins || 0} label="Wins" color="#10b981" />
              <CareerChip value={(career.winRate || 0) + '%'} label="Win Rate" color={accent} />
              {career.totalRuns > 0 && <CareerChip value={career.totalRuns} label="Runs" color="#92400e" />}
              {career.totalWickets > 0 && <CareerChip value={career.totalWickets} label="Wickets" color="#e74c3c" />}
              {career.totalGoals > 0 && <CareerChip value={career.totalGoals} label="Goals" color="#3b82f6" />}
            </div>
          </div>
        )}

        {/* Cricket-specific recap */}
        {isCricket && cricket && (
          <>
            {/* Top performers (the lead) */}
            {(topScorer || topWicketTaker) && (
              <div style={{ padding:'14px 20px', borderBottom:'1px solid rgba(255,255,255,0.09)' }}>
                <div style={{ fontSize:'0.65rem', color:'#9aa0ab', letterSpacing:2, textTransform:'uppercase', fontWeight:700, marginBottom:8 }}>Top performers</div>
                <div style={{ display:'grid', gridTemplateColumns: topScorer && topWicketTaker ? '1fr 1fr' : '1fr', gap:8 }}>
                  {topScorer && (
                    <div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:10, padding:'10px 12px' }}>
                      <div style={{ fontSize:'0.62rem', color:'#92400e', letterSpacing:1.5, textTransform:'uppercase', fontWeight:700 }}> Most runs</div>
                      <div style={{ fontSize:'0.95rem', fontWeight:700, color:'#3b3b4f', marginTop:2 }}>{topScorer.name}</div>
                      <div style={{ fontSize:'1.3rem', fontWeight:900, color:'#92400e' }}>{topScorer.runs}</div>
                    </div>
                  )}
                  {topWicketTaker && (
                    <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'10px 12px' }}>
                      <div style={{ fontSize:'0.62rem', color:'#dc2626', letterSpacing:1.5, textTransform:'uppercase', fontWeight:700 }}> Most wickets</div>
                      <div style={{ fontSize:'0.95rem', fontWeight:700, color:'#3b3b4f', marginTop:2 }}>{topWicketTaker.name}</div>
                      <div style={{ fontSize:'1.3rem', fontWeight:900, color:'#dc2626' }}>{topWicketTaker.wickets}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* PLAYER CONTRIBUTIONS — ranked highest to lowest. The single
                stat indoor cricketers care about most. Top contributor gets
                a gold-bordered hero callout; the rest sit in a clean ranked
                strip with their +/- in team colour. Players will scroll
                straight to this section to see where they finished. */}
            {contributions.length > 0 && (() => {
              const viewerIdx = viewerKey ? contributions.findIndex(c => c.name.trim().toLowerCase() === viewerKey) : -1;
              const viewerEntry = viewerIdx >= 0 ? contributions[viewerIdx] : null;
              return (
              <div style={{ padding:'14px 20px', borderBottom:'1px solid rgba(255,255,255,0.09)' }}>
                <div style={{ fontSize:'0.65rem', color:'#9aa0ab', letterSpacing:2, textTransform:'uppercase', fontWeight:700, marginBottom:8 }}>★ Player contributions · ranked</div>

                {/* Personal callout — only when the viewed player actually
                    played in this match. Tells them "you finished #N" up
                    top so they don't have to scan the list. */}
                {viewerEntry && (
                  <div style={{ padding:'10px 14px', borderRadius:10, background: viewerEntry.contribution >= 0 ? 'rgba(16,185,129,0.08)' : 'rgba(220,38,38,0.06)', border: `1.5px solid ${viewerEntry.contribution >= 0 ? '#10b981' : '#dc2626'}`, marginBottom:10, display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:36, height:36, borderRadius:'50%', background: viewerEntry.contribution >= 0 ? '#10b981' : '#dc2626', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:'0.95rem', flexShrink:0 }}>
                      #{viewerIdx + 1}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'0.62rem', color:'#9aa0ab', letterSpacing:1.5, fontWeight:700, textTransform:'uppercase' }}>You finished</div>
                      <div style={{ fontSize:'0.95rem', fontWeight:800, color:'#3b3b4f', marginTop:1 }}>
                        #{viewerIdx + 1} of {contributions.length}
                      </div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontSize:'1.6rem', fontWeight:900, color: viewerEntry.contribution >= 0 ? '#10b981' : '#dc2626', lineHeight:1 }}>
                        {viewerEntry.contribution >= 0 ? '+' : ''}{viewerEntry.contribution}
                      </div>
                    </div>
                  </div>
                )}
                {/* Top contributor hero */}
                {topContributor && topContributor.contribution >= 0 && (
                  <div style={{ padding:'12px 14px', borderRadius:10, background:'linear-gradient(135deg, #fef3c7, #fff7ed)', border:'1.5px solid #fbbf24', marginBottom:10 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ fontSize:'1.4rem' }}></div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:'0.6rem', color:'#92400e', letterSpacing:1.5, fontWeight:800, textTransform:'uppercase' }}>Top contributor</div>
                        <div style={{ fontSize:'1rem', fontWeight:800, color:'#3b3b4f', marginTop:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{topContributor.name}</div>
                        <div style={{ fontSize:'0.7rem', color:'#9aa0ab', marginTop:1 }}>
                          {topContributor.batRuns} bat · {topContributor.bowlRuns} bowl{topContributor.wickets > 0 ? ' · ' + topContributor.wickets + 'w' : ''}
                        </div>
                      </div>
                      <div style={{ textAlign:'right' }}>
                        <div style={{ fontSize:'1.7rem', fontWeight:900, color:'#92400e', lineHeight:1 }}>+{topContributor.contribution}</div>
                        <div style={{ fontSize:'0.55rem', color:'#92400e', letterSpacing:1.5, textTransform:'uppercase', marginTop:2 }}>Contribution</div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Full ranked list */}
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  {contributions.map((c, idx) => {
                    const isPositive = c.contribution > 0;
                    const isZero = c.contribution === 0;
                    const teamColor = c.team === 'home' ? '#10b981' : '#3b82f6';
                    const isViewer = viewerKey && c.name.trim().toLowerCase() === viewerKey;
                    return (
                      <div key={c.name} style={{ display:'grid', gridTemplateColumns:'24px 1fr auto auto', alignItems:'center', gap:8, padding:'8px 12px', background: isViewer ? `${accent}14` : (idx === 0 ? 'transparent' : '#fafafe'), border: isViewer ? `1.5px solid ${accent}` : '1.5px solid transparent', borderRadius:6 }}>
                        <div style={{ fontSize:'0.7rem', color:'#9aa0ab', fontWeight:700, textAlign:'center' }}>#{idx + 1}</div>
                        <div style={{ minWidth:0, overflow:'hidden' }}>
                          <div style={{ fontSize:'0.85rem', fontWeight:isViewer ? 800 : 600, color:'#3b3b4f', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {c.name}
                            {isViewer && <span style={{ marginLeft:6, fontSize:'0.55rem', fontWeight:800, color:'#fff', background:accent, padding:'2px 6px', borderRadius:3, letterSpacing:1, textTransform:'uppercase', verticalAlign:'middle' }}>YOU</span>}
                          </div>
                          <div style={{ fontSize:'0.62rem', color:teamColor, fontWeight:700, marginTop:1 }}>{c.team === 'home' ? homeName : awayName}</div>
                        </div>
                        <div style={{ fontSize:'0.66rem', color:'#9aa0ab', textAlign:'right' }}>
                          <div>{c.batRuns}b · {c.bowlRuns}c</div>
                          {c.wickets > 0 && <div style={{ color:'#dc2626' }}>{c.wickets}w</div>}
                        </div>
                        <div style={{
                          minWidth:48, textAlign:'right',
                          fontSize:'1rem', fontWeight:900,
                          color: isPositive ? '#10b981' : isZero ? '#9aa0ab' : '#dc2626',
                        }}>
                          {isPositive ? '+' : ''}{c.contribution}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              );
            })()}

            <CricketInnings teamName={homeName} pairs={homeInnings} expandedPair={expandedPair.home} setExpanded={i => setExpandedPair(s => ({ ...s, home: s.home === i ? -1 : i }))} accent={accent} />
            <CricketBowling teamName={homeName + ' bowled'} bowlers={awayBowling} />

            <CricketInnings teamName={awayName} pairs={awayInnings} expandedPair={expandedPair.away} setExpanded={i => setExpandedPair(s => ({ ...s, away: s.away === i ? -1 : i }))} accent={accent} />
            <CricketBowling teamName={awayName + ' bowled'} bowlers={homeBowling} />
          </>
        )}

        {/* Non-cricket sports: lighter summary */}
        {!isCricket && (
          <div style={{ padding:'20px', textAlign:'center', color:'#9aa0ab' }}>
            <div style={{ fontSize:'2.5rem', marginBottom:8 }}></div>
            <div style={{ fontSize:'0.9rem' }}>Full match breakdowns for {sportName.toLowerCase()} are coming next.</div>
            <div style={{ fontSize:'0.78rem', marginTop:4 }}>For now, check the venue's match report at wisasports.com.</div>
          </div>
        )}

        {/* CTA strip — deep-link to the venue's full WISA match report
            (ball-by-ball replay + detailed best & fairest votes + extra
            stats Spawtz never surfaced). Falls back to wildcatz if a
            venue slug isn't on the match row. */}
        {m?.id && (
          <a
            href={`https://www.wisasports.com/${m?.venue?.slug || 'wildcatz'}/match/${m.id}`}
            target="_blank" rel="noopener noreferrer" style={{
              display:'block', margin:'14px 20px 6px',
              padding:'14px 16px', borderRadius:12,
              background:`linear-gradient(135deg, ${accent}, #34d399)`,
              color:'#fff', textDecoration:'none',
              textAlign:'center', fontWeight:800, fontSize:'0.95rem',
              letterSpacing:'0.3px',
              boxShadow:`0 6px 18px ${accent}33`,
            }}
          >
            View full match report →
          </a>
        )}
        <div style={{ padding:'10px 20px 18px', textAlign:'center', color:'#bababf', fontSize:'0.7rem' }}>
          Full ball-by-ball replay, votes &amp; advanced stats on wisasports.com
        </div>
      </div>
    </div>
  );
}

function CareerChip({ value, label, color }) {
  return (
    <div style={{ background:'#fff', border:'1px solid rgba(255,255,255,0.09)', borderRadius:10, padding:'8px 6px', textAlign:'center' }}>
      <div style={{ fontSize:'1.1rem', fontWeight:900, color: color || '#3b3b4f', lineHeight:1 }}>{value}</div>
      <div style={{ fontSize:'0.6rem', color:'#9aa0ab', letterSpacing:1, textTransform:'uppercase', marginTop:3 }}>{label}</div>
    </div>
  );
}

function CricketInnings({ teamName, pairs, expandedPair, setExpanded, accent }) {
  if (!pairs || pairs.length === 0) return null;
  // 2026-06-03: display pair runs NET of the 5-run-per-wicket penalty so the
  // sum of all pairs matches the team total at the top of the recap. Previously
  // showed gross pair runs (sum 119) next to team total (84 = 119 − 7×5), which
  // looked wrong to anyone glancing at the page.
  const WICKET_PENALTY = 5;
  const netRunsFor = (p) => (p.pairRuns || 0) - WICKET_PENALTY * (p.pairWickets || 0);
  const total = pairs.reduce((s, p) => s + netRunsFor(p), 0);
  const wkts = pairs.reduce((s, p) => s + p.pairWickets, 0);
  return (
    <div style={{ padding:'14px 20px', borderBottom:'1px solid rgba(255,255,255,0.09)' }}>
      <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:8 }}>
        <div style={{ fontSize:'0.78rem', fontWeight:800, color:'#3b3b4f' }}>{teamName} batting</div>
        <div style={{ fontSize:'0.95rem', fontWeight:900, color: accent }}>{total}<span style={{ fontSize:'0.7rem', color:'#9aa0ab', fontWeight:600 }}> / {wkts} wkts</span></div>
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
        {pairs.map((p, idx) => (
          <div key={idx} style={{ background:'#fafafe', borderRadius:8, padding:'10px 12px' }}>
            <div onClick={() => setExpanded(idx)} style={{ cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:22, height:22, borderRadius:6, background: accent, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.65rem', fontWeight:800, flexShrink:0 }}>P{p.pairNum}</div>
              <div style={{ flex:1, minWidth:0, fontSize:'0.78rem', color:'#3b3b4f' }}>
                <div style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
                  <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.batter1} <span style={{ color:'#9aa0ab' }}>· {p.runs1}</span></span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', gap:8, marginTop:2 }}>
                  <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.batter2} <span style={{ color:'#9aa0ab' }}>· {p.runs2}</span></span>
                </div>
              </div>
              <div style={{ textAlign:'right', flexShrink:0 }}>
                {/* 2026-06-03: show NET runs (after wicket penalty) so the
                    column sums to the team total above. Tooltip exposes the
                    gross run total in case anyone wants the raw figure. */}
                <div title={`${p.pairRuns} raw runs − ${WICKET_PENALTY*(p.pairWickets||0)} wkt penalty`} style={{ fontSize:'1.1rem', fontWeight:900, color:'#3b3b4f', lineHeight:1 }}>{netRunsFor(p)}</div>
                <div style={{ fontSize:'0.6rem', color:'#9aa0ab', marginTop:2 }}>{p.pairWickets} wkt{p.pairWickets===1?'':'s'}</div>
              </div>
              <div style={{ color:'#bababf', fontSize:'0.9rem', flexShrink:0 }}>{expandedPair === idx ? '▾' : '▸'}</div>
            </div>
            {expandedPair === idx && p.balls.length > 0 && (
              <div style={{ marginTop:10, padding:'8px 4px 4px', borderTop:'1px solid rgba(255,255,255,0.09)' }}>
                <div style={{ fontSize:'0.6rem', color:'#9aa0ab', letterSpacing:1.5, textTransform:'uppercase', fontWeight:700, marginBottom:6 }}>Ball by ball</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(70px, 1fr))', gap:4 }}>
                  {p.balls.map((b, i) => {
                    const isWicket = b.type === 'wicket';
                    const isExtra = b.type === 'wide' || b.type === 'noball';
                    const isBoundary = b.type === 'run' && (b.runs === 4 || b.runs >= 6);
                    const labelText = isWicket ? 'W' : isExtra ? (b.type === 'wide' ? 'WD' : 'NB') : (b.runs ?? 0);
                    const bg = isWicket ? '#fef2f2' : isExtra ? '#fef9c3' : isBoundary ? '#dcfce7' : (b.runs === 0 ? '#f3f4f6' : '#fff');
                    const fg = isWicket ? '#dc2626' : isExtra ? '#a16207' : isBoundary ? '#15803d' : '#3b3b4f';
                    return (
                      <div key={i} title={`${b.batter} off ${b.bowler}${b.dismissal ? ' — ' + b.dismissal : ''}`} style={{ background:bg, color:fg, padding:'4px 6px', borderRadius:5, textAlign:'center', fontSize:'0.7rem', fontWeight:700, border:'1px solid rgba(255,255,255,0.09)' }}>
                        {labelText}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CricketBowling({ teamName, bowlers }) {
  if (!bowlers || bowlers.length === 0) return null;
  return (
    <div style={{ padding:'14px 20px', borderBottom:'1px solid rgba(255,255,255,0.09)' }}>
      <div style={{ fontSize:'0.78rem', fontWeight:800, color:'#3b3b4f', marginBottom:8 }}>{teamName}</div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 50px 40px 50px', gap:'4px 10px', alignItems:'center', fontSize:'0.65rem', color:'#9aa0ab', letterSpacing:1, textTransform:'uppercase', fontWeight:700, paddingBottom:4, borderBottom:'1px solid rgba(255,255,255,0.09)' }}>
        <span>Bowler</span><span style={{ textAlign:'right' }}>Ov</span><span style={{ textAlign:'right' }}>W</span><span style={{ textAlign:'right' }}>R</span>
      </div>
      {bowlers.map((b, i) => (
        <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 50px 40px 50px', gap:'4px 10px', alignItems:'center', padding:'6px 0', borderBottom: i < bowlers.length - 1 ? '1px solid #f7f7fb' : 'none', fontSize:'0.85rem' }}>
          <span style={{ color:'#3b3b4f', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{b.name}</span>
          <span style={{ textAlign:'right', color:'#9aa0ab' }}>{b.overs}</span>
          <span style={{ textAlign:'right', fontWeight:800, color: b.wickets > 0 ? '#dc2626' : '#9aa0ab' }}>{b.wickets}</span>
          <span style={{ textAlign:'right', color:'#3b3b4f' }}>{b.runs}</span>
        </div>
      ))}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ─── TEAM PROFILE MODAL ─────────────────────────────────────────────────────
// Public, shareable. The artefact captains forward to recruits + opposition.
// Header (team + sport + comp), headline stats, recent form, squad table
// RANKED by avg contribution, recent matches, next fixture, share button.
// ═════════════════════════════════════════════════════════════════════════════
function TeamProfileModal({ data, accent, onClose, onOpenPlayer, onOpenMatch }) {
  if (!data) {
    return (
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', backdropFilter:'blur(4px)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
        <div style={{ background:'#fff', borderRadius:14, padding:'24px 28px', fontSize:'0.9rem', color:'#555' }}>Loading team…</div>
      </div>
    );
  }
  if (data.error) {
    return (
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
        <div style={{ background:'#fff', borderRadius:14, padding:'24px 28px', fontSize:'0.9rem', color:'#dc2626' }}>Could not load this team: {data.error}</div>
      </div>
    );
  }
  const { team, ladder, roster, form, nextMatch, recentMatches, sportSlug, rating } = data;
  const teamName = team?.name || 'Team';
  const sportName = team?.competition?.sport?.name || '';
  const compName = team?.competition?.name || '';
  const winRate = ladder && ladder.played > 0 ? Math.round((ladder.wins / ladder.played) * 100) : null;
  const isCricket = sportSlug === 'cricket';

  // Initials for the avatar circle
  const initials = teamName.split(/\s+/).slice(0,2).map(s => s[0] || '').join('').toUpperCase().slice(0,2) || 'T';

  const handleShare = async () => {
    const url = `${window.location.origin}/?team=${team.id}`;
    const shareText = `Check out ${teamName} on All4Indoor — ${ladder ? `${ladder.wins} wins · ${ladder.played} games` : 'live stats'}.`;
    try {
      if (navigator.share) {
        await navigator.share({ title: teamName + ' on All4Indoor', text: shareText, url });
        return;
      }
    } catch {}
    try {
      await navigator.clipboard.writeText(url);
      alert('Link copied — paste it anywhere');
    } catch {
      alert(url);
    }
  };

  const formColor = (r) => r === 'W' ? '#10b981' : r === 'L' ? '#dc2626' : '#9aa0ab';

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', backdropFilter:'blur(4px)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div onClick={e => e.stopPropagation()} className="a4i-fadein" style={{ background:'#fff', borderRadius:20, maxWidth:720, width:'100%', maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
        {/* Hero header */}
        <div style={{ background:'linear-gradient(135deg, #1a1a2e, #0d4a42, #ff2e17)', color:'#fff', padding:'28px 32px', position:'relative' }}>
          <button onClick={onClose} style={{ position:'absolute', top:16, right:16, background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', width:32, height:32, borderRadius:'50%', cursor:'pointer', fontSize:'1.1rem', display:'flex', alignItems:'center', justifyContent:'center' }} aria-label="Close">×</button>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            {/* 2026-05-31: sport-themed gradient on team avatar — matches
                the player profile treatment. Falls back to the brand
                accent gradient when sportSlug is unknown. */}
            {(() => {
              const grad = (sportSlug && SPORT_GRADIENTS[sportSlug]) || `linear-gradient(135deg, ${accent}, #34d399)`;
              return (
                <div style={{ width:76, height:76, borderRadius:18, background:grad, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:900, fontSize:'1.6rem', border:'3px solid rgba(255,255,255,0.3)' }}>
                  {team?.logo_url ? <img src={team.logo_url} alt="" style={{ width:'100%', height:'100%', borderRadius:15, objectFit:'cover' }} /> : initials}
                </div>
              );
            })()}
            <div style={{ flex:1, minWidth:0 }}>
              <h2 style={{ fontSize:'1.55rem', fontWeight:900, margin:'0 0 4px' }}>{teamName}</h2>
              {/* 2026-05-31: auto team title — Dynasty / Title Contender /
                  Hot Hand / Veterans / Tough Out / Rising. Derived from
                  ladder record so a glance tells the visitor who they're
                  looking at. */}
              {(() => {
                if (!ladder || ladder.played < 3) return null;
                const t = teamTitle({ wins: ladder.wins, losses: ladder.losses, draws: ladder.draws });
                if (!t) return null;
                return (
                  <div style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'3px 10px', background:'rgba(251,191,36,0.18)', border:'1px solid rgba(251,191,36,0.4)', borderRadius:999, fontSize:'0.72rem', fontWeight:700, color:'#fde68a', marginBottom:4 }}>
                    <span>{t.icon}</span><span style={{ letterSpacing:0.5 }}>{t.label}</span>
                  </div>
                );
              })()}
              <div style={{ fontSize:'0.85rem', opacity:0.85 }}>{sportName} · {compName}</div>
              {ladder?.position && (
                <div style={{ display:'inline-block', marginTop:8, padding:'4px 10px', background:'rgba(255,255,255,0.15)', borderRadius:6, fontSize:'0.78rem', fontWeight:700 }}>
                  #{ladder.position} on the ladder
                </div>
              )}
            </div>
            <button onClick={handleShare} style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.3)', color:'#fff', padding:'8px 14px', borderRadius:8, cursor:'pointer', fontSize:'0.78rem', fontWeight:700, whiteSpace:'nowrap' }}>
              ⇪ Share
            </button>
          </div>
        </div>

        {/* Headline stat tiles */}
        <div style={{ padding:'20px 28px 0', display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:10 }}>
          <StatTile value={ladder?.played ?? '—'} label="Games" />
          <StatTile value={ladder?.wins ?? '—'} label="Wins" color="#10b981" />
          <StatTile value={winRate != null ? winRate + '%' : '—'} label="Win Rate" color={accent} />
          <StatTile value={ladder?.points ?? '—'} label="Pts" color="#fbbf24" />
        </div>

        {/* 2026-05-28: Team rating breakdown — total + result vs scoring split,
            plus best/worst season cards. Skipped if rating hasn't loaded or
            team hasn't played enough matches. */}
        {rating && rating.played > 0 && (
          <div style={{ padding:'18px 28px 0' }}>
            <div style={{ fontSize:'0.65rem', color:'#9aa0ab', letterSpacing:2, fontWeight:700, textTransform:'uppercase', marginBottom:8 }}>Team rating</div>
            <div style={{ background: rating.rating >= 0 ? 'linear-gradient(135deg, #ecfdf5, #d1fae5)' : 'linear-gradient(135deg, #fef2f2, #fecaca)', borderRadius:14, padding:'14px 16px', border:`1px solid ${rating.rating >= 0 ? '#a7f3d0' : '#fca5a5'}` }}>
              <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
                <div>
                  <div style={{ fontFamily:"'Black Han Sans', sans-serif", fontSize:'2.4rem', fontWeight:900, lineHeight:1, color: rating.rating >= 0 ? '#047857' : '#b91c1c' }}>
                    {rating.rating > 0 ? '+' : ''}{rating.rating}
                  </div>
                  <div style={{ fontSize:'0.72rem', color:'#6b7280', marginTop:4, fontWeight:600 }}>All-time rating</div>
                </div>
                <div style={{ display:'flex', gap:14, fontSize:'0.75rem', color:'#9aa0ab' }}>
                  <div>
                    <div style={{ fontSize:'0.62rem', color:'#6b7280', fontWeight:700, letterSpacing:1, textTransform:'uppercase' }}>Result pts</div>
                    <div style={{ fontWeight:800, fontSize:'1rem', color: rating.resultPoints >= 0 ? '#047857' : '#b91c1c' }}>{rating.resultPoints > 0 ? '+' : ''}{rating.resultPoints}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:'0.62rem', color:'#6b7280', fontWeight:700, letterSpacing:1, textTransform:'uppercase' }}>Scoring pts</div>
                    <div style={{ fontWeight:800, fontSize:'1rem', color: rating.scoringPoints >= 0 ? '#047857' : '#b91c1c' }}>{rating.scoringPoints > 0 ? '+' : ''}{rating.scoringPoints}</div>
                  </div>
                </div>
              </div>
              {(rating.bestSeason || rating.worstSeason) && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop:12 }}>
                  {rating.bestSeason && (
                    <div style={{ background:'#fff', borderRadius:10, padding:'8px 12px', border:'1px solid rgba(255,255,255,0.09)' }}>
                      <div style={{ fontSize:'0.6rem', color:'#9aa0ab', fontWeight:700, letterSpacing:1, textTransform:'uppercase' }}> Best season</div>
                      <div style={{ fontWeight:700, fontSize:'0.82rem', marginTop:2 }}>{rating.bestSeason.season}</div>
                      <div style={{ fontSize:'0.7rem', color:'#047857', fontWeight:700, marginTop:1 }}>+{rating.bestSeason.rating} · {rating.bestSeason.wins}W-{rating.bestSeason.losses}L</div>
                    </div>
                  )}
                  {rating.worstSeason && rating.worstSeason.season !== rating.bestSeason?.season && (
                    <div style={{ background:'#fff', borderRadius:10, padding:'8px 12px', border:'1px solid rgba(255,255,255,0.09)' }}>
                      <div style={{ fontSize:'0.6rem', color:'#9aa0ab', fontWeight:700, letterSpacing:1, textTransform:'uppercase' }}> Worst season</div>
                      <div style={{ fontWeight:700, fontSize:'0.82rem', marginTop:2 }}>{rating.worstSeason.season}</div>
                      <div style={{ fontSize:'0.7rem', color:'#b91c1c', fontWeight:700, marginTop:1 }}>{rating.worstSeason.rating > 0 ? '+' : ''}{rating.worstSeason.rating} · {rating.worstSeason.wins}W-{rating.worstSeason.losses}L</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recent form */}
        {form.length > 0 && (
          <div style={{ padding:'18px 28px 0' }}>
            <div style={{ fontSize:'0.65rem', color:'#9aa0ab', letterSpacing:2, fontWeight:700, textTransform:'uppercase', marginBottom:8 }}>Recent form · newest first</div>
            <div style={{ display:'flex', gap:6 }}>
              {form.map((r, i) => (
                <div key={i} style={{ width:32, height:32, borderRadius:8, background:formColor(r), color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:'0.85rem' }}>
                  {r}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Next fixture */}
        {nextMatch && (
          <div style={{ padding:'18px 28px 0' }}>
            <div style={{ fontSize:'0.65rem', color:'#9aa0ab', letterSpacing:2, fontWeight:700, textTransform:'uppercase', marginBottom:8 }}>Next fixture</div>
            <div style={{ background:'#fafafe', borderRadius:10, padding:'12px 14px', border:'1px solid rgba(255,255,255,0.09)' }}>
              <div style={{ fontSize:'0.92rem', fontWeight:700, color:'#3b3b4f' }}>
                {nextMatch.home_team?.name} vs {nextMatch.away_team?.name}
              </div>
              <div style={{ fontSize:'0.75rem', color:'#9aa0ab', marginTop:3 }}>
                {new Date(nextMatch.scheduled_at).toLocaleString('en-AU', { weekday:'short', day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}
                {nextMatch.court?.name && ` · ${nextMatch.court.name}`}
              </div>
            </div>
          </div>
        )}

        {/* Squad — RANKED by avg contribution */}
        <div style={{ padding:'20px 28px 0' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:10 }}>
            <div style={{ fontSize:'0.65rem', color:'#9aa0ab', letterSpacing:2, fontWeight:700, textTransform:'uppercase' }}>
              Squad{isCricket ? ' · ranked by avg contribution' : ''}
            </div>
            <div style={{ fontSize:'0.7rem', color:'#9aa0ab' }}>{roster.length} player{roster.length === 1 ? '' : 's'}</div>
          </div>
          {roster.length === 0 ? (
            <div style={{ padding:'14px', textAlign:'center', color:'#9aa0ab', fontSize:'0.85rem', background:'#fafafe', borderRadius:8 }}>No players registered yet</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
              {roster.map((p, idx) => {
                const av = p.avgContribution;
                const avColor = av == null ? '#9aa0ab' : av > 0 ? '#10b981' : av < 0 ? '#dc2626' : '#9aa0ab';
                return (
                  <div key={p.userId}
                    onClick={() => onOpenPlayer?.(p.userId)}
                    style={{ display:'grid', gridTemplateColumns:'28px 1fr auto', alignItems:'center', gap:10, padding:'10px 12px', background:'#fafafe', borderRadius:8, cursor:'pointer', transition:'background 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f0fdf4'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fafafe'; }}>
                    <div style={{ width:24, height:24, borderRadius:6, background: idx === 0 ? '#fbbf24' : 'rgba(255,255,255,0.14)', color: idx === 0 ? '#92400e' : '#6b7280', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.7rem', fontWeight:800 }}>
                      {idx + 1}
                    </div>
                    <div style={{ minWidth:0 }}>
                      <div style={{ fontSize:'0.92rem', fontWeight:700, color:'#3b3b4f', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {p.name}
                        {p.isCaptain && <span style={{ marginLeft:6, fontSize:'0.55rem', fontWeight:800, color:'#ca8a04', background:'rgba(202,138,4,0.12)', padding:'2px 6px', borderRadius:3, letterSpacing:1, textTransform:'uppercase' }}>Captain</span>}
                      </div>
                      <div style={{ fontSize:'0.68rem', color:'#9aa0ab', marginTop:1 }}>
                        {p.gamesForTeam > 0
                          ? `${p.gamesForTeam} game${p.gamesForTeam === 1 ? '' : 's'} · ${p.wins} win${p.wins === 1 ? '' : 's'}${p.winRate != null ? ' (' + p.winRate + '%)' : ''}`
                          : 'No games yet'}
                        {isCricket && p.gamesForTeam > 0 && ` · ${p.batRuns} runs · ${p.wickets} wkts`}
                      </div>
                    </div>
                    {/* TWO-LINE stat block: cumulative total on top, per-game
                        avg underneath. Total is the headline Spawtz-style"all time on this team" number; avg shows form. */}
                    <div style={{ textAlign:'right' }}>
                      {av != null ? (
                        <>
                          <div style={{ fontSize:'1.1rem', fontWeight:900, color:avColor, lineHeight:1 }}>
                            {p.totalContribution >= 0 ? '+' : ''}{p.totalContribution}
                          </div>
                          <div style={{ fontSize:'0.6rem', color:'#9aa0ab', letterSpacing:0.5, marginTop:2 }}>
                            Total · <span style={{ color:avColor, fontWeight:700 }}>{av >= 0 ? '+' : ''}{av}/game</span>
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize:'0.7rem', color:'#bababf' }}>—</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent matches — clickable */}
        {recentMatches.length > 0 && (
          <div style={{ padding:'20px 28px 0' }}>
            <div style={{ fontSize:'0.65rem', color:'#9aa0ab', letterSpacing:2, fontWeight:700, textTransform:'uppercase', marginBottom:8 }}>Recent matches · tap for recap</div>
            <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
              {recentMatches.map(m => {
                const c = formColor(m.result);
                return (
                  <div key={m.id} onClick={() => onOpenMatch?.(m.id)}
                    style={{ display:'grid', gridTemplateColumns:'28px 1fr 70px 60px 16px', alignItems:'center', gap:10, padding:'8px 12px', background:'#fafafe', borderRadius:6, cursor:'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f0fdf4'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fafafe'; }}>
                    <div style={{ width:22, height:22, borderRadius:5, background:c, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:'0.7rem' }}>{m.result}</div>
                    <div style={{ fontSize:'0.84rem', color:'#3b3b4f', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>vs {m.oppName}</div>
                    <div style={{ fontSize:'0.85rem', fontWeight:800, color:c, textAlign:'center' }}>{m.usScore}–{m.oppScore}</div>
                    <div style={{ fontSize:'0.7rem', color:'#9aa0ab', textAlign:'right' }}>{m.when}</div>
                    <div style={{ color:'#9aa0ab', textAlign:'right' }}>›</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ padding:'18px 28px 22px', marginTop:18, textAlign:'center', color:'#bababf', fontSize:'0.72rem' }}>
          {teamName} on all4indoor · the global indoor sports network
        </div>
      </div>
    </div>
  );
}

function StatTile({ value, label, color }) {
  return (
    <div style={{ background:'#fafafe', borderRadius:10, padding:'14px 8px', textAlign:'center' }}>
      <div style={{ fontSize:'1.4rem', fontWeight:900, color: color || '#3b3b4f' }}>{value}</div>
      <div style={{ fontSize:'0.65rem', color:'#9aa0ab', letterSpacing:1, textTransform:'uppercase' }}>{label}</div>
    </div>
  );
}

// Small label-value row used inside the Career Cricket panel. `bold`
// flags the headline stats (Runs, Strike Rate, Wickets, Economy) so the
// eye lands on those; `muted` greys the row a touch for "dot balls" type
// numbers that are interesting but not flex-worthy.
function CareerRow({ label, value, bold, muted }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: '0.78rem', color: muted ? '#9aa0ab' : '#3b3b4f' }}>
      <span style={{ color: '#9aa0ab' }}>{label}</span>
      <span style={{ fontWeight: bold ? 900 : 600, fontSize: bold ? '0.95rem' : '0.82rem' }}>{value}</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TRADING CARD MODAL
// 1080×1080 SVG-based player card. Designed to be shared to group chats,
// posted to socials, and used in the Sunday "Your Week" wrap. Pure SVG so
// it renders crisply at any export size. PNG export via canvas+blob, with
// Web Share API on supported devices and a download fallback otherwise.
//
// Inputs come pre-flattened from the parent player profile so we don't have
// to re-fetch anything — we just need to draw.
// ═══════════════════════════════════════════════════════════════════════════
function TradingCardModal({ player, onClose }) {
  const svgRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);

  // Build the SVG markup once per render. 1080x1080 base; CSS scales for
  // on-screen preview, but the export math uses the full intrinsic size.
  const W = 1080, H = 1080;
  const TEAL = '#ff2e17', ORANGE = '#ff4d00', GOLD = '#fbbf24';

  // ── Wildcatz logo as data URL ────────────────────────────────────────
  // Pre-fetch /wildcatz-logo.png and convert to base64 so it can be
  // embedded directly in the SVG. Canvas rasterisation won't fetch
  // external resources from inside an SVG, so data URLs are the safe
  // path. While the logo is loading we fall back to the text wordmark.
  const [logoDataUrl, setLogoDataUrl] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    fetch('/wildcatz-logo.png')
      .then(r => r.blob())
      .then(blob => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }))
      .then(dataUrl => { if (!cancelled) setLogoDataUrl(dataUrl); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const fmtStat = (n, dp = 0) => n == null || isNaN(n) ? '—' : (dp ? (Math.round(n * Math.pow(10, dp)) / Math.pow(10, dp)).toFixed(dp) : n);
  const contribSign = (n) => n == null ? '' : (n >= 0 ? '+' : '');
  const rankLabel = player.ptsRank ? `#${player.ptsRank}` : (player.eloRank ? `#${player.eloRank}` : '—');

  // Sparkline data (up to last 10 matches). Reuse the parent's sparkline if
  // computed, else fall back to empty.
  const spark = Array.isArray(player.sparkline) ? player.sparkline.slice(-10) : [];
  const sparkMax = Math.max(1, ...spark.map(v => Math.abs(v || 0)));

  // Skill badges — keep to top 4 to avoid crowding
  const badges = (player.skillBadges || []).slice(0, 4);

  // Convert the rendered SVG to a PNG blob via the browser canvas API.
  async function exportPng() {
    if (!svgRef.current) return null;
    setBusy(true);
    try {
      const svg = svgRef.current;
      const xml = new XMLSerializer().serializeToString(svg);
      const blob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      const dataUrl = await new Promise((resolve, reject) => {
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = W; canvas.height = H;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, W, H);
          URL.revokeObjectURL(url);
          canvas.toBlob(b => resolve(b), 'image/png');
        };
        img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
        img.src = url;
      });
      return dataUrl;
    } finally {
      setBusy(false);
    }
  }

  async function handleShare() {
    const blob = await exportPng();
    if (!blob) return;
    const file = new File([blob], `${player.name.replace(/\s+/g, '_')}_wildcatz.png`, { type: 'image/png' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: `${player.name} — Wildcatz`,
          text: `${player.name} on all4indoor.com — ${player.games} games${player.winRate != null ? `, ${player.winRate}% win rate` : ''}`,
        });
        return;
      } catch {}
    }
    // Fallback: download
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${player.name.replace(/\s+/g, '_')}_wildcatz.png`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }

  async function handleDownload() {
    const blob = await exportPng();
    if (!blob) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${player.name.replace(/\s+/g, '_')}_wildcatz.png`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)', zIndex: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ maxWidth: 520, width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff' }}>
          <div style={{ fontSize: '1rem', fontWeight: 800, letterSpacing: 0.5 }}> Your Card</div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: '1.2rem' }}></button>
        </div>

        {/* The SVG preview — same markup that gets rasterised on export */}
        <div style={{ width: '100%', aspectRatio: '1 / 1', maxHeight: '70vh', background: '#000', borderRadius: 16, overflow: 'hidden', boxShadow: '0 20px 80px rgba(0,0,0,0.5)' }}>
          <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%', display: 'block' }}>
            {/* Background gradient — Wildcatz teal-to-charcoal */}
            <defs>
              <linearGradient id="cardBg" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#0d4a42" />
                <stop offset="50%" stopColor="#1a1a2e" />
                <stop offset="100%" stopColor="#0d3325" />
              </linearGradient>
              <linearGradient id="goldChip" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={GOLD} />
                <stop offset="100%" stopColor="#f97316" />
              </linearGradient>
              <radialGradient id="claws" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="rgba(255,255,255,0.05)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0)" />
              </radialGradient>
            </defs>
            <rect width={W} height={H} fill="url(#cardBg)" />
            {/* WILDCATZ watermark */}
            <text x={W/2} y={H/2 + 80} textAnchor="middle" fontSize="220" fontWeight="900" fill="rgba(26,138,125,0.05)" letterSpacing="20" style={{ fontFamily: "system-ui, sans-serif" }}>WILDCATZ</text>

            {/* Top: brand bar — Wildcatz logo on the left as an embedded
                image (data URL, so canvas export works), all4indoor.com
                tagline on the right. Falls back to the text wordmark
                while the logo is still loading. */}
            <rect x="0" y="0" width={W} height="100" fill="rgba(0,0,0,0.3)" />
            {logoDataUrl ? (
              <image href={logoDataUrl} x="50" y="10" height="80" preserveAspectRatio="xMinYMid meet" />
            ) : (
              <>
                <text x="60" y="60" fontSize="32" fontWeight="900" fill={ORANGE} letterSpacing="6" style={{ fontFamily: 'system-ui, sans-serif' }}>WILDCATZ</text>
                <text x="60" y="78" fontSize="14" fontWeight="700" fill="rgba(255,255,255,0.5)" letterSpacing="4" style={{ fontFamily: 'system-ui, sans-serif' }}>INDOOR SPORTS</text>
              </>
            )}
            <text x={W - 60} y="60" textAnchor="end" fontSize="14" fontWeight="700" fill="rgba(255,255,255,0.45)" letterSpacing="3" style={{ fontFamily: 'system-ui, sans-serif' }}>ALL4INDOOR.COM</text>

            {/* Avatar circle */}
            <circle cx="180" cy="270" r="100" fill="rgba(26,138,125,0.4)" stroke="rgba(255,255,255,0.4)" strokeWidth="6" />
            <text x="180" y="295" textAnchor="middle" fontSize="72" fontWeight="900" fill="#fff" style={{ fontFamily: 'system-ui, sans-serif' }}>{player.avatar || (player.name || '').slice(0,2).toUpperCase()}</text>

            {/* Name — 2026-06-02: tightened auto-shrink for long names. Card
                is 1080 wide and name starts at x=320, so usable width ≈ 720.
                With system-ui caps glyph at ~0.62× font-size, a 72px name
                fits ~16 chars, 56px fits ~20 chars, 44px fits ~26 chars.
                Anyone longer than that gets squeezed via textLength so the
                whole name fits without clipping on iOS Share preview. */}
            {(() => {
              const upper = (player.name || '').toUpperCase();
              const len = upper.length;
              const size = len > 26 ? 40 : len > 20 ? 48 : len > 14 ? 60 : 72;
              const maxW = 720;
              // Estimate width — if longer than maxW even after sizing, use
              // textLength to squeeze it (SVG's built-in fit primitive).
              const estW = len * size * 0.62;
              return (
                <text x="320" y="260" fontSize={size} fontWeight="900" fill="#fff" style={{ fontFamily: 'system-ui, sans-serif' }}
                  {...(estW > maxW ? { textLength: maxW, lengthAdjust: 'spacingAndGlyphs' } : {})}>
                  {upper}
                </text>
              );
            })()}
            <text x="320" y="310" fontSize="22" fontWeight="600" fill="rgba(255,255,255,0.65)" style={{ fontFamily: 'system-ui, sans-serif' }}>{player.sports.slice(0, 3).join(' · ').toUpperCase() || 'WILDCATZ MEMBER'}</text>
            {/* Rank chip */}
            {(player.ptsRank || player.eloRank) && (
              <g>
                <rect x="320" y="335" width="180" height="44" rx="10" fill="url(#goldChip)" />
                <text x="410" y="364" textAnchor="middle" fontSize="22" fontWeight="900" fill="#1a1a2e" style={{ fontFamily: 'system-ui, sans-serif' }}>{` RANK ${rankLabel}`}</text>
              </g>
            )}

            {/* 4-up stats grid */}
            <g transform="translate(60, 440)">
              <StatTileSvg x={0}   y={0} w={460} h={180} label="CONTRIBUTION" value={`${contribSign(player.careerContribution)}${fmtStat(player.careerContribution)}`} sub={`${player.cricketGamesPlayed || 0} cricket games`} accent={TEAL} />
              <StatTileSvg x={500} y={0} w={460} h={180} label="WIN RATE" value={player.winRate != null ? `${fmtStat(player.winRate, 1)}%` : '—'} sub={`${player.games || 0} games`} accent={GOLD} />
              <StatTileSvg x={0}   y={210} w={460} h={180} label="AVG / GAME" value={player.avgContribution != null ? `${contribSign(player.avgContribution)}${fmtStat(player.avgContribution, 1)}` : '—'} sub="Per cricket match" accent="#10b981" />
              <StatTileSvg x={500} y={210} w={460} h={180} label="POINTS" value={player.pts != null ? fmtStat(player.pts) : (player.elo != null ? fmtStat(player.elo) : '—')} sub={player.pts != null ? 'Season points' : 'ELO'} accent={ORANGE} />
            </g>

            {/* Sparkline */}
            {spark.length >= 2 && (() => {
              const x0 = 60, y0 = 870, w = W - 120, h = 80;
              const step = w / (spark.length - 1);
              const points = spark.map((v, i) => {
                const x = x0 + i * step;
                const y = y0 + h/2 - (v / sparkMax) * (h/2 - 4);
                return `${x},${y}`;
              }).join(' ');
              return (
                <g>
                  <line x1={x0} y1={y0 + h/2} x2={x0 + w} y2={y0 + h/2} stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
                  <polyline points={points} fill="none" stroke="#10b981" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
                  {spark.map((v, i) => {
                    const x = x0 + i * step;
                    const y = y0 + h/2 - (v / sparkMax) * (h/2 - 4);
                    return <circle key={i} cx={x} cy={y} r="6" fill={v >= 0 ? '#10b981' : '#ef4444'} stroke="#fff" strokeWidth="2" />;
                  })}
                </g>
              );
            })()}

            {/* Skill badges row */}
            {badges.length > 0 && (
              <g transform={`translate(60, ${spark.length >= 2 ? 760 : 870})`}>
                {badges.map((b, i) => {
                  const x = (i % 2) * 490;
                  const y = Math.floor(i / 2) * 50;
                  return (
                    <g key={i} transform={`translate(${x}, ${y})`}>
                      <rect width="470" height="40" rx="10" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
                      <text x="20" y="27" fontSize="22" style={{ fontFamily: 'system-ui, sans-serif' }}>{b.emoji}</text>
                      <text x="58" y="26" fontSize="18" fontWeight="800" fill={b.color || '#fff'} style={{ fontFamily: 'system-ui, sans-serif' }}>{(b.name || '').toUpperCase()}</text>
                      <text x="58" y="38" fontSize="11" fontWeight="600" fill="rgba(255,255,255,0.55)" style={{ fontFamily: 'system-ui, sans-serif' }}>{b.desc || ''}</text>
                    </g>
                  );
                })}
              </g>
            )}

            {/* Footer band */}
            <rect x="0" y={H - 90} width={W} height="90" fill="rgba(0,0,0,0.45)" />
            <text x={W/2} y={H - 50} textAnchor="middle" fontSize="20" fontWeight="700" fill="rgba(255,255,255,0.65)" letterSpacing="3" style={{ fontFamily: 'system-ui, sans-serif' }}>SEE FULL STATS · ALL4INDOOR.COM</text>
            <text x={W/2} y={H - 22} textAnchor="middle" fontSize="14" fontWeight="600" fill="rgba(255,255,255,0.35)" letterSpacing="2" style={{ fontFamily: 'system-ui, sans-serif' }}>WILDCATZ INDOOR SPORTS · TOWNSVILLE</text>
          </svg>
        </div>

        {/* Action buttons — Share preferred (native share sheet on mobile), Download fallback */}
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button disabled={busy} onClick={handleShare}
            style={{ flex: 2, padding: '14px', borderRadius: 12, border: 'none', background: busy ? '#444' : 'linear-gradient(135deg, #fbbf24, #f97316)', color: '#1a1a2e', fontSize: '0.95rem', fontWeight: 900, cursor: busy ? 'wait' : 'pointer', letterSpacing: 1 }}>
            {busy ? 'GENERATING…' : ' SHARE'}
          </button>
          <button disabled={busy} onClick={handleDownload}
            style={{ flex: 1, padding: '14px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: '0.95rem', fontWeight: 800, cursor: busy ? 'wait' : 'pointer', letterSpacing: 1 }}>
             SAVE
          </button>
        </div>
        <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginTop: 4 }}>
          1080×1080 PNG · perfect for Instagram, group chats, or your trophy wall.
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// YOUR WEEK · SUNDAY WRAP
// Swipeable Spotify-Wrapped style recap of the player's last 7 days.
// Panels: cover → games → contribution → best moment → badges → share.
// All data computed client-side from existing tables — no new endpoints.
// ═══════════════════════════════════════════════════════════════════════════
function WeekWrapModal({ playerId, onClose, onShareCard }) {
  const [data, setData] = React.useState(null);
  const [panel, setPanel] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      // 7 days ending today (Brisbane). Sunday morning push will fire after
      // Saturday matches, so this window captures the whole sports week.
      const end = new Date();
      const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
      const startISO = start.toISOString();
      const [{ data: user }, { data: matchAtt }] = await Promise.all([
        supabase.from('public_players').select('id, first_name, last_name').eq('id', playerId).single(),
        supabase.from('match_attendance')
          .select('match_id, match:matches(id, scheduled_at, home_score, away_score, home_team_id, away_team_id, status, competition:competitions(name, sport:sports(name, slug, icon)))')
          .eq('user_id', playerId)
          .gte('created_at', startISO),
      ]);
      if (cancelled || !user) return;
      const me = { id: user.id, name: `${user.first_name || ''} ${user.last_name || ''}`.trim() };

      // Filter to actual matches this week (completed or in_progress)
      const myMatches = (matchAtt || [])
        .map(a => a.match)
        .filter(m => m && (m.status === 'completed' || m.status === 'in_progress'));

      // Cricket contribution: pull scorecards for any cricket matches
      const cricketIds = myMatches.filter(m => /cricket/i.test(m.competition?.sport?.slug || '')).map(m => m.id);
      const { data: scorecards } = cricketIds.length
        ? await supabase.from('cricket_scorecards').select('match_id, balls_data').in('match_id', cricketIds)
        : { data: [] };
      const cardsByMatch = Object.fromEntries((scorecards || []).map(s => [s.match_id, s]));

      // Compute weekly contribution (bat runs - bowl runs conceded)
      let weekContribution = 0;
      let weekRuns = 0, weekWickets = 0, weekBoundaries = 0;
      const matchScores = []; // { matchId, contribution, oppName, won, scheduled_at }
      for (const m of myMatches) {
        let batRuns = 0, bowlConceded = 0, fours = 0, wickets = 0;
        const sc = cardsByMatch[m.id];
        if (sc?.balls_data) {
          for (const key of Object.keys(sc.balls_data)) {
            for (const b of sc.balls_data[key] || []) {
              const batter = (b.batter || '').toLowerCase().trim();
              const bowler = (b.bowler || '').toLowerCase().trim();
              const meName = (me.name || '').toLowerCase().trim();
              if (batter === meName) { batRuns += b.runs || 0; if (b.runs === 4) fours += 1; }
              if (bowler === meName) { bowlConceded += b.runs || 0; if (b.type === 'wicket') wickets += 1; }
            }
          }
        }
        // 2026-07-07: apply -5-per-wicket indoor cricket bonus (matches
        // match-report contribution formula).
        const contribution = batRuns - bowlConceded + (5 * wickets);
        weekContribution += contribution;
        weekRuns += batRuns; weekWickets += wickets; weekBoundaries += fours;
        const onHome = matchAtt.find(a => a.match_id === m.id);
        matchScores.push({ matchId: m.id, contribution, runs: batRuns, wickets, scheduled_at: m.scheduled_at, sport: m.competition?.sport?.icon || '' });
      }
      matchScores.sort((a, b) => b.contribution - a.contribution);
      const bestMatch = matchScores[0] || null;

      // Skill-badge-style auto highlights (lightweight; full logic lives in
      // the main detail loader for the trading-card export)
      const highlights = [];
      if (weekContribution > 0) highlights.push({ emoji: '', label: 'POSITIVE WEEK', value: `+${weekContribution} net contribution` });
      if (weekBoundaries >= 3) highlights.push({ emoji: '', label: 'BOUNDARY BEAST', value: `${weekBoundaries} fours this week` });
      if (weekWickets >= 2) highlights.push({ emoji: '', label: 'WICKET TAKER', value: `${weekWickets} wickets this week` });
      if (myMatches.length >= 3) highlights.push({ emoji: '', label: 'IRON PLAYER', value: `${myMatches.length} games in 7 days` });

      // Career snapshot used for the trading card hand-off
      const cardSnapshot = {
        id: me.id,
        name: me.name,
        avatar: (me.name || '').split(/\s+/).map(n => n[0]).join('').slice(0,2).toUpperCase(),
        games: myMatches.length,
        winRate: null,
        careerContribution: weekContribution,
        cricketGamesPlayed: cricketIds.length,
        avgContribution: cricketIds.length ? weekContribution / cricketIds.length : null,
        sports: [...new Set(myMatches.map(m => m.competition?.sport?.name).filter(Boolean))],
        skillBadges: highlights.map(h => ({ emoji: h.emoji, name: h.label, desc: h.value, color: '#10b981' })),
        sparkline: matchScores.slice(0, 10).reverse().map(m => m.contribution),
      };

      setData({
        me, myMatches, weekContribution, weekRuns, weekWickets, weekBoundaries,
        bestMatch, highlights, matchScores, cardSnapshot,
        dateLabel: `${start.toLocaleDateString('en-AU', { day:'numeric', month:'short' })} – ${end.toLocaleDateString('en-AU', { day:'numeric', month:'short' })}`,
      });
    })();
    return () => { cancelled = true; };
  }, [playerId]);

  if (!data) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 350, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
        <div style={{ fontSize: '1rem' }}>Loading your week…</div>
      </div>
    );
  }

  if (!data.myMatches.length) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 350, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
        onClick={onClose}>
        <div onClick={e => e.stopPropagation()} style={{ background: 'linear-gradient(135deg, #1a1a2e, #0d4a42)', color: '#fff', borderRadius: 20, padding: 32, maxWidth: 380, textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: 12 }}></div>
          <div style={{ fontSize: '1.4rem', fontWeight: 900, marginBottom: 6 }}>No games this week</div>
          <div style={{ opacity: 0.7, fontSize: '0.9rem', marginBottom: 16 }}>Sit one out, comeback bigger. Check fixtures for next week.</div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', padding: '10px 24px', borderRadius: 10, fontWeight: 800, cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    );
  }

  // ── Build the panels ─────────────────────────────────────────────────
  const panels = [];
  // 1. Cover
  panels.push({ bg: 'linear-gradient(135deg, #ff4d00 0%, #fbbf24 100%)', content: (
    <>
      <div style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: 4, opacity: 0.8 }}>WILDCATZ · {data.dateLabel.toUpperCase()}</div>
      <div style={{ fontSize: '3.2rem', fontWeight: 900, lineHeight: 1, margin: '20px 0 12px', textShadow: '0 4px 24px rgba(0,0,0,0.3)' }}>YOUR<br/>WEEK</div>
      <div style={{ fontSize: '1.6rem', fontWeight: 800, marginTop: 16 }}>{data.me.name}</div>
      <div style={{ marginTop: 24, fontSize: '0.85rem', opacity: 0.85 }}>Tap to start →</div>
    </>
  )});
  // 2. Games played
  panels.push({ bg: 'linear-gradient(135deg, #ff2e17 0%, #0d4a42 100%)', content: (
    <>
      <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: 3, opacity: 0.7 }}>YOU SHOWED UP</div>
      <div style={{ fontSize: 'clamp(6rem, 30vw, 12rem)', fontWeight: 900, lineHeight: 0.9, margin: '20px 0', color: '#fbbf24' }}>{data.myMatches.length}</div>
      <div style={{ fontSize: '1.4rem', fontWeight: 800 }}>game{data.myMatches.length === 1 ? '' : 's'} this week</div>
      {data.weekRuns > 0 && <div style={{ marginTop: 16, fontSize: '0.95rem', opacity: 0.85 }}>{data.weekRuns} runs · {data.weekWickets} wicket{data.weekWickets === 1 ? '' : 's'} · {data.weekBoundaries} four{data.weekBoundaries === 1 ? '' : 's'}</div>}
    </>
  )});
  // 3. Contribution
  panels.push({ bg: data.weekContribution >= 0 ? 'linear-gradient(135deg, #10b981 0%, #065f46 100%)' : 'linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%)', content: (
    <>
      <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: 3, opacity: 0.7 }}>NET CONTRIBUTION</div>
      <div style={{ fontSize: 'clamp(6rem, 30vw, 12rem)', fontWeight: 900, lineHeight: 0.9, margin: '20px 0', textShadow: '0 4px 32px rgba(0,0,0,0.3)' }}>{data.weekContribution >= 0 ? '+' : ''}{data.weekContribution}</div>
      <div style={{ fontSize: '1.2rem', fontWeight: 700, opacity: 0.9 }}>{data.weekContribution >= 0 ? 'in the green ' : 'tough week — bounce back time'}</div>
      <div style={{ marginTop: 14, fontSize: '0.9rem', opacity: 0.75 }}>{data.weekRuns} runs scored vs {data.weekRuns - data.weekContribution} conceded</div>
    </>
  )});
  // 4. Best moment
  if (data.bestMatch) {
    panels.push({ bg: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)', content: (
      <>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: 3, opacity: 0.7 }}>YOUR BEST GAME</div>
        <div style={{ fontSize: '4rem', margin: '14px 0' }}>{data.bestMatch.sport}</div>
        <div style={{ fontSize: 'clamp(4rem, 22vw, 8rem)', fontWeight: 900, lineHeight: 0.9, color: '#fbbf24' }}>{data.bestMatch.contribution >= 0 ? '+' : ''}{data.bestMatch.contribution}</div>
        <div style={{ marginTop: 12, fontSize: '1.05rem', fontWeight: 700 }}>{data.bestMatch.runs} runs · {data.bestMatch.wickets} wicket{data.bestMatch.wickets === 1 ? '' : 's'}</div>
        <div style={{ marginTop: 10, fontSize: '0.85rem', opacity: 0.8 }}>{new Date(data.bestMatch.scheduled_at).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short' })}</div>
      </>
    )});
  }
  // 5. Highlights
  if (data.highlights.length) {
    panels.push({ bg: 'linear-gradient(135deg, #1a1a2e 0%, #0d4a42 100%)', content: (
      <>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: 3, opacity: 0.7 }}>YOUR HIGHLIGHTS</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 24, width: '100%' }}>
          {data.highlights.map((h, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.08)', borderLeft: '4px solid #10b981', padding: '14px 18px', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ fontSize: '2.2rem' }}>{h.emoji}</div>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{ fontSize: '0.95rem', fontWeight: 900, color: '#10b981', letterSpacing: 1 }}>{h.label}</div>
                <div style={{ fontSize: '0.78rem', opacity: 0.75, marginTop: 2 }}>{h.value}</div>
              </div>
            </div>
          ))}
        </div>
      </>
    )});
  }
  // 6. Share
  panels.push({ bg: 'linear-gradient(135deg, #fbbf24 0%, #ff4d00 100%)', content: (
    <>
      <div style={{ fontSize: '0.8rem', fontWeight: 800, letterSpacing: 4, opacity: 0.85 }}>SEND IT</div>
      <div style={{ fontSize: '2.6rem', fontWeight: 900, lineHeight: 1, margin: '20px 0' }}>FLEX IT<br/>TO YOUR<br/>MATES</div>
      <button onClick={() => onShareCard && onShareCard(data.cardSnapshot)}
        style={{ marginTop: 24, padding: '16px 32px', background: '#1a1a2e', color: '#fbbf24', border: 'none', borderRadius: 14, fontSize: '1.05rem', fontWeight: 900, letterSpacing: 1, cursor: 'pointer', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}>
         GET YOUR CARD
      </button>
      <div style={{ marginTop: 22, fontSize: '0.8rem', opacity: 0.75 }}>all4indoor.com/?wrap={data.me.id}</div>
    </>
  )});

  const goNext = () => setPanel(p => Math.min(p + 1, panels.length - 1));
  const goPrev = () => setPanel(p => Math.max(p - 1, 0));
  const isLast = panel === panels.length - 1;

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 350 }}>
      {/* Progress bars (Instagram Story style) */}
      <div style={{ position: 'absolute', top: 12, left: 12, right: 12, display: 'flex', gap: 4, zIndex: 5 }}>
        {panels.map((_, i) => (
          <div key={i} style={{ flex: 1, height: 3, background: 'rgba(255,255,255,0.25)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: i < panel ? '100%' : i === panel ? '100%' : '0%', background: '#fff', transition: 'width 0.3s' }} />
          </div>
        ))}
      </div>
      {/* Close */}
      <button onClick={onClose} style={{ position: 'absolute', top: 32, right: 16, zIndex: 5, background: 'rgba(0,0,0,0.4)', border: 'none', color: '#fff', width: 38, height: 38, borderRadius: '50%', cursor: 'pointer', fontSize: '1.3rem' }}></button>

      {/* Tap zones — left half goes back, right half goes forward */}
      <div onClick={goPrev} style={{ position: 'absolute', top: 0, left: 0, width: '35%', height: '100%', zIndex: 3, cursor: 'pointer' }} />
      <div onClick={goNext} style={{ position: 'absolute', top: 0, right: 0, width: '65%', height: '100%', zIndex: 3, cursor: 'pointer' }} />

      {/* Panel */}
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: panels[panel].bg, color: '#fff', textAlign: 'center',
        padding: '40px 32px', transition: 'background 0.4s',
      }}>
        {panels[panel].content}
        {!isLast && (
          <div style={{ position: 'absolute', bottom: 24, fontSize: '0.7rem', opacity: 0.5, letterSpacing: 1 }}>
            {panel + 1} / {panels.length}  ·  tap →
          </div>
        )}
      </div>
    </div>
  );
}

// SVG stat tile used inside the trading card. Plain rect + 3 text lines.
function StatTileSvg({ x, y, w, h, label, value, sub, accent }) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      <rect width={w} height={h} rx="16" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
      <rect x="0" y="0" width="6" height={h} rx="3" fill={accent} />
      <text x="30" y="42" fontSize="20" fontWeight="700" fill="rgba(255,255,255,0.55)" letterSpacing="3" style={{ fontFamily: 'system-ui, sans-serif' }}>{label}</text>
      <text x="30" y="124" fontSize="78" fontWeight="900" fill={accent} style={{ fontFamily: 'system-ui, sans-serif' }}>{value}</text>
      <text x="30" y="160" fontSize="18" fontWeight="600" fill="rgba(255,255,255,0.45)" style={{ fontFamily: 'system-ui, sans-serif' }}>{sub}</text>
    </g>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// LEADERBOARD CARD — one per active sport on the home page. Top 10 players
// over the last 14 days, ranked by the sport's headline stat. Each row clicks
// through to the player profile so users can dig in.
// ───────────────────────────────────────────────────────────────────────────
function LeaderboardCard({ sport, rows, accent, onOpenPlayer }) {
  const fmtStat = (n) => sport.stat === 'contribution' ? `${n >= 0 ? '+' : ''}${n}` : `${n}`;
  const statTooltip = {
    contribution: 'Contribution = (runs you batted) - (runs you conceded bowling) + 5 per wicket. Positive numbers = you carried your side. Indoor-cricket standard.',
    goals:        'Career goals scored in this sport.',
    wins:         'Match wins in the last 14 days.',
  }[sport.stat];

  // 2026-08-06 (Jake): "this part of all4indoor lacks polish".
  //
  // "Wildcatz Indoor Sports" was printed on EVERY row of ALL FOUR columns —
  // forty repetitions of one value that is identical on every line. It is most
  // of the noise on that screen, and it carries no information at all while
  // there is one venue. Show it only when the column actually spans more than
  // one, which is the only time it distinguishes anything.
  const venues = new Set(rows.slice(0, 10).map(r => r.venue).filter(Boolean));
  const showVenue = venues.size > 1;

  return (
    // Was `background: '#fff'` — a white card on a black page, which is the
    // single reason this section read as unfinished next to everything around
    // it. On the surface ramp now, with the sport colour as a top edge rather
    // than a gradient block: same at-a-glance column identity, a lot less shout.
    <div style={{ background: C.surface, border: `1px solid ${C.line}`, borderRadius: R.lg, overflow: 'hidden' }}>
      <div style={{ height: 3, background: sport.tone }} />
      <div style={{ padding: '13px 15px 10px' }}>
        <div style={{ fontSize: '0.95rem', fontWeight: 900, color: sport.tone, fontFamily: "'Archivo', sans-serif" }}>
          {sport.label}
        </div>
        <div title={statTooltip || undefined}
             style={{ fontSize: '0.65rem', color: C.ink3, letterSpacing: 1, textTransform: 'uppercase',
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      cursor: statTooltip ? 'help' : 'default', marginTop: 2 }}>
          {/* 2026-08-06: was the abbreviation "CONTRIB", which is jargon where
              a word fits perfectly well. */}
          Top 10 &middot; {sport.stat === 'contribution' ? 'contribution' : sport.statLabel.toLowerCase()}
          {statTooltip && <span style={{ opacity: 0.7 }}>&#9432;</span>}
        </div>
      </div>
      <div style={{ padding: '0 6px 6px' }}>
        {rows.slice(0, 10).map((r, idx) => {
          const stat = r[sport.stat] || 0;
          const isTop = idx === 0;
          const pod = podium(idx + 1);
          return (
            <div key={r.id || r.name}
              onClick={() => onOpenPlayer && r.id && onOpenPlayer(r.id)}
              style={{ display: 'grid', gridTemplateColumns: '24px 1fr auto', alignItems: 'center', gap: 8,
                       padding: '7px 9px', borderRadius: R.sm, cursor: r.id ? 'pointer' : 'default',
                       background: isTop ? 'rgba(255,255,255,0.035)' : 'transparent',
                       transition: `background ${M.fast}` }}
              onMouseEnter={e => { if (r.id) e.currentTarget.style.background = C.raised; }}
              onMouseLeave={e => { e.currentTarget.style.background = isTop ? 'rgba(255,255,255,0.035)' : 'transparent'; }}>
              {/* 2026-08-06: the first three positions rendered EMPTY STRINGS.
                  The ternary read `idx === 0 ? '' : idx === 1 ? '' : idx === 2 ? ''
                  : '#'+(idx+1)` — medal emoji had been stripped out at some point
                  and left three blanks behind, so the top three had no rank at all
                  while 4-10 did. Gold, silver and bronze now, matching the team
                  ladder, which already did it properly. */}
              <div style={{ fontSize: '0.72rem', fontWeight: 800, textAlign: 'center',
                            color: idx < 3 ? pod.bg : C.ink3, ...NUM }}>
                {idx + 1}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '0.85rem', fontWeight: isTop ? 800 : 600, color: C.ink,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                {(r.games > 0 || (showVenue && r.venue)) && (
                  <div style={{ fontSize: '0.62rem', color: C.ink3, marginTop: 1,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.games > 0 ? `${r.games} game${r.games === 1 ? '' : 's'}` : ''}
                    {r.games > 0 && showVenue && r.venue ? ' · ' : ''}
                    {showVenue ? (r.venue || '') : ''}
                  </div>
                )}
              </div>
              {/* Tabular figures: +110 / +91 / +75 down a column shimmer out of
                  alignment without them, which is most of why a stats table
                  reads as amateur. */}
              <div style={{ fontSize: '1rem', fontWeight: 900, color: sport.tone, textAlign: 'right',
                            minWidth: 40, fontFamily: "'Archivo', sans-serif", ...NUM }}>
                {fmtStat(stat)}
              </div>
            </div>
          );
        })}
        {rows.length === 0 && (
          <div style={{ padding: '14px', textAlign: 'center', color: C.ink3, fontSize: '0.8rem' }}>No matches yet this fortnight</div>
        )}
      </div>
    </div>
  );
}
